import type { PhoneNumber } from 'libphonenumber-js/types';

/**
 * SHARED SCHEMA — single source of truth for types used by both the server
 * and the customer dashboard.
 *
 * This file exists as an EXACT copy in two repos:
 *   - webot-server:                  server/types/sharedSchema.ts   (canonical)
 *   - webot-ui-customer-dashboard:   src/@types/sharedSchema.ts     (copy)
 *
 * Any edit here MUST be copied verbatim to the other repo (byte-for-byte —
 * don't reformat). Client-only divergences (e.g. Date fields arriving as ISO
 * strings) belong in the dashboard's `src/@types/signatures.ts`-style
 * override files, never in this file.
 */

export type BaseDocument = {
  id: string;
  path?: string;
  createdTime: Date;
  updatedTime: Date;
  skipUpdatedTimeAroundDate?: Date; // can be set to current date, to prevent updatedTime update around this date
};

export const ADMINS_COLLECTION_NAME = 'dashboard-admins';
export const ADMINS_DOCUMENT_ID = 'global-admins-list';
export type AdminsDocument = Partial<BaseDocument> & {
  adminEmails: string[];
};

/**
 * DB explorer (owner-only Postgres console, /dashboard/admin/db). Both repos
 * gate on this exact list — the server 403s anyone else, the dashboard hides
 * the page and redirects. Deliberately NOT the `dashboard-admins` allowlist:
 * raw database access (including arbitrary SQL) is a strictly tighter circle
 * than webot-admin, and widening it should be a reviewed code change, not a
 * Firestore edit.
 */
export const DB_EXPLORER_ALLOWED_EMAILS = ['haimbendanan@gmail.com'] as const;

/**
 * Sales map (מפת משרדים, /dashboard/admin/sales-map): every Israeli agency and realtor with their Webot billing
 * status, plus live GoHighLevel reads and writes. Both repos gate on this exact list — the server rejects everyone
 * else on every route, the dashboard hides the page. Deliberately NOT the `dashboard-admins` allowlist: the data is
 * every realtor's phone number and every customer's payment history, and the page writes to our GoHighLevel CRM.
 * Widening it is a reviewed code change (a test pins the list), never a Firestore edit.
 */
export const SALES_MAP_ALLOWED_EMAILS = [
  'haimbendanan@gmail.com',
  'yarden1040@gmail.com',
  'tal.abksis@gmail.com'
] as const;

/**
 * Sales training portal (פורטל המכירות, /dashboard/admin/sales-training): the onboarding and daily-work portal for sales
 * reps, built from recorded sales calls (customer voices, transcripts) and our pricing and sales policy. Both repos
 * gate on this exact list — the server rejects everyone else on every route, the dashboard hides the page.
 * Deliberately a SEPARATE list from SALES_MAP_ALLOWED_EMAILS, and never the admin list. Widening it is a reviewed code
 * change (a test pins the list), never a Firestore edit. Since 2026-10-27 it is the owners only (Haim: "keep this sales
 * training only for me and yarden"): reps learn from the simpler portal v2 (/dashboard/admin/sales-training-v2), whose
 * readers the owners add from that page.
 */
export const SALES_TRAINING_ALLOWED_EMAILS = ['haimbendanan@gmail.com', 'yarden1040@gmail.com'] as const;

/**
 * The portal's owners (Haim, Yarden) — a subset of SALES_TRAINING_ALLOWED_EMAILS. The server serves an owners' build
 * (with the review tab) to them only, and the dashboard shows them the owner-only sections of the rep build (e.g. the
 * numbers behind the portal). They are also the owners of portal v2: they always read it and they alone decide who else
 * may (the readers are rows in the crm database, never this list). A test pins it.
 */
export const SALES_TRAINING_OWNER_EMAILS = ['haimbendanan@gmail.com', 'yarden1040@gmail.com'] as const;

/**
 * Sales recruiting (מיון מועמדים למכירות, /dashboard/admin/recruiting): the job applicants for the sales team — their
 * phones, what they answered in the screening form, and the reviewers' notes and verdicts — plus actions that message
 * them over WhatsApp. Both repos gate on this exact list — the server rejects everyone else on every route, the
 * dashboard hides the page. Its OWN list, not the sales map's and not the training portal's: the sales reps hold both
 * of those, and who applied for a job on their team, and what we think of each one, is not theirs to read. Widening it
 * is a reviewed code change (a test pins the list), never a Firestore edit.
 */
export const SALES_RECRUITING_ADMIN_EMAILS = [
  'haimbendanan@gmail.com',
  'yarden1040@gmail.com',
  'keshetmarzan@gmail.com'
] as const;

export const OnboardingSteps = [
  'phoneNumber',
  'userRolePick',
  'realEstateRolePick',
  'featuresPicker',
  'test'
] as const;
export type OnboardingStep = typeof OnboardingSteps[number];

export enum BulkMessagingTier {
  free = 'free',
  paid = 'paid'
}

export enum CrmType {
  realEstate = 'realEstate',
  generic = 'generic'
}

export const CallTranscriptionScenarios = ['callWithRealtor', 'callWithSalesRep'] as const;
export type CallTranscriptionScenario = typeof CallTranscriptionScenarios[number];

// Retired 2026-09-11 and removed from this list: 'crmPreview', 'reports'. Old user docs
// still carry them, left in place on purpose — they gate nothing, and the admin users
// screen keeps them out of its draft. Mirrored from the server copy.
export const AdvancedFeatures = [
  'bulkMessaging',
  'listingsMatch',
  'listingsNotifications',
  'simpleQuestionResponseBot',
  'signatures',

  // the AI agent
  'ai',
  'calendar',
  'listings',
  // Both contacts pages — /dashboard/contacts and the new /dashboard/crm.
  'crm',
  // Facebook group/page lead scraping (see docs/facebook-scraping-design.md).
  // Preview feature — gates the settings section, the sidebar entry, and the
  // /facebook-leads route so it stays hidden until explicitly granted.
  'facebookLeads',
  // Grants a monthly coin allocation for the AI creatives generator. Named for the
  // coins grant, not the creatives feature itself.
  'ai-coins-monthly',
  // Per-customer Google Calendar two-way sync (see docs/rules/google-calendar-sync.md).
  // Preview feature — gates the connect card and the Google section of the calendar
  // settings. Without it the calendar page looks exactly as it did before the
  // integration shipped. Also the rollout throttle: an unverified Google OAuth app
  // is capped at ~100 granted users, and ~490 customers have the calendar feature.
  // Don't drop it before Google Auth Platform shows the app VERIFIED: 62 of the 100
  // lifetime grants were already used on 2026-09-11, and the cap never resets.
  'googleCalendarSync',
  // Official WhatsApp Business Platform integration. Preview feature - gates the connect card,
  // the sidebar entry and every /api/v1/whatsapp customer route.
  'officialWhatsapp',
  // Automatic posting of the realtor's listings into their Facebook groups, through the
  // Webot posting extension (see docs/rules/fb-group-posting.md in webot-server). Gates the
  // page, the sidebar entry and every /api/v1/fb-posting route, the extension's included.
  'facebookGroupPosting'
] as const;
export type AdvancedFeature = typeof AdvancedFeatures[number];

export const mobileAlertsListingType = ['none', 'onePerDay', 'forAllListings'] as const;
export type MobileAlertsListingType = typeof mobileAlertsListingType[number];

export const CallTranscriptionLanguages = ['he', 'ar', 'ru', 'fr', 'en'] as const;
export type CallTranscriptionLanguage = typeof CallTranscriptionLanguages[number];

/**
 * Policy for how the AI chat handles state-mutating MCP tools (add tag, create
 * task, add to minisite, etc.).
 *  - 'confirm': The AI is told NOT to call mutation tools directly. Instead it
 *    proposes mutations as inline action buttons the user clicks to commit.
 *    Highest user trust; nothing changes without an explicit click.
 *  - 'auto': The AI may call mutation tools directly when confident. Action
 *    buttons appear only as follow-ups / undo affordances.
 *  - 'off': Mutation tools are not surfaced at all. Read-only chat experience.
 */
export type ChatActionPolicy = 'confirm' | 'auto' | 'off';

/**
 * Per-user preferences for the AI chat at /dashboard/ai. Stored as a nested
 * field on the user doc so it syncs across devices. All fields optional —
 * undefined means "use the default".
 */
export interface ChatPreferences {
  /**
   * When true, the AI is instructed to emit rich inline components
   * (<webot-listing/>, <webot-contact/>, <webot-action/>, etc.) and richer
   * artifact types alongside its text reply. When false, replies are plain
   * markdown only. Default: true.
   */
  richComponentsEnabled?: boolean;

  /** See ChatActionPolicy. Default: 'confirm'. */
  actionPolicy?: ChatActionPolicy;
}

/** Subtle decorative overlay presets for the minisite hero background. */
export const MINISITE_HERO_PATTERNS = ['none', 'dots', 'waves', 'grid', 'diagonal'] as const;
export type MinisiteHeroPattern = typeof MINISITE_HERO_PATTERNS[number];

export const CUSTOMER_SUCCESS_STATUSES = [
  'happy',
  'needsFollowUp',
  'unhappy',
  'didNotAnswer',
  'needToTryToCallAgain',
  'cancelledSubscription'
] as const;
export type CustomerSuccessStatus = typeof CUSTOMER_SUCCESS_STATUSES[number];

export const USERS_COLLECTION_NAME = 'dashboard-users-v2';
export type User = Partial<BaseDocument> & {
  // email: string;
  photoURL: string;
  ownerFullName: string;
  ownerPhoneNumber?: string; // international format
  onboardingCompleted?: boolean;
  agreedToTerms?: boolean; // whether the user accepted the terms of service during onboarding
  agreedToTermsAt?: Date; // when the user accepted the terms of service

  organizationIds?: string[]; // list of organization IDs the user belongs to. Populated when the dashboard-orgs objects changes

  userImageUrl: string; // the image of the user, used in creatives and other relevant places
  companyLogoUrl: string; // the logo of the user's company, used in creatives and other relevant places
  signatureImageUrl?: string; // the realtor's presaved signature image, stamped on outgoing signature documents when a field requests `realtor.signature_image`
  signatureImageMode?: 'drawn' | 'uploaded'; // how signatureImageUrl was captured (drawn on a pad vs uploaded image). UI-only; both are the same hosted image downstream. Defaults to 'drawn'.
  companyId?: string; // Israeli ח.פ / company registration number, stamped on signature documents when a field requests `realtor.company_id`
  realtorLicense?: string; // Israeli מס' רישיון תיווך / brokerage license number, stamped on signature documents when a field requests `realtor.license`

  onboardingStep?: OnboardingStep;
  interestedWebotFeatures?: string[];
  realEstateRole?: string;
  ownerFirstName?: string;
  companyName?: string;

  virtualNumbers: string[];
  featureFlagsV2?: string[];

  apiToken: string;
  bulkMessagingTier?: BulkMessagingTier;
  crmType?: CrmType;

  /** Per-user preferences for the AI chat surface (/dashboard/ai). */
  chatPreferences?: ChatPreferences;

  /** Signature template ids this user has favorited from the marketplace. */
  favoriteSignatureTemplateIds?: string[];
  /** Signature template-bundle ids this user has favorited from the marketplace. */
  favoriteSignatureBundleIds?: string[];
  /**
   * Per-user overrides of the Webot-wide default signature templates, keyed by
   * signing scenario ({@link SignatureDefaultTemplateKeys}). A key
   * present here wins over the global default; an absent key falls back to it.
   */
  signatureDefaultTemplates?: SignatureDefaultTemplates;

  advancedFeatures?: AdvancedFeature[];

  /**
   * Promo/announcement banners the user has closed, keyed by banner id (the
   * dashboard's `PROMO_BANNER_IDS`) → when they closed it. Lives on the user
   * doc rather than localStorage so a dismissal follows the realtor across
   * devices and browsers, and so we can see what people actually close.
   * The timestamp (rather than a bare `true`) is what lets a campaign decide
   * to re-show after N days without needing another field.
   */
  dismissedBanners?: Record<string, Date>;

  /**
   * Admin-set surveillance flag (managed from the admin users screen, never
   * user-editable). When true:
   *  - the dashboard turns ON PostHog session recording for this user's sessions
   *    (recording is off for everyone else)
   *  - the server WhatsApps the webot admins whenever the user connects back to
   *    the app, deduped to at most one alert per hour (trackedUserAlert.service.ts)
   * Absent/false = normal user: no recording, no alerts.
   */
  posthogTrackingEnabled?: boolean;

  interestedInLocations?: ProcessedListingAddress[]; // relevant only for realtors
  interestedInCitiesDistinct?: string[]; // auto generated from interestedInLocations
  interestedInNeighborhoodsDistinct?: string[]; // auto generated from interestedInLocations

  alertsOnNewListings_onAdTypes?: AdType[]; // default if not set is ["private", "commercial"]
  alertsOnNewListings_onListedForTypes?: ListingListedFor[]; // which markets to get listing alerts for (new listings + price reductions). default if not set is ["sale"]
  alertsOnNewListings_mobileAlertsSettings?: MobileAlertsListingType; // default if not set is "forAllListings"

  wantToReceiveAlertsOnNewListingInMyInterestedLocations?: boolean; // default if not set is true
  wantToReceiveAlertsOnNewListingsThatMatchMyContacts?: boolean; // default if not set is true
  wantToReceiveAlertsOnOwnListingsThatMatchContactsInMyOrgs?: boolean; // default if not set is true
  wantToReceiveAlertsOnListingPriceReductions?: boolean; // default if not set is true
  wantToReceiveAlertsOnMyListingExclusivityExpiry?: boolean; // WhatsApp reminders 14 + 7 days before a listing's exclusivity ends. default if not set is true
  wantToReceiveAlertsOnMyListingLeaseEnding?: boolean; // WhatsApp reminders 60 + 30 days before the rental agreement on a rented listing ends. default if not set is true

  additionalInternalCustomerData?: string;

  /**
   * The realtor's saved pacing for bulk WhatsApp sends — the throttle the Chrome
   * extension applies between messages, plus the optional cool-down after every N.
   *
   * Stored on the user rather than kept per-send because it is a property of how
   * cautious THIS account wants to be with its WhatsApp number, not of the batch
   * being sent. Before this field existed the controls lived in local state inside
   * `NewBulkMessageDialog`, so every send reset to `DEFAULT_DELAY_SETTINGS` and a
   * realtor who had deliberately slowed their sending down silently got the fast
   * default back the next time.
   *
   * Absent = the defaults, which is what the dashboard falls back to. The server
   * does not read this: pacing is enforced by the extension, from the operation
   * document the dashboard writes.
   */
  bulkMessageDelaySettings?: {
    delayMinSeconds: number;
    delayMaxSeconds: number; // never below delayMinSeconds; the UI clamps on blur
    cooldownEnabled: boolean;
    cooldownAfterCount: number;
    cooldownDurationSeconds: number;
  };

  callTranscriptionScenario?: CallTranscriptionScenario;
  callTranscriptionScenarioAdditionalPromptNotes?: string;
  callTranscriptionLanguages?: CallTranscriptionLanguage[]; // if not set, default is "he"
  callTranscriptionProvider?: 'legacy' | 'gemini'; // if not set, default is 'gemini' (single native-audio call); 'legacy' = speechmatics + openai

  customerSuccessStatus?: CustomerSuccessStatus;
  customerSuccessLastCallTime?: Date;
  customerSuccessDidFollowUp?: boolean;
  customerSuccessRepNotes?: string;
  lastSeenUpdateId?: number; // Track the last feature update ID the user has seen

  madlanTopAgentValue?: 'top_10' | 'top_10_gold' | null;
  madlanAgentId?: string;
  madlanAgentEmail?: string; // Madlan agent email (agentId)
  madlanAgentPassword?: string; // Madlan agent password

  yad2RealtorNames?: string[]; // realtor's name(s) as displayed on Yad2 listings
  yad2AgencyNames?: string[]; // realtor's agency name(s) as displayed on Yad2 listings
  madlanRealtorNames?: string[]; // realtor's name(s) as displayed on Madlan listings
  madlanAgencyNames?: string[]; // realtor's agency name(s) as displayed on Madlan listings

  ownerMetaPixelId?: string; // to be displayed on public mini-sites
  minisiteThemeColor?: string; // hex color for the minisite hero gradient background
  minisiteTextColor?: string; // hex color for text on the minisite hero banner
  /** @deprecated Legacy single bio field. Replaced by minisiteTagline (short) + minisiteAboutText (long). Still read as a fallback for users who never migrated. */
  minisiteCustomShortBio?: string; // short bio / about text displayed on the minisite hero
  minisiteTagline?: string; // short one-line phrase under the realtor's name on the minisite hero (e.g. "המתווך המוביל בחדרה והסביבה")
  minisiteAboutText?: string; // long about/description text for the minisite "about" section; markdown supported
  minisiteInstagramUrl?: string; // realtor's Instagram profile URL, shown on the minisite
  minisiteFacebookUrl?: string; // realtor's Facebook page URL, shown on the minisite
  minisiteAddress?: string; // realtor's office address, shown in the minisite footer (optional)
  minisiteHeroPattern?: MinisiteHeroPattern; // subtle decorative overlay on the minisite hero background (default: none)
  minisiteHeroImageUrl?: string; // realtor-uploaded hero background image, shown behind a theme-color overlay
  /**
   * @deprecated Dead since 2026-07 and now unreferenced by any reader or writer in either repo.
   * Kept only because historical user docs carry it. Do not reintroduce a per-user show/hide
   * toggle — see `watermarkTermsAcceptedAt`, which is the single gate.
   */
  minisiteShowWatermarkFreeImages?: boolean;
  /**
   * When the realtor confirmed — via the mandatory checkbox shown before every
   * watermark-removal run — that they hold the full rights to remove watermarks from
   * the listing photos and to market those properties. This stored timestamp IS the
   * proof of agreement; the checkbox is still rendered (pre-checked) on every later
   * removal as a re-affirmation, but the field is written once, on first acceptance.
   *
   * ⚠️ IT IS ALSO THE ONLY GATE ON SHOWING CLEAN PHOTOS (2026-09-30). Accept once, and every
   * listing that has watermark-free photos renders clean on all of this realtor's minisites,
   * forever, at no cost — including links already sent. It replaced the per-(user, listing)
   * `ProcessedListingData.watermarkRemovalRequestedBy` array, which gated nothing (the clean
   * images are uploaded `publicRead` with token-less URLs and pointed to from a world-gettable
   * collection), was a public roster of customer email addresses, and made a fully-clean listing
   * report "0 מתוך 12 תמונות נקיות" to anyone who had not personally run it.
   *
   * Written by the dashboard (`updateUserSettings`) and, for realtors who accepted in an older
   * client that never persisted it, stamped server-side after a successful removal run.
   */
  watermarkTermsAcceptedAt?: Date;

  /*
  if creativesPerMonth is null or undefined, consider this as 3 - we give 3 free creatives.

  if creativesPerMonthResetDay is null or undefined, 2 options:
  - if the user has at least one advancedFeature, we will use creativesPerMonthResetDay = user.createdTime
  - otherwise, the user will not get renewal of creatives (he should purchase)
  */
  /** @deprecated Replaced by the coins system (coins-packs / coins.service.ts). Read only by the migration script. */
  creativesPerMonthQuota?: number; // the number of creatives that the user can generate per month
  /** @deprecated Replaced by the coins system. Read only by the migration script. */
  creativesPerMonthQuotaResetDate?: Date; // the date when the quota resets

  // TODO deprecate all below
  /** @deprecated use organizationIds + dashboard-orgs admins instead */
  hasAccessToOtherUsers?: Array<string>;
  featureFlags?: {
    canViewNewContactsPageInMenu: boolean | null;
    hideDeleteLeadsButton: boolean | null;
    showAutoReplyBot: boolean | null;
    showCalendar: boolean | null;
  };
};

export type CallSummaryDailyAgregation = Partial<BaseDocument> & {
  ANSWER: number;
  NOANSWER: number;
  BUSY: number;
  CANCEL: number;
  IVR_HANGUP: number;
  CONGESTION: number;
};

/**
 * User fields the admin users screen may edit (POST /api/v1/admin/users/update).
 * The server validates every submitted field against these lists — anything not
 * listed is rejected, so the endpoint can never become a generic field writer.
 * The dashboard renders its editable-profile form from the same lists.
 */
export const ADMIN_EDITABLE_USER_TEXT_FIELDS = [
  'ownerFullName',
  'ownerFirstName',
  'ownerPhoneNumber',
  'companyName',
  'companyId',
  'realtorLicense',
  'madlanAgentEmail',
  'madlanAgentId',
  'additionalInternalCustomerData',
  'customerSuccessRepNotes'
] as const;
export type AdminEditableUserTextField = typeof ADMIN_EDITABLE_USER_TEXT_FIELDS[number];

export const ADMIN_EDITABLE_USER_BOOLEAN_FIELDS = [
  'onboardingCompleted',
  'wantToReceiveAlertsOnNewListingInMyInterestedLocations',
  'wantToReceiveAlertsOnNewListingsThatMatchMyContacts',
  'wantToReceiveAlertsOnOwnListingsThatMatchContactsInMyOrgs',
  'wantToReceiveAlertsOnListingPriceReductions',
  'wantToReceiveAlertsOnMyListingExclusivityExpiry',
  'wantToReceiveAlertsOnMyListingLeaseEnding'
] as const;
export type AdminEditableUserBooleanField = typeof ADMIN_EDITABLE_USER_BOOLEAN_FIELDS[number];

export const ADMIN_EDITABLE_USER_STRING_ARRAY_FIELDS = [
  'yad2RealtorNames',
  'yad2AgencyNames',
  'madlanRealtorNames',
  'madlanAgencyNames'
] as const;
export type AdminEditableUserStringArrayField =
  typeof ADMIN_EDITABLE_USER_STRING_ARRAY_FIELDS[number];

/** Enum-valued admin-editable fields, validated against their value lists (CUSTOMER_SUCCESS_STATUSES / CrmType). */
export const ADMIN_EDITABLE_USER_ENUM_FIELDS = ['customerSuccessStatus', 'crmType'] as const;
export type AdminEditableUserEnumField = typeof ADMIN_EDITABLE_USER_ENUM_FIELDS[number];

export type AdminEditableUserFields = Partial<
  Pick<
    User,
    | AdminEditableUserTextField
    | AdminEditableUserBooleanField
    | AdminEditableUserStringArrayField
    | AdminEditableUserEnumField
  >
>;

/** How much of ANOTHER member's contact record you see when you encounter one. */
export const crmPoolAccessLevels = ['none', 'basic', 'full'] as const;
export type CrmPoolAccess = typeof crmPoolAccessLevels[number];

/** Role within one agency: `admin` iff the email is in `Organization.admins`, else `member`. */
export const crmOrgRoles = ['admin', 'member'] as const;
export type CrmOrgRole = typeof crmOrgRoles[number];

/**
 * What one agency member may do. The whole permission vocabulary — four fields, no
 * grant/revoke arrays, no capability strings.
 *
 * `browse` is branded "מאגר משרדי" (the agency pool) in the UI.
 */
export type CrmMemberPermissions = {
  pool: CrmPoolAccess;
  /** May browse/search the WHOLE agency book, not just records they bump into. */
  browse: boolean;
  /** May download/export contacts. */
  export: boolean;
  /** May delete contacts. */
  delete: boolean;
  /**
   * May send BULK messages (WhatsApp campaigns) to contacts.
   *
   * Added because `export: false` was walkable around. That control is aimed at an agent
   * taking the agency's book on the way out — and a campaign takes the same book to the
   * same place, one message at a time, with the agency paying Meta for the privilege. As
   * of this writing the campaign path consults NO CRM permission and does NO owner
   * scoping: it accepts a bare list of phone strings, so an agent who can see a
   * colleague's contacts can message every one of them regardless of what `export` says.
   *
   * DEFAULT TRUE for both roles, like `export` and `delete`: bulk messaging is
   * unconditional today, so any other default would silently remove a capability every
   * agent has the moment this ships.
   *
   * ⚠️ VOCABULARY ONLY UNTIL THE CAMPAIGN PATH READS IT. A permission an agency can
   * toggle but nothing enforces is worse than no permission at all, because the switch
   * reads as a control. Render it accordingly until the server check lands.
   */
  message: boolean;
};

/** Agency-wide numeric caps. Absent = the server defaults (5000 / 500 / 2000). */
export type CrmOrgSettings = {
  maxExportRows: number;
  maxBulkDelete: number;
  /** How many contacts one bulk edit may write. Lower than the export cap on purpose. */
  maxBulkEdit: number;
};

/**
 * How an agency keeps its contacts (`docs/crm/contacts-postgres-authority-plan.md`, D4):
 *
 *   personal   every member has their own book (the default; an absent `crmBookMode`)
 *   shared     ONE record per person for the whole agency, under the Postgres book key
 *              `org:<orgId>`, with "my contacts" a holder relation beside it
 *   migrating  the switch INTO the pooled book is running — every write to a member's book
 *              or the office book is refused with a retry message until it finishes
 *   reversing  the switch back OUT is running — the same refusal
 *
 * A const tuple so the server's registry query and the dashboard's switch enumerate the
 * pooled modes from one list.
 */
export const CONTACT_BOOK_MODES = ['personal', 'shared', 'migrating', 'reversing'] as const;
export type ContactBookMode = typeof CONTACT_BOOK_MODES[number];

export const ORGANIZATIONS_COLLECTION_NAME = 'dashboard-orgs';
export type Organization = Partial<BaseDocument> & {
  admins: string[];
  users: string[];
  logo: string;
  name: string;
  featureFlagsV2?: string[];
  /**
   * Per-member CRM permission overrides, keyed by LOWERCASED EMAIL (== `User.id`).
   *
   * The permission model lives here, on the org document, rather than in Postgres —
   * three reasons, all measured:
   *  1. The roster (`users` / `admins`) is on this same document, so there is nothing to
   *     sync and no drift class. The Postgres control-plane tables were empty in
   *     production for exactly that reason.
   *  2. The CRM Cloud SQL instance is ZONAL with no HA. Authorization must not be less
   *     available than the data it guards.
   *  3. Membership was never actually JOINED in SQL — it is resolved per request and
   *     passed as `owner_user_id = ANY($members::text[])` — so Postgres residency bought
   *     the predicate nothing.
   *
   * An ABSENT entry means the role default, which the SERVER resolves and serves over the
   * wire — the browser deliberately holds no copy of the baseline, because this screen
   * stores values against it and a disagreeing copy would compute them wrong.
   *
   * ⚠️ READING THIS FIELD IN THE BROWSER DOES NOT MAKE IT ENFORCEMENT. It is here so the
   * type mirrors the server's byte for byte; every decision is made server-side.
   */
  crmPermissions?: Record<string, CrmMemberPermissions>;
  /** Agency-wide CRM caps. Absent = the server defaults. */
  crmSettings?: CrmOrgSettings;
  /**
   * The agency's contact-book mode (`ContactBookMode`); absent = `'personal'`. Written by the
   * SERVER only (the pooled-book flip and its reversal) — `firestore.rules` grants clients no
   * write on `dashboard-orgs` — and read on the server only through the pooled-books registry
   * (`crm/pooledBooks.ts`) and the scope resolver, never from a request. The two stamps say
   * when and by whom it last changed.
   */
  crmBookMode?: ContactBookMode;
  crmBookModeChangedAt?: Date;
  crmBookModeChangedBy?: string;
};

export const CONTACT_TAGS_COLLECTION_NAME = 'contact-tags';
export type ContactTag = Partial<BaseDocument> & {
  userId: string;
  distinctTags: string[];
};

// under dashboard-users-v2/<userId>/contacts
export const CONTACTS_SUBCOLLECTION_NAME = 'contacts';

/**
 * Per-contact change SIGNALS — the real-time layer of the contacts→Postgres program
 * (docs/crm/contacts-postgres-authority-plan.md, D2). One tiny document per changed contact:
 *
 *   dashboard-users-v2/{email}/contact-signals/{contactId}   a personal book
 *   dashboard-orgs/{orgId}/contact-signals/{contactId}       a pooled agency book
 *
 * `v` is `crm_contact.version`, the counter every write bumps; `at` is when the signal was
 * written; `deleted` marks a contact that is gone. The server's outbox drain is the ONLY
 * writer; clients only read. An open card or panel listens to its contact's one document and
 * refetches `POST /contacts/get` when `v` exceeds the version it holds — so a change made by a
 * call transcription, a webhook, Daniela or a colleague reaches the screen in about half a
 * second with no socket, and a contact that did not change costs nothing. Distinct documents
 * on purpose: Firestore's per-document write limit never applies to a busy book.
 *
 * `at` is a `Date` on the server (the admin converter turns Timestamps into Dates); the web
 * SDK hands the dashboard a Timestamp, which the signal hook converts before comparing.
 */
export const CONTACT_SIGNALS_SUBCOLLECTION_NAME = 'contact-signals';
export type ContactSignalDocument = { v: number; at: Date; deleted?: true };

/**
 * Which LEGACY dashboard table asked for the whole contact book
 * (`POST /api/v1/get-data/all-contacts`, body `{ source }`).
 *
 * Every screen except these reads the Postgres mirror through a small query; the two that
 * still render the whole book in the browser name themselves, so the download meter
 * (`contacts_whole_book_rows{source}`) can say which one is keeping the endpoint alive.
 * A closed list, because the value becomes a metric LABEL — an unknown one reads `unknown`.
 */
export const WHOLE_BOOK_DOWNLOAD_SOURCES = ['contacts_page', 'send_wizard'] as const;
export type WholeBookDownloadSource = typeof WHOLE_BOOK_DOWNLOAD_SOURCES[number];
export const wholeBookDownloadSource = (value: unknown): WholeBookDownloadSource | 'unknown' =>
  (WHOLE_BOOK_DOWNLOAD_SOURCES as readonly unknown[]).includes(value) ? (value as WholeBookDownloadSource) : 'unknown';
export type BaseContact = Partial<BaseDocument> & {
  contactOrigin: string;
  isInSpamDatabase?: boolean;
  lastBulkMessageReceivedTime?: string;
  _metadata?: any;
};

export type GenericContact = BaseContact & {
  callerPhoneNumber?: string; // this should be international format, but for some contacts, it is local format. Need to find why (there is a task) and backfill
  callerFirstName?: string;
  callerLastName?: string;
  mail?: string; // contact email address (source for the `contact.email` signature prefill)
  additionalPhoneNumbers?: string[]; // secondary phone numbers beyond the primary callerPhoneNumber
  tehudatZehut?: string; // Israeli national ID number (source for the `contact.id_number` signature prefill)
  address?: string; // כתובת הלקוח — the contact's own free-text address (source for the `contact.address` signature prefill + seller-questionnaire owner prefill; captured fill-if-empty from signed documents)
  additionalNotes?: string; // deprecated - use user-notes collection instead
  /**
   * A MIRROR, not the source of truth (2026-09). Derived from the personal `whatsapp-suppressions`
   * row: true ⇔ a `suppressAll` row exists for (owner, number). Kept because Firestore and the
   * CRM's Postgres mirror filter on it for free. Any writer may set it (the contact form, imports,
   * the extension); the server's contact write path and the contacts-sync drain reconcile the row
   * from it, and a row the flag did not create wins over an untick — the blocked page is the only
   * place such a row is lifted. See `decideContactBlockSync` in the server.
   */
  optedOutOfBulkMessages?: boolean;
  contactOrigin: string;
  contactOriginAdditionalInfo?: string;
  bulkMessagingNumberDoesntExists?: boolean; // set automatically after attempting to send a bulk message to this contact, and the number doesn't exist
  lastBulkMessageReceived?: {
    mediaLink: string;
    message: string;
    sentTime: string;
    sentTo: string;
    updatedFrom: string;
  };

  // AUTO GENERATED BY FUNCTION
  contactId?: string; // used for contacts lookups - the contact international phone number
  contactFullName?: string; // full name of the contact, if available
  userId?: string; // used for contacts lookups - the user mail
  organizationIds?: string[]; // used for contacts lookups -the organizations the contact is associated with

  /**
   * A TOMBSTONE from the soft-delete era. Nothing writes this any more.
   *
   * `POST /api/v1/contacts/delete` used to write it and leave the document in place, which
   * kept every legacy Firestore reader — the old contacts page, the Cloud Functions, the
   * MCP tools — showing a contact the realtor had deleted. It now archives the whole
   * document into `crm_contact_audit_payload` and REMOVES it; the archive is written first
   * and a failure there refuses the delete, which is what replaces the tombstone as the
   * recovery story (Firestore has PITR disabled and no backup schedules).
   *
   * The field stays because the tombstones do. The list still filters on
   * `deleted_at_ms IS NULL` — the five ordered feed indexes are PARTIAL on it — and the
   * bulk edit still refuses to write one. CLIENT CODE MUST STILL TREAT A CONTACT CARRYING
   * THIS AS GONE, which the old contacts page does not, and that is a known gap.
   *
   * A `Date`, never a string, for as long as any remain: the contacts converter maps a
   * `Date` to a Firestore Timestamp and the mirror's serializer maps that to epoch-ms,
   * which is the only shape `crm_safe_epoch_ms` accepts. An ISO string yields NULL, i.e. a
   * contact that reports itself deleted and sits in the list.
   */
  deletedAt?: Date;
  /** Lowercased email of whoever deleted it. */
  deletedBy?: string;
};

export type SharedContactComment = {
  id: string;
  userId: string;
  comment: string;
  commentTime: Date;
  userPhotoURL: string;
  userDisplayName: string;
};

export type SharedContact = Partial<BaseDocument> & {
  comments: SharedContactComment[];
  organizationId: string;
  callerPhoneNumberInternational: string;
};

export const genericPropertyDetailsKeys = [
  'hasBars',
  'hasGarden',
  'hasParking',
  'hasStorage',
  'hasTerrace',
  'isRenovated',
  'hasElevator',
  'hasProtectedSpace',
  'hasAirConditioning',
  'hasHandicappedAccess',
  'hasHousingUnit',
  'hasMamak',
  'hasMiklat'
] as const;

export type GenericPropertyDetails = {
  [K in typeof genericPropertyDetailsKeys[number]]: boolean;
};

/**
 * One value a merged copy carried for a field the office's record could not settle — see
 * `mergeConflicts`. `userId` is the member whose copy said it — or the office's own book key,
 * `org:<orgId>`, for the value the record ALREADY carried when a later member's book was merged
 * into it (the member sync): nobody in particular put that value there, the office did.
 */
export type ContactMergeConflictValue = { userId: string; value: string | number | boolean | null };

/**
 * A disagreement the pooled-book merge recorded on the office's record for a human to settle
 * (plan D4 of `docs/crm/contacts-postgres-authority-plan.md`). Two shapes, one type:
 *
 *  - two member copies carried different non-empty values for `field` — the pipeline status, its
 *    refinement, or one end of a range (budget, rooms, floors, size): the record keeps the base
 *    copy's and every copy's value is listed here;
 *  - a copy's value for one end of a range contradicted the range's other end (a `roomsMin`
 *    above the record's `roomsMax`): the field was left as the record had it — usually empty —
 *    and the value that was NOT taken is listed here.
 *
 * A name or an e-mail spelled differently is not one. Written by the merge and the member sync
 * only. Nothing shows it on a card yet and NOTHING CLEARS IT — the values are kept with the
 * record for the screen that will, and an edit of the field leaves its entry standing. Absent on
 * every personal-book contact.
 */
export type ContactMergeConflict = { field: string; values: ContactMergeConflictValue[] };

export type RealEstateContact = GenericContact &
  Partial<GenericPropertyDetails> & {
    customerType: CustomerType | null; // legacy single-type mirror — always customerTypes[0] ?? null. Read via getContactCustomerTypes()
    customerTypes?: CustomerType[] | null; // canonical: a contact can be several types at once (e.g. both קונה and מוכר)
    sellingListingIds?: string[]; // listing doc ids (yad2-listings-latest) this contact is selling — relevant for מוכר
    rentingOutListingIds?: string[]; // listing doc ids (yad2-listings-latest) this contact is renting out — relevant for משכיר
    customerBudget: number; // buyer's budget. Semantically the MAX (upper bound in listing matching). For a range, pair with customerBudgetMin.
    customerBudgetMin?: number; // optional lower bound for buyer budget — when set, the buyer budget is a range [customerBudgetMin, customerBudget]. Leave undefined for a single "up to" budget.
    customerBudgetRent?: number; // renter's monthly budget. Semantically the MAX. For a range, pair with customerBudgetRentMin.
    customerBudgetRentMin?: number; // optional lower bound for renter monthly budget — when set, the rent budget is a range [customerBudgetRentMin, customerBudgetRent].
    street: string; // deprecated - use interestedInLocations instead
    neighborhood: string; // deprecated - use interestedInLocations instead
    roomsMin: Room | null;
    roomsMax: Room | null;
    floorsMin: number | null;
    floorsMax: number | null;
    excludeTopFloor?: boolean | null; // seeker does NOT want a top-floor apartment (לא קומה אחרונה)
    squareMeterMin?: number | null; // desired built area (שטח בנוי) lower bound, sqm — pairs with listing.squareMeter
    squareMeterMax?: number | null; // desired built area upper bound, sqm
    plotSizeMin?: number | null; // desired plot size (שטח מגרש) lower bound, sqm — relevant for landed properties
    plotSizeMax?: number | null; // desired plot size upper bound, sqm
    parkingSpacesCount?: number | null; // minimum parking spots the seeker needs (null = no requirement)
    windDirectionsCount?: number | null; // desired number of air directions (כיווני אוויר), 1-4
    // LEGACY — a DERIVED mirror of the crm* fields, written only by the write chokepoint
    // (`deriveLegacySeriousness`). Optional because a contact created after the CRM v2 cutover
    // simply does not carry it. NEVER set it from new code and never offer its values to a user
    // or to the AI — read/write crmStatus / crmSubStatus / crmIsHot instead.
    customerSeriousness?: CustomerSeriousness | null;
    // CRM lead status model v2 (A1). One universal lifecycle status (חדש/בטיפול/מושהה/נחתמה עסקה/אבוד)
    // — single even for multi-type contacts. Read via getContactCrmStatus().
    crmStatus?: CrmStatus | null;
    // Optional refinement, meaningful only under בטיפול; a CrmSubStatusDefinition id. Read via getContactCrmSubStatus().
    crmSubStatus?: string | null;
    // Hot-lead flag (🔥 toggle) — replaces the v1 temperature axis. Read via getContactCrmIsHot().
    crmIsHot?: boolean | null;

    tags?: string[];
    interestedInLocations?: ProcessedListingAddress[];
    interestedInCitiesDistinct?: string[]; // auto generated from interestedInLocations
    interestedInNeighborhoodsDistinct?: string[]; // auto generated from interestedInLocations
    /**
     * Area texts the customer named that the location registry could not place — a phonetic
     * spelling, "the centre", a neighborhood spoken as a city. Written by call transcription next
     * to whatever DID resolve into `interestedInLocations`, so a miss is visible on the contact
     * instead of silently dropped (the 2026-09-06 lost-areas incident). The dashboard renders each
     * as a clickable chip that opens the location picker prefilled; picking moves the text into
     * `interestedInLocations` and removes it from here. Never fed to listing matching.
     */
    unresolvedLocations?: string[];

    interestedInPropertyTypes?: string[];

    AICustomerDetailsMetadata?: PhoneCallParsedOpenAiResponseCustomerDetails;

    /** Pooled agency record only (an `org:` book): the disagreements the merge could not settle. Server-written. */
    mergeConflicts?: ContactMergeConflict[];
  };

// ── Contact origin (lead source) ──────────────────────────────────────────────
// `contactOrigin` is free text — realtors can type anything in the contact
// modal — but every SYSTEM writer stamps one of these canonical values. The
// historical values are English and MUST stay as-is (existing contacts carry
// them); display goes through getContactOriginLabel(), which maps known values
// to Hebrew and returns anything else verbatim. No stored-data backfill.
export const SYSTEM_CONTACT_ORIGINS = {
  aiCallTranscription: 'AI call transcription', // contact auto-created from a transcribed call (callsProcessing)
  webhook: 'webhook', // external update-contact webhook (GoHighLevel etc.)
  mcp: 'mcp', // created by an AI client through the MCP server / AI chat
  homelyBot: 'Homely with webot AI', // lead parsed by the Homely WhatsApp bot
  dashboardSearch: 'search', // created from the dashboard contact-search flow
  bulkMessagingLog: 'Bulk messaging log', // created from the bulk-messaging log widget
  whatsappImport: 'WhatsApp contacts', // WhatsApp contacts/groups import flows
  orgAssignment: 'org assignment', // copied into an agent's book by an org admin's מתווך מטפל designation
  manual: 'הוזן ידנית', // saved from the contact modal without picking a source
  clientIntakeForm: 'client intake form' // written by the realtor's lead form, טופס לידים (client portal)
} as const;

// Hebrew display labels for the legacy/system (English) origin values.
export const contactOriginLabels: Record<string, string> = {
  [SYSTEM_CONTACT_ORIGINS.aiCallTranscription]: 'תמלול שיחה (AI)',
  [SYSTEM_CONTACT_ORIGINS.webhook]: 'ליד חיצוני (Webhook)',
  [SYSTEM_CONTACT_ORIGINS.mcp]: 'צ׳אט AI',
  [SYSTEM_CONTACT_ORIGINS.homelyBot]: 'בוט Webot',
  [SYSTEM_CONTACT_ORIGINS.dashboardSearch]: 'חיפוש בדשבורד',
  [SYSTEM_CONTACT_ORIGINS.bulkMessagingLog]: 'הודעות תפוצה',
  [SYSTEM_CONTACT_ORIGINS.whatsappImport]: 'ייבוא מוואטסאפ',
  [SYSTEM_CONTACT_ORIGINS.orgAssignment]: 'הוקצה במשרד',
  [SYSTEM_CONTACT_ORIGINS.clientIntakeForm]: 'טופס לידים'
};

/** Display label for a contactOrigin: Hebrew for known system values, the raw (already-human) string otherwise. */
export function getContactOriginLabel(origin?: string | null): string {
  if (!origin) return '';
  return contactOriginLabels[origin] ?? origin;
}

/**
 * ── THE CONTACT EXPORT/IMPORT FILE FORMAT ────────────────────────────────────────
 *
 * ONE list, held here because it IS the round trip: the CRM export writes these columns
 * (`contactsBulkRules.CONTACTS_CSV_COLUMNS`, server) and the dashboard's file import reads
 * them back (`utils/contacts/crmExportImportColumns.ts`). Two repos, one vocabulary.
 *
 * ⚠️ HEADERS ARE THE CONTACT'S OWN FIELD NAMES, NOT HEBREW LABELS — and that is the whole
 * design, not an oversight. It is what the contacts page's Excel export has always done
 * (`ContactPageMenu.handleExcelExport` hands `xlsx.json_to_sheet` the contact documents
 * themselves, so the sheet's headers are the document's keys), and it is exactly why that
 * file re-imports cleanly while a prettier one does not: the importer's job is to map a
 * header onto a field, and a header that already IS the field name has nothing to map, so
 * nothing to get wrong. A Hebrew-label export was tried on the CRM page and every column
 * whose label did not happen to match a table column fell through to the importer's
 * unknown-column branch, which wrote it onto the document as raw debris.
 *
 * The trade is deliberate: the file is a touch less pretty to read in Excel, and it
 * survives a round trip. The realtor's use for it is editing and re-uploading.
 */
export const CONTACT_EXPORT_FIELDS = [
  'id', // the document id = the contact's international phone. First, because it identifies the row.
  'callerFirstName',
  'callerLastName',
  'customerTypes',
  'crmStatus',
  'crmSubStatus',
  'crmIsHot',
  'customerBudget',
  'customerBudgetMin',
  'customerBudgetRent',
  'roomsMin',
  'roomsMax',
  'interestedInLocations',
  'interestedInPropertyTypes',
  // The amenity bag (ממ"ד, חניה, מעלית…), one column each, `true` only when ticked — the
  // same cell the hot flag gets. Left out until 2026-11-02, so an export re-uploaded lost them.
  ...genericPropertyDetailsKeys,
  'tags',
  'contactOrigin',
  'additionalNotes',
  'sellingListingIds',
  'rentingOutListingIds',
  'createdTime',
  'updatedTime'
] as const;

/**
 * The exported columns whose value is JSON, because a spreadsheet cell is a string and
 * these are not: `["VIP","ממליץ"]`, `[{"city":"פתח תקווה","neighborhood":"כפר גנים א","street":""}]`.
 *
 * The import `JSON.parse`s exactly this list on the way back in
 * (`UploadFileWithExplanations`), which is what makes an area's city→neighborhood PAIRING
 * survive — two flat `ערים`/`שכונות` columns cannot express it, and guessing the pairing is
 * what `location-ssot` exists to forbid.
 */
export const CONTACT_EXPORT_JSON_FIELDS = [
  'customerTypes',
  'interestedInLocations',
  'interestedInPropertyTypes',
  'tags',
  'sellingListingIds',
  'rentingOutListingIds'
] as const;

/**
 * Every contact field a FILE IMPORT may write. Mirrors `WRITABLE_CONTACT_FIELDS` in
 * `server/api/services/crm/contactWriteRules.ts` — same test for inclusion: the realtor
 * types it, and no Cloud Function, scraper or AI pass owns it.
 *
 * This is the gate the UPLOADER applies, before anything is sent. It is no longer the only
 * one: the import posts to `POST /api/v1/contacts/bulk-create`, so every row also passes
 * `validateContactPatch` server-side — which REJECTS an unknown key rather than dropping
 * it. Keeping this list in step with `WRITABLE_CONTACT_FIELDS` is therefore what stops the
 * import 400ing on a column the uploader was willing to send.
 *
 * ⚠️ Everything absent here is absent on purpose, and three of them are dangerous rather
 * than merely useless: `userId` and `organizationIds` decide who can SEE the contact — a
 * colleague's export re-uploaded would hand them your row, or take yours away — and
 * `contactId` / `contactFullName` are Cloud-Function-derived, so a stale sheet value races
 * the function that owns them. The export deliberately emits none of the four.
 */
export const CONTACT_IMPORTABLE_FIELDS = [
  'callerFirstName',
  'callerLastName',
  'mail',
  'contactOrigin',
  'contactOriginAdditionalInfo',
  'additionalNotes',
  'customerTypes',
  'crmStatus',
  'crmSubStatus',
  'crmIsHot',
  'customerBudget',
  'customerBudgetMin',
  'customerBudgetRent',
  'customerBudgetRentMin',
  'roomsMin',
  'roomsMax',
  'floorsMin',
  'floorsMax',
  'tags',
  'interestedInPropertyTypes',
  'interestedInLocations',
  'tehudatZehut',
  'address',
  'additionalPhoneNumbers',
  'optedOutOfBulkMessages',
  'excludeTopFloor',
  'squareMeterMin',
  'squareMeterMax',
  'plotSizeMin',
  'plotSizeMax',
  'parkingSpacesCount',
  'windDirectionsCount',
  'sellingListingIds',
  'rentingOutListingIds',
  // The amenity bag. Missing here from 2026-09-01 to 2026-11-02, and every amenity column of
  // every file import was dropped without a word. A cell arrives as `true` or not at all
  // (`coerceImportedContactValues`): never `false`, which would un-tick on a re-import, and
  // never the TEXT `"TRUE"` (`contacts-data-hygiene`: 1,556 contacts once held the word).
  ...genericPropertyDetailsKeys
] as const;

// Precomputed "smart defaults" for the new/edit-contact modal, one doc per realtor
// (keyed by user id/email). Aggregated from the realtor's own preferred areas
// (User doc) + their last ~50 edited contacts, cached and refreshed lazily.
// See contactSmartDefaults.service.ts. Consumed by the dashboard to prefill
// area/budget/property-type suggestions the moment the modal opens.
export const CONTACT_SMART_DEFAULTS_COLLECTION_NAME = 'contact-smart-defaults';
export type ContactBudgetRange = { min: number; max: number };
export type ContactSmartDefaults = {
  areaLocations: ProcessedListingAddress[]; // suggested areas as full addresses (city+neighborhood, ranked most-relevant first), ready to add to a contact's interestedInLocations. City-only interests appear as { city, neighborhood: '' }.
  propertyTypes: string[]; // property types this realtor typically works with (empty when there is not enough signal)
  saleBudget: { suggested?: ContactBudgetRange; presets: ContactBudgetRange[] };
  rentBudget: { suggested?: ContactBudgetRange; presets: ContactBudgetRange[] };
  basedOnContactCount: number; // how many recent contacts fed the aggregation
  computedAt: string; // ISO timestamp of the last (re)compute
};

// under dashboard-users-v2/<userId>/contacts
export const SENT_MESSAGES_COLLECTION_NAME = 'sent-messages';
export type BulkMessageSentMessage = Partial<BaseDocument> & {
  sentTo: string;
  message: string;
  sentTime: string;
  mediaLink: string;
  listingId?: string;
  customFileName: string;

  // AUTO GENERATED BY FUNCTION
  contactId?: string; // used for contacts lookups - the contact international phone number
  userId?: string; // used for contacts lookups - the user mail
  organizationIds?: string[]; // used for contacts lookups -the organizations the contact is associated with
};

/**
 * LEGACY - DEPRECATED
 * Customer seriousness:
 * 1. לא רציני = Not serious
 * 2. רציני = Serious
 * 3. אין התאמה = No match
 * 4. לקוח חם = Hot customer 🔥
 * 5. בעסקה = About to sign deal
 * 6. נחתמה עסקה = Signed deal
 */
export const customerSeriousness = [
  'לא רציני',
  'רציני',
  'אין התאמה',
  'לקוח חם',
  'בעסקה',
  'נחתמה עסקה'
] as const;

export type CustomerSeriousness = typeof customerSeriousness[number];

// ─────────────────────────────────────────────────────────────────────────────
// CRM lead status model v2 (A1) — see docs/crm/crm-status-model.md
//
// One universal lifecycle status per contact (single even for multi-type contacts):
//   חדש → בטיפול → (מושהה) → נחתמה עסקה / אבוד
// plus:
//   - an OPTIONAL single sub-status, meaningful only under בטיפול. The offered sub-statuses depend on
//     the contact's pipeline sides (buyer/renter vs seller/landlord), grouped by side in the dropdown.
//   - a hot-lead flag (crmIsHot, 🔥 toggle) — replaces the v1 hot/warm/cold temperature axis; this is
//     the AI-suggestible axis (X1).
//
// `customerSeriousness` is kept as a DERIVED legacy mirror (see deriveLegacySeriousness) so existing
// readers (listing matcher, old contact chips/tabs) keep working with zero migration.
// ─────────────────────────────────────────────────────────────────────────────

/** The two pipeline sides. Buyer/renter share one; seller/landlord share the other. */
export type CrmPipelineSide = 'buyer' | 'seller';

/** Which board a customerType belongs to. Non-lead types (מתווך/עו"ד/שמאי) map to no side. */
export function crmSideForCustomerType(type: CustomerType): CrmPipelineSide | null {
  if (type === 'קונה' || type === 'שוכר') return 'buyer';
  if (type === 'מוכר' || type === 'משכיר') return 'seller';
  return null;
}

/** The pipeline sides a contact appears on, derived from its (multi-)customerTypes. */
export function getContactCrmSides(
  contact:
    | { customerType?: CustomerType | null; customerTypes?: CustomerType[] | null }
    | null
    | undefined
): CrmPipelineSide[] {
  const sides = new Set<CrmPipelineSide>();
  for (const t of getContactCustomerTypes(contact)) {
    const side = crmSideForCustomerType(t);
    if (side) sides.add(side);
  }
  return Array.from(sides);
}

/** The universal lifecycle statuses (fixed for everyone; per-org customization deferred). */
export const crmStatuses = ['new', 'in_progress', 'paused', 'won', 'lost', 'irrelevant'] as const;
export type CrmStatus = typeof crmStatuses[number];

export const crmStatusLabels: Record<CrmStatus, string> = {
  new: 'חדש',
  in_progress: 'בטיפול',
  paused: 'מושהה',
  won: 'נחתמה עסקה',
  lost: 'אבוד', // a lead we engaged but lost (competitor, went cold) — reachable manually only
  irrelevant: 'לא רלוונטי' // never a real lead (not serious / no match)
};

/** Reverse lookup of crmStatusLabels: Hebrew display label → status id (file imports, AI parsing). */
export function crmStatusFromLabel(label: string | null | undefined): CrmStatus | null {
  if (typeof label !== 'string' || !label.trim()) return null;
  const trimmed = label.trim();
  const entry = (Object.entries(crmStatusLabels) as [CrmStatus, string][]).find(
    ([, l]) => l === trimmed
  );
  return entry?.[0] ?? null;
}

/**
 * A sub-status refinement under בטיפול. `id` is a stable string persisted on the contact — never
 * rename an existing id (add a new one + migrate instead). Fixed defaults for now; per-org custom
 * sub-statuses were dropped from this pass (return later with their editor UI).
 */
export type CrmSubStatusDefinition = {
  id: string;
  side: CrmPipelineSide; // which pipeline the sub-status belongs to (dropdown grouping)
  label: string; // Hebrew display label
  order: number;
  icon?: string; // stable icon key (mapped to an icon component on the dashboard — see CrmStatusIcon)
};

export const DEFAULT_CRM_BUYER_SUBSTATUSES: readonly CrmSubStatusDefinition[] = [
  { id: 'buyer_first_contact', side: 'buyer', label: 'קשר ראשוני', order: 1, icon: 'contacted' },
  { id: 'buyer_presenting', side: 'buyer', label: 'בהצגת נכסים', order: 2, icon: 'presenting' },
  { id: 'buyer_negotiating', side: 'buyer', label: 'במו״מ', order: 3, icon: 'negotiating' }
] as const;

export const DEFAULT_CRM_SELLER_SUBSTATUSES: readonly CrmSubStatusDefinition[] = [
  {
    id: 'seller_appraisal',
    side: 'seller',
    label: 'פגישת הערכת שווי',
    order: 1,
    icon: 'appraisal'
  },
  {
    id: 'seller_listing_signed',
    side: 'seller',
    label: 'חתימת בלעדיות',
    order: 2,
    icon: 'listing_signed'
  },
  { id: 'seller_marketing', side: 'seller', label: 'בשיווק', order: 3, icon: 'marketing' },
  { id: 'seller_offers', side: 'seller', label: 'הצעות / מו״מ', order: 4, icon: 'offers' }
] as const;

export const DEFAULT_CRM_SUBSTATUSES: readonly CrmSubStatusDefinition[] = [
  ...DEFAULT_CRM_BUYER_SUBSTATUSES,
  ...DEFAULT_CRM_SELLER_SUBSTATUSES
];

/** Ordered sub-status definitions offered for the given sides (dropdown contents, grouped by side). */
export function getCrmSubStatusDefinitionsForSides(
  sides: readonly CrmPipelineSide[]
): CrmSubStatusDefinition[] {
  return DEFAULT_CRM_SUBSTATUSES.filter((d) => sides.includes(d.side));
}

export function findCrmSubStatusDefinition(id: string): CrmSubStatusDefinition | undefined {
  return DEFAULT_CRM_SUBSTATUSES.find((d) => d.id === id);
}

/** Read guard: v1 briefly stored an object here (uncommitted local testing) — treat anything that isn't a known status as unset. */
export function getContactCrmStatus(
  contact: { crmStatus?: CrmStatus | null } | null | undefined
): CrmStatus | null {
  const value = contact?.crmStatus as unknown;
  return typeof value === 'string' && (crmStatuses as readonly string[]).includes(value)
    ? (value as CrmStatus)
    : null;
}

export function getContactCrmSubStatus(
  contact: { crmSubStatus?: string | null } | null | undefined
): string | null {
  return typeof contact?.crmSubStatus === 'string' ? contact.crmSubStatus : null;
}

export function getContactCrmIsHot(
  contact: { crmIsHot?: boolean | null } | null | undefined
): boolean {
  return contact?.crmIsHot === true;
}

const CRM_IN_DEAL_SUBSTATUS_IDS = new Set([
  'buyer_negotiating',
  'seller_listing_signed',
  'seller_marketing',
  'seller_offers'
]);
const CRM_SERIOUS_SUBSTATUS_IDS = new Set(['buyer_presenting', 'seller_appraisal']);

/**
 * New axes → legacy `customerSeriousness` mirror (lossy, best-effort), most-advanced signal first.
 * We never derive 'לא רציני': it means a dead lead (the migration maps it to `lost`), so an active
 * lead must not mirror to it — early statuses without another signal mirror to null.
 */
export function deriveLegacySeriousness(
  status: CrmStatus | null | undefined,
  subStatus: string | null | undefined,
  isHot: boolean | null | undefined
): CustomerSeriousness | null {
  if (status === 'won') return 'נחתמה עסקה';
  if (status === 'lost' || status === 'irrelevant') return 'אין התאמה';
  if (subStatus && CRM_IN_DEAL_SUBSTATUS_IDS.has(subStatus)) return 'בעסקה';
  if (isHot) return 'לקוח חם';
  if (subStatus && CRM_SERIOUS_SUBSTATUS_IDS.has(subStatus)) return 'רציני';
  return null;
}

/**
 * The write helper every caller uses to keep the canonical axes (crmStatus + crmSubStatus + crmIsHot)
 * and the legacy mirror coherent. Pass whichever fields the caller touched:
 *   - a canonical field present → enforce coherence (a sub-status only lives under בטיפול; writing a
 *     sub-status implies בטיפול) and recompute the legacy mirror from the (merged) canonical values;
 *   - only the legacy field present → derive the hot flag from it (the lifecycle stage is unknowable
 *     from a partial legacy write; the one-time migration handles the full backfill).
 * `existing` supplies the not-written axes so the mirror reflects the full post-merge state.
 */
export function buildCrmStatusUpdate(
  update: {
    crmStatus?: CrmStatus | null;
    crmSubStatus?: string | null;
    crmIsHot?: boolean | null;
    customerSeriousness?: CustomerSeriousness | null;
  },
  existing?: {
    crmStatus?: CrmStatus | null;
    crmSubStatus?: string | null;
    crmIsHot?: boolean | null;
  } | null
): {
  crmStatus?: CrmStatus | null;
  crmSubStatus?: string | null;
  crmIsHot?: boolean | null;
  customerSeriousness?: CustomerSeriousness | null;
} {
  const result: {
    crmStatus?: CrmStatus | null;
    crmSubStatus?: string | null;
    crmIsHot?: boolean | null;
    customerSeriousness?: CustomerSeriousness | null;
  } = {};

  const wroteCanonical =
    update.crmStatus !== undefined ||
    update.crmSubStatus !== undefined ||
    update.crmIsHot !== undefined;

  if (wroteCanonical) {
    if (update.crmStatus !== undefined) result.crmStatus = update.crmStatus;
    if (update.crmSubStatus !== undefined) result.crmSubStatus = update.crmSubStatus;
    if (update.crmIsHot !== undefined) result.crmIsHot = update.crmIsHot;

    let effectiveStatus =
      update.crmStatus !== undefined ? update.crmStatus : getContactCrmStatus(existing);
    let effectiveSub =
      update.crmSubStatus !== undefined ? update.crmSubStatus : getContactCrmSubStatus(existing);
    const effectiveHot =
      update.crmIsHot !== undefined ? update.crmIsHot : existing?.crmIsHot ?? null;

    // Coherence: writing a sub-status implies בטיפול (unless the status was explicitly written too)…
    if (
      update.crmSubStatus != null &&
      update.crmStatus === undefined &&
      effectiveStatus !== 'in_progress'
    ) {
      effectiveStatus = 'in_progress';
      result.crmStatus = 'in_progress';
    }
    // …and a sub-status cannot outlive a status other than בטיפול.
    if (effectiveStatus !== 'in_progress' && effectiveSub != null) {
      effectiveSub = null;
      result.crmSubStatus = null;
    }

    result.customerSeriousness = deriveLegacySeriousness(
      effectiveStatus,
      effectiveSub,
      effectiveHot
    );
    return result;
  }

  if (update.customerSeriousness !== undefined) {
    result.customerSeriousness = update.customerSeriousness;
    result.crmIsHot =
      update.customerSeriousness === 'לקוח חם' || update.customerSeriousness === 'בעסקה';
    return result;
  }

  return result;
}

/**
 * One-time migration: legacy `customerSeriousness` + the contact's sides → the new axes. Used ONLY by
 * adHocOperations/migrate-contacts-to-crm-status.ts. Sides pick the בעסקה sub-status variant; per
 * Haim's call, both לא רציני and אין התאמה map to `irrelevant` (never a real lead). `lost` (אבוד) is
 * reachable manually only — no legacy value maps to it.
 */
export function mapSeriousnessToCrmStatus(
  seriousness: CustomerSeriousness | null | undefined,
  sides: readonly CrmPipelineSide[]
): { crmStatus: CrmStatus | null; crmSubStatus: string | null; crmIsHot: boolean } {
  switch (seriousness) {
    case 'לא רציני':
      return { crmStatus: 'irrelevant', crmSubStatus: null, crmIsHot: false };
    case 'רציני':
      return { crmStatus: 'in_progress', crmSubStatus: null, crmIsHot: false };
    case 'לקוח חם':
      return { crmStatus: 'in_progress', crmSubStatus: null, crmIsHot: true };
    case 'בעסקה':
      return {
        crmStatus: 'in_progress',
        crmSubStatus: sides.includes('buyer')
          ? 'buyer_negotiating'
          : sides.includes('seller')
          ? 'seller_offers'
          : null,
        crmIsHot: true
      };
    case 'אין התאמה':
      return { crmStatus: 'irrelevant', crmSubStatus: null, crmIsHot: false };
    case 'נחתמה עסקה':
      return { crmStatus: 'won', crmSubStatus: null, crmIsHot: false };
    default:
      return { crmStatus: null, crmSubStatus: null, crmIsHot: false };
  }
}

export const rooms = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6] as const;

export type Room = typeof rooms[number];

export const parkingSpots = [1, 2, 3, 4, 5] as const;

export type ParkingSpot = typeof parkingSpots[number];

/**
 * Every selectable floor value: the domain of the floor RANGE filters and of a
 * contact's `floorsMin` / `floorsMax` search profile.
 *
 * CONTIGUOUS on purpose. This list used to read
 * `…, 18, 19, 20, 25, 30, 35, 40, 45, 50` — harmless while floors were only ever
 * rendered as a dropdown, but a visible bug once the pickers became +/− steppers:
 * `ScaleStepper` walks its scale by INDEX (that is exactly what gives rooms their
 * 0.5 steps), so above floor 20 every tap moved FIVE floors.
 *
 * Densifying the domain is deliberate, and preferred over handing the stepper a
 * private integer scale: `Floor` stays honest (a stepper on a private scale would
 * emit 21, 22, … which are not `Floor`, papered over by the existing `as Floor`
 * casts), and `getClosestValidFloor` — which snaps a contact's stored floor back
 * onto THIS list every time a search is seeded — stays an identity in range
 * instead of silently rewriting a saved "21" as "20".
 *
 * Mirrored in webot-server `server/types/sharedSchema.ts`; keep the two identical.
 */
export const floors = [
  -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46,
  47, 48, 49, 50
] as const;

export type Floor = typeof floors[number];

/**
 * Customer types:
 * 1. מוכר = Seller
 * 2. קונה = Buyer
 * 3. שוכר = Tenant
 * 4. משכיר = Landlord
 * 5. מתווך = Realtor
 * 6. עו"ד = Attorney
 * 7. שמאי = Appraiser
 */
export const customerTypes = ['מוכר', 'קונה', 'שוכר', 'משכיר', 'מתווך', 'עו"ד', 'שמאי'] as const;
export type CustomerType = typeof customerTypes[number];

/**
 * Multi-type contacts: `customerTypes` (array) is the canonical field; `customerType` (single) is a
 * legacy mirror kept for old readers/writers. Old documents may hold only the legacy field, so all
 * reads must go through getContactCustomerTypes() — no backfill of existing docs is required.
 */
export function getContactCustomerTypes(
  contact:
    | { customerType?: CustomerType | null; customerTypes?: CustomerType[] | null }
    | null
    | undefined
): CustomerType[] {
  if (!contact) return [];
  if (Array.isArray(contact.customerTypes) && contact.customerTypes.length > 0)
    return contact.customerTypes;
  return contact.customerType ? [contact.customerType] : [];
}

export function contactHasCustomerType(
  contact:
    | { customerType?: CustomerType | null; customerTypes?: CustomerType[] | null }
    | null
    | undefined,
  type: CustomerType
): boolean {
  return getContactCustomerTypes(contact).includes(type);
}

/**
 * Builds the pair of fields every writer must persist together. Dedupes, sorts in canonical enum
 * order (so the legacy mirror is deterministic), and derives the mirror as the first element.
 */
export function buildCustomerTypesUpdate(
  types: readonly (CustomerType | null | undefined)[] | null | undefined
): {
  customerTypes: CustomerType[];
  customerType: CustomerType | null;
} {
  const unique = Array.from(new Set((types ?? []).filter((t): t is CustomerType => Boolean(t))));
  unique.sort((a, b) => customerTypes.indexOf(a) - customerTypes.indexOf(b));
  return { customerTypes: unique, customerType: unique[0] ?? null };
}

export const callEndResults = [
  'ANSWER',
  'NOANSWER',
  'BUSY',
  'CANCEL',
  'IVR_HANGUP',
  'CONGESTION'
] as const;

export type CallEndResult = typeof callEndResults[number];

export const callDirections = ['inbound', 'outbound'] as const;

export type CallDirection = typeof callDirections[number];

export type TeamMember = Partial<User> & {
  email: string;
  sentMessagesCount: number;
  contactsCount: number;
};

export type CallSummaryOwnerData = {
  id: string;
  owner: TeamMember;
  firestorePath: string;
  callerPhoneNumber: PhoneNumber;
};

// under payCall-settings/<localNumber>/calls-summary
export const CALL_SUMMARY_SUBCOLLECTION_NAME = 'calls-summary';
export type CallSummary = Partial<BaseDocument> & {
  callerNumber: string; // local number of the customer. Usually, customers call our clients so this was called "callerNumber". But even if this is an outbound call from our client, the caller number will still be the number of the customer. caller === customer.
  callerNumberInternational: string;
  callerNumberWaFormat: string;
  customerName: string;
  duration: string;
  startAt: string;
  endAt: string;
  groupName: string;
  virtualNumber: string; // local virtual number of user
  realNumber: string; // local real number of user
  recordingLink: string;
  recordingLinkDirect: string;
  recordingLinkReplicatedToFirebaseUrl?: string; // The link to the recording that was replicated to Firebase Storage
  result: CallEndResult;
  status: string;
  callDirection: CallDirection;
  _metadata?: CallSummaryOwnerData;

  // AUTO GENERATED BY FUNCTION
  contactId?: string; // used for contacts lookups - the contact international phone number
  userId?: string; // used for contacts lookups - the user mail
  organizationIds?: string[]; // used for contacts lookups -the organizations the contact is associated with
};

export const virtualNumberProviders = ['paycall', 'maskyoo'] as const;
export type VirtualNumberProvider = typeof virtualNumberProviders[number];

export const PAY_CALL_SETTINGS_COLLECTION_NAME = 'payCall-settings';
export type PayCallSettings = Partial<BaseDocument> & {
  customerIntroName: string;
  customerOrganizationName: string;
  customerUserName: string;
  noAnswerWhatsappMessage: string; // seems to be deprecated - use noAnswerWhatsappMessageTemplate instead
  noPhoneReplyCallToAction: string;
  noResponseBotWhatsappForwardingNumber: string; // local number
  notesAboutCustomer: string; // internal
  number: string; // local virtual number
  ownerMails: string[];
  hasAiEnabled: boolean;
  provider?: VirtualNumberProvider; // default is paycall
  /**
   * Customer-defined free-text label identifying what this virtual number is used
   * for ("the source") — e.g. "שלט חוצות אילון", "מודעת יד2", "קמפיין פייסבוק".
   * Set by the customer from the dashboard; surfaced in the calls page dropdown so
   * they can tell at a glance which number belongs to which lead source.
   */
  tag?: string;
};

export const listingVisibility = ['private', 'organization'] as const;
export type ListingVisibility = typeof listingVisibility[number];

/**
 * Canonical location stamp written alongside (never instead of) `address`.
 *
 * The free-text `address` fields stay exactly as the source wrote them — every
 * existing consumer joins on those strings — while this stamp carries stable
 * government-anchored ids for matching, search and cross-source correlation.
 * Defined here rather than in `server/types/locations.ts` so this file stays
 * self-contained and safe to mirror into the dashboard repo.
 */
export type CanonicalLocationStampData = {
  cityCode: number;
  cityId: string;
  neighborhoodId?: string;
  /** Parent of `neighborhoodId`, denormalized so queries can match either zoom level. */
  neighborhoodParentId?: string;
  streetId?: string;
  method: 'source-id' | 'name-exact' | 'name-variant' | 'spatial' | 'manual' | 'unresolved';
  confidence: number;
  flags?: string[];
  /**
   * Set when the winning match only became possible because of an admin/AI
   * correction — an alias that override added, a master it created, or a merge
   * redirect it installed.
   *
   * Deliberately NOT a `method` value: the AI never resolves a listing, it authors
   * an override, and the listing then resolves deterministically through it. So
   * `method` keeps saying which tier fired, and this says who made that tier able
   * to fire. Both are needed to answer "how did this address get resolved".
   */
  resolvedVia?: { overrideId: string; by: 'admin' | 'ai'; op: string };
  /**
   * Fields the SOURCE actually named that could not be placed. `[]`/absent means
   * everything the source said was resolved. A city-level stamp with
   * `['neighborhood']` here is a partial answer, not a success — this is what the
   * admin unresolved queue keys on.
   */
  unresolvedFields?: ('city' | 'neighborhood' | 'street')[];
  resolvedAt: Date;
  registryVersion: string;
};

export type ProcessedListingAddressCoordinates = {
  lat: number;
  lon: number;
};

export type ProcessedListingAddress = {
  city: string;
  neighborhood: string;
  street: string;
  houseNumber?: number;
  apartmentNumber?: number;
  floor?: number;
  coords?: ProcessedListingAddressCoordinates;
  // Canonical id of a PICKED location (from an autocomplete suggestion), so a saved
  // interested-location or a search filter can match by stable id instead of raw text
  // — recovering listings written with a different source spelling that map to the same
  // master. Additive; absent on a listing's own address and on manually-typed addresses.
  canonicalLocationId?: string;
  canonicalLevel?: 'city' | 'neighborhood' | 'street';
  /**
   * UI-ONLY provenance: this location entered the current selection as a member
   * of a saved location group. It exists so a picked group renders as ONE
   * distinct chip instead of N plain ones, and so re-opening a contact shows the
   * group it was filled from.
   *
   * It can never reach a query: `algoliaClient.buildPgSearchRequest` builds each
   * wire location by naming its five fields explicitly (city / neighborhood /
   * street / canonicalLocationId / canonicalLevel — see algoliaClient.ts:171-184),
   * and `listingsMatch.service.ts` only ever reads those. Nothing else must start
   * reading these two.
   */
  fromGroupId?: string;
  fromGroupName?: string;
};

/**
 * How many areas one contact's `interestedInLocations` may hold — the same number the
 * server's `contactWriteRules.ts` enforces, so every picker stops adding before a save
 * can be rejected.
 *
 * Raised from 50 on customer request: an agency working a whole region legitimately lists
 * every neighborhood of two or three cities on a single seeker. The cap exists only to
 * stop an unbounded array reaching Firestore, not to express a product rule. Deliberately
 * NOT shared with `MAX_LOCATIONS_PER_LOCATION_GROUP`: a group is a shorthand that gets
 * expanded into a selection, and the selection is what this bounds.
 */
export const MAX_INTERESTED_IN_LOCATIONS = 200;

/**
 * How many "could not place this" area texts a contact keeps in `unresolvedLocations`. Each is
 * one short string the realtor is expected to fix or dismiss, so this is a hygiene cap, not a
 * product rule.
 */
export const MAX_UNRESOLVED_LOCATIONS = 20;

/** Subcollection under `dashboard-users-v2/{userEmail}` holding one doc per group. */
export const LOCATION_GROUPS_SUBCOLLECTION = 'location-groups';
export const MAX_LOCATIONS_PER_LOCATION_GROUP = 50;
export const MAX_LOCATION_GROUPS_PER_USER = 40;
export const MAX_LOCATION_GROUP_NAME_LENGTH = 40;

/**
 * A realtor-defined, named set of locations — a UI shorthand, never a search
 * primitive. Picking a group EXPANDS it into its member locations at pick time
 * (`expandLocationGroup`), so every consumer downstream keeps receiving plain
 * `ProcessedListingAddress[]` and no matcher, filter builder or alert path has
 * to learn what a group is.
 */
export type LocationGroup = {
  id: string; // == Firestore document id
  name: string; // friendly, <= MAX_LOCATION_GROUP_NAME_LENGTH
  locations: ProcessedListingAddress[]; // <= MAX_LOCATIONS_PER_LOCATION_GROUP, deduped
  emoji?: string; // optional badge chosen by the realtor
  /**
   * Both timestamps are written by the CLIENT, in the same single `set()` as the
   * rest of the group, and both names are deliberate.
   *
   * `location-groups` is in `METADATA_STAMP_EXCLUDED_PATHS`, so the generic
   * metadata Cloud Function never touches these documents — it re-merges the
   * create-time snapshot ~0.5-6.5s after a create, which would revert an area
   * added right after creating a group and would RESURRECT a group created and
   * immediately deleted.
   *
   * And the edit stamp is NOT called `updatedTime`: `GenericConverter.toFirestore`
   * strips that field on write (the stamper is supposed to own it), so a field by
   * that name would be silently dropped on every write and then never re-applied —
   * permanently absent, and Firestore's `orderBy` drops documents missing the
   * field it orders by. Same reason `Report` carries an explicit `lastEditedAt`.
   */
  createdTime?: Date;
  lastEditedAt?: Date;
};

export const ListingOrigins = ['yad2', 'webot', 'madlan'] as const;
export type ListingOrigin = typeof ListingOrigins[number];

export const ListingStatuses = ['sold', 'onSale', 'removed', 'frozen', 'inNegotiation'] as const;
export type ListingStatus = typeof ListingStatuses[number];

export const propertyTypes = [
  'אולמות',
  'בית מלון',
  "בית פרטי/ קוטג'",
  'בניין מגורים',
  'בניין משרדים',
  'גג/ פנטהאוז',
  'דו משפחתי',
  'דופלקס',
  'דיור מוגן',
  'דירה',
  'דירה מחולקת',
  'דירת גן',
  'חלל עבודה משותף',
  'חנויות/ שטח מסחרי',
  'חניה',
  'טריפלקס',
  'יחידת דיור',
  'כללי',
  'מבני תעשייה',
  'מגרשים',
  'מחסן',
  'מחסנים',
  'מסעדה',
  'מרפאה',
  'מרתף/ פרטר',
  'משק חקלאי/ נחלה',
  'משק עזר',
  'משרדים',
  'נכס מניב',
  'סטודיו',
  'סטודיו/ לופט',
  "קב' רכישה/ זכות לנכס",
  'תיירות ונופש'
] as const;

export type PropertyType = typeof propertyTypes[number];

export const listingListedFor = ['sale', 'rent'] as const;
export type ListingListedFor = typeof listingListedFor[number];

export const addressDisplayMode = ['full', 'cityNeighborhood', 'cityStreet'] as const;

export type AddressDisplayMode = typeof addressDisplayMode[number];

export const transactionType = ['sale', 'rent'] as const;

export type TransactionType = typeof transactionType[number];

export const purposeType = ['residential', 'commercial'] as const;

export type PurposeType = typeof purposeType[number];

export const evictionType = ['specificDate', 'immediate', 'flexible'] as const;

export type EvictionType = typeof evictionType[number];

export type PriceHistoryEntry = {
  price: number;
  date: Date;
};

/**
 * A watermark-free (AI-cleaned) version of one of a listing's photos, stored on the
 * listing document itself. The stored images are a SHARED CACHE across all users —
 * once one realtor cleans an image, a later realtor's removal run reuses it as an
 * instant no-op (`alreadyExisted`) instead of paying for regeneration.
 *
 * DISPLAY is per-user, not shared: a realtor sees the clean versions only after THEY
 * performed a removal run on the listing (recorded in
 * `ProcessedListingData.watermarkRemovalRequestedBy`, or — for removals that predate
 * that field — authorship via `createdBy`). A realtor who never removed keeps seeing
 * the watermarked originals. See utils/listings/watermarkFree.ts →
 * userHasRemovedListingWatermarks.
 */
export type WatermarkFreeImage = {
  sourceImageUrl: string; // the original (watermarked) URL from `imageUrls`
  cleanImageUrl: string; // our storage URL of the AI-cleaned version
  createdBy: string; // mail of the user whose request generated this image
  createdAt: Date;
};

export const PROCESSING_LISTINGS_COLLECTION_NAME = 'yad2-listings-latest';
export type ProcessedListingData = Partial<BaseDocument> &
  Partial<GenericPropertyDetails> & {
    listingOrigin: ListingOrigin;
    addressCoordinates?: ProcessedListingAddressCoordinates;

    originalListingId: string;
    originalListingOwnersId?: string[];
    originalListingVisibility?: ListingVisibility;
    originalListingOrganizationId?: string;

    /**
     * `webot` rows only: the marketplace ads this listing is a claimed copy of,
     * mirrored from `RealEstateListing` by `convertListingToProcessedListingData`.
     *
     * They reach this collection for DISPLAY: the listings board and every search
     * surface render mirror rows, and a claimed copy has to be able to say
     * "מקור: יד2" and link the ad it came from — otherwise it reads as the
     * realtor's own listing with no provenance at all.
     *
     * ⚠ They are NOT how the board decides what to hide. `searchListingsPg`
     * resolves the caller's claim set from `real-estate-listings` (the authority)
     * via `loadClaimedSourceIdsForUser`, so the substitution is correct even
     * before the mirror Cloud Function is redeployed to carry these. See that
     * function's comment.
     */
    sourceListingIds?: string[];
    sourceListings?: ListingSourceRef[];

    /**
     * The seller/landlord the REALTOR recorded on her own listing.
     *
     * ⚠ THIS FIELD IS **NEVER** WRITTEN INTO `yad2-listings-latest`. The mirror
     * Cloud Function (`updateWebotListingsOnAlgolia`) deliberately does not carry
     * it, and `convertListingToProcessedListingData` deliberately does not map it.
     *
     * WHY, precisely: that collection is `allow get: if true` in
     * `firestore.rules` — UNAUTHENTICATED — because the share-link favourites doc
     * hands anonymous buyers the ids of the listings a realtor chose to show them,
     * and the public client page point-reads those very documents. The mirror
     * write is a non-merge `set()` of this whole object, and the same object is
     * copied again onto every referencing calendar event's `listingsData`. So a
     * `listingContacts` here is the property owner's private mobile inside a page
     * our own customer publishes to her buyers: they call the seller directly and
     * cut her out of her own deal. That is commercial harm to the customer, not a
     * privacy nit. IF SOMEONE RE-ADDS THE WRITE, every owner phone on every
     * claimed listing becomes publicly fetchable by document id, retroactively and
     * silently — nothing in the UI changes and no error is raised.
     *
     * So a row carrying this field is only ever one of two things:
     *
     *   1. CLIENT-SIDE SYNTHESIZED — never read back from Firestore. The listings
     *      board's optimistic just-claimed row (`buildOptimisticClaimHit`) and the
     *      create path's picker-ready snapshot (`createOrUpdateListing`) both
     *      build one in memory so a listing saved seconds ago can already show the
     *      owner the realtor just typed.
     *   2. OVERLAID FROM THE AUTHORITY by the caller — `real-estate-listings`,
     *      whose document id is the SAME id, read through
     *      `utils/listings/recordedListingContacts.ts` (hook:
     *      `hooks/useRecordedListingContacts`). That read is also strictly fresher
     *      than the mirror, which lags a save by up to about a minute.
     *
     * The field stays on the type because both of those hand their row to
     * `resolveListingSeller`, which is the ONE place that decides whose name and
     * phone a listing shows. `sellerName` / `sellerPhoneNumber` / `agencyName`
     * cannot answer that alone: on a scraped row they are the property's seller,
     * while on a `webot` row the mirror Cloud Function OVERWRITES them with the
     * OWNING REALTOR's own details, so there they are the LISTING AGENT — which is
     * why a claimed ad used to show the realtor her own number back as "the
     * seller" and offer to save her as her own מוכר. Those three fields keep their
     * current values and meanings regardless (the listings-match and
     * rental-to-sale seller-name filters, the `/facets/seller-names` endpoint, the
     * Postgres `seller_name` generated column and its two indexes, the search
     * audit trail and the MCP `search_listings` path all read them as they are).
     *
     * ⚠ DISPLAY DATA ONLY, and therefore also: no Postgres generated column, no
     * `search_text` term, no facet and no index. Nothing may make an owner's phone
     * searchable or enumerable the way `sellerPhoneNumber` is.
     */
    listingContacts?: ListingContact[];

    publishedTime?: any; //todo remove
    lastUpdatedTime?: any; //todo remove
    listingPublishedTime: Date;
    listingUpdatedTime: Date;
    address: ProcessedListingAddress; // The normalized address
    addressBeforeNormalization?: ProcessedListingAddress; //The original address
    canonicalLocation?: CanonicalLocationStampData; // Canonical ids; purely additive to `address`
    buildingTotalFloors?: number;
    agencyName?: string;
    sellerName?: string;
    sellerPhoneNumber?: string;
    /**
     * Number of times the dedicated phone-backfill pass (yad2PhoneBackfill.service.ts)
     * has tried — and failed — to fetch a missing seller phone for this listing.
     * Caps at MAX_PHONE_BACKFILL_ATTEMPTS, after which the pass stops retrying.
     * Absent/0 = never attempted by the backfill pass.
     */
    phoneBackfillAttempts?: number;
    /** When the dedicated phone-backfill pass last attempted this listing (spaces out retries across runs). */
    lastPhoneBackfillAttempt?: Date;
    locationDetails?: string;
    description?: string;
    coverImageUrl?: string;
    imageUrls?: string[];
    /** AI watermark-free versions of `imageUrls` entries (see WatermarkFreeImage). */
    watermarkFreeImages?: WatermarkFreeImage[];
    /**
     * @deprecated NO LONGER WRITTEN OR READ (2026-09-30). Mails of users who performed a
     * watermark-removal run on this listing; it used to gate DISPLAY of the clean versions
     * per user.
     *
     * Deleted as a gate because it gated nothing: the clean images are uploaded with
     * `predefinedAcl: 'publicRead'` and a token-less URL, pointed to from this collection
     * (`allow get: if true`), and `favorite-listings-of-contact` (`allow read: if true`) hands any
     * anonymous buyer the listing ids — so it was a client-side `if` in front of files the whole
     * internet could already fetch. Meanwhile it WAS a world-readable roster of our customers'
     * email addresses keyed by the listings they work on, and it made a fully-clean listing report
     * "0 מתוך 12 תמונות נקיות" to anyone who had not personally run it.
     *
     * Display now gates on `User.watermarkTermsAcceptedAt` alone. The stored arrays are scheduled
     * for deletion (they are customer PII on a world-gettable collection) — paced, because every
     * touched listing re-enqueues into `listings-sync-queue`.
     */
    watermarkRemovalRequestedBy?: string[];
    videoUrl?: string; // Optional YouTube video link shown on the public listing page
    adType: AdType;
    price: number;
    priceHistory?: PriceHistoryEntry[];
    lastPriceChangeDate?: Date;
    propertyCondition?: string;
    roomsCount: number;
    squareMeter: number;
    originalListingUrl: string;
    tags?: string[];

    purposeType?: PurposeType;
    transactionType?: TransactionType;
    addressDisplayMode?: AddressDisplayMode;

    propertyType: PropertyType;

    // Madlan integration fields
    madlanBulletinId?: string; // Saved after successful Madlan publish
    madlanConfirmedAddress?: string; // Saved after successful Madlan publish
    madlanLocationResolution?: REST_schema.MadlanLocationResolutionType; // Saved after successful Madlan publish
    madlanPublishedByWebotUserMail?: string; // Mail of the Webot user that published the listing to Madlan

    token?: string; // todo remove
    subcategoryId?: number; //todo remove

    parkingSpotsCount?: number; // additionalDetails.parkingSpacesCount
    isEnterDateFlexible?: boolean; // additionalDetails.isEnterDateFlexible
    hasBoiler?: boolean; // inProperty.includeBoiler
    isAssetExclusive?: boolean; // inProperty.isAssetExclusive
    includesFurniture?: boolean; // inProperty.includeFurniture
    isImmediateEntrance?: boolean; // inProperty.isImmediateEntrance

    entranceType?: EvictionType; // evictionType
    entranceDate?: Date; // additionalDetails.entranceDate
    // originalSlimPartialData?: RawYad2ListingSlimPartialData; removing to save space
    // originalFullData?: RawYad2ListingFullItemData; removing to save space

    // TODO - find the appropriate backfill from Yad2
    sizeOfPlotM2?: number; // ?
    sizeOfGardenM2?: number; // ?
    sizeOfTerraceM2?: number; // ?
    listingStatus?: ListingStatus;

    listingListedFor?: ListingListedFor;
    _geoloc?: {
      lat: number;
      lng: number;
    };
  };

export type PartialProcessedListingDataForNotifications = Pick<
  ProcessedListingData,
  | 'id'
  | 'price'
  | 'address'
  | 'roomsCount'
  | 'squareMeter'
  | 'coverImageUrl'
  | 'adType'
  | 'createdTime'
  | 'listingOrigin'
  | 'listingListedFor'
> & {
  matchingContactsCount?: number;
  matchingContactsMaxConfidence?: number;
  matchingContactsAvgConfidence?: number;
  /**
   * Which version of the match rules produced the two confidences above
   * (`LISTING_MATCH_RULES_VERSION`). Absent on entries written before 2026-09-30.
   *
   * These entries are the notifications chip's FALLBACK when no `listing-matches` cache doc exists,
   * and nothing ever rewrites them — so without the stamp the chip keeps printing percentages from
   * a scale that no longer exists after a scoring change. The chip drops the percentages and keeps
   * the COUNT, which stays correct because nothing in `isMatch` reads the score.
   */
  matchRulesVersion?: number;
  /**
   * Why this listing reached the realtor's feed: it sits inside one of their own
   * `interestedInLocations` (city / neighborhood / street), as judged at notification time by
   * `isRealtorInterestedInGettingAlertsForListingLocation` — canonical ids and city spellings
   * included. An alert fires on EITHER this or a contact match, so a notification with
   * `matchingContactsCount === 0` is always an area one, and both can be true at once.
   *
   * Optional because entries written before 2026-09-29 have no such key; the dashboard's
   * "באזורים שלי" filter falls back to `matchingContactsCount === 0 || city ∈ interestedInCitiesDistinct`
   * for those (over-includes at sub-city granularity rather than silently hiding a listing).
   */
  isInRealtorInterestedLocations?: boolean;
};

// ============================================================================
// Real-estate listings — the realtor's OWN listings (`real-estate-listings`).
//
// Distinct from PROCESSING_LISTINGS_COLLECTION_NAME (`yad2-listings-latest`),
// which holds listings we scraped from the market. These are created and edited
// from the dashboard at /dashboard/listings.
//
// The full document shape lives here so both repos type it identically; the
// dashboard's `src/@types/listings/*` re-export these rather than redeclaring.
// ============================================================================

export const REAL_ESTATE_LISTINGS_COLLECTION_NAME = 'real-estate-listings';

/**
 * Listing types, split by the ייעוד (purpose) they are offered under — the
 * "סוג נכס" dropdown narrows to one of the two lists once a ייעוד is picked.
 *
 * The two lists OVERLAP, and that is the point: `sharedPurposeListingType` holds
 * the types a realtor legitimately reaches from EITHER ייעוד. A plot is bought to
 * build a house as often as to build a shop, so filing it under one ייעוד only
 * makes it unreachable from the other.
 *
 * `listingType` is composed from the three DISJOINT lists rather than by
 * concatenating the two overlapping ones, so a shared type appears exactly once
 * in the unfiltered dropdown (rendered before a ייעוד is picked) and once in the
 * zod enums built from it.
 */
export const residentialOnlyListingType = [
  'apartment',
  'gardenApartment',
  'pentHouse',
  'duplex',
  'privateHouse',
  'semiDetached',
  'farm',
  'auxiliaryFarm',
  'studio',
  'triplex',
  'basement',
  'residentialBuilding'
] as const;

export const commercialOnlyListingType = [
  'offices',
  'storesCommercialSpace',
  'warehouses',
  'industrialBuildings',
  'clinics',
  'officeBuilding',
  'sharedWorkspace',
  'halls',
  'hotel',
  'parkingLot'
] as const;

/** Offered under BOTH ייעודים — see the note above before adding to this list. */
export const sharedPurposeListingType = ['lot'] as const;

export const residentialListingType = [
  ...residentialOnlyListingType,
  ...sharedPurposeListingType
] as const;
export type ResidentialListingType = typeof residentialListingType[number];

export const commercialListingType = [
  ...commercialOnlyListingType,
  ...sharedPurposeListingType
] as const;
export type CommercialListingType = typeof commercialListingType[number];

export const listingType = [
  ...residentialOnlyListingType,
  ...commercialOnlyListingType,
  ...sharedPurposeListingType
] as const;
export type ListingType = typeof listingType[number];

export const listingState = [
  'likeNew',
  'renovated',
  'maintained',
  'newConstruction',
  'needsRenovation'
] as const;
export type ListingState = typeof listingState[number];

/** Mime types the listing file-upload accepts. Types `ListingFilePreview.type`. */
export const ALLOWED_LISTING_FILE_MIME_TYPES = [
  'image/png', // .png
  'image/gif', // .gif
  'image/jpeg', // .jpg, .jpeg
  'application/pdf', // .pdf
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
] as const;
export type AllowedListingFileMimeType = typeof ALLOWED_LISTING_FILE_MIME_TYPES[number];

export type ListingImageData = {
  src: string;
  alt: string;
};

/** A listing photo as persisted on the document. */
export type ListingImagePreview = ListingImageData & {
  id: string;
};

/** A listing attachment (contract, plan, …) as persisted on the document. */
export type ListingFilePreview = {
  id: string;
  src: string;
  type: AllowedListingFileMimeType;
  name: string;
  size: number;
  nameOverride?: string;
};

/**
 * The realtor identity snapshot embedded on a listing (`ownersData`).
 *
 * NOT the dashboard account — that's {@link User}. This is the smaller
 * listing-facing profile mirrored from the `listingData` subcollection, so a
 * listing can render its owner's name and photo without extra reads.
 */
export type ListingUser = {
  id: string;
  name: string;
  email: string;
  avatarUrl: string;
  listingBio: string;
  phoneNumber: string;
  listingAlias: string;
  coverAvatarUrl: string | null;
  coverAvatarColor: string | null;
};

/** A co-owner reference: email is the only guaranteed field. */
export type ListingAdditionalUser = Partial<ListingUser> & Pick<ListingUser, 'email'>;

export type ListingAdditionalUserWithFee = ListingAdditionalUser & {
  fee: number | null;
  finalFee: number | null;
  feeIncludesTaxes: boolean;
};

/**
 * Contact details denormalised onto the listing (seller/landlord side).
 *
 * The three name/phone fields are FREE TEXT and always have been: a realtor
 * naming a seller mid-call must not be forced through contact creation first,
 * so the entry stands on its own with no contact behind it.
 *
 * `contactId` upgrades that snapshot into a real link when the phone resolves
 * to — or creates — a contact in the realtor's book. It is the contact's
 * Firestore document id, i.e. its international (E.164) phone number, which is
 * why it can differ from `callerPhoneNumber` if the realtor later edits the
 * displayed number without re-keying the contact. Absent = typed but unlinked,
 * which is a valid and common state; never treat it as an error.
 *
 * The reverse edge is `RealEstateContact.sellingListingIds` /
 * `rentingOutListingIds`. Both are written by the same action, but a failure
 * between them leaves this side set and the contact side missing — the UI reads
 * this side, so that degrades to a missing CRM back-link, not a broken screen.
 */
export type ListingContact = Pick<RealEstateContact, 'callerPhoneNumber' | 'callerFirstName' | 'callerLastName'> & {
  contactId?: string;
};

/**
 * A marketplace listing that is the SAME PROPERTY as the webot listing carrying
 * this reference — the link a realtor creates by claiming a market ad, or by
 * attaching a published version to a listing she already had.
 *
 * One property can be published on several marketplaces at once, so this is an
 * ARRAY on the listing rather than a single id. A later, separate piece of work
 * uses the same field to relate listings across marketplaces.
 *
 * `url` is snapshotted at link time on purpose: the marketplace document can be
 * removed by the next scrape (a de-listed ad), and the realtor still wants the
 * link she followed to be recoverable from her own record.
 */
export type ListingSourceRef<TDate = Date> = {
  /** `yad2-listings-latest` document id. */
  listingId: string;
  origin: ListingOrigin;
  /** `originalListingUrl` as it read when the link was made. */
  url?: string;
  linkedAt: TDate;
  /** Mail of the realtor who made the link. */
  linkedBy: string;
};

export type ListingPriceHistoryEntry<TDate = Date> = {
  price: number;
  userMail: string;
  updatedAt: TDate;
};

/**
 * A realtor-owned listing document.
 *
 * ## Why the `TDate` parameter
 *
 * This collection is read with a converter that does NOT normalise Firestore
 * timestamps, so the two repos genuinely hold different runtime types for the
 * date fields: the server reads `Date` (via `GenericConverter`), the dashboard
 * reads `firebase.firestore.Timestamp` (via `listingConverter`). Parameterising
 * lets one field list serve both — the server uses the `Date` default, the
 * dashboard instantiates `RealEstateListing<Timestamp>` as its `Listing`.
 *
 * Do NOT "simplify" this to `Date` — the dashboard calls `.toDate()` on these
 * fields, which would then be a type error at ~100 call sites.
 *
 * ## "Required" is a claim, not a guarantee
 *
 * The non-optional fields below mirror what the dashboard's create/edit form
 * writes today. Firestore enforces nothing: documents written before a field
 * existed simply lack it, and server flows do encounter such docs. Read
 * defensively (`listing.owners ?? []`) rather than trusting the type.
 */
export type RealEstateListing<TDate = Date> = {
  id: string;
  /** Added by the server's `GenericConverter`; absent on the dashboard. */
  path?: string;

  /** Emails of the realtors who own this listing (== `User.id`). */
  owners: string[];
  ownersData: null | ListingAdditionalUserWithFee[];
  createdBy: string;
  createdByOrganization: null | string;
  userFavorites: string[];
  visibility: ListingVisibility;
  status: ListingStatus;

  createdTime: TDate;
  updatedTime: TDate;

  /**
   * End date of the realtor's exclusivity (בלעדיות) agreement on this listing.
   * Date-only in practice — stored as midnight, so always compare on the
   * calendar day in Israel time rather than on the raw instant.
   * null = no exclusivity recorded.
   */
  exclusivity: null | TDate;
  /**
   * The rental agreement (חוזה שכירות) on a RENTED listing: its first and last day.
   * Date-only and stored as midnight like `exclusivity`, so compare on the calendar
   * day in Israel time. `leaseEndDate` is what the `lease-reminders-job` reads.
   * Both exist only while the listing is rented (`isRentedListing`): every writer
   * nulls them on a listing that is not. null = not recorded, and a document
   * written before 2026-10 lacks the fields entirely.
   */
  leaseStartDate: null | TDate;
  leaseEndDate: null | TDate;
  signDate: null | TDate;
  evictionDate: null | TDate;
  evictionType: null | EvictionType;

  name: string;
  description: string;
  projectName: string;
  additionalNotes: string;
  locationDetails: string;
  userDefinedListingId: string;

  city: string;
  address: string;
  streetNumber: null | number;
  neighborhood: string;
  apartmentNumber: null | number;
  addressAreaNickname: string;
  addressDisplayMode: null | AddressDisplayMode;
  addressCoordinates: null | ProcessedListingAddressCoordinates;

  price: number;
  finalListingPrice: null | number;
  priceHistory: null | ListingPriceHistoryEntry<TDate>[];
  pendingPriceUpdates: boolean;
  municipalityTaxes: null | number;
  cleaningFee: null | number;
  fee: null | number;
  feeIncludesTaxes: boolean;

  rooms: null | number;
  floor: null | number;
  floorOutOf: null | number;
  sizeOfPropertyM2: null | number;
  sizeOfPlotM2: null | number;
  sizeOfGardenM2: null | number;
  sizeOfTerraceM2: null | number;
  parkingSpotsCount: null | number;

  listingType: null | ListingType;
  listingState: null | ListingState;
  purposeType: null | PurposeType;
  transactionType: null | TransactionType;
  housingUnit: boolean;

  hasBars: boolean;
  hasGarden: boolean;
  hasTerrace: boolean;
  hasStorage: boolean;
  hasParking: boolean;
  hasElevator: boolean;
  hasAirConditioning: boolean;
  protectedSpace: boolean;
  // ממ"ק / מקלט, under the names scraped listings and contacts use, so the mirror converter copies
  // them straight through (`protectedSpace` above is the legacy spelling of `hasProtectedSpace`).
  // Absent on documents written before 2026-10: read as `=== true`.
  hasMamak: boolean;
  hasMiklat: boolean;
  accessibleForDisabled: boolean;

  pictures: null | ListingImagePreview[];
  files: null | ListingFilePreview[];
  videoUrl?: null | string;

  /**
   * The marketplace ads that are this same property. Written ONLY by the
   * listings claim/link endpoints — see `sourceListingIds` for why.
   */
  sourceListings?: ListingSourceRef<TDate>[];
  /**
   * Flat mirror of `sourceListings[].listingId`.
   *
   * It exists because neither store can filter the rich array: Firestore's
   * `array-contains` needs scalars, and the Postgres mirror can only index a
   * flat `doc->'sourceListingIds'`. Every read path that asks "is this market
   * ad already claimed?" uses this one.
   *
   * ⚠ NOT derived by a Cloud Function. Both fields are written together, in the
   * same object, by the single server chokepoint that owns them
   * (`listings.service.ts` claim / link-source / unlink-source). A trigger would
   * add lag, re-enter a collection that already fans out to six functions, and
   * open a window where the mirror disagrees with the array. The cost of that
   * choice is that `sourceListings` must stay out of every client-writable path
   * — it is in the dashboard's `NotEditableListing` for exactly this reason.
   */
  sourceListingIds?: string[];

  listingContacts: null | ListingContact[];
  buyerOrigin: null | string[];
  buyerOriginData: null | ListingAdditionalUserWithFee[];
  dealAdditionDetails: string;
  buyerAttorneyName: string;
  buyerAttorneyPhoneNumber: string;
  sellerAttorneyName: string;
  sellerAttorneyPhoneNumber: string;

  madlanBulletinId: null | string;
  madlanConfirmedAddress: null | string;
  madlanLocationResolution?: REST_schema.MadlanLocationResolutionType; // Saved after successful Madlan publish
  madlanPublishedByWebotUserMail?: string | null; // Email of the Webot user who published the listing to Madlan
};

/**
 * One "we already sent this exclusivity reminder" marker, written by the
 * `exclusivity-reminders-job` cron.
 *
 * The doc id is deterministic — `${listingId}__${offsetDays}d__${exclusivityDay}` —
 * and the job `create()`s it, so a re-run (retry, double schedule, two Cloud Run
 * instances) fails with ALREADY_EXISTS instead of sending the message twice. The
 * exclusivity date is part of the id on purpose: if the realtor renews the
 * exclusivity to a new date, that's a new agreement and it earns a fresh pair of
 * reminders.
 */
export const EXCLUSIVITY_REMINDERS_COLLECTION_NAME = 'listing-exclusivity-reminders';

/** How many days before the exclusivity end date each reminder fires. */
export const EXCLUSIVITY_REMINDER_OFFSETS_IN_DAYS = [14, 7] as const;
export type ExclusivityReminderOffsetInDays = typeof EXCLUSIVITY_REMINDER_OFFSETS_IN_DAYS[number];

export type ExclusivityReminderSent = Partial<BaseDocument> & {
  listingId: string;
  /** Owner the reminder was sent to (User.id / email). */
  userId: string;
  offsetDays: ExclusivityReminderOffsetInDays;
  /** Exclusivity end date as a `YYYY-MM-DD` day in Israel time. */
  exclusivityDay: string;
  sentAt: Date;
  toNumberInternational?: string;
};

/**
 * A realtor's listing that was rented out. There is no `rented` status: it is a
 * rent listing whose deal was closed, which the dashboard labels "הושכר".
 */
export const isRentedListing = (listing: {
  status?: ListingStatus | null;
  transactionType?: TransactionType | null;
}): boolean => listing.transactionType === 'rent' && listing.status === 'sold';

/**
 * One "we already sent this lease-end reminder" marker, written by the
 * `lease-reminders-job` cron. Same `create()` claim as the exclusivity ledger,
 * with the doc id `${listingId}__${offsetDays}d__${leaseEndDay}__${userId}`. The
 * end date is part of the id on purpose: a renewed lease has a new end date and
 * earns a fresh pair of reminders.
 */
export const LEASE_REMINDERS_COLLECTION_NAME = 'listing-lease-reminders';

/** How many days before the end of a rental agreement each reminder fires. */
export const LEASE_REMINDER_OFFSETS_IN_DAYS = [60, 30] as const;
export type LeaseReminderOffsetInDays = typeof LEASE_REMINDER_OFFSETS_IN_DAYS[number];

/**
 * A lease reminder is due on its day and on the days after it, this many days in
 * all: one the job could not send on its day (it did not run, Meta refused it) goes
 * out late instead of never. Must stay smaller than the gap between two offsets.
 * Shared because the dashboard tells the realtor which days the reminders fall on.
 */
export const LEASE_REMINDER_WINDOW_IN_DAYS = 3;

export type LeaseReminderSent = Partial<BaseDocument> & {
  listingId: string;
  /** Owner the reminder was sent to (User.id / email). */
  userId: string;
  offsetDays: LeaseReminderOffsetInDays;
  /** Lease end date as a `YYYY-MM-DD` day in Israel time. */
  leaseEndDay: string;
  sentAt: Date;
  toNumberInternational?: string;
};

/**
 * Server-only dedup ledger for the "tracked user connected" admin WhatsApp
 * alert (users flagged with `posthogTrackingEnabled`). One doc per user id
 * holding the last time admins were alerted; a transaction refuses to advance
 * it more than once per hour — see trackedUserAlert.service.ts.
 */
export const TRACKED_USER_CONNECT_ALERTS_COLLECTION_NAME = 'tracked-user-connect-alerts';

export type TrackedUserConnectAlert = Partial<BaseDocument> & {
  /** User.id (email) of the tracked user. */
  userId: string;
  lastAlertAt: Date;
};

export const USER_LISTING_NOTIFICATIONS_COLLECTION_NAME = 'user-listing-notifications';

export type UserListingNotifications = Partial<BaseDocument> & {
  userId: string;
  startOfWeek: Date;
  listingNotifications: PartialProcessedListingDataForNotifications[];
};

export const LISTING_MATCHES_COLLECTION_NAME = 'listing-matches';

/**
 * How well a contact matches a listing (server: listings/listingMatchRules.ts).
 * `full` — every criterion the contact stated is met AND the contact stated enough to judge
 * (see `dataDepth`). `partial` — at least one criterion is off by a tolerated amount (a type
 * upgrade, 1 room, over budget…), the listing doesn't say, or the contact card holds nothing
 * beyond a budget and an area. Something the CONTACT never stated is not a reason.
 *
 * ⚠️ SERVER-SIDE ONLY. "התאמה מלאה"/"התאמה חלקית" was removed from every screen 2026-09-22:
 * realtors read a thin contact's `full` as "this one fits best" and a detailed contact's
 * `partial` as the opposite, and the % beside it disagreed with both. As of 2026-09-24 no client
 * surface reads the tier at all — the ring's colour, the "התאמות מובילות" filter and the bulk
 * select box all cut on the PERCENTAGE, which is the one number the realtor is reading. The tier
 * survives as the server's `dataDepth` verdict; putting it back on a screen puts back a second
 * axis that contradicts the first.
 */
export const listingMatchTiers = ["full", "partial"] as const;
export type ListingMatchTier = typeof listingMatchTiers[number];

export const listingMatchReasonCriteria = [
  "location",
  "propertyType",
  "budget",
  "rooms",
  "size",
  "floor",
  "feature",
  "dataDepth"
] as const;
export const listingMatchReasonStatuses = ["match", "partial", "unknown", "mismatch"] as const;

/**
 * One criterion's verdict for a (contact, listing) pair, with a display-ready Hebrew label.
 *
 * Statuses, worst first: `mismatch` — the listing CONTRADICTS what the customer asked for (a
 * downgrade in property type, a ממ"ד the listing says it has none of, a built area well under the
 * minimum they stated). `partial` — off by a tolerated amount. `unknown` — the customer asked and
 * the listing does not say. `match` — fits.
 *
 * `size` is the built area (שטח בנוי, m²) — the contact's `squareMeterMin` / `squareMeterMax`
 * against the listing's `squareMeter`. Its label carries both numbers whenever the listing sits
 * outside what was asked (`92 מ"ר · מבוקש מ-100`), so a client never has to compute the gap.
 *
 * A `mismatch` is what makes a pair a WIDE match (`isLooseMatch`): it is returned, scored at zero
 * on that criterion, and hidden behind the realtor's own "הצגת התאמות רחבות יותר" tick.
 *
 * `dataDepth` is the odd one out: it is not a comparison with the listing at all but a statement
 * about the CONTACT CARD — emitted (status `unknown`) when the card holds nothing beyond a budget
 * and an area, so nothing below could be checked. It is what the matches table's warning reads.
 */
export type ListingMatchReason = {
  criterion: typeof listingMatchReasonCriteria[number];
  status: typeof listingMatchReasonStatuses[number];
  label: string;
  /** `criterion: 'feature'` only — which feature, so a client never has to parse `label`. */
  featureKey?: keyof GenericPropertyDetails;
  /**
   * `criterion: 'feature'` only, and only for a criterion the contact expressed as "any of
   * these": every key the contact ticked in the group. Today that is the מרחב מוגן group
   * (`hasProtectedSpace` / `hasMamak` / `hasMiklat`) — ticking two of them means either will do,
   * so they are ONE reason rather than two. `featureKey` stays the group's representative.
   */
  featureKeys?: Array<keyof GenericPropertyDetails>;
  /**
   * EVERYTHING the contact stated for this criterion, display-ready, when the `label` names only
   * one of them. Today: `propertyType`, where the label names the listing's own type plus (for a
   * partial match) the one type that justified it.
   *
   * The label stays narrow on purpose — seven type names in one chip overflowed the table cell —
   * so the rest live here for the details panel's "+N". Without it the panel said
   * `דירה · מבוקש: דירה מחולקת` to a realtor whose customer had also ticked דופלקס and דירת גן,
   * which reads as "that is all they wanted" (reported 2026-09-22).
   */
  requestedValues?: string[];
};

export type CachedMatchingContact = {
  contactId: string;
  callerFirstName?: string;
  callerLastName?: string;
  customerBudget?: number;
  crmIsHot?: boolean | null;
  confidencePercent: number;
  // The tier only — never the reasons. The biggest cache docs are already ~550KB
  // (4,771 contacts) against Firestore's 1MB document cap; the reasons ride the live
  // suggestions response instead. Absent on docs written before tiers existed.
  matchTier?: ListingMatchTier;
};

/**
 * Version of the listing↔buyer match rules (the server's `listings/listingMatchRules.ts`, which
 * re-exports this). BUMP IT whenever tiers, reasons, their labels or the SCORE change — see the
 * changelog on the re-export, which is where the history lives.
 *
 * It lives in the shared schema, not next to the rules, because BOTH repos need it: the server
 * stamps it onto everything it stores (`listing-match-results` snapshots, `listing-matches` cache
 * docs, notification entries) and the dashboard compares against it to decide whether a stored
 * percentage is still on the current scale.
 */
export const LISTING_MATCH_RULES_VERSION = 10;

export type ListingMatchCacheSourceFlow = 'notification' | 'suggestion-controller';

export type ListingMatchCacheDoc = Partial<BaseDocument> & {
  userId: string;
  organizationIds: string[];
  listingId: string;
  listingOrigin: ListingOrigin;
  listingListedFor?: ListingListedFor; // absent on docs written before rent support — treat as 'sale'

  matchingContactsCount: number;
  matchingContactsMaxConfidence: number;
  matchingContactsAvgConfidence: number;

  myContacts: CachedMatchingContact[];

  orgMatchingContactsCount: number;
  orgMatchingContactsMaxConfidence: number;
  orgMatchingContactsAvgConfidence: number;

  sourceFlow: ListingMatchCacheSourceFlow;
  computedAt: Date;
  /**
   * Which version of the match rules produced the confidences in this doc
   * (`LISTING_MATCH_RULES_VERSION`). Absent on docs written before 2026-09-30.
   *
   * These docs are OVERWRITTEN IN PLACE on the next compute for their (user, listing) pair, and
   * nothing recomputes them eagerly — so a scoring change straddles for days, with the dashboard's
   * listing-notifications chip printing percentages from a scale that no longer exists. The chip
   * drops its percentages (keeping the COUNT, which is safe because nothing in `isMatch` reads the
   * score) when this stamp is missing or older than the rules the client knows.
   */
  rulesVersion?: number;
};

export type Yad2Customer = {
  name: string;
  agencyName?: string;
  email?: string;
  id: number;
  phone: string;
  isVirtualPhoneNumber: boolean;
  brokerPhone: string;
};

export type Yad2Address = {
  city: {
    id: string;
    text: string;
  };
  neighborhood: {
    id: string;
    text: string;
  };
  street: {
    id: string;
    text: string;
  };
  house: {
    number: number;
    floor: number;
  };
  coords: {
    lat: number;
    lon: number;
  };
};
export type RawYad2ListingSlimPartialData = Partial<BaseDocument> & {
  dates: {
    updatedAt?: string; // only in some cases
    createdAt?: string; // only in some cases
  };
  address: Partial<Yad2Address>;
  customer: Partial<Yad2Customer>;
  metaData: {
    coverImage: string;
  };
  subcategoryId: number;
  adType: AdType;
  price: number;
  token: string;
  additionalDetails: {
    property: {
      text: string;
    };
    roomsCount: number;
    squareMeter: number;
  };
  tags: { name: string }[];
  orderId: number;
  priority: number;
};

export const adTypes = ['commercial', 'private'] as const;
export type AdType = typeof adTypes[number];

export type RawYad2ListingFullItemData = Partial<BaseDocument> & {
  token: string;
  orderId: number;
  adNumber: number;
  adType: AdType;
  categoryId: number;
  subcategoryId: number;
  priority: number;
  statusId: number;
  price: number;
  additionalDetails: {
    balconiesCount: number;
    entranceDate: string; // format: YYYY-MM-DDT00:00:00
    squareMeter: number;
    squareMeterGarden: number;
    squareMeterBuild: number;
    roomsCount: number;
    parkingSpacesCount: number;
    buildingTopFloor: number;
    isEnterDateFlexible: boolean;
    propertyGroup: {
      id: number;
      text: string;
      textEng: string;
    };
    property: {
      id: number;
      text: string;
      textEng: string;
    };
    propertyCondition: {
      id: number;
      text: string;
    };
  };
  inProperty: {
    isKitchenKosher: boolean;
    isAssetExclusive: boolean;
    includeAirconditioner: boolean;
    includeBalcony: boolean;
    includeBars: boolean;
    includeBoiler: boolean;
    includeFurniture: boolean;
    includeElevator: boolean;
    includeParking: boolean;
    includeSecurityRoom: boolean; // ממ"ד
    includeFloorShelter: boolean; // ממ"ק
    includeBuildingShelter: boolean; // מקלט
    includeWarehouse: boolean;
    isHandicapped: boolean;
    includeRavBariach: boolean;
    includeTadiran: boolean;
    includeTornado: boolean;
    isImmediateEntrance: boolean;
    isRenovated: boolean;
    // TODO - more here?
  };
  searchText: string;
  customer: Yad2Customer;
  address: Yad2Address;
  metaData: {
    coverImage: string;
    images: string[];
    description: string;
  };
  dates: {
    updatedAt: string;
    createdAt: string;
    endsAt: string;
    rebouncedAt: string;
  };
  abovePrice: string;
  tags: {
    name: string;
    id: number;
    priority: number;
  }[];
  //...
};

export type SingleListingItemScrappingResponse = {
  sellerPhoneNumber: string; // new manual addition - the seller phone number
  sellerName: string; // new manual addition - the seller name
  props: {
    pageProps: {
      dehydratedState: {
        queries: [
          {
            queryKey: string[];
            state: {
              data: RawYad2ListingFullItemData;
            };
          }
        ];
      };
    };
  };
};

export type ListingsItemsPageScrappingResponse = {
  url: string;
  props: {
    pageProps: {
      statusCode: number;
      searchApiQuery: {
        page: number;
      };
      feed: {
        private: RawYad2ListingSlimPartialData[];
        agency: RawYad2ListingSlimPartialData[];
        yad1: RawYad2ListingSlimPartialData[];
        platinum: RawYad2ListingSlimPartialData[];
        kingOfTheHar: any[];
        trio: any[];
        booster: any[];
        leadingBroker: any[];
        pagination: any;
      };
    };
  };
};

export type GenericScrappingIDE_Response = {
  url: string;
  stringRawData: string;
};

export type ScrappingJobState = {
  currentState: 'idle' | 'scrappingPages' | 'scrappingSingleListings';
  currentCollectionId: string;
  listingsPagesFetched: number;
  totalUniqueListingsFetchedInPages: number;
  listingsToRefetchDueToMissingImages: number;
  newListingsFetchedInPages: number;
  newListingsCreated: number;
};

export type PhoneCallParsedOpenAiResponseCustomerDetails = {
  callerFirstName?: string;
  callerLastName?: string;
  additionalNotes?: string;

  // Only for real estate calls
  customerBudget?: number;
  city?: string;
  neighborhood?: string;
  interestedInLocations?: ProcessedListingAddress[];
  /** See `RealEstateContact.unresolvedLocations`; carried here so it reaches the contact through the same merge. */
  unresolvedLocations?: string[];
  /**
   * TRANSIENT — the model's raw answer, present only between the model and the area resolver.
   * `transcriptAndSummarizeCall` moves both onto `PhoneCallParsedOpenAiResponse.locationExtraction`
   * and deletes them here, so they are never merged onto a contact and never persisted under
   * `customerDetails`.
   */
  requestedAreas?: CallRequestedArea[];
  discussedListingAddresses?: string[];
  interestedInPropertyTypes?: string[];
  street?: string;
  roomsMin?: Room;
  roomsMax?: Room;
  customerType?: CustomerType;
  // LEGACY — call analysis no longer extracts this (CRM v2: analysis never decides status; a new
  // contact starts at crmStatus 'new'). Kept because historical AICustomerDetailsMetadata carry it.
  customerSeriousness?: CustomerSeriousness;
  floorsMin?: number;
  floorsMax?: number;
  hasBars?: boolean;
  hasGarden?: boolean;
  hasParking?: boolean;
  hasStorage?: boolean;
  hasTerrace?: boolean;
  isRenovated?: boolean;
  hasElevator?: boolean;
  hasProtectedSpace?: boolean;
  hasAirConditioning?: boolean;
  hasHandicappedAccess?: boolean;
  hasHousingUnit?: boolean;
  hasMamak?: boolean;
  hasMiklat?: boolean;
};

/** One area the customer asked for, as the model heard it: place names only, one place per entry. */
export type CallRequestedArea = { city?: string; neighborhood?: string; street?: string };

/**
 * Audit trail of the location leg of a call analysis: what the model heard and what the registry
 * made of it. Lives beside `customerDetails`, never inside it, so nothing here is merged onto a
 * contact — `resolved` is what became `interestedInLocations`, `unresolved` what became
 * `unresolvedLocations`.
 */
export type CallLocationExtraction = {
  requestedAreas: CallRequestedArea[];
  /** Specific properties the realtor described or offered, as spoken. Informational; never an area. */
  discussedListingAddresses: string[];
  resolved: ProcessedListingAddress[];
  unresolved: string[];
};

export type PhoneCallParsedOpenAiResponse = {
  customerDetails: PhoneCallParsedOpenAiResponseCustomerDetails;
  callSummary: string;
  customerSummary: string;
  speakerNames: {
    original: string;
    actual: string;
  }[];
  /** Absent on documents written before 2026-09-06. */
  locationExtraction?: CallLocationExtraction;
};

export type CallSplitTranscription = {
  speaker: string;
  sentence: string;
};

// under payCall-settings/<localNumber>/calls-summary/<callId>/enrichment/transcriptAndSummary
export const CALL_TRANSCRIPT_AND_SUMMARY_SUBCOLLECTION_NAME = 'enrichment/transcriptAndSummary';
export type CallTranscriptAndSummary = Partial<BaseDocument> & {
  AI: PhoneCallParsedOpenAiResponse;
  jobId: string;
  summary: string;
  textTranscription: string;
  splitTranscription: CallSplitTranscription[];
  /**
   * False when the recording held no actual conversation — ringback, hold music, silence, a
   * voicemail greeting, an IVR menu, or nothing but the automated call announcement. Such a
   * document is a MARKER: it exists so the call is not transcribed again, and every analysis
   * field on it is deliberately empty. Nothing is sent to the realtor and nothing is written to
   * the contact for one of these.
   *
   * Absent on documents written before 2026-08-24, which is why every consumer must treat
   * `undefined` as "assume there was a conversation" — the old behaviour.
   */
  hasConversation?: boolean;
  /** What the recording actually contained: conversation | ringback | hold_music | silence | voicemail | ivr | noise | other. */
  audioContent?: string;
};

export const CONTACT_FAVORITE_LISTINGS_COLLECTION_NAME = 'favorite-listings-of-contact';
export type FavoriteListingsOfContact = Partial<BaseDocument> & {
  realtorId: string; // the user mail
  listingsListsIds: string[]; // listings lists ids that have been added to the contact's favorites
  listingIds: string[];
  listingIdsUserLiked: string[];
  listingIdsUserVisited: string[]; // to track which listings the user has visited
  listingIdsUserDisliked: string[];
  listingIdsWithPicturesHidden: string[]; // to hide all pictures of a listing
  /**
   * @deprecated Dead since 2026-07 and unreferenced by any reader or writer in either repo. Kept
   * only because historical minisite docs carry it. Clean photos now show for every listing that
   * has them once the realtor accepted the rights terms — `User.watermarkTermsAcceptedAt`.
   */
  showWatermarkFreeImages?: boolean;
  /**
   * Listings this contact has a ONE-OFF presentation for, overriding the realtor's global record
   * in `listing-presentations`. Written by the "התאמה ללקוח הזה בלבד" escape hatch only; a listing
   * absent from here takes the global version, which is the normal path.
   *
   * A queryable top-level array rather than a flag inside `additionalDetails`, because the dialog
   * has to be able to ask "how many of my customers have a custom version of this listing?" — a
   * nested `additionalDetails.{listingId}.overridesGlobal` would need a composite index PER
   * listingId, which is impossible. Same convention as `listingIdsWithPicturesHidden`.
   */
  listingIdsWithCustomPresentation?: string[];
  additionalDetails: {
    [listingId: string]: {
      /**
       * The realtor's private note about THIS buyer and this property. Genuinely per-contact —
       * it is a sentence about a person — and it deliberately does NOT move to the global record.
       * Never rendered publicly.
       */
      listingNotes: string;
      /**
       * @see ListingPresentation — as of 2026-09-30 these are written per contact only for a
       * deliberate one-off exception (`listingIdsWithCustomPresentation`). The normal path writes
       * the realtor's global record, which fans out to here.
       */
      descriptionOverride: string;
      hiddenPictures: {
        /**
         * Whether the hidden set below applies. ⚠️ Independent of the set: switching to "show all"
         * or "hide all" flips this to false and LEAVES `pictures` alone. Clearing it lost a
         * hand-picked list on a mode flip, with no undo.
         */
        enabled: boolean;
        /** ORIGINAL (watermarked) picture urls — see docs/rules/listing-presentation-and-watermarks.md. */
        pictures: string[];
      };
    };
  };

  // AUTO GENERATED BY FUNCTION
  contactId?: string; // used for contacts lookups - the contact international phone number
  userId?: string; // used for contacts lookups - the user mail
  organizationIds?: string[]; // used for contacts lookups -the organizations the contact is associated with
};

/**
 * How the realtor's own browser recognises their minisite on the client portal, which has no sign-in:
 * the page's answer carries `ownerKey` (`REST_schema.PublicMinisiteResponse`) and the dashboard keeps the
 * signed-in account's key in the browser. Both are the first `MINISITE_OWNER_KEY_LENGTH` hex digits of
 * sha256(`MINISITE_OWNER_KEY_SALT` + the account's email, trimmed and lower-cased): never the email itself.
 */
export const MINISITE_OWNER_KEY_SALT = 'webot-minisite-owner:';
export const MINISITE_OWNER_KEY_LENGTH = 32;

export const LISTING_PRESENTATIONS_COLLECTION_NAME = 'listing-presentations';

export type ListingPicturesVisibilityMode = 'all' | 'some' | 'none';

/**
 * How a realtor presents ONE listing — their description rewrite and which photos buyers may see.
 * **One doc per (userId, listingId)**, with a DETERMINISTIC id (`listingPresentationDocId`) so the
 * pair can never acquire two records.
 *
 * This is the "edit once" record (2026-09-30). Before it, these fields lived only inside each
 * contact's minisite doc (`FavoriteListingsOfContact.additionalDetails[listingId]`), so a realtor
 * editing a listing for forty buyers did it forty times — and the one reuse that existed
 * (`getListingPriorEditsFromOtherMinisites`) copied at ADD time and never revisited, so a later
 * edit reached nobody.
 *
 * ⚠️ IT IS A WRITE-TIME AUTHORITY, NOT A NEW READ PATH. Saving it fans the values out to every one
 * of that realtor's minisites containing the listing, and the public page keeps reading exactly
 * what it reads today. That is deliberate: the minisite is an anonymous, client-only SPA route, so
 * a new blocking fetch there would mean a stale cached bundle, or one failed request, showing a
 * buyer the un-edited listing on a link that was already sent. The global record is what seeds the
 * NEXT minisite the listing is added to, and what the edit dialog reads back.
 *
 * `listingNotes` is absent by design: it is a note about a person, not about a property.
 */
export type ListingPresentation = Partial<BaseDocument> & {
  listingId: string;
  /** The realtor's mail, lowercased. */
  userId: string;
  /** Absent = show the listing's own text. NEVER stored as `''` — clearing deletes the field. */
  descriptionOverride?: string;
  /** Absent == 'all'. */
  picturesVisibilityMode?: ListingPicturesVisibilityMode;
  /**
   * ORIGINAL (watermarked) image urls. ⚠️ Kept even when the mode is not `'some'` — the mode and
   * the set are independent, and clearing it on a mode flip would lose the realtor's curation for
   * every customer at once.
   */
  hiddenPictures?: string[];
};

/**
 * One record per (realtor, listing), enforced by construction rather than by a transaction: an
 * auto-id collection queried by fields can mint duplicates on a double-click or two tabs, and the
 * resolver would then pick one arbitrarily — a bug that reproduces once a month and is unfindable.
 *
 * The mail is lowercased and `/` is escaped, since a Firestore document id may not contain one.
 */
export const listingPresentationDocId = (userId: string, listingId: string): string =>
  `${userId.trim().toLowerCase().replace(/\//g, '_')}|${listingId}`;

export const USER_NOTES_COLLECTION_NAME = 'user-notes';

/**
 * Who wrote a SYSTEM comment: an automated source that created or changed a contact (Haim,
 * 2026-10-19: "add a system comment when creating/updating from form, webhook, daniela, bot etc.").
 * The comment's own text names the source on its first line, so it reads right wherever notes are
 * shown; this marker is for the surfaces that style it.
 */
export const CONTACT_SYSTEM_NOTE_SOURCES = ['form', 'webhook', 'ai', 'bot', 'call', 'signature'] as const;
export type ContactSystemNoteSource = typeof CONTACT_SYSTEM_NOTE_SOURCES[number];
export const CONTACT_SYSTEM_NOTE_SOURCE_LABELS: Record<ContactSystemNoteSource, string> = {
  form: 'טופס לידים',
  webhook: 'וובהוק',
  ai: 'דניאלה',
  bot: 'בוט',
  call: 'שיחה',
  signature: 'חתימה דיגיטלית'
};

/**
 * A system comment as ONE line, for a feed (the customer page's activity, the timeline): its first
 * line (what happened, and from where), then its one change or how many there were. The comment's
 * own last line may already say how many more ("ועוד 6 שינויים"); those are counted, not shown. An
 * INDENTED line continues the change above it (a quoted text's own lines, contactChangeNote.ts),
 * never a change of its own.
 */
export const systemNoteSummary = (text: string): string => {
  const [head = '', ...rest] = text.split('\n').filter((line) => line.trim());
  const changes: string[] = [];
  for (const line of rest) {
    if (/^\s/.test(line) && changes.length) changes[changes.length - 1] += ` ${line.trim()}`;
    else changes.push(line.trim());
  }
  if (changes.length === 0) return head.trim();
  if (changes.length === 1) {
    const [only] = changes;
    return `${head.trim()} · ${only.length > 160 ? `${only.slice(0, 159).trimEnd()}…` : only}`;
  }
  const count = changes.reduce((sum, line) => {
    const more = /^ועוד (\d+) שינויים$/.exec(line);
    return sum + (more ? Number(more[1]) : 1);
  }, 0);
  return `${head.trim()} · ${count} שינויים`;
};

export type UserNote = {
  id: string; // client-generated id (timestamp + random)
  note: string;
  noteTime: Date;
  // Pinned notes surface first (and on the customer-page overview). Absent == not pinned.
  pinned?: boolean;
  // Voice note: public download URL of the recorded audio (Firebase Storage). When set,
  // `note` holds the transcription (or empty string when transcription is unavailable).
  audioUrl?: string;
  /**
   * Per-note org sharing. When the field is present and non-empty, the
   * note is visible to anyone in those orgs (not just the author). Since
   * 2026-10-29 a new note starts SHARED with every org the author is in
   * (dashboard `utils/noteSharing.ts`, server `contactNotes.service.ts`);
   * making it private REMOVES the field entirely (not `[]`), so the read
   * path can rely on a simple `array-contains` query without having to
   * filter empty arrays.
   */
  organizationIds?: string[];
  /** Present on a SYSTEM comment only: written by the server for an automated write, never by a person. */
  system?: { source: ContactSystemNoteSource };
};
export type UserNotesDocument = Partial<BaseDocument> & {
  userId: string; // user email
  contactId?: string; // contact international phone number (set when scoped to a contact)
  listingId?: string; // algolia objectID / listing id (set when scoped to a listing)
  // Facebook lead notes scope key (person / post / comment — see
  // facebookNoteScopeKey). Kept separate from contactId so contactId stays a
  // real phone number.
  facebookNoteScope?: string;
  notes: UserNote[]; // most-recent first
  /**
   * Union of per-note `organizationIds`, maintained automatically by the
   * write hooks. Exists so the doc is queryable via `array-contains` —
   * the per-note field alone wouldn't be (Firestore can't query into
   * array-of-objects). Removed (not `[]`) whenever no note is shared.
   */
  organizationIds?: string[];
};

// Doc-id format:
//  - contact-scoped: `{userId}__{contactId}`
//  - listing-scoped: `{userId}__listing__{listingId}`
export const userNotesDocId = (params: {
  userId: string;
  contactId?: string;
  listingId?: string;
  facebookNoteScope?: string;
}): string => {
  if (params.listingId) return `${params.userId}__listing__${params.listingId}`;
  // The scope key is self-prefixed (`fb-person-*`/`fb-post-*`/`fb-comment-*`),
  // so it can't collide with a contactId (phone) or the listing form.
  if (params.facebookNoteScope) return `${params.userId}__${params.facebookNoteScope}`;
  return `${params.userId}__${params.contactId}`;
};

// ── Contact documents (מסמכים tab of the customer page) ─────────────────────
// Generic per-contact file archive. Stored as a subcollection of the contact:
//   dashboard-users-v2/{userEmail}/contacts/{intlPhone}/documents/{docId}
export const CONTACT_DOCUMENTS_SUBCOLLECTION_NAME = 'documents';
export const contactDocumentCategories = ['customerDoc', 'signedContract', 'other'] as const;
export type ContactDocumentCategory = typeof contactDocumentCategories[number];
export type ContactDocument = Partial<BaseDocument> & {
  contactId: string; // contact international phone number (== parent contact doc id)
  userId?: string; // owner user email (== grandparent doc id; denormalized for collectionGroup lookups)
  organizationIds?: string[]; // org visibility, same convention as contacts (absent when not shared)
  name: string; // display file name
  category: ContactDocumentCategory;
  storagePath: string; // Firebase Storage object path
  downloadUrl: string; // resolved download URL for the stored object
  mimeType: string;
  sizeBytes: number;
  uploadedByEmail: string;
  uploadedAt: Date;
  /**
   * The storage object is shared with other records of this file: an office's undo handed the
   * file to several realtors, one record each over the same bytes (the server's
   * `crm/contactFilesPortRules.ts`). Deleting such a record leaves the bytes where they are.
   */
  storageShared?: boolean;
};

export const LISTING_PAGE_VIEWS_COLLECTION_NAME = 'listing-page-views';

export type ListingPageViewEvent = {
  timestamp: Date;
  userAgent?: string;
  listingId?: string; // if set, this was a listing detail view; if absent, it was a page-level view
  referrer?: string;
};

export type ListingPageViewsDocument = Partial<BaseDocument> & {
  favoriteListingsId: string;
  realtorId: string;
  contactId: string;

  totalPageViews: number;
  totalListingDetailViews: number;
  listingDetailViewCounts: Record<string, number>; // listingId -> view count

  lastPageViewAt?: Date;
  lastListingDetailViewAt?: Date;

  recentViewEvents: ListingPageViewEvent[];
};

export const LISTINGS_REALTOR_SAVED_LISTS_COLLECTION_NAME = 'saved-listing-lists';
export type ListingsRealtorSavedList = Partial<BaseDocument> & {
  name: string;
  userId: string; // email of the user who created the list
  listingIds: string[]; // array of listing object IDs
};

export const LISTINGS_REALTOR_BLACKLIST_COLLECTION_NAME = 'listings-realtor-blacklist';
export type ListingsRealtorBlacklist = Partial<BaseDocument> & {
  realtorId: string;
  listingIds: string[];
};

export const CALENDAR_SETTINGS_COLLECTION_NAME = 'calendar-settings-v2';
export type CalendarSettings = Partial<BaseDocument> & {
  admin: string;
  owners: string[];
  calendarName: string;
  ownerFullName: string;
  ownerPhoneNumberInternational: string;
  reminderHourBeforeEvent: boolean; // default - true
  reminderOnMorningDayBefore: boolean; // default - false
  reminderOnEveningBeforeEvent: boolean; // default - false
  reminderOnMorningOfEvent: boolean; // default - false -for meetings
  reminderOnMorningOfTask: boolean; // default - true - for tasks - will send 1 message in the morning of the task(s), regardless of the time of the task, to remind about them. Will be 1 message even if there are multiple tasks in the same day.
  reminderHourBeforeTask: boolean; // default - false - for tasks - will send a reminder message 1 hour before the task, based on the time of the task. Will be 1 message per task. Relevant only for tasks with a specific time, and not for all-day tasks.

  /**
   * Server-written mirror of `google-calendar-connections/{email}` (no secrets).
   * Present so the dashboard's existing settings listener renders the Google
   * Calendar connection state live. The authoritative record is the connection
   * doc; treat this as display state only.
   */
  googleCalendar?: GoogleCalendarConnectionSummary;
};

/**
 * Where an attendee came from.
 *  - `manual`   — picked by the realtor in the dashboard attendee field (the default).
 *  - `description` — auto-detected: a phone number written inside the event's free-text
 *                 description (typically an event created in Google Calendar, where there
 *                 is no attendee picker). See `server/common/phoneExtraction.ts`.
 *  - `google`   — a real Google Calendar attendee (has an email, may have no phone).
 */
export const calendarEventAttendeeSources = ['manual', 'description', 'google'] as const;
export type CalendarEventAttendeeSource = typeof calendarEventAttendeeSources[number];

export type CalendarEventAttendee = {
  firstName?: string;
  lastName?: string;
  phoneNumberObj?: PhoneNumber;
  phoneNumberInternational?: string;
  email?: string;
  source?: CalendarEventAttendeeSource;
};

export const calendarEventTypes = ['meeting', 'task'] as const;

export type CalendarEventType = typeof calendarEventTypes[number];

export const calendarEventStatuses = ['pending', 'done'] as const;

export type CalendarEventStatus = typeof calendarEventStatuses[number];

/**
 * Google Calendar sync state carried on a single CalendarEvent.
 *
 * `contentHash` is the loop breaker for two-way sync: it is the hash of the
 * CANONICAL GOOGLE-SIDE form of the event as of the last time the two sides
 * agreed. Before writing in either direction we recompute the hash and skip the
 * write when it matches — so an echo (we push → Google notifies us → we pull)
 * is a no-op instead of an infinite ping-pong that also re-fires WhatsApp.
 * See `docs/rules/google-calendar-sync.md`.
 */
export type CalendarEventGoogleSync = {
  eventId: string; // Google's event id
  calendarId: string; // the Google calendar the event lives on ('primary' or a calendar id)
  origin: 'webot' | 'google'; // which side created it
  contentHash: string;
  /**
   * Digest of the attendee list at the last push. Attendees are NOT part of
   * `contentHash` (they exist in Google only as rendered description text that
   * we strip), so this is the separate signal that an attendee-only edit still
   * needs pushing — otherwise Google's visible attendee line goes stale and a
   * later Google-side edit can resurrect a removed attendee and message them.
   */
  attendeesHash?: string;
  etag?: string;
  htmlLink?: string; // deep link to the event in Google Calendar
  lastPushedAt?: Date; // webot -> google
  lastPulledAt?: Date; // google -> webot
  lastError?: string; // last outbound failure, cleared on success
};

// under calendar-settings-v2/<calendarId>/calendar-events
export const CALENDAR_EVENTS_SUBCOLLECTION_NAME = 'calendar-events';
export type CalendarEvent = Partial<BaseDocument> & {
  title: string;
  /** INTERNAL to the realtor. Never sent to an attendee: that is what `clientNote` is for. */
  description?: string;
  /**
   * The ONLY realtor-typed text an attendee receives: one paragraph that rides the new-meeting,
   * rescheduled and reminder WhatsApps (never the cancellation). Meetings only. Webot-only: never
   * pushed to Google, never part of `googleSync.contentHash`, never filled from a Google event.
   */
  clientNote?: string;
  location?: string; // free text; round-trips with Google's `location` field
  textColor: string;
  allDay?: boolean;
  start: Date;
  end: Date;
  backgroundColor?: string;
  eventType: CalendarEventType;
  status?: CalendarEventStatus;
  attendees?: CalendarEventAttendee[];
  listingIds?: string[];
  listingsData?: ProcessedListingData[];

  googleSync?: CalendarEventGoogleSync;

  /**
   * The office colleague who put this TASK in this calendar (lowercased email). Absent on the
   * owner's own tasks. Written only by `POST /api/v1/calendar-tasks/assign` — the browser rules
   * refuse it — and it is what lets that colleague list, edit and cancel the task through the
   * server while it stays the OWNER's: their list, their reminders, their Google Calendar.
   * See docs/rules/calendar-task-assignment.md.
   */
  assignedByUserId?: string;

  // AUTO GENERATED BY FUNCTION
  contactId?: string; // used for contacts lookups - the contact international phone number
  userId?: string; // used for contacts lookups - the user mail
  organizationIds?: string[]; // used for contacts lookups -the organizations the contact is associated with
};

/**
 * Longest `clientNote` the form accepts and the server sends, in UTF-16 units (what `String.length`,
 * an input's `maxLength` and the send rail's per-parameter cap all count): it travels as one WhatsApp
 * template parameter.
 */
export const CALENDAR_CLIENT_NOTE_MAX_LENGTH = 300;

/** GET /api/v1/webot-calendar/client-note-status: whether the dashboard may offer the `clientNote` input. */
export type CalendarClientNoteStatusResponse = { enabled: boolean };

export type Caller = Partial<BaseDocument> & {
  callerNumberInternational?: string;
};

// ---------------------------------------------------------------------------
// Google Calendar two-way sync
// ---------------------------------------------------------------------------

/**
 * `google-calendar-connections/{userEmail}` — SERVER-ONLY (default-deny in
 * firestore.rules; it holds the OAuth refresh token). Everything the dashboard
 * needs is mirrored into `CalendarSettings.googleCalendar` as a
 * `GoogleCalendarConnectionSummary`, which carries no secrets.
 */
export const GOOGLE_CALENDAR_CONNECTIONS_COLLECTION_NAME = 'google-calendar-connections';

export const googleCalendarConnectionStatuses = [
  'connected',
  'needs_reauth',
  'disconnected'
] as const;
export type GoogleCalendarConnectionStatus = typeof googleCalendarConnectionStatuses[number];

/** Which way events flow. `two-way` is the default and what the UI offers. */
export const googleCalendarSyncDirections = ['two-way', 'to-google', 'from-google'] as const;
export type GoogleCalendarSyncDirection = typeof googleCalendarSyncDirections[number];

export type GoogleCalendarWatchChannel = {
  channelId: string; // uuid we generate
  resourceId: string; // opaque id Google gives back
  /** Secret echoed by Google in `X-Goog-Channel-Token`; we reject webhooks that don't match. */
  token: string;
  expiration: Date;
  createdAt: Date;
};

export type GoogleCalendarConnection = Partial<BaseDocument> & {
  userId: string; // webot user email — also the doc id
  googleEmail: string; // the Google account that granted consent
  calendarId: string; // 'primary' by default
  calendarSummary?: string; // human-readable calendar name, for the UI
  timeZone?: string; // the Google calendar's own timezone (IANA)
  status: GoogleCalendarConnectionStatus;
  direction: GoogleCalendarSyncDirection;
  syncTasks: boolean; // also mirror Webot tasks into Google (default false)
  /**
   * Auto-detect attendees on events created in Google Calendar: phone numbers
   * found in the event's title/description/location become attendees who get
   * WhatsApp invites/reminders. OFF by default (absent = off) — customers
   * complained about clients being messaged off events they typed into Google.
   */
  autoDetectAttendees?: boolean;
  scopes: string[];

  // --- secrets (never leave the server) ---
  refreshToken?: string;
  accessToken?: string;
  accessTokenExpiryDate?: number;

  // --- incremental sync state ---
  syncToken?: string;
  /** Set when Google 410s the sync token; the next run does a full window re-sync. */
  syncTokenInvalidatedAt?: Date;
  /**
   * The far edge of the window the current syncToken was built from. A Google
   * sync token permanently inherits its full sync's `timeMax`, so once `now`
   * approaches this the window is refreshed (a new full sync rolls it forward).
   */
  syncWindowUntil?: Date;
  watch?: GoogleCalendarWatchChannel;
  /**
   * When the pull cron first found the owner without the features a connection needs. Nothing
   * syncs from then on; if they are not back within a few days the connection is removed.
   * Cleared the moment the owner holds them again.
   */
  pausedSince?: Date;

  connectedAt: Date;
  /** Guards the first sync: events already in Google when the user connects must NOT fire WhatsApp. */
  initialSyncCompletedAt?: Date;
  lastPulledAt?: Date;
  lastPushedAt?: Date;
  lastReconciledAt?: Date;
  lastError?: string;
  lastErrorAt?: Date;
};

/**
 * Non-secret projection of the connection, mirrored onto
 * `calendar-settings-v2/{email}.googleCalendar` so the dashboard's existing
 * settings snapshot renders connection state live, with no polling.
 */
export type GoogleCalendarConnectionSummary = {
  status: GoogleCalendarConnectionStatus;
  googleEmail?: string;
  calendarId?: string;
  calendarSummary?: string;
  direction?: GoogleCalendarSyncDirection;
  syncTasks?: boolean;
  autoDetectAttendees?: boolean;
  connectedAt?: Date;
  lastSyncedAt?: Date;
  lastError?: string;
};

export type cardcomAccountSnapshot = Partial<BaseDocument> & {
  accountId: number;
  customerName: string;
  identificationIdNumber: string;
  localMobilePhoneNumber: string;
  email: string;

  hadAtLeastOneRecuringPaymentInHistory?: boolean;
  isRecurringPaymentActive?: boolean;

  paymentsHistory?: {
    date: Date;
    amount: number;
    amountBeforeTax: number;
  }[];

  // Recurring charges Cardcom has not collected (history rows whose Status is not SUCCESSFUL), rewritten whole by every
  // sync: `date` is the LAST attempt, `dueDate` when the charge fell due, `status` Cardcom's own (DEBTAUTOBILLING,
  // LOSTDEBT, PAYBYOTHERE, ONHOLD, ...).
  failedPaymentsHistory?: {
    date: Date;
    amount: number;
    amountBeforeTax: number;
    status?: string;
    dueDate?: Date;
    billingAttempts?: number;
  }[];

  lastPaymentDate?: Date;
  lastPaymentAmount?: number; // todo backfill

  paymentsHistoryTotalPaid?: number;

  connectedToOrganizationId?: string;
  connectedToUserIds?: string[];

  // todo remove
  lastPaymentsFromReport?: {
    date: Date;
    amount: number;
    amountBeforeTax: number;
  }[];

  // todo remove
  lastPaymentsFromReportTotal?: number;

  // todo remove
  lastPayments?: {
    date: Date;
    amount: number;
  }[];

  // todo remove
  lastPaymentsTotal?: number;
};

export const OFFICIAL_WHATSAPP_PHONE_NUMBERS_COLLECTION_NAME = 'official-whatsapp-phone-numbers';
export type OfficialWhatsappPhoneNumber = Partial<BaseDocument> & {
  facebookAccount: string;
  ownerDetails: string;
  whatsappPermanentToken: string;
  whatsappPhoneNumber: string;
  whatsappPhoneNumberId: string;
};

export type BotDefinitionStartNode = {
  id: string;
  match: string[];
  matchType: string;
  name: string;
  response: {
    mediaLink: string;
    text: string;
  };
  status: string;
  actions: any[];
};

export enum AutoReplyBotTier {
  free = 'free',
  paid_shared = 'paid_shared',
  paid_dedicated = 'paid_dedicated'
}

export const OFFICIAL_WHATSAPP_BOTS_COLLECTION_NAME = 'official-whatsapp-bots';
export type OfficialWhatsappBot = Partial<BaseDocument> & {
  botDefinition: BotDefinitionStartNode[];
  ownerAlias?: string;
  ownerFullName?: string;
  ownerShortName?: string;
  ownerMail?: string;
  ownerPhoneNumber?: string;
  tier?: AutoReplyBotTier;
  whatsappPhoneNumber?: string;
  customerWebhook?: string;
};

export const SYSTEM_SENT_WHATSAPP_MESSAGES_COLLECTION_NAME = 'system-sent-wa-messages';
export type SystemSentWhatsappMessage = Partial<BaseDocument> & {
  systemWhatsappPhoneNumberInternational: string;
  systemWhatsappPhoneNumberId: string;
  body: any;
  toNumberInternational: string;
  toUserId?: string;
  error?: any;
  onBehalfOfUserId?: string;
  // Meta's wamid from the send response — used to correlate webhook status events.
  wamid?: string;
};

// under official-whatsapp-phone-numbers/<phoneNumberInternational>/sent-message-on-behalf-tracker
export const SYSTEM_SENT_WHATSAPP_MESSAGES_ON_BEHALF_OF_USER_TRACKER_SUBCOLLECTION_NAME =
  'sent-message-on-behalf-tracker';
export type SystemSentWhatsappMessageOnBehalfOfUserTracker = Partial<BaseDocument> & {
  // fields ported from pipedream logic
  lastContactDate: Date;
  customerPointOfContact: {
    customerIntroName: string; // full name
    customerShortName: string;
    customerVirtualNumberInternational?: string; // optional
    customerForwardNumberInternational: string;
  };

  // new
  onBehalfOfUserId?: string;
};

// ============================================================================================
// OFFICIAL WHATSAPP (Meta Cloud API, per-customer) — see docs/whatsapp/design-final.md
// and docs/whatsapp/design-corrections.md (the corrections file WINS on any disagreement).
//
// Distinct from OfficialWhatsappPhoneNumber above, which models Webot's OWN system numbers on
// Webot's own WABA. This block models a CUSTOMER's own WhatsApp Business Account, connected by
// the customer through Meta Embedded Signup, where Meta bills the customer directly.
// ============================================================================================

/** Graph API version pinned for every official-WhatsApp call. Bump deliberately, never ambiently. */
export const WHATSAPP_GRAPH_API_VERSION = 'v25.0';

/**
 * How the customer's number is attached to the Cloud API.
 *  - 'coexistence': the number STAYS active in the WhatsApp Business app on their phone. This is
 *    the default, and the only mode most realtors will accept — their number is on every ad they
 *    have ever published. Cost: 20 msg/sec ceiling, no green tick, business name + profile picture
 *    LOCK at onboarding, the Business Profile API is unsupported, and the number can NEVER be
 *    moved to another WABA or provider.
 *  - 'cloud_api': a dedicated second number registered straight onto the Cloud API. The fallback
 *    if coexistence turns out to be unavailable for +972 (gate G0), and cleaner on the merits.
 */
export const whatsappOnboardingModes = ['coexistence', 'cloud_api'] as const;
export type WhatsappOnboardingMode = typeof whatsappOnboardingModes[number];

/**
 * Lifecycle of a customer's connection.
 *
 * `needs_payment` is a first-class state, not an error: Meta bills the customer directly, so a
 * connection with no funding source attached is fully wired and completely unable to send. It is
 * the single highest-risk assumption in the whole feature (500 non-technical realtors each
 * entering a card into Meta Business Suite), so it gets its own status and its own UI.
 */
export const whatsappConnectionStatuses = [
  'pending_es', // created, waiting for the customer to finish Meta's Embedded Signup popup
  'onboarding', // Embedded Signup returned; the server-side state machine is running
  'needs_payment', // wired, but no funding source on the WABA — cannot send
  'connected', // fully operational
  'needs_reauth', // the token was revoked; only the customer reconnecting fixes it
  'restricted', // Meta has restricted the account or the number
  'halted', // WEBOT stopped this tenant (quality brake or an operator)
  'disconnected', // the customer disconnected, or coexistence lapsed through inactivity
  'failed' // onboarding could not complete
] as const;
export type WhatsappConnectionStatus = typeof whatsappConnectionStatuses[number];

/** Meta's own quality rating for a phone number. Drives the automatic brake. */
export const whatsappQualityRatings = ['GREEN', 'YELLOW', 'RED', 'UNKNOWN'] as const;
export type WhatsappQualityRating = typeof whatsappQualityRatings[number];

/**
 * Meta's messaging-tier strings → unique business-initiated recipients per rolling 24h.
 *
 * ONE definition, because two things must agree on it: the campaign send budget (server,
 * `computeSendBudget`) and the limit chip on the connection card (dashboard). The tier is READ
 * from Meta (`whatsapp_business_manager_messaging_limit`) and never inferred from business
 * verification — measured 2026-10-06, two of three unverified portfolios were at `TIER_2K` while
 * the card said 250. A string missing here is UNKNOWN: the budget falls back to 250 and the chip
 * says nothing.
 */
export const WHATSAPP_TIER_DAILY_UNIQUE: Record<string, number> = {
  TIER_50: 50,
  TIER_250: 250,
  TIER_1K: 1000,
  TIER_2K: 2000,
  TIER_10K: 10000,
  TIER_100K: 100000,
  TIER_UNLIMITED: Number.MAX_SAFE_INTEGER
};

/**
 * The ordered steps of the onboarding state machine. Stored so the machine is IDEMPOTENT: it can
 * be re-driven from any point (by a retry, or by the reconcile cron) without repeating work.
 *
 * `profile_set` runs on 'cloud_api' ONLY — the Business Profile API is not supported on
 * coexistence numbers (see design-corrections.md C2).
 */
export const whatsappOnboardingSteps = [
  'code_exchanged',
  'waba_verified',
  'phone_number_resolved',
  'app_subscribed',
  'pin_set',
  'number_registered',
  'profile_set',
  'payment_checked',
  'coexistence_sync_requested'
] as const;
export type WhatsappOnboardingStep = typeof whatsappOnboardingSteps[number];

export const WHATSAPP_CONNECTIONS_COLLECTION_NAME = 'whatsapp-connections';
/**
 * SERVER-ONLY. Holds the customer's Graph access token, so nothing client-side may ever read it.
 * The non-secret subset is mirrored to `whatsapp-tenant-status` for the dashboard.
 *
 * Doc id: the user's email (== User.id), matching the `google-calendar-connections` precedent.
 */
export type WhatsappConnection = Partial<BaseDocument> & {
  userId: string; // owner email (== User.id)
  organizationIds?: string[];

  provider: 'meta_direct'; // present from day one: the SCHEMA migration is the expensive part
  mode: WhatsappOnboardingMode;
  status: WhatsappConnectionStatus;

  // --- Meta identifiers -------------------------------------------------------------------
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  /** The Meta business PORTFOLIO's display name (on_behalf_of_business_info.name). */
  businessName?: string;
  /** The WABA's own display name (the "WhatsApp account" row in WhatsApp Manager). */
  wabaName?: string;
  displayPhoneNumber?: string; // e.g. "+972 54-123-4567" as Meta renders it
  phoneNumberInternational?: string; // our normalised E.164, e.g. "+972541234567"
  verifiedName?: string; // the Hebrew business name — LOCKED after onboarding on coexistence

  // --- Token -------------------------------------------------------------------------------
  /**
   * The tenant's Meta access token.
   *
   * `accessToken` is the PLAINTEXT field the onboarding and messaging code reads and writes
   * today (same storage posture as `whatsappPermanentToken`). The AES-256-GCM triple below is
   * the target shape from `docs/whatsapp/design-final.md` — once `whatsappCrypto.ts` exists it
   * becomes the ONLY module allowed to touch it. Both are declared while that migration is
   * pending; anything reading a token must fall back across the two.
   */
  accessToken?: string;
  accessTokenCipher?: string;
  accessTokenIv?: string;
  accessTokenTag?: string;
  tokenCheckedAt?: Date;

  // --- Health -----------------------------------------------------------------------------
  qualityRating?: WhatsappQualityRating;
  messagingLimitTier?: string; // Meta's own string, e.g. "TIER_1K"
  businessVerificationStatus?: string; // from business_verification_status
  accountStatus?: string; // from status
  healthStatus?: unknown; // from health_status — shape is Meta's, kept opaque
  primaryFundingId?: string; // absent/null ⇒ needs_payment
  isSubscribedToApp?: boolean; // the hourly subscribed_apps audit writes this

  // --- Sending posture --------------------------------------------------------------------
  sendingPaused?: boolean;
  sendingPausedReason?: string;
  sendingPausedAt?: Date;

  // --- Onboarding progress ----------------------------------------------------------------
  completedSteps?: WhatsappOnboardingStep[];
  onboardingError?: { code?: string; message: string; at: Date };

  // --- Operational ------------------------------------------------------------------------
  lastWebhookAt?: Date;
  lastReconciledAt?: Date;
  lastAppOpenedAt?: Date; // coexistence: ~13 days of no app activity silently disconnects
  connectedAt?: Date;
  disconnectedAt?: Date;

  /**
   * Marks Webot's OWN WABA rather than a customer's. Such events must route rather than land in
   * the `unrouted` bucket, which would Slack-alert per event forever — see corrections C10.
   */
  internal?: boolean;
};

/**
 * One thing standing between the realtor and a working number, as a STRUCTURED row rather than a
 * sentence.
 *
 * `userFacingIssue` / `userFacingAction` carry exactly one problem and no link, which is wrong
 * whenever there are several — and there usually are. Measured on the first customer connection
 * (2026-08-30): the number was BANNED, the WABA had no payment method, the owning business was
 * unverified and the display name had been DECLINED, all at once. A single sentence can name one
 * of those, so the realtor fixes it, sees no change, and concludes the product is broken.
 *
 * Each issue therefore carries its own deep link into the Meta surface that resolves it. The
 * links are the whole point: none of these are fixable inside Webot, and "go to Meta Business
 * Settings" is not findable advice.
 */
export type WhatsappConnectionIssueCode =
  | 'banned'
  | 'account_violation'
  | 'no_payment_method'
  | 'business_unverified'
  | 'display_name_declined'
  | 'number_not_verified'
  | 'app_unsubscribed'
  | 'quality_red'
  | 'other';

export type WhatsappConnectionIssue = {
  code: WhatsappConnectionIssueCode;
  /** `blocking` = cannot send at all. `warning` = works, but degraded or at risk. */
  severity: 'blocking' | 'warning';
  /** Short Hebrew label. */
  title: string;
  /** Hebrew explanation of what it means, in a realtor's terms. */
  detail: string;
  /** Hebrew call to action, e.g. "הוספת אמצעי תשלום". */
  actionLabel?: string;
  /** Deep link into the exact Meta screen that fixes it. */
  actionUrl?: string;
  /** Meta's own error code when it came from `health_status`, for support tickets. */
  metaCode?: number;
};

export const WHATSAPP_TENANT_STATUS_COLLECTION_NAME = 'whatsapp-tenant-status';
/**
 * Client-READ, server-WRITE mirror of the non-secret parts of {@link WhatsappConnection}.
 *
 * A separate collection rather than a field on the user doc on purpose: `dashboard-users-v2` is
 * the most-written document in the dashboard, and a rules guard protecting a mirror field there
 * would have blast radius across every settings screen. This also faithfully copies the
 * google-calendar precedent, which mirrors onto a FEATURE doc, not the user doc.
 *
 * Doc id: the user's email.
 */
export type WhatsappTenantStatus = Partial<BaseDocument> & {
  userId: string;
  mode: WhatsappOnboardingMode;
  /**
   * What the realtor asked for, which `mode` no longer tells you once Meta has overruled it.
   * The connected card needs both to say the right sentence: `cloud_api` alone cannot distinguish
   * "you chose an API-only number" from "Meta refused to keep this number on your phone".
   */
  requestedMode?: WhatsappOnboardingMode;
  status: WhatsappConnectionStatus;
  displayPhoneNumber?: string;
  verifiedName?: string;
  /**
   * The Meta identity trio, so the realtor can verify they are looking at the RIGHT portfolio
   * before fixing billing/verification there: portfolio name + business id + WABA id.
   */
  wabaId?: string;
  businessId?: string;
  businessName?: string;
  wabaName?: string;
  qualityRating?: WhatsappQualityRating;
  messagingLimitTier?: string;
  isBusinessVerified?: boolean;
  sendingPaused?: boolean;
  completedSteps?: WhatsappOnboardingStep[];
  connectedAt?: Date;
  /** Plain-Hebrew statement of what is wrong. Never Meta's raw English error string. */
  userFacingIssue?: string;
  /** Plain-Hebrew statement of the ONE thing the realtor should do next. */
  userFacingAction?: string;
  /**
   * Everything blocking or degrading the connection, each with its own Meta deep link. Written
   * by the on-demand status check and by any webhook that learns of a restriction. An EMPTY
   * array means "checked, nothing wrong"; absent means "never checked".
   */
  issues?: WhatsappConnectionIssue[];
  /** When Meta was last asked directly (the status check), as opposed to a webhook arriving. */
  lastCheckedAt?: Date;
};

export const WHATSAPP_NUMBER_MAP_COLLECTION_NAME = 'whatsapp-number-map';
/**
 * SERVER-ONLY routing table: Meta `phone_number_id` → tenant.
 *
 * Kept as its own collection so webhook routing is a single point-read on the hot path instead
 * of a query. Rows are never deleted — a late webhook for a retired `phone_number_id` must still
 * resolve to the tenant it belonged to. `activeUntil` is an audit stamp, nothing more.
 *
 * Doc id: the `phone_number_id`.
 */
export type WhatsappNumberMapEntry = Partial<BaseDocument> & {
  phoneNumberId: string;
  wabaId: string;
  userId: string;
  activeFrom: Date;
  activeUntil?: Date;
  internal?: boolean;
};

/** Processing state of one webhook event in the durable inbox. */
export const whatsappInboxStatuses = [
  'pending',
  'processing',
  'done',
  'failed',
  'unrouted'
] as const;
export type WhatsappInboxStatus = typeof whatsappInboxStatuses[number];

export const WHATSAPP_WEBHOOK_INBOX_COLLECTION_NAME = 'whatsapp-webhook-inbox';
/**
 * SERVER-ONLY. The durable ack buffer, and the reason inbound messages cannot be lost.
 *
 * Meta delivers at-least-once, unordered, and there is NO API that replays what we acked and
 * dropped — so an event is persisted BEFORE the 200, never after. Cloud Run also throttles CPU
 * outside request processing, which makes post-ack work explicitly not guaranteed to run.
 *
 * The doc id IS the dedup key: `.create()` failing with ALREADY_EXISTS (gRPC 6) is the duplicate
 * signal, so durability and dedup cost exactly one write. This collection is also the replay tape.
 */
export type WhatsappWebhookInboxEntry = Partial<BaseDocument> & {
  eventKey: string; // == doc id
  wabaId?: string;
  phoneNumberId?: string;
  userId?: string; // resolved tenant; absent ⇒ status 'unrouted'
  field: string; // e.g. 'messages', 'message_template_status_update'
  value: unknown; // the raw `value` object, exactly as Meta sent it
  receivedAt: Date;
  /**
   * True when the event belongs to one of WEBOT's OWN system numbers rather than a customer's.
   * Both rails share this one endpoint, so the flag is what decides which handler runs — and it
   * is why a system-number event must never be reported as `unrouted`.
   */
  internal?: boolean;
  status: WhatsappInboxStatus;
  attempts?: number;
  /** Lease held by whichever worker is processing, so the inline path and the sweeper cannot race. */
  leaseUntil?: Date;
  lastError?: string;
  processedAt?: Date;
};

export const WHATSAPP_CONVERSATIONS_COLLECTION_NAME = 'whatsapp-conversations';
/**
 * One thread between a business NUMBER and one consumer.
 *
 * Doc id: `<numberKey>__<waId>` — keyed on the NUMBER (E.164 digits), deliberately NOT on
 * `phoneNumberId`. A re-onboard (which coexistence inactivity forces after ~13 idle days) mints
 * a NEW `phone_number_id`; keying on it would fork every thread and make the realtor's whole
 * inbox appear to reset (corrections C20). Keying on the number rather than the user is what
 * lets several users share one inbox and one user hold several numbers — access is governed by
 * the `whatsapp-number-access` doc, not by fields here.
 */
export type WhatsappConversation = Partial<BaseDocument> & {
  /** The E.164-digits tenant key. Every client query MUST pin `numberKey ==` (rules demand it). */
  numberKey?: string;
  /** Informational only ('__webot__' on system numbers). Access control lives on the access doc. */
  userId?: string;
  organizationIds?: string[];
  waId: string; // the consumer's WhatsApp id (digits, no '+')
  phoneNumberId?: string; // current tenant number — a field, never part of identity
  contactId?: string; // joined Webot contact, when one exists
  displayName?: string;
  lastMessageAt?: Date;
  lastMessagePreview?: string;
  lastInboundAt?: Date;
  /**
   * When the 24-hour customer-service window closes. Free-form messages are only permitted
   * before this instant; after it, only an approved template may be sent.
   *
   * Maintained transactionally with a monotonic max — a webhook redelivered days late must NOT
   * resurrect a window that already expired, or the UI tells the realtor a send is free and Meta
   * answers 131047.
   */
  windowExpiresAt?: Date;
  unreadCount?: number;
  /**
   * Denormalised mirror of the smart assistant's grip on this thread (see
   * WhatsappAssistantSession, server-side). Written by the assistant service only, so the
   * inbox thread list can badge "the bot is handling this" off the listener it already has.
   * Absent = the assistant was never involved.
   */
  assistantStatus?: WhatsappAssistantSessionStatus;
};

export const whatsappMessageDirections = ['inbound', 'outbound'] as const;
export type WhatsappMessageDirection = typeof whatsappMessageDirections[number];

/**
 * Delivery state. RANK-ORDERED, and applied monotonically: Meta's statuses arrive out of order
 * (a `read` can land before its `delivered`), so a status only ever moves a message FORWARD.
 * `failed` is terminal and absorbs everything.
 */
export const whatsappMessageStatuses = ['pending', 'sent', 'delivered', 'read', 'failed'] as const;
export type WhatsappMessageStatus = typeof whatsappMessageStatuses[number];

export const WHATSAPP_MESSAGES_COLLECTION_NAME = 'whatsapp-messages';
/**
 * One WhatsApp message, inbound or outbound.
 *
 * Doc id: `wa_<sha256(phoneNumberId|wamid) truncated>` — a HASH, not the raw wamid. A wamid is
 * base64 and can legally contain '/', which Firestore forbids in a document id.
 *
 * A root collection carrying `{userId, organizationIds}` (the `user-notes` convention) so an org
 * read needs no `get()` on a parent document in firestore.rules.
 */
export type WhatsappMessage = Partial<BaseDocument> & {
  /** The E.164-digits tenant key — the access-control anchor (see WhatsappConversation). */
  numberKey?: string;
  /** Informational only ('__webot__' on system numbers). Access control lives on the access doc. */
  userId?: string;
  organizationIds?: string[];
  conversationId: string;
  waId: string;
  phoneNumberId?: string;
  wamid: string;
  direction: WhatsappMessageDirection;
  status: WhatsappMessageStatus;
  statusRank?: number; // the monotonic guard backing `status`
  type: string; // 'text' | 'image' | 'template' | 'interactive' | …
  text?: string;
  mediaId?: string;
  mediaUrl?: string; // re-hosted to GCS; Meta's media URLs expire
  mediaMimeType?: string;
  caption?: string;
  /** Voice notes only — Hebrew transcript shown under the audio player (same engine as the AI). */
  transcription?: string;
  templateName?: string;
  /** With templateName, the full template identity — the per-template analytics key. */
  templateLanguage?: string;
  /** Which rail sent it: campaign runner / inbox escape hatch / direct API / legacy automations. */
  source?: 'campaign' | 'inbox' | 'api' | 'automation';
  campaignId?: string;
  /** True when the consumer sent this from their phone under coexistence (`smb_message_echoes`). */
  fromPhoneEcho?: boolean;
  contextWamid?: string; // the message this one replies to
  timestamp: Date;
  errorCode?: number;
  errorMessage?: string;
  pricingCategory?: string; // 'marketing' | 'utility' | 'authentication' | 'service'
};

/**
 * The number-tenant key: E.164 digits, no '+' — `'+972 53-352-3159'` → `'972533523159'`.
 * Stable across re-onboards (unlike `phone_number_id`) and doc-id safe by construction.
 */
export const numberKeyFromPhone = (value: string): string => value.replace(/[^\d]/g, '');

export const WHATSAPP_NUMBER_ACCESS_COLLECTION_NAME = 'whatsapp-number-access';

/**
 * Client-READ (members only), server-write. One doc per surfaced number, doing double duty:
 *
 * 1. **Membership** — `memberMails` is THE access-control list for the number's inbox. The
 *    firestore rules for conversations/messages resolve it with a `get()` (same pattern as
 *    `payCall-settings.ownerMails`), and the dashboard's number dropdown is an
 *    `array-contains` query on it.
 * 2. **Status mirror** — the non-secret status subset (same field names as
 *    {@link WhatsappTenantStatus}) so the dashboard renders the number's health from the same
 *    listener that powers the dropdown. System numbers get a static `connected`.
 *
 * Doc id: the `numberKey` (E.164 digits).
 */
export type WhatsappNumberAccess = Partial<BaseDocument> & {
  numberKey: string;
  /** 'customer' = a tenant connection owns it; 'system' = one of Webot's own numbers. */
  kind: 'customer' | 'system';
  /** The connecting user (may disconnect it). Absent on system numbers. */
  ownerMail?: string;
  /** Everyone who sees this number in their dashboard. */
  memberMails: string[];
  displayPhoneNumber?: string;
  verifiedName?: string;
  /** The number's WABA — lets the campaign wizard match templates to the sending number. */
  wabaId?: string;
  /** The Meta identity (portfolio name/id, WABA name) — shown on the connection card. */
  businessId?: string;
  businessName?: string;
  wabaName?: string;
  // --- status mirror subset (see WhatsappTenantStatus) --------------------------------------
  status?: WhatsappConnectionStatus;
  qualityRating?: WhatsappQualityRating;
  sendingPaused?: boolean;
  isBusinessVerified?: boolean;
  /** Meta's own tier string. Absent = not read yet: show NO limit, never assume the lowest one. */
  messagingLimitTier?: string;
  userFacingIssue?: string | null;
  userFacingAction?: string | null;
  /**
   * Whether WhatsApp still works on the owner's PHONE for this number — the fact realtors ask
   * support about most often. Optional because docs written before 2026-09-24 do not carry it:
   * a consumer must treat `undefined` as "unknown" and say NOTHING, never assume `cloud_api`.
   */
  mode?: WhatsappOnboardingMode;
  requestedMode?: WhatsappOnboardingMode;
  /**
   * Audit stamp of the last membership change made through the dashboard's share dialog
   * (`POST /whatsapp/numbers/:numberKey/sharing`). Absent on docs only ever written by
   * onboarding/disconnect or by support.
   */
  membershipUpdatedAt?: Date;
  membershipUpdatedBy?: string;
};

/** Firestore forbids '/' in a doc id, and emails carry '.'; both map to '_'. */
export const sanitizeDocIdPart = (value: string): string => value.replace(/[/.]/g, '_');

// --------------------------------------------------------------------------------------------
// Templates — realtor-authored Meta message templates, per-WABA.
// --------------------------------------------------------------------------------------------

// --------------------------------------------------------------------------------------------
// Smart assistant — the programmable, TOOL-LESS auto-responder on an official number.
// Config is per NUMBER (never per user), like conversations, messages and templates above.
// The session/transcript documents are SERVER-ONLY and have no mirror here.
// --------------------------------------------------------------------------------------------

export const WHATSAPP_ASSISTANTS_COLLECTION_NAME = 'whatsapp-assistants';

/** How a conversation comes under the assistant's control. */
export const whatsappAssistantAutoStartModes = ['outbound_only', 'new_conversations'] as const;
export type WhatsappAssistantAutoStart = typeof whatsappAssistantAutoStartModes[number];

/** The assistant's grip on one thread. Only `active` produces replies. */
export const whatsappAssistantSessionStatuses = ['active', 'handed_off', 'completed', 'stopped'] as const;
export type WhatsappAssistantSessionStatus = typeof whatsappAssistantSessionStatuses[number];

/** One step of the follow-up ladder (see WhatsappAssistantFollowUps). */
export type WhatsappAssistantFollowUpStep = {
  /** Hours of silence after the assistant's last message (the opener, a reply or the previous follow-up). */
  afterHours: number;
  /**
   * The APPROVED template sent when the person's 24h window is closed, which is always the case for
   * someone who never answered the opener. Absent = the step goes only while the window is open,
   * written by the assistant itself, and is skipped otherwise.
   */
  templateName?: string;
  templateLanguage?: string;
};

/**
 * Follow-ups to a person who went quiet. Each step goes `afterHours` after the assistant's last
 * message, at most once per conversation (the count is never reset by a reply; a re-open resets it).
 * Inside the 24h window the assistant writes the follow-up itself; outside it, the step's template
 * goes. Never to a conversation a human took over, that closed or was asked to stop, and only in
 * the sending hours (the server's `whatsappAssistantFollowUpRules.ts`). OFF unless `enabled`.
 */
export type WhatsappAssistantFollowUps = {
  enabled: boolean;
  steps: WhatsappAssistantFollowUpStep[];
};

/** What the dashboard and the server both allow on a follow-up ladder. */
export const WHATSAPP_ASSISTANT_FOLLOW_UP_LIMITS = { maxSteps: 3, minHours: 1, maxHours: 72 } as const;

/**
 * Client-READ (members of the number), server-write. Doc id = the `numberKey`.
 *
 * `systemPrompt` is the owner-authored half of the persona; the server always prepends its own
 * non-negotiable guardrails (WhatsApp formatting, "never claim to be human", no invented
 * commercial terms, the control-tag protocol) which the owner cannot remove.
 */
export type WhatsappAssistantConfig = Partial<BaseDocument> & {
  numberKey: string;
  /** OFF by default, always. Enabling is an explicit act, never a side effect. */
  enabled: boolean;
  systemPrompt: string;
  /**
   * - `outbound_only`: answers ONLY in threads the assistant opened itself.
   * - `new_conversations`: additionally adopts a first inbound on a thread with no history.
   */
  autoStart: WhatsappAssistantAutoStart;
  openerTemplateName?: string;
  openerTemplateLanguage?: string;
  /** Hard per-conversation turn ceiling — prompt-independent. */
  maxTurnsPerConversation: number;
  /** Follow-ups to a person who went quiet. Edited from the dashboard (whitelisted, validated). */
  followUps?: WhatsappAssistantFollowUps;
  /** Last editor's email — display/audit only, never an access field. */
  updatedBy?: string;
};

export const WHATSAPP_TEMPLATES_COLLECTION_NAME = 'whatsapp-templates';

/**
 * Client-READ (members of the number), server-write. Keyed on (name, language) because both are
 * IMMUTABLE on Meta's side and together unique per WABA, whereas the Meta template id is unknown
 * until create returns.
 *
 * The first segment is the NUMBER, not the author. Templates live on the WABA and are shared by
 * everyone in the number's `memberMails`, so a user-keyed id mints a second doc for the same Meta
 * template the moment a teammate syncs — and the templates page then renders each one twice.
 */
export const whatsappTemplateDocId = (numberKey: string, name: string, language: string): string =>
  `${sanitizeDocIdPart(numberKey)}__${name}__${language}`;

export const whatsappTemplateStatuses = [
  'DRAFT', // Webot-local, never submitted to Meta
  'PENDING',
  'IN_REVIEW',
  'APPROVED',
  'REJECTED',
  'PAUSED',
  'DISABLED',
  'FLAGGED',
  'IN_APPEAL',
  'LIMIT_EXCEEDED',
  'LOCKED',
  'ARCHIVED',
  'UNARCHIVED',
  'PENDING_DELETION',
  'DELETED',
  'REINSTATED'
] as const;
export type WhatsappTemplateStatus = typeof whatsappTemplateStatuses[number];

export const whatsappTemplateCategories = ['MARKETING', 'UTILITY', 'AUTHENTICATION'] as const;
export type WhatsappTemplateCategory = typeof whatsappTemplateCategories[number];

/**
 * What a template parameter is filled from at send time. 'static' = one value for the whole
 * campaign; 'perRecipient' = supplied per recipient (group member fields land here).
 */
export const whatsappParamBindings = [
  'contact.firstName',
  'contact.lastName',
  'contact.fullName',
  'contact.phone',
  /** First entry of `interestedInLocations` — the area the customer actually named first. */
  'contact.interestedCity',
  'contact.interestedNeighborhood',
  /** The contact's minisite, as a FULL public URL. For body text only — never a URL button. */
  'contact.minisiteLink',
  /** The same minisite as a BARE doc id, for a URL button's dynamic suffix. */
  'contact.minisiteId',
  /**
   * The link to the campaign's LISTING inside the contact's minisite (`…/myListings/{id}/{listingId}`).
   * Resolves only on a campaign that carries a `listingId` and for a contact whose minisite holds a
   * listing — the listing-share flow adds the listing to each recipient's minisite first.
   */
  'contact.minisiteListingLink',
  'listing.city',
  'listing.street',
  'listing.neighborhood',
  'listing.rooms',
  'listing.sizeSqm',
  'listing.price',
  'listing.floor',
  /**
   * The listing's page in the RECIPIENT's own minisite (`…/myListings/<minisite>/<listing>`), resolved per
   * recipient at campaign create — the minisite is created, or given the listing, if it lacks it. Never a
   * Yad2 / Madlan ad and never a page built in the browser (Haim, 2026-10-21).
   */
  'listing.link',
  /** The listing ad's own text, flattened to one line and cut at LISTING_DESCRIPTION_MAX_CHARS. */
  'listing.description',
  /** "4 חדרים, 98 מ"ר, קומה 3, הדר, חיפה" — composed from the listing's fields; where, never the address. */
  'listing.summary',
  'realtor.name',
  'realtor.company',
  'realtor.phone',
  'static',
  'perRecipient'
] as const;
export type WhatsappParamBinding = typeof whatsappParamBindings[number];

/**
 * The bindings the server can fill BY ITSELF, per recipient, with no realtor input.
 *
 * Everything else needs a human or a context we do not have on the campaign path:
 *  - `static` / `perRecipient` are the realtor's own values, by definition.
 *  - `listing.*` comes from the ONE listing the realtor picks in the send dialog (`isListingBinding`):
 *    constant for the send, filled in the browser and carried as the campaign's static values
 *    (`isListingFieldBinding`). The one exception is `listing.link`, below: per recipient.
 *  - `realtor.*` is campaign-CONSTANT rather than per-recipient, so it is resolved once at create
 *    and folded into the campaign's static values instead of being repeated 5,000 times.
 *
 * Read this predicate, never `binding !== 'static'`. That shortcut is wrong twice over: a template
 * synced from WhatsApp Manager is stamped `perRecipient` with a raw slug as its label, and a param
 * written before this field existed has `binding: undefined`. Both would be claimed as "we fill
 * this for you" by the loose test, and both are false.
 */
export const AUTO_RESOLVABLE_BINDINGS = [
  'contact.firstName',
  'contact.lastName',
  'contact.fullName',
  'contact.phone',
  'contact.interestedCity',
  'contact.interestedNeighborhood',
  'contact.minisiteLink',
  'contact.minisiteId',
  'contact.minisiteListingLink',
  // The campaign's listing in each recipient's own minisite — the same link as the one above.
  'listing.link'
] as const;
export type WhatsappAutoBinding = typeof AUTO_RESOLVABLE_BINDINGS[number];

export const isAutoResolvableBinding = (binding?: WhatsappParamBinding): binding is WhatsappAutoBinding =>
  binding !== undefined && (AUTO_RESOLVABLE_BINDINGS as readonly string[]).includes(binding);

/** Campaign-constant bindings, resolved once per campaign rather than per recipient. */
export const isRealtorBinding = (binding?: WhatsappParamBinding): boolean =>
  binding === 'realtor.name' || binding === 'realtor.company' || binding === 'realtor.phone';

/**
 * `listing.*` — filled from ONE listing the realtor picks in the send dialog (2026-10-21). Constant
 * for the whole send, like `realtor.*`: the dialog fills the values from the picked listing, the
 * realtor may edit them, and they ride as the campaign's static values.
 */
export const isListingBinding = (binding?: WhatsappParamBinding): boolean => typeof binding === 'string' && binding.startsWith('listing.');

/**
 * The listing fields the SEND DIALOG fills from the picked listing — every `listing.*` but `listing.link`,
 * which is per recipient (the page in that contact's minisite) and resolved by the server. A template
 * with any `listing.*` still ASKS for a listing (`isListingBinding`): the link needs it too.
 */
export const isListingFieldBinding = (binding?: WhatsappParamBinding): boolean => isListingBinding(binding) && !isAutoResolvableBinding(binding);

/**
 * What a `listing.*` field is filled from: a structural subset of the listing document (a listings
 * search hit carries all of it). There is no link here on purpose: `listing.link` is the recipient's
 * minisite page, which only the server can know.
 */
export type ListingBindingSource = {
  address?: { city?: string; neighborhood?: string; street?: string; houseNumber?: string | number; floor?: string | number } | null;
  roomsCount?: number | string | null;
  squareMeter?: number | string | null;
  price?: number | string | null;
  description?: string | null;
};

/**
 * The fixed address in front of a URL button's variable. A button synced back from Meta carries its
 * braces percent-encoded (`https://app.webot.co.il/%7B%7Blisting_path%7D%7D` — every starter pack's
 * "נכס חדש"), so either spelling ends it. Read through this, never `split('{{')`.
 */
export const urlButtonFixedPart = (url: string): string => url.split(/\{\{|%7B%7B/i)[0];

/**
 * The variable a URL button's address ends in: the stored `paramName`, else the name read off the
 * address itself (`{{name}}`, or Meta's encoded `%7B%7Bname%7D%7D`). A doc synced from Meta before
 * `mergeButtonsFromMeta` kept `paramName` lost it for good — the starter pack's "נכס חדש" on 2 of
 * the 3 numbers holding it approved (measured 2026-10-25) — and then the send dropped the button's
 * parameter (Meta 132000 for every recipient) while the send dialog called the button a fixed
 * address. Read a URL button's variable through this, never `button.paramName` alone.
 */
export const urlButtonParamName = (button: { type: string; url?: string; paramName?: string }): string | undefined => {
  if (button.type !== 'URL') return undefined;
  if (button.paramName) return button.paramName;
  return /(?:\{\{|%7B%7B)\s*([a-z][a-z0-9_]*)\s*(?:\}\}|%7D%7D)\s*$/i.exec(button.url ?? '')?.[1];
};

/** One template parameter's worth of a listing's description: one line, the whole body stays under Meta's 1,024. */
export const LISTING_DESCRIPTION_MAX_CHARS = 300;

const listingPositiveNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.replace(/[^\d.]/g, '')) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const listingText = (value: unknown): string => (typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '');

/**
 * ONE value for ONE `listing.*` binding — the same text in the send dialog's preview and in the
 * message. '' when the listing says nothing, and the param's fallback applies as for any binding.
 * Formats follow the starter templates, whose bodies carry the units ("{{rooms}} חדרים", "{{price}}
 * ש"ח"). A built area under 10 m² is a placeholder ("4 rooms, 1 m²"), never a size. The description
 * is flattened to one line: Meta refuses a parameter with a newline, a tab or four spaces in a row.
 */
export const listingBindingValue = (binding: WhatsappParamBinding | undefined, listing: ListingBindingSource): string => {
  const address = listing.address ?? {};
  const street = [listingText(address.street), listingText(address.houseNumber)].filter(Boolean).join(' ');
  const rooms = listingPositiveNumber(listing.roomsCount);
  const area = listingPositiveNumber(listing.squareMeter);
  const sizeSqm = area !== undefined && area >= 10 ? Math.round(area) : undefined;
  const price = listingPositiveNumber(listing.price);
  const floorText = listingText(address.floor);
  const floor = floorText === '0' ? 'קרקע' : floorText;
  switch (binding) {
    case 'listing.city':
      return listingText(address.city);
    case 'listing.neighborhood':
      return listingText(address.neighborhood);
    case 'listing.street':
      return street;
    case 'listing.rooms':
      return rooms !== undefined ? String(rooms) : '';
    case 'listing.sizeSqm':
      return sizeSqm !== undefined ? String(sizeSqm) : '';
    case 'listing.price':
      return price !== undefined ? Math.round(price).toLocaleString('en-US') : '';
    case 'listing.floor':
      return floor;
    case 'listing.link':
      // Per recipient, resolved by the server (the contact's minisite page) — never from the listing.
      return '';
    case 'listing.description': {
      const flat = listingText(listing.description).replace(/\s+/g, ' ');
      if (flat.length <= LISTING_DESCRIPTION_MAX_CHARS) {
        return flat;
      }
      const cut = flat.slice(0, LISTING_DESCRIPTION_MAX_CHARS);
      return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), LISTING_DESCRIPTION_MAX_CHARS - 40)).trimEnd()}…`;
    }
    case 'listing.summary':
      // WHERE, not the address: the neighbourhood, else the street's NAME, and the city — never the
      // house number (Haim, 2026-10-26: "let's have a simpler address… maybe the street name or the
      // neighborhood name"). This line goes to many buyers at once; the exact address waits for the
      // listing page the button opens.
      return [
        rooms !== undefined ? `${rooms} חדרים` : '',
        sizeSqm !== undefined ? `${sizeSqm} מ"ר` : '',
        floor ? (floor === 'קרקע' ? 'קומת קרקע' : `קומה ${floor}`) : '',
        [listingText(address.neighborhood) || listingText(address.street), listingText(address.city)].filter(Boolean).join(', '),
      ]
        .filter(Boolean)
        .join(', ');
    default:
      return '';
  }
};

/** How many recipients a binding could actually be filled for — shown before a campaign starts. */
export type WhatsappParamCoverage = {
  paramName: string;
  hebrewLabel: string;
  binding: WhatsappParamBinding;
  /** Recipients whose value came from the binding itself. */
  resolved: number;
  /** Recipients who will receive `fallbackValue` instead. */
  fallback: number;
  /**
   * Recipients NOT messaged at all, because the value was missing and the param has no fallback.
   * An empty fallback is read as "skip rather than send something wrong" — see createCampaign.
   */
  skipped: number;
};

export type WhatsappTemplateParam = {
  /** Meta requires lowercase ASCII: ^[a-z][a-z0-9_]*$ . The realtor NEVER sees this. */
  paramName: string;
  /** What the realtor sees, e.g. "שם הלקוח". Meta never sees this. */
  hebrewLabel: string;
  /** Required by Meta AND drives the builder preview. */
  exampleValue: string;
  binding: WhatsappParamBinding;
  /** Used when the bound field is empty — Meta REJECTS an empty parameter outright. */
  fallbackValue: string;
  maxChars?: number;
};

export type WhatsappTemplateButton =
  | { type: 'QUICK_REPLY'; text: string; payload?: string; isOptOut?: boolean }
  | { type: 'URL'; text: string; url: string; exampleSuffix?: string; paramName?: string }
  | { type: 'PHONE_NUMBER'; text: string; phoneNumber: string };

export type WhatsappTemplate = Partial<BaseDocument> & {
  userId: string;
  organizationIds?: string[];
  wabaId: string;
  /**
   * The number this template belongs to — and the ACCESS ANCHOR: everyone in that number's
   * `whatsapp-number-access.memberMails` sees it, exactly as they see its conversations. Every
   * client query MUST pin `numberKey ==` (the rule is proven from the query constraint), and
   * doc-scoped ops (edit/delete/unpause/sync) route their Graph calls through this number's
   * credentials.
   *
   * Optional only because docs written before the number-scope migration may predate it; treat
   * an absent value as legacy and fall back to `userId`.
   */
  numberKey?: string;
  metaTemplateId?: string;

  name: string; // lowercase ascii + underscore, ≤512
  language: 'he' | 'en_US'; // 'he' — NEVER 'iw', NEVER 'he_IL'
  parameterFormat: 'NAMED' | 'POSITIONAL'; // 'NAMED' for everything we author
  hebrewTitle: string; // the realtor-facing name; never show `name`

  requestedCategory: WhatsappTemplateCategory;
  /** META'S answer. THIS is what drives price — persist it, not what we asked for. */
  category: WhatsappTemplateCategory;
  previousCategory?: WhatsappTemplateCategory;

  status: WhatsappTemplateStatus;
  qualityScore?: WhatsappQualityRating;

  headerFormat?: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  headerText?: string;
  /** Meta's approval SAMPLE handle. Spent at create; never sendable. */
  headerMediaHandle?: string;
  /** Public https URL of the same bytes, sent as the header's media link on every send. */
  headerMediaUrl?: string;
  headerMediaMimeType?: string; // ≤60; PLAIN TEXT ONLY — Meta rejects emoji/newlines (subcode 2388072)
  /**
   * 'listing' — the header image is the PICKED LISTING's photo at every send (chosen in the send
   * dialog from its pictures, or uploaded), and the send dialog asks for a listing. The approval
   * sample stays what Meta reviewed. Webot-side metadata: changing it never touches Meta.
   */
  headerMediaSource?: 'listing';
  bodyText: string; // ≤1024 Meta chars; we hard-block at 800 for Hebrew
  footerText?: string; // ≤60, NO variables
  buttons?: WhatsappTemplateButton[];
  params: WhatsappTemplateParam[];

  rejectedReason?: string;
  rejectionDetail?: string;
  /** rejection_info.recommendation — Meta's own free-text fix advice. Show it, always. */
  rejectionRecommendation?: string;
  pauseTitle?: string;
  pauseCount?: number;
  pausedUntil?: Date;
  disabledAt?: Date;

  editTimestamps?: Date[];
  submittedAt?: Date;
  approvedAt?: Date;
  lastSyncedAt?: Date;
  contentHash: string;
};

// --------------------------------------------------------------------------------------------
// Groups — uploaded recipient lists, campaignable without becoming contacts.
// --------------------------------------------------------------------------------------------

export const WHATSAPP_GROUPS_COLLECTION_NAME = 'whatsapp-groups';
export const WHATSAPP_GROUP_MEMBERS_SUBCOLLECTION_NAME = 'members';

export type WhatsappGroup = Partial<BaseDocument> & {
  /** The creator/owner. Org-shared audiences keep the creator here; sharing rides `scope`+`orgId`. */
  userId: string;
  name: string;
  memberCount: number;
  /** Absent = personal (every pre-2026-09 doc). 'org' audiences are visible to the whole agency. */
  scope?: 'personal' | 'org';
  orgId?: string;
  createdBy?: string;
  source?: 'manual' | 'file_import' | 'whatsapp_import' | 'crm_selection' | 'crm_filter';
  description?: string;
};

export type WhatsappGroupMember = Partial<BaseDocument> & {
  phone: string; // E.164 == doc id
  waId: string; // digits, no '+'
  firstName?: string;
  lastName?: string;
  displayName?: string;
  /** Link to the CRM contact this member came from (usually == phone). */
  contactId?: string;
  ownerUserId?: string;
  /** Extra columns from the uploaded file, usable as perRecipient template params. */
  customFields?: Record<string, string>;
};

/** "Audiences" (קהלים) — the product name for recipient groups since the campaign rewrite. */
export const AUDIENCES_COLLECTION_NAME = WHATSAPP_GROUPS_COLLECTION_NAME;
export type Audience = WhatsappGroup;

// --------------------------------------------------------------------------------------------
// Suppressions — the blocked-numbers list. Storage is server-only (`whatsapp-suppressions`);
// the dashboard talks to it exclusively through the /whatsapp/blacklist* REST surface below.
// --------------------------------------------------------------------------------------------

export const whatsappSuppressionReasons = [
  'opt_out_button', // tapped the הסר quick-reply → suppressAll
  'opt_out_keyword', // wrote הסר / הסרה / … → suppressAll
  'meta_user_preferences', // Meta's own marketing-messages toggle → marketing only, Meta can lift it
  'error_131050', // user turned off marketing messages → marketing only, permanent
  'error_131026', // not a WhatsApp user / undeliverable → suppressAll
  'error_131049', // per-user frequency cap → marketing only, cooldownUntil now+24h, NOT an opt-out
  'blocked_by_business', // 130403
  'realtor_manual', // added by the realtor in the blocked-numbers screen
  'ops_manual',
  'complaint',
  'not_on_whatsapp', // extension reported "no such chat" — the number is not on WhatsApp → suppressAll
  'contact_flag' // migrated from the legacy `optedOutOfBulkMessages` contact flag → suppressAll
] as const;
export type WhatsappSuppressionReason = (typeof whatsappSuppressionReasons)[number];

// --------------------------------------------------------------------------------------------
// Campaigns — the bulk-send engine.
// --------------------------------------------------------------------------------------------

export const WHATSAPP_CAMPAIGNS_COLLECTION_NAME = 'whatsapp-campaigns';
export const WHATSAPP_CAMPAIGN_RECIPIENTS_SUBCOLLECTION_NAME = 'recipients';

export const whatsappCampaignStatuses = [
  'draft',
  'scheduled', // scheduledFor in the future; the runner promotes it when due
  'running',
  'paused_by_user',
  'paused_by_system',
  'completed',
  'cancelled',
  'failed'
] as const;
export type WhatsappCampaignStatus = typeof whatsappCampaignStatuses[number];

export const whatsappPauseReasons = [
  'quality_red',
  'template_paused',
  'template_disabled',
  'template_param_bug',
  'template_too_long',
  'high_failure_rate',
  'account_restricted',
  'needs_payment',
  'needs_reauth',
  'manual_ops',
  'kill_switch'
] as const;
export type WhatsappPauseReason = typeof whatsappPauseReasons[number];

export const whatsappRecipientStates = [
  'queued',
  'sending',
  'sent',
  'delivered',
  'read',
  'failed',
  'skipped'
] as const;
export type WhatsappRecipientState = typeof whatsappRecipientStates[number];

export const whatsappSkipReasons = [
  'opted_out',
  'suppressed',
  'recent_bulk',
  'unreachable',
  'marketing_capped',
  'invalid_phone',
  'landline',
  'self',
  'duplicate',
  'experiment_holdout',
  'daily_budget',
  'tenant_not_ready',
  'kill_switch'
] as const;
export type WhatsappSkipReason = typeof whatsappSkipReasons[number];

/**
 * The narrowest recency window a send may ask for, and the default: a message sent to the person
 * minutes ago is a double send, whatever the realtor chose (`duplicate` in decideMaySend).
 */
export const WHATSAPP_MIN_RECENCY_MINUTES = 10;

/**
 * The skips a realtor may override from a campaign's people list ("שליחה בכל זאת"): the campaign's
 * own RECENCY window, and the duplicate guard once its minutes have passed. The runner's gate still
 * runs again at the send — only the window is lifted, for that one person. Never an opt-out, a
 * block, a dead number or a Meta verdict.
 */
export const WHATSAPP_OVERRIDABLE_SKIP_REASONS: readonly WhatsappSkipReason[] = ['recent_bulk', 'duplicate'];

export type WhatsappCampaign = Partial<BaseDocument> & {
  userId: string;
  organizationIds?: string[];
  wabaId: string;
  phoneNumberId: string;
  /**
   * When set, the campaign sends FROM this number (the creator must be a member of its access
   * doc) instead of the creator's own connection — e.g. a system number shared with an admin.
   * Absent = the creator's own connection, the default.
   */
  numberKey?: string;

  name: string; // Hebrew, realtor-authored
  status: WhatsappCampaignStatus;

  /**
   * Absent = 'campaign'. A `one_off` is a scheduled SINGLE SEND riding the same engine (≤10
   * recipients, auto attestation); the campaigns page and live list filter these out.
   */
  kind?: 'campaign' | 'one_off';
  /** ONE_OFF ONLY: recipient E.164s mirrored onto the parent for the panel's pending query. */
  contactIds?: string[];

  templateDocId: string;
  templateName: string;
  templateLanguage: string;
  templateCategory: WhatsappTemplateCategory;
  paramOrder: string[];
  staticParamValues: Record<string, string>;
  /**
   * Campaign-level defaults for params whose per-recipient value came up empty (e.g. a
   * first-name binding on a member with no name). Applied between the resolved value and the
   * template's own fallbackValue. Editable while the campaign hasn't finished (the preview
   * gate's "השלמת ערכים חסרים").
   */
  paramFallbacks?: Record<string, string>;
  /**
   * Per-param tallies for the auto-resolvable bindings, counted over the WHOLE recipient set at
   * create. The honest answer to "will this actually fill in?" — `missingParams` cannot give it,
   * because a bound param is required to carry a fallbackValue and therefore never counts as
   * missing. Absent when the template uses no bindings.
   */
  bindingCoverage?: WhatsappParamCoverage[];
  /**
   * THIS campaign's header image/video, overriding the template's default. Header media is
   * per-message in Meta's model, so the same approved template carries a different property's
   * photo each campaign. Only the FORMAT has to match what was approved, never the bytes.
   */
  headerMediaUrl?: string;
  listingId?: string;

  totals: {
    selected: number;
    queued: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    skipped: number;
    replied: number;
  };

  consentAttestation: {
    acceptedByUserId: string;
    acceptedAt: Date;
    textShown: string;
    contactCount: number;
  };

  pacing: {
    perHourCap: number;
    minGapMs: number;
    maxGapMs: number;
    sendWindow: { startHour: number; endHour: number };
  };

  skipIfMessagedWithinMinutes: number;

  scheduledFor?: Date;
  startedAt?: Date;
  finishedAt?: Date;
  systemPauseReason?: WhatsappPauseReason;
  pausedReason?: string;

  /** Rows dropped at CREATE (first 200 + full count) — lets the detail page explain the gap. */
  skippedAtCreate?: { phone: string; reason: string }[];
  skippedAtCreateCount?: number;

  leaseUntil: Date;
  leaseOwner?: string;
  lastRunnerTickAt?: Date;
  nextEligibleSendAt?: Date;
  hourWindowStart?: Date;
  hourWindowSentCount?: number;
  errorCounts?: Record<string, number>;
  /**
   * Where the brake's observation window starts: the attempt count (sent + delivered + read +
   * failed) and the 131049 count at the moment a human last resumed the campaign. The brake judges
   * only what happened after it, so a resume is a fresh 20-attempt chance rather than an instant
   * re-pause on the first failure. Absent = the campaign's start.
   */
  brakeBaseline?: { attempted: number; e131049: number };

  /**
   * The creator's name and face, SNAPSHOT at create (`getUserProfileBasics`). A campaign on a
   * shared number is read by colleagues who may not share an org directory with its creator, so
   * the doc carries enough to render "נוצר על ידי" without a lookup; the dashboard prefers the
   * live directory entry when it has one. Absent on campaigns created before 2026-10-08.
   */
  createdByName?: string | null;
  createdByAvatarUrl?: string | null;
  /**
   * Inbound replies joined to this campaign (kept on each recipient doc as `response`, maintained
   * by `whatsappCampaignResponses.service.ts`): distinct responders, every message they sent
   * (stored or only counted), and the responders nobody on the number has answered yet.
   * `totals.replied` keeps counting responders for the older surfaces. `optedOut` counts the
   * recipients who asked to be removed after this campaign reached them (`recipient.optedOutAt`) —
   * a separate bucket: an unsubscribe is neither a responder nor awaiting.
   *
   * Absent until the first response or unsubscribe, and EACH COUNTER is absent until its own first
   * event (they are written by `increment`): a campaign whose only event is an unsubscribe holds
   * `{ optedOut: 1 }`. Read every one with `?? 0` — and `responders` with `?? totals.replied`.
   */
  responseStats?: { responders?: number; messages?: number; awaiting?: number; optedOut?: number };
};

export type WhatsappCampaignRecipient = Partial<BaseDocument> & {
  campaignId: string;
  userId: string;
  contactId: string; // E.164 == doc id
  waId: string;
  displayName?: string;
  source: 'contact' | 'group';
  groupId?: string;

  state: WhatsappRecipientState;
  statusRank: number;
  skipReason?: WhatsappSkipReason;
  /**
   * A realtor pressed "שליחה בכל זאת" on this person after the campaign's recency window skipped
   * them: the runner judges THIS recipient with WHATSAPP_MIN_RECENCY_MINUTES only. Every other gate
   * (opt-out, blocked list, budget, window) still applies.
   */
  recencyOverride?: boolean;
  resolvedParams: Record<string, string>;
  wamid?: string;
  bizOpaqueCallbackData?: string;

  errorCode?: number;
  errorTitle?: string;
  errorDetails?: string;
  attempts: number;

  queuedAt?: Date;
  claimedAt?: Date;
  sentAt?: Date;
  deliveredAt?: Date;
  readAt?: Date;
  failedAt?: Date;
  /**
   * First inbound reply after this campaign's send — stamped by the touch-keyed inbound join
   * (`whatsappCampaignResponses.service.ts`; the creator-keyed pair-state join is the fallback for
   * campaigns sent before touches existed), which also increments `totals.replied`. Write-once.
   */
  repliedAt?: Date;
  /** The person's reply thread for this campaign — see WhatsappCampaignRecipientResponse. Absent until they write back. */
  response?: WhatsappCampaignRecipientResponse;
  /**
   * When this person asked to be removed (the הסרה tap, a stop keyword) after this campaign reached
   * them — inside the same 7-day join window as a response, stamped by the same inbound join, which
   * also increments `responseStats.optedOut`. Write-once. Independent of `response`: an unsubscribe
   * on its own opens no card, and one that follows a real message is on the card AND stamped here.
   */
  optedOutAt?: Date;
};

// --------------------------------------------------------------------------------------------
// Campaign responses — what a recipient wrote back, kept ON THE RECIPIENT DOC.
// --------------------------------------------------------------------------------------------

/** How many of a responder's messages the response STORES; later ones are only counted. */
export const CAMPAIGN_RESPONSE_MAX_MESSAGES = 3;

/**
 * Who answered a responder: the dashboard inbox, the owner's phone (coexistence echo), the
 * campaign page, or another rail (the smart assistant, the API).
 */
export const whatsappCampaignResponseReplySources = ['inbox', 'phone', 'campaign', 'other'] as const;
export type WhatsappCampaignResponseReplySource = typeof whatsappCampaignResponseReplySources[number];

export type WhatsappCampaignResponseMessage = {
  wamid: string;
  type: string; // 'text' | 'image' | 'audio' | …
  text?: string;
  caption?: string;
  at: Date;
};

/** OUR answer to a responder, whichever rail sent it — shown under their messages. */
export type WhatsappCampaignResponseReply = {
  wamid?: string;
  type: string;
  text?: string;
  at: Date;
  source: WhatsappCampaignResponseReplySource;
  /** The dashboard user who sent it (inbox / campaign page); absent for a phone echo or another rail. */
  userId?: string;
};

/**
 * `WhatsappCampaignRecipient.response` — one person's reply thread for THIS campaign: the first
 * CAMPAIGN_RESPONSE_MAX_MESSAGES messages they sent after it reached them (later ones only
 * counted), and our answer once anyone on the number gave one. ON THE RECIPIENT DOC, not a
 * subcollection, so the recipients list is one query that can filter and sort on it
 * (`response.status`, `response.lastAt` — a composite index on the `recipients` collection group).
 * Recording STOPS at `replied` — the thread is a conversation now and the inbox owns it — and the
 * join window is 7 days from the campaign's send.
 */
export type WhatsappCampaignRecipientResponse = {
  status: 'awaiting' | 'replied';
  conversationId: string;
  messages: WhatsappCampaignResponseMessage[];
  /** Every message counted, stored or not. */
  messageCount: number;
  firstAt: Date;
  lastAt: Date;
  reply?: WhatsappCampaignResponseReply;
  /**
   * SERVER-ONLY, never projected: the wamids of the messages counted but not stored (the 4th
   * onward, last 20), so a redelivered webhook cannot count the same message twice.
   */
  countedWamids?: string[];
};

export const WHATSAPP_CAMPAIGN_TOUCH_COLLECTION_NAME = 'whatsapp-campaign-touch';
export const whatsappCampaignTouchDocId = (numberKey: string, waId: string): string => `${numberKey}__${waId}`;

/**
 * SERVER-ONLY. The LAST campaign that reached a person from a number — the join an inbound reply
 * uses to find its campaign. Keyed on the NUMBER, not the creator: on a shared number the webhook's
 * user is the number's owner while the campaign may be a colleague's, which is exactly where the
 * older `whatsapp-pair-send-state` join (keyed on the creator) misses. Written by the runner after
 * every successful send; the newest campaign wins.
 */
export type WhatsappCampaignTouch = Partial<BaseDocument> & {
  numberKey: string;
  waId: string;
  campaignId: string;
  recipientId: string; // E.164 — the recipient doc id
  userId: string; // the campaign's creator
  wamid: string;
  sentAt: Date;
};

export const GENERATED_USER_CREATIVES_COLLECTION_NAME = 'generated-user-creatives';
export type GeneratedUserCreative = Partial<BaseDocument> & {
  status:
    | 'starting'
    | 'in-progress'
    | 'completed'
    | 'failed'
    | 'deleted'
    | 'overwritten-by-regeneration';
  originalCreativeId?: string; // for regeneration requests - when regenerating
  userId: string;
  // "image"    — a marketing creative (creatives generator).
  // "makeover" — an AI-edited makeover of a single real listing photo.
  // Both share the SAME monthly quota.
  creativeType: 'image' | 'makeover';
  prompt: string;
  generatedCreativeUrl: string;
  usedImageUrls?: string[]; // the original images used with this creative (for inpainting etc)
  // Listings this creative was generated for. Used by the listing detail modal
  // (Creatives tab) to surface creatives that reference the open listing.
  // Array-contains queryable.
  listingIds?: string[];
  errorMessage?: string;
  editCount: number; // tracks how many times this creative has been edited (0 = original, max 2)
  creativeQuality?: MakeoverQuality; // output resolution for an image creative (1K/2K)

  // ---------- AI makeover fields (only when creativeType === "makeover") ----------
  sourceImageUrl?: string; // the single original listing photo this makeover restyles ("before")
  listingId?: string; // the listing this makeover belongs to (single id)
  makeoverSceneType?: MakeoverSceneType; // scene category that drove the prompt
  makeoverIntensity?: MakeoverIntensity; // how much the makeover changed (tidy → full)
  makeoverStylePrompt?: string; // the raw style the user picked (so a retry can prefill it)
  makeoverAdditionalNotes?: string; // free-text specific instructions the user gave for this makeover
  makeoverBatchId?: string; // groups the (up to 5) makeovers requested together
  makeoverEngine?: MakeoverEngine; // engine that ran (standard=gpt-image-2 / premium=nb2; 'value' on older records = Qwen)
  makeoverQuality?: MakeoverQuality; // output resolution used (1K / 2K)
};

/**
 * Scene category for an AI listing-image makeover. "auto" uses a universal
 * prompt that works for any photo (interior room, whole apartment, garden).
 */
export type MakeoverSceneType = 'auto' | 'interior' | 'exterior' | 'garden';

/**
 * How much the makeover is allowed to change, from lightest to heaviest:
 *  - 'tidy'    — only declutter / clean, change nothing else
 *  - 'refresh' — light restyle of decor/lighting, keep furniture & finishes
 *  - 'restyle' — replace furniture/decor/paint, keep flooring & built-ins
 *  - 'full'    — full makeover incl. flooring & finishes (still same layout/structure)
 */
export type MakeoverIntensity = 'tidy' | 'refresh' | 'restyle' | 'full';

/**
 * Makeover model "engine" — the abstracted performance tier the customer picks.
 *  - 'standard' → OpenAI gpt-image-2 (low quality) via WaveSpeed — the default
 *  - 'premium'  → Nano Banana 2 / Gemini 3.1 Flash Image
 *  - 'value'    → LEGACY, merged into 'standard' on 2026-09-17 (it was Qwen Image Edit). Old
 *                 clients, stored picker preferences and replayed chat tool calls still send
 *                 it, so it is accepted and mapped by resolveMakeoverEngine — never rejected.
 */
export type MakeoverEngine = 'value' | 'standard' | 'premium';

/** Output resolution the customer picks (orthogonal to engine). Also used for creatives. */
export type MakeoverQuality = '1K' | '2K';

export const DEFAULT_MAKEOVER_ENGINE: MakeoverEngine = 'standard';
export const DEFAULT_MAKEOVER_QUALITY: MakeoverQuality = '1K';

/**
 * Real provider cost (USD per generated image). Source of truth that makes margin visible
 * and drives the coin price below. VERIFY before re-pricing.
 */
export const MAKEOVER_PROVIDER_COST_USD: Record<MakeoverEngine, Record<MakeoverQuality, number>> = {
  premium: { '1K': 0.067, '2K': 0.101 }, // Nano Banana 2 (Gemini 3.1 Flash Image) — June 2026 estimate
  standard: { '1K': 0.02, '2K': 0.03 }, // gpt-image-2 low on WaveSpeed — published price, Sept 2026
  value: { '1K': 0.02, '2K': 0.03 } // legacy alias of standard
};

/**
 * What we CHARGE per makeover image, in coins ("coin v2", factor 10). Proportional to
 * provider cost, with Premium·2K pinned to the legacy makeover price (10) and the cheapest
 * option floored at 1 coin. Standard runs gpt-image-2 low, which costs what Qwen did, so it
 * took the old Value price (was 3 · 4 on Nano Banana 1).
 */
export const MAKEOVER_COIN_COST: Record<MakeoverEngine, Record<MakeoverQuality, number>> = {
  premium: { '1K': 7, '2K': 10 },
  standard: { '1K': 1, '2K': 2 },
  value: { '1K': 1, '2K': 2 }
};

/** The engine a request actually runs on: legacy 'value' → 'standard', missing → the default. */
export const resolveMakeoverEngine = (engine?: MakeoverEngine): MakeoverEngine =>
  engine === 'value' ? 'standard' : engine ?? DEFAULT_MAKEOVER_ENGINE;

/** Coin price for one makeover image at the given engine + quality (with safe defaults). */
export const getMakeoverCoinCost = (
  engine: MakeoverEngine = DEFAULT_MAKEOVER_ENGINE,
  quality: MakeoverQuality = DEFAULT_MAKEOVER_QUALITY
): number =>
  MAKEOVER_COIN_COST[engine]?.[quality] ??
  MAKEOVER_COIN_COST[DEFAULT_MAKEOVER_ENGINE][DEFAULT_MAKEOVER_QUALITY];

/**
 * Output qualities each engine actually supports. Every engine offers both today; the table
 * stays so a 1K-only engine can be added without touching the pickers or the billing clamp.
 */
export const MAKEOVER_ENGINE_QUALITIES: Record<MakeoverEngine, MakeoverQuality[]> = {
  value: ['1K', '2K'],
  standard: ['1K', '2K'],
  premium: ['1K', '2K']
};
export const engineSupportsQuality = (engine: MakeoverEngine, quality: MakeoverQuality): boolean =>
  (MAKEOVER_ENGINE_QUALITIES[engine] ?? ['1K', '2K']).includes(quality);
/** Clamp a requested quality to one the engine supports (falls back to '1K'). */
export const resolveMakeoverQuality = (
  engine: MakeoverEngine,
  quality: MakeoverQuality
): MakeoverQuality => (engineSupportsQuality(engine, quality) ? quality : '1K');

/**
 * Social-media creative generation: customers pick QUALITY only (the model is fixed).
 * Priced ~2× a Premium makeover (the legacy 2:1 socialMedia:makeover ratio). 2K (20) =
 * the legacy flat socialMedia price; 1K is the cheaper option.
 */
export const DEFAULT_CREATIVE_QUALITY: MakeoverQuality = '1K';
export const CREATIVE_COIN_COST: Record<MakeoverQuality, number> = {
  '1K': 14,
  '2K': 20
};
export const getCreativeCoinCost = (quality: MakeoverQuality = DEFAULT_CREATIVE_QUALITY): number =>
  CREATIVE_COIN_COST[quality] ?? CREATIVE_COIN_COST[DEFAULT_CREATIVE_QUALITY];

export const LISTING_MAKEOVER_SHOWCASES_COLLECTION_NAME = 'listing-makeover-showcases';

export type ListingMakeoverShowcaseImage = {
  makeoverId: string;
  makeoverImageUrl: string; // the AI "after" image
  sourceImageUrl: string; // the original "before" listing photo
  sceneType?: MakeoverSceneType;
};

/**
 * The set of AI makeover images to display in the "AI generated" section of a
 * minisite, for ONE listing. There is ONE doc per (userId, listingId), with an
 * auto-generated id (queried by fields, not a deterministic id):
 *  - By default it belongs to a single realtor (`userId` = their mail) and shows
 *    on all of THAT realtor's minisites containing the listing.
 *  - If the realtor is an org admin and turns on "share with my office", we set
 *    `organizationId` on the SAME doc, which makes it visible on every org
 *    realtor's minisite — UNLESS that realtor has their own doc for the listing,
 *    which TAKES PRECEDENCE.
 */
export type ListingMakeoverShowcase = Partial<BaseDocument> & {
  listingId: string;
  userId: string; // the realtor mail that owns this showcase
  organizationId?: string; // set only when shared at the office level
  enabled: boolean; // master on/off for showing this showcase on minisites
  images: ListingMakeoverShowcaseImage[];
};

// ===================== COINS =====================
// Coins are the billing currency for AI creative edits. Customers buy coin packs
// (offline, via support; granted by an admin) and spend coins per billable edit.
// Balance lives across `coins-packs` (user- or org-level), each add/remove recorded
// in `coins-operations`. See server/api/services/coins.service.ts.

/**
 * Billable creative edit types and their coin costs.
 */
export type CreativeEditType = 'socialMedia' | 'makeover';
// Re-denominated ×10 ("coin v2"): every balance, grant and price was inflated by
// COIN_DENOMINATION_FACTOR so cheaper options can cost a fraction of the old 1 coin
// without fractional coins. `makeover` here is the LEGACY flat cost (= the Premium·2K
// anchor); the real per-makeover price now comes from MAKEOVER_COIN_COST below.
export const COIN_DENOMINATION_FACTOR = 10;
export const CREATIVE_EDIT_COSTS: Record<CreativeEditType, number> = {
  socialMedia: 20,
  makeover: 10
};

export const COINS_PACKS_COLLECTION_NAME = 'coins-packs';
export const COINS_OPERATIONS_COLLECTION_NAME = 'coins-operations';

export type CoinsPackSource = 'monthly' | 'oneTimePurchase' | 'gift' | 'migration';

export type CoinsPack = Partial<BaseDocument> & {
  userId: string; // owner email (== User.id). Set even for org packs (the holder/grantee).
  organizationId?: string; // present only for org-level packs
  scope: 'user' | 'org'; // explicit tier discriminator (consumption order: monthly → org → user)
  initialCoins: number;
  currentCoins: number; // mutated atomically on consumption; never < 0
  paidPriceCents?: number; // e.g. 10000 = ₪100 (prices are VAT-exclusive)
  currency?: 'ILS';
  startDate?: Date;
  expirationDate?: Date | null; // null/absent = permanent; monthly packs = end of grant month
  receivedSource: CoinsPackSource;
  monthlyGrantPeriod?: string; // 'YYYY-MM' for monthly packs (idempotency component)
  revoked?: boolean; // soft-revoke — excluded from balance/consume
  coinsOperationId?: string; // back-ref to the 'add' operation that created this pack
  coinDenominationVersion?: number; // last re-denomination applied (idempotency marker; see redenominate-coins-10x.ts)
};

export type CoinsOperationType = 'add' | 'remove' | 'revoke';
export type CoinsOperation = Partial<BaseDocument> & {
  userId: string;
  organizationId?: string;
  coinsPackId: string; // one operation is written PER pack touched (a 2-coin consume may write 2 ops)
  operationType: CoinsOperationType;
  coinsAmount: number; // always positive; sign implied by operationType
  details: string; // human note: 'Creatives socialMedia edit (2)', 'admin grant by X', etc.
  correlationId?: string; // groups the ops of a single multi-pack consume / refund
  editType?: CreativeEditType; // for consume operations
  creativeId?: string; // link to the generated creative this paid for
  performedByAdminEmail?: string; // for admin grants/revokes
  balanceAfter?: number; // total balance snapshot after the op (debugging)
};

/** Lean per-pack summary returned in a balance breakdown. */
export type CoinBalancePackSummary = {
  id: string;
  scope: 'user' | 'org';
  organizationId?: string;
  receivedSource: CoinsPackSource;
  currentCoins: number;
  initialCoins: number;
  expirationDate?: Date | null;
};

/** Aggregated coin balance for a user, split by consumption tier. Excludes expired/revoked/zero packs. */
export type CoinBalanceBreakdown = {
  total: number;
  monthly: number; // sum of live monthly packs
  org: number; // sum of live org packs across all the user's orgs
  user: number; // sum of live non-monthly user packs
  monthlyExpiresAt: Date | null; // soonest live monthly-pack expiry (for the UI hint)
  packs: CoinBalancePackSummary[];
};

// ===================== SIGNATURES =====================
// Digital signature feature. Firestore is the source of truth. Signing is
// handled entirely by our native engine: the document is rendered to a PDF with
// field-tag markup, we parse the tags into `sentFields`, mint a slug, serve the
// native signing ceremony, then compose the signed PDF + audit certificate
// in-process. See .claude/plans/tidy-yawning-hippo.md for rationale.

export const SIGNATURE_TEMPLATES_COLLECTION_NAME = 'signature-templates';
export const SIGNATURE_DOCUMENTS_COLLECTION_NAME = 'signature-documents';
export const SIGNATURE_OTPS_COLLECTION_NAME = 'signature-otps';
// Per-user document counter for a signature template. Lets favorited templates
// (and system/org templates the user doesn't own) have a private numbering
// sequence that doesn't leak across users — while the template content stays
// shared. Doc id is `${userId}_${templateId}`.
export const SIGNATURE_TEMPLATE_USER_COUNTERS_COLLECTION_NAME = 'signature-template-user-counters';
// Reusable named groupings of templates a realtor sends together. Sending a
// bundle creates one SignatureDocument per included template, all tagged with
// the bundle's id so the sent list (and sign page) can present them together.
export const SIGNATURE_TEMPLATE_BUNDLES_COLLECTION_NAME = 'signature-template-bundles';
// O(1) slug → (documentId, signerId) lookup. One row per signer, doc id === the
// signer's signing slug. Replaces the full-collection scan that the public
// signing page + OTP service previously used to resolve a slug. Written at send
// time (and lazily backfilled on a scan-fallback hit).
export const SIGNATURE_SLUG_INDEX_COLLECTION_NAME = 'signature-slug-index';
// Webot-wide signature settings. Single doc (id below) holding the
// admin-managed global default templates per signing scenario.
export const SIGNATURE_SETTINGS_COLLECTION_NAME = 'signature-settings';
export const SIGNATURE_GLOBAL_SETTINGS_DOC_ID = 'global';

// Where a field's initial value comes from when a document is sent.
// `none` means the sender fills it manually in the NewDocument UI.
export const SignaturePrefillSources = [
  'none',
  'contact.name',
  'contact.phone',
  'contact.email',
  'contact.id_number',
  'contact.additional_phones',
  'contact.address',
  'listing.address',
  'listing.price',
  'listing.rooms',
  'listing.square_meter',
  'realtor.name',
  'realtor.phone',
  'realtor.email',
  'realtor.company',
  'realtor.license',
  'realtor.company_logo',
  'realtor.signature_image',
  'realtor.company_id',
  'realtor.user_info_block',
  'document.formatted_number',
  'today',
  // The date the document was SIGNED (not the send date). Rendered as a
  // readonly date field that our engine stamps with the signing date when the
  // signed PDF is composed at completion (see signaturePdfComposer.service).
  'signing_date'
] as const;
export type SignaturePrefillSource = typeof SignaturePrefillSources[number];

// Who fills a field and when.
// - 'prefilled': realtor commits a value (auto-filled from contact/listing or
//   typed manually) before sending; it's baked into the sent PDF and shown to
//   the signer as readonly.
// - 'signer': signer fills it during signing.
// - 'fixed': value is baked into the template at edit time (stored in
//   `defaultValue`). Stamped on every document, never editable at send time,
//   never rendered as a signer input. Only meaningful for text-shaped types
//   (text, number).
// Signature + initials are always 'signer' implicitly.
export const SignatureFieldFillModes = ['prefilled', 'signer', 'fixed'] as const;
export type SignatureFieldFillMode = typeof SignatureFieldFillModes[number];

// 'email' renders as a <text-field> tag with a `pattern` attribute enforcing
// email format on the signer side — there's no dedicated email-field tag, the
// validation is exposed through the standard HTML pattern attribute and
// enforced server-side by signatureFieldValidation.service.
// 'phone' is a phone-shaped text field (HTML5 pattern validation). SMS-OTP
// is enforced at the signer level (see signatureOtp.service.ts), not per-field.
export const SignatureFieldTypes = [
  'signature',
  'initials',
  'text',
  'date',
  'checkbox',
  'number',
  // 'percentage' is a number-shaped field constrained to 0-100, rendered with
  // a default trailing "%" suffix (see `suffix` + `showSuffix` on the field).
  // Rendered as a <number-field> tag at send time — there's no native
  // percentage tag, the suffix is stamped beside the input as adjacent HTML.
  'percentage',
  // 'price' is the DYNAMIC PRICE field: the sender picks either a commission
  // percentage (1 / 1.5 / 2 / custom) or an absolute ₪ amount in the send
  // form. The stored value is the final display string ("1.5%" or
  // "₪1,500,000" — comma-grouped with the shekel sign), baked verbatim into
  // the document; no suffix machinery involved.
  'price',
  // 'radio' is a sender-side single-choice picker. The template author
  // defines a clear prompt, explanatory text, and the allowed options in
  // `radioFieldConfig`; the selected option's human-readable label is baked
  // into the document before it is sent for signing.
  'radio',
  'image',
  'phone',
  'email'
] as const;
export type SignatureFieldType = typeof SignatureFieldTypes[number];

/**
 * The pattern an email field's signer input carries. The field-tag vocabulary has no email
 * tag: the dashboard renders an email field as a `<text-field>` with this pattern, so a SENT
 * document's frozen `sentFields` hold it as `type: 'text'` + `validationPattern`, never as
 * `type: 'email'`. Every document sent so far carries exactly this string: never edit it, add
 * a new pattern to {@link isSignatureEmailField} instead.
 */
export const SIGNATURE_EMAIL_VALIDATION_PATTERN = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';

/** The parts of a field, template or sent, that decide what the signer must fill. */
type SignatureFieldRequirement = { type: string; required?: boolean; validationPattern?: string };

/** An email field in either shape: a template field or a sent one (text + the email pattern). */
export const isSignatureEmailField = (field: SignatureFieldRequirement): boolean =>
  field.type === 'email' || field.validationPattern === SIGNATURE_EMAIL_VALIDATION_PATTERN;

/**
 * Whether the SIGNER must fill a field before they can sign: the one rule behind the server's
 * submit validation, the fields the signing page is served, and what the send form and the AI
 * tell the realtor. An email field never has to be filled, whatever its stored `required`
 * says (Trello #231, 2026-10-19): all seven Webot default contracts asked every signer for
 * one, and a signer with no email (often an elderly one) could not get past it. A typed email
 * is still validated; one left empty is simply absent from the signed document. The stored
 * flag is kept: it still decides the send form's "missing value" warning on a PREFILLED email.
 */
export const isSignatureFieldRequiredForSigner = (field: SignatureFieldRequirement): boolean =>
  !!field.required && !isSignatureEmailField(field);

/**
 * Pixel width reserved for the suffix (or prefix) span when rendered beside a
 * field's value/input. Shared between the editor (used to grow/shrink the
 * field's wPx when the realtor toggles the suffix on/off) and the HTML
 * renderer (used to position the suffix span and shrink the field input's
 * `right:`/`left:` so they don't overlap).
 */
export const SIGNATURE_FIELD_AFFIX_WIDTH_PX = 24;

/**
 * Default suffix text seeded into a field the first time the realtor flips
 * `showSuffix` on. Percentage gets "%"; number is opt-in (empty) so the
 * realtor types their own unit (e.g. "kg", "sqm"). Keyed by field type;
 * unlisted types don't surface the suffix toggle in the editor at all.
 */
export const DEFAULT_FIELD_SUFFIX_BY_TYPE: Partial<Record<SignatureFieldType, string>> = {
  percentage: '%',
  number: ''
};

/**
 * Default suffix appended after a percentage pick on the dynamic-price field
 * ("1.5%" → "1.5% ממחיר הנכס"). Absolute-amount picks default to no suffix.
 * Only applied when the template hasn't overridden `percentSuffix` — an
 * explicit empty string on the config means "no suffix".
 */
export const DEFAULT_PRICE_PERCENT_SUFFIX = 'ממחיר הנכס';

/**
 * The fixed label the realtor can pick for a rental commission on the
 * dynamic-price field when `showOneMonthRentOption` is on. Stored verbatim as
 * the field value and baked into the document.
 */
export const PRICE_ONE_MONTH_RENT_LABEL = 'דמי שכירות עבור חודש אחד';

/**
 * Per-template configuration for the dynamic-price ('price') field's send-form
 * picker. Lets a template tailor the picker — primarily to adapt it from a
 * sale-commission picker (percentage of property price) to a rental picker
 * (one month's rent, or a percentage). Ignored for non-price fields.
 *
 * The picker composes the FINAL display string (value + suffix) and stores it
 * as the field value, so the document renderer stays untouched — a price value
 * is still baked verbatim.
 */
export type SignaturePriceFieldConfig = {
  /**
   * Show the commission %-preset buttons (1% / 1.5% / 2% / custom %). Defaults
   * to true (the historical behaviour). Turn off for pure one-month-rent /
   * amount rental templates.
   */
  showPercentOptions?: boolean;
  /**
   * Text appended after a %-based pick, e.g. "ממחיר הנכס" → "1.5% ממחיר הנכס".
   * Undefined falls back to DEFAULT_PRICE_PERCENT_SUFFIX; empty string means
   * no suffix.
   */
  percentSuffix?: string;
  /**
   * Text appended after an absolute ₪-amount pick. Undefined/empty means no
   * suffix (the historical behaviour — "₪5,000").
   */
  amountSuffix?: string;
  /**
   * Offer a one-click "דמי שכירות עבור חודש אחד" (PRICE_ONE_MONTH_RENT_LABEL)
   * choice in the picker. Defaults to false. Used by rental templates.
   */
  showOneMonthRentOption?: boolean;
};

/** One permitted answer in a sender-side single-choice ('radio') field. */
export type SignatureRadioOption = {
  /** Stable machine value stored in the send-form state. */
  value: string;
  /** Human-readable text shown in the picker and baked into the document. */
  label: string;
  /** Optional clarification shown directly below this option. */
  description?: string;
};

/**
 * Per-template configuration for a sender-side single-choice ('radio')
 * field. The title and description make the decision explicit; options are
 * the only values the sender may submit.
 */
export type SignatureRadioFieldConfig = {
  title: string;
  description: string;
  options: SignatureRadioOption[];
};

export const SignatureDateFormats = [
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  'DD-MM-YYYY',
  'DD.MM.YYYY',
  'long'
] as const;
export type SignatureDateFormat = typeof SignatureDateFormats[number];

export const DEFAULT_SIGNATURE_DATE_FORMAT: SignatureDateFormat = 'DD/MM/YYYY';

/**
 * Absolute placement on top of the rendered document (CSS pixels relative to
 * the document body wrapper, A4 width = 794px). Every field carries a
 * position — fields are always overlay-positioned on the page; the
 * surrounding text content lives in the TipTap document body and the fields
 * float above it.
 */
export type SignatureFieldPosition = {
  xPx: number;
  yPx: number;
  wPx: number;
  hPx: number;
};

export type SignatureTemplateField = {
  id: string; // uuid, generated by backend
  name: string; // e.g. "buyer_signature" — also the field-tag `name` attribute
  type: SignatureFieldType;
  required: boolean;
  /** Absolute position on the document body. */
  position?: SignatureFieldPosition;
  signerRoleId: string; // matches SignatureTemplate.signerRoles[].id
  prefillSource?: SignaturePrefillSource;
  defaultValue?: string;
  /** Optional — defaults to 'prefilled' for non-signature types, 'signer' for signature/initials. */
  fillMode?: SignatureFieldFillMode;
  /** True when the field was dropped from a preset picker tile. Kept for UI — the server doesn't treat these differently. */
  lockedPrefillSource?: boolean;
  /** True when the editor must NOT let the realtor change fillMode (e.g. a verified-phone contact tile is signer-only by design — letting it be flipped to prefilled would defeat the SMS-OTP purpose). UI-only flag. */
  lockedFillMode?: boolean;
  /** Rendered font size in pixels (maps to the field tag's `font-size` attribute). Defaults to 12 when absent. */
  fontSize?: number;
  /** Horizontal alignment of the stamped value. Defaults to 'right' (Hebrew UX). */
  align?: 'left' | 'center' | 'right';
  /** Editor-only preview text so the realtor can see how the field will look. Not rendered into the document and not emitted as a field tag. */
  previewText?: string;
  /** Renders date-typed fields with this format both in the editor and in the rendered HTML. Ignored for non-date fields. Defaults to DD/MM/YYYY. */
  dateFormat?: SignatureDateFormat;
  /** Links fields that share one value — filling any one populates the others (prefilled: on send; signer: our engine grouping by this id). */
  linkGroupId?: string;
  /** Human-readable label shown to the signer on the signing canvas (rendered from the field tag's `title`). Only applicable to signer-fillable fields — ignored for prefilled fields, which never reach the signer's UI as inputs. Supports Markdown. */
  signerLabel?: string;
  /** Help text shown under the input on the signing canvas (rendered from the field tag's `description`). Same scope as `signerLabel` — signer-fillable only. Supports Markdown. */
  signerDescription?: string;
  /**
   * When true, a short label (e.g. "שם הלקוח:") is stamped onto the PDF
   * adjacent to the field box — visible to both realtor and signer regardless
   * of who fills the value. Used to identify contact fields like name / phone
   * / email / id-number on documents that need explicit captions next to the
   * answer area.
   */
  showLabel?: boolean;
  /**
   * Which label to render when `showLabel` is true. Stored independently from
   * `prefillSource` so it survives a fillMode switch to 'signer' (which clears
   * prefillSource). Set to the field's prefillSource at the moment showLabel
   * was turned on.
   *
   * Legacy: newer fields write `labelText` directly with the user-editable
   * caption. Renderers fall back to deriving text from `labelKey` for
   * pre-existing fields written before the editable-text feature shipped.
   */
  labelKey?: SignaturePrefillSource;
  /**
   * The actual caption rendered next to the field (and stamped on the PDF)
   * when `showLabel` is true. Pre-filled with the default for the field's
   * source the moment the realtor flips the toggle on, then freely editable
   * — so the realtor can customise to match their document's wording.
   */
  labelText?: string;
  /**
   * Hex CSS colour (e.g. "#000000") for the stamped/previewed text. Defaults
   * to black when unset. Affects the value text only — the side-label stamp
   * stays black for contrast/readability.
   */
  color?: string;
  /**
   * Per-line configuration for the realtor user-info block field
   * (`prefillSource === "realtor.user_info_block"`). Lets the realtor pick
   * which lines render and override each line's prefix label. Ignored for
   * non-block fields.
   */
  userInfoBlockConfig?: UserInfoBlockConfig;
  /**
   * When true, render `suffix` after the field's value (or beside the
   * signer-fillable input). Mirrors the `showLabel` toggle for prefixes —
   * keeping a separate boolean lets the realtor toggle the suffix off
   * without losing the text they typed.
   *
   * Only surfaced in the editor for `number` and `percentage` fields. For
   * percentage the editor seeds `suffix = '%'` on field creation so the
   * default is visible in the editor modal, not hidden behind the toggle.
   */
  showSuffix?: boolean;
  /**
   * Text rendered after the value when `showSuffix` is true. Stored
   * independently of `showSuffix` so toggling off preserves the realtor's
   * typed text (same pattern as `labelText`).
   */
  suffix?: string;
  /**
   * Per-template configuration for the dynamic-price ('price') field's
   * send-form picker (suffixes, whether to show %-presets, whether to offer
   * the one-month-rent option). Ignored for non-price fields. See
   * {@link SignaturePriceFieldConfig}.
   */
  priceFieldConfig?: SignaturePriceFieldConfig;
  /** Prompt, explanation, and allowed choices for a sender-side radio field. */
  radioFieldConfig?: SignatureRadioFieldConfig;
};

/** Lines available inside the user-info block, in render order. */
export const USER_INFO_BLOCK_LINE_KEYS = [
  'name',
  'license',
  'phone',
  'companyId',
  'company'
] as const;
export type UserInfoBlockLineKey = typeof USER_INFO_BLOCK_LINE_KEYS[number];

export type UserInfoBlockLineConfig = {
  /** Whether the line renders. Defaults to true when the entry is absent. */
  enabled: boolean;
  /**
   * Prefix label rendered before the value (e.g. "טלפון: "). Empty string
   * means "no prefix". Undefined falls through to the per-line built-in
   * default (see DEFAULT_USER_INFO_BLOCK_LABELS in signatureFormatters).
   */
  customLabel?: string;
};

export type UserInfoBlockConfig = Partial<Record<UserInfoBlockLineKey, UserInfoBlockLineConfig>>;

export const DEFAULT_SIGNATURE_FIELD_FONT_SIZE = 12;
export const DEFAULT_SIGNATURE_FIELD_ALIGN: 'right' = 'right';

export const effectiveSignatureFillMode = (f: {
  type: SignatureFieldType;
  fillMode?: SignatureFieldFillMode;
}): SignatureFieldFillMode => {
  if (f.type === 'signature' || f.type === 'initials') return 'signer';
  return f.fillMode || 'prefilled';
};

export type SignatureSignerRole = {
  id: string;
  name: string; // display name, e.g. "Buyer", "Seller", "Signer"
};

/**
 * Editable HTML body of a template. Stored on `SignatureTemplate.content` and
 * is the source of truth for both the editor and the document we render and
 * send. The TipTap document is canonical (reopen-able in the editor); the
 * rendered HTML is generated from it on save and used when sending.
 */
export type SignatureTemplateContent = {
  /**
   * TipTap (ProseMirror) JSON document — canonical editable form.
   *
   * STORAGE NOTE: persisted in Firestore as a JSON STRING, not this object —
   * Firestore caps nesting at 20 levels and nested lists blow past it (each
   * list level costs ~4 levels of maps/arrays). The signatures service
   * serializes on write and parses on read (see templateConverter in
   * signatures.service.ts), so REST payloads and in-memory values are always
   * the object shape declared here.
   */
  tiptapDoc: Record<string, unknown>;
  /** Rendered HTML, derived from tiptapDoc. Used for previews and as the body rendered to the sent PDF at send time. */
  html: string;
};

// ===== Listings block =====
// A repeatable block laid out as an absolutely-positioned overlay over the
// document body — same positioning model as fields. The realtor can place
// multiple blocks on a template (each gets its own pixel-anchored box), and
// at document send time each block is materialised as a table (with or
// without borders) or a bulleted list expanding to fit however many listings
// the realtor picked.

export const ListingsBlockFormats = [
  'table-bordered',
  'table-borderless',
  'bulleted-list'
] as const;
export type ListingsBlockFormat = typeof ListingsBlockFormats[number];

export const ListingsBlockColumns = ['address', 'price', 'rooms', 'square_meter'] as const;
export type ListingsBlockColumn = typeof ListingsBlockColumns[number];

/**
 * Which listings the send-modal picker offers for a listings block:
 *   - 'sale' (default): only for-sale listings.
 *   - 'rent': only rental listings.
 *   - 'both': no transaction-type filter.
 */
export const ListingsBlockListingFilters = ['sale', 'rent', 'both'] as const;
export type ListingsBlockListingFilter = typeof ListingsBlockListingFilters[number];
export const DEFAULT_LISTINGS_BLOCK_LISTING_FILTER: ListingsBlockListingFilter = 'sale';

export type ListingsBlock = {
  /** Stable id — needed for keyed React lists and per-block selection. */
  id: string;
  format: ListingsBlockFormat;
  columns: ListingsBlockColumn[];
  /** Absolute position on the document body. Same model as SignatureTemplateField.position. */
  position: SignatureFieldPosition;
  /**
   * Per-column override widths (CSS px). When absent for a column, the
   * column falls back to the weighted default (LISTINGS_BLOCK_COLUMN_WEIGHTS)
   * proportional to the block's total width. Mixing override + default is
   * intentional — let the realtor pin one column without forcing them to
   * configure all of them.
   */
  columnWidthsPx?: Partial<Record<ListingsBlockColumn, number>>;
  /** Font size in CSS pixels. */
  fontSize?: number;
  /** Hex CSS colour for table text. Defaults to inherit. */
  color?: string;
  /**
   * @deprecated Superseded by the hidden-addresses flow (`allowHiddenAddresses`
   * below). No UI or render path reads this anymore; kept only so legacy
   * template documents deserialize cleanly.
   */
  hideAddressNumber?: boolean;
  /**
   * When true, this template SUPPORTS sending with fully-hidden (redacted)
   * addresses. The actual decision happens per-send (a checkbox next to the
   * listings picker); when the realtor opts in:
   *   - the table's address column shows a redacted address (neighborhood +
   *     city by default, overridable per listing at send time),
   *   - the consent bullet lines below render under the table, and
   *   - a second "addresses addendum" document (full addresses + signature)
   *     is presented to the signer AFTER they sign the main document.
   */
  allowHiddenAddresses?: boolean;
  /**
   * Editable consent text rendered as bullet lines directly below the table
   * when a document is sent with hidden addresses. Defaults to
   * {@link DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES} when absent.
   */
  hiddenAddressConsentLines?: string[];
  /**
   * When true, the send modal's listings picker for this template accepts
   * exactly ONE listing (picking another replaces the current pick).
   * Prerequisite for {@link sellerQuestionnaireEnabled}.
   */
  restrictToSingleListing?: boolean;
  /**
   * When true, documents sent from this template attach the statutory SELLER
   * QUESTIONNAIRE (שאלון מוכר — תקנה 7(ד) לתקנות המתווכים במקרקעין) as a
   * post-signing task: after the signer signs the main document they fill a
   * fully-coded interactive form (property identification + 9 disclosure
   * questions) and sign it, which is what completes the ceremony. Requires
   * {@link restrictToSingleListing} — the questionnaire describes ONE property.
   */
  sellerQuestionnaireEnabled?: boolean;
  /**
   * Which listings the send-modal picker offers for this block: 'sale' (only
   * for-sale listings — default), 'rent' (only rentals), or 'both'. Applied at
   * send time as an Algolia facet filter on `listingListedFor`. Defaults to
   * {@link DEFAULT_LISTINGS_BLOCK_LISTING_FILTER} when absent.
   */
  listingFilter?: ListingsBlockListingFilter;
};

/**
 * Singleton "additional terms" block — an absolutely-positioned region on the
 * document body where the realtor can append free-form terms AT SEND TIME
 * (not in the template). The template author places it where dynamic content
 * belongs (typically right before the signature section); when a template
 * carries this block, the send form offers a rich-text input (bold /
 * underline / bullets / numbers) plus an editable section title.
 *
 * Left empty at send time, the block renders nothing. When filled, it renders
 * the title + terms HTML and participates in the SAME runtime reflow that
 * listings tables use — content that grows past the designed height pushes
 * everything below it (fields, other blocks, body text) down by the measured
 * overflow.
 */
export type AdditionalTermsBlock = {
  /** Stable id — keyed React lists + editor selection. */
  id: string;
  /** Absolute position on the document body. Same model as SignatureTemplateField.position. */
  position: SignatureFieldPosition;
  /** Font size in CSS pixels for the terms text. */
  fontSize?: number;
  /** Hex CSS colour for the terms text. Defaults to inherit. */
  color?: string;
};

/**
 * Default section title seeded into the send form's additional-terms input.
 * Rendered underlined above the terms content.
 */
export const DEFAULT_ADDITIONAL_TERMS_TITLE = 'שונות';

/**
 * Default consent bullets rendered under a listings table sent with hidden
 * addresses. Editable per listings block in the template editor.
 */
export const DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES: string[] = [
  'אנו נותנים הסכמתנו לתנאי הזמנת שירותי תיווך כמפורט לעיל. ידוע לנו כי לאחר חתימתנו ובכפוף לה תמסרו לנו את פרטיו המדוייקים של הנכס.',
  'הלקוח מצהיר בזאת כי לא ראה את הנכס המדובר קודם לכן דרך מקור אחר ואינו מכיר עסקה זו. אם לאחר החתימה הלקוח טוען שראה את הנכס קודם לכן, עליו לספק הוכחה לכך.',
  'העסקה תהיה מחייבת על אף שכתובת הנכס תוצג לי לאחר החתימה. הסכם זה יחייב את הצדדים לכל עניין ודבר וייחשב כהסכם מחייב לכל דבר ועניין.'
];

// ===================== SELLER QUESTIONNAIRE (שאלון מוכר) =====================
// Statutory questionnaire the property-rights owner fills when selling/renting
// through a realtor — תקנה 7(ד) לתקנות המתווכים במקרקעין (אתיקה וחובות
// מקצועיות), התשפ"ד-2024. Presented as a fully-coded interactive form AFTER the
// main document is signed; the completed answers + signature are rendered into
// the final signed PDF server-side.

/** Bump when the statutory question set / wording changes. */
export const SELLER_QUESTIONNAIRE_VERSION = '2026-02-v1.0';

export type SellerQuestionnaireQuestionDef = {
  /** Stable key the answers map is keyed by. */
  key: string;
  /** The statutory question text (Hebrew — legal wording, not translated). */
  text: string;
  /** Whether the statutory form offers an "אחר" option besides כן/לא. */
  allowOther: boolean;
};

/** Section ב of the statutory form — the 9 disclosure questions, in order. */
export const SELLER_QUESTIONNAIRE_QUESTIONS: readonly SellerQuestionnaireQuestionDef[] = [
  { key: 'moisture', text: 'האם יש רטיבויות בנכס?', allowOther: true },
  { key: 'building_violations', text: 'האם יש חריגות בנייה בנכס?', allowOther: true },
  {
    key: 'infrastructure_defects',
    text: 'האם קיימים ליקויים מהותיים בתשתיות הנכס, לרבות מים, חשמל, ביוב וגז?',
    allowOther: true
  },
  {
    key: 'dangerous_building',
    text: 'האם התקבל מידע רשמי ממחלקת ההנדסה ברשות המקומית בנוגע לסטטוס "בניין מסוכן"?',
    allowOther: true
  },
  {
    key: 'legal_claims',
    text: 'האם ידוע לך על תביעות הנוגעות לנכס או לזכויות בו, ובכלל זה הליכים פליליים הנוגעים לבניית הנכס או לשימוש בו והליכים משפטיים?',
    allowOther: true
  },
  {
    key: 'professional_opinions',
    text: 'האם קיימות חוות דעת מקצועיות הנוגעות לנושאים מהותיים באשר לנכס, למעט חוות דעת שמאיות?',
    allowOther: false
  },
  {
    key: 'special_maintenance',
    text: 'האם יש צורך בהחזקה מיוחדת או בשימוש בדרך מיוחדת בנכס על מערכותיו בכדי למנוע פגיעה בנכס, במשתמש בנכס או באדם אחר?',
    allowOther: false
  },
  { key: 'other_defects', text: 'האם קיימים ליקויים אחרים בנכס?', allowOther: false },
  {
    key: 'nuisances',
    text: 'האם קיימים מטרדי רעש, ריח, קרינה, או מטרדים אחרים הפוגעים באיכות החיים בנכס?',
    allowOther: false
  }
];

export const SellerQuestionnaireAnswerOptions = ['yes', 'no', 'other'] as const;
export type SellerQuestionnaireAnswerOption = typeof SellerQuestionnaireAnswerOptions[number];

export type SellerQuestionnaireQuestionAnswer = {
  answer: SellerQuestionnaireAnswerOption;
  /** Free-text "נא פרט" elaboration. Required when `answer` is 'other'. */
  details?: string;
};

export const SellerQuestionnaireRightTypes = ['ownership', 'lease', 'subLease', 'other'] as const;
export type SellerQuestionnaireRightType = typeof SellerQuestionnaireRightTypes[number];

export const SellerQuestionnaireRegistrationTypes = [
  'landRegistry',
  'israelLandAuthority',
  'housingCompany',
  'notRegistered',
  'other'
] as const;
export type SellerQuestionnaireRegistrationType =
  typeof SellerQuestionnaireRegistrationTypes[number];

/** פרטי הנכס — property identification. Only `address` is required (and prefilled from the picked listing). */
export type SellerQuestionnairePropertyDetails = {
  /** כתובת הנכס — required, prefilled from the listing (full, never redacted). */
  address: string;
  /** גוש */
  block?: string;
  /** חלקה */
  parcel?: string;
  /** תת חלקה */
  subParcel?: string;
  /** מספר מגרש */
  lotNumber?: string;
  /** סוג הנכס (דירה, מסחרי, אחר) */
  propertyType?: string;
};

/** פרטי הזכויות בנכס */
export type SellerQuestionnaireRights = {
  rightType?: SellerQuestionnaireRightType;
  rightTypeOther?: string;
  registration?: SellerQuestionnaireRegistrationType;
  registrationOther?: string;
};

/** פרטי בעל הזכות בנכס הממלא את השאלון */
export type SellerQuestionnaireOwnerDetails = {
  /** מספר זהות / דרכון / ח.פ */
  idNumber?: string;
  /** שם מלא יחיד / תאגיד */
  fullName?: string;
  address?: string;
  phone?: string;
  email?: string;
};

/** פרטי המתווך במקרקעין שמסר את השאלון — snapshotted from the sender's profile at send time. */
export type SellerQuestionnaireRealtorDetails = {
  /** מספר רישיון תיווך */
  licenseNumber?: string;
  fullName?: string;
  address?: string;
  phone?: string;
  email?: string;
};

/** Everything the signer submits on the questionnaire form. */
export type SellerQuestionnaireAnswers = {
  property: SellerQuestionnairePropertyDetails;
  rights: SellerQuestionnaireRights;
  owner: SellerQuestionnaireOwnerDetails;
  /** Keyed by {@link SELLER_QUESTIONNAIRE_QUESTIONS} `key`. Every question must be answered. */
  questions: Record<string, SellerQuestionnaireQuestionAnswer>;
};

/**
 * Stored on the {@link SignatureDocument} at send time — the questionnaire's
 * prefill snapshot. The per-signer owner prefill (name/phone/email/ת"ז) is
 * computed server-side when the questionnaire payload is served.
 */
export type SignatureSellerQuestionnaire = {
  version: string;
  property: SellerQuestionnairePropertyDetails;
  realtor: SellerQuestionnaireRealtorDetails;
  /**
   * Whether the property was offered for sale or for rent — the picked listing's type at
   * send time. It names the questionnaire on every screen: 'rent' is "שאלון בעל זכות בנכס"
   * (a landlord fills it; regulation 7(ד) covers renting too), anything else "שאלון מוכר".
   */
  listedFor?: ListingListedFor;
};

/**
 * What a template — and every document sent from it — IS (Trello #231 item 8). Absent: a contract —
 * the signer signs the document, and a listings block with the questionnaire adds it as a post-signing
 * task (main → addendum → questionnaire, unchanged). 'ownerQuestionnaire': the statutory questionnaire
 * IS the document: the owner goes straight to the coded form, nothing is signed before it, the filled
 * questionnaire's PDF is the signed document and every screen files it as a questionnaire, never as a
 * contract. A document takes it from its TEMPLATE at send (never from the request), and such a send
 * must carry the questionnaire payload. Only a Webot template carries it today
 * (adHocOperations/create-questionnaire-only-template.ts); a duplicate of it keeps it.
 */
export const SignatureDocumentKinds = ['ownerQuestionnaire'] as const;
export type SignatureDocumentKind = typeof SignatureDocumentKinds[number];

/** A template or a document whose only content is the owner's questionnaire. */
export const isQuestionnaireOnlyDocument = (doc: { documentKind?: SignatureDocumentKind } | null | undefined): boolean =>
  doc?.documentKind === 'ownerQuestionnaire';

/**
 * The name of a questionnaire document, by its property: a rental's is "שאלון בעל זכות בנכס" (a
 * landlord fills it), anything else "שאלון מוכר" — the dashboard's `questionnaireName` rule. A
 * questionnaire-only document is named this at send, so every message and screen reads it.
 */
export const questionnaireDocumentTitle = (listedFor: ListingListedFor | null | undefined): string =>
  listedFor === 'rent' ? 'שאלון בעל זכות בנכס' : 'שאלון מוכר';

export const DEFAULT_LISTINGS_BLOCK_FONT_SIZE = 14;
export const DEFAULT_LISTINGS_BLOCK_COLUMNS: ListingsBlockColumn[] = [
  'address',
  'price',
  'rooms',
  'square_meter'
];

/** Relative column widths — see frontend mirror for rationale. */
export const LISTINGS_BLOCK_COLUMN_WEIGHTS: Record<ListingsBlockColumn, number> = {
  address: 4,
  price: 2,
  rooms: 1,
  square_meter: 1
};

export const SignatureTemplateVisibilities = ['private', 'organization', 'public'] as const;
export type SignatureTemplateVisibility = typeof SignatureTemplateVisibilities[number];
export const DEFAULT_SIGNATURE_TEMPLATE_VISIBILITY: SignatureTemplateVisibility = 'private';

/**
 * Whether a caller may leave a signature template or bundle at `visibility`.
 * 'public' reaches every Webot user (a public bundle is listed in everyone's
 * marketplace), so it is reserved for Webot admins; the other two are open to
 * any owner. The server tests the RESULTING visibility of every write with it
 * (`updateTemplate`, `createBundle`, `updateBundle`), and the dashboard uses
 * the same function to decide which options to offer — hiding the option is a
 * courtesy, the server check is the gate.
 */
export const canSetSignatureVisibility = (visibility: SignatureTemplateVisibility, isWebotAdmin: boolean): boolean =>
  visibility !== 'public' || isWebotAdmin;

/**
 * Signing scenarios a realtor can pick a default template for. Keys are stable
 * identifiers; Hebrew display labels live in the dashboard i18n:
 *   buyer → החתמת קונה, seller → החתמת מוכר,
 *   sellerExclusive → החתמת מוכר בבלעדיות,
 *   renter → החתמת שוכר, landlord → החתמת משכיר,
 *   landlordExclusive → החתמת משכיר בבלעדיות,
 *   ownerQuestionnaire → שאלון בלבד (the questionnaire-only document a seller or a landlord is sent
 *     instead of their order: "שאלון בלבד" in the send form, the property card's button, Daniela's
 *     scenario; a template slot, never a quick-pick role of its own),
 *   brokerCooperation → שת״פ בין מתווכים.
 */
export const SignatureDefaultTemplateKeys = [
  'buyer',
  'seller',
  'sellerExclusive',
  'renter',
  'landlord',
  'landlordExclusive',
  'ownerQuestionnaire',
  'brokerCooperation'
] as const;
export type SignatureDefaultTemplateKey = typeof SignatureDefaultTemplateKeys[number];

/**
 * Scenarios that can add an EXCLUSIVITY (בלעדיות) document: the scenario's own
 * template and the one under the key on the right are sent together, as one
 * ad-hoc bundle. The keys on the right are template slots only — never a
 * signing scenario of their own in the send form's quick-pick.
 */
export const SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS: Partial<Record<SignatureDefaultTemplateKey, SignatureDefaultTemplateKey>> = {
  seller: 'sellerExclusive',
  landlord: 'landlordExclusive'
};

/**
 * Scenarios whose exclusivity document is included unless the realtor opts
 * out. A seller is normally signed exclusively; a landlord normally is not, so
 * for a landlord the document is opt-in.
 */
export const SIGNATURE_EXCLUSIVITY_ON_BY_DEFAULT: readonly SignatureDefaultTemplateKey[] = ['seller'];

/** The slot of the questionnaire-only document ("שאלון בלבד"), sent INSTEAD of a role's own order. */
export const SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY: SignatureDefaultTemplateKey = 'ownerQuestionnaire';

/** The roles whose owner fills the questionnaire, and so may be sent it alone. */
export const SIGNATURE_QUESTIONNAIRE_ONLY_ROLES: readonly SignatureDefaultTemplateKey[] = ['seller', 'landlord'];

/**
 * The default-template keys ONE send of `scenario` resolves, in send order.
 * `withExclusivity` undefined means the scenario's own default. The single
 * rule behind the send form's checkbox and the AI's prepare_signature_document.
 */
export const signatureTemplateKeysForScenario = (scenario: SignatureDefaultTemplateKey, withExclusivity?: boolean): SignatureDefaultTemplateKey[] => {
  const exclusivityKey = SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS[scenario];
  const include = withExclusivity ?? SIGNATURE_EXCLUSIVITY_ON_BY_DEFAULT.includes(scenario);
  return exclusivityKey && include ? [scenario, exclusivityKey] : [scenario];
};

/**
 * Scenario key → template id. Partial: an unset key has no default template.
 * Two layers exist — a Webot-admin-managed global map (the default every user
 * gets, stored on {@link SignatureGlobalSettings}) and a per-user override map
 * ({@link User.signatureDefaultTemplates}). The effective value per key is the
 * user's entry when present, else the global entry.
 */
export type SignatureDefaultTemplates = Partial<Record<SignatureDefaultTemplateKey, string>>;

/**
 * Singleton doc (`signature-settings/global`) holding Webot-wide signature
 * settings that only Webot admins may write. Read by every signatures user to
 * resolve effective default templates.
 */
export type SignatureGlobalSettings = Partial<BaseDocument> & {
  defaultTemplates?: SignatureDefaultTemplates;
};

/**
 * Pastel colour keys for a template's optional {@link SignatureNameTag}. Kept
 * as stable keys (not hex) so the palette can be tuned in the dashboard without
 * a data migration; the dashboard maps each key to a {bg, text, border} pastel.
 */
export const SignatureNameTagColors = [
  'slate',
  'indigo',
  'sky',
  'teal',
  'green',
  'amber',
  'rose',
  'purple'
] as const;
export type SignatureNameTagColor = typeof SignatureNameTagColors[number];

/** Max length (characters) of a name-tag label. Enforced in the editor UI. */
export const SIGNATURE_NAME_TAG_MAX_LENGTH = 6;
export const DEFAULT_SIGNATURE_NAME_TAG_COLOR: SignatureNameTagColor = 'indigo';

/**
 * Optional short "name tag" rendered as a small pastel badge immediately before
 * a template's name everywhere it's shown in the dashboard (marketplace,
 * template lists, pickers). Replaces the old convention of prefixing system
 * template names with emojis (🚀 "easy" / 🛡 "robust"). One tag per template.
 */
export type SignatureNameTag = {
  /** Short label, up to {@link SIGNATURE_NAME_TAG_MAX_LENGTH} characters. */
  label: string;
  /** Pastel colour key — see {@link SignatureNameTagColors}. */
  color: SignatureNameTagColor;
};

export type SignatureTemplate = Partial<BaseDocument> & {
  ownerUserId: string; // realtor's user id (email); 'webot:system' for built-in defaults
  /**
   * How the sender identifies the signer. Existing templates default to the
   * CRM contact picker; `manual` asks for a name and phone without creating
   * or linking a contact record.
   */
  signerInputMode?: 'contact' | 'manual';
  /** Organization that owns this template. Populated from the creator's primary org at create time. Optional for legacy + system templates. */
  organizationId?: string;
  /** True for built-in Webot default templates that are visible to all users. Seeded via admin scripts. */
  isSystemDefault?: boolean;
  /** When this template was created via the duplicate flow, the source template id. Useful for traceability + showing "duplicated from" in the UI. */
  duplicatedFromTemplateId?: string;
  /**
   * Who can see this template.
   *   - 'private' (default): only the owner.
   *   - 'organization': every member of organizationId.
   *   - 'public': every authenticated user. Webot admins only — see
   *     {@link canSetSignatureVisibility}.
   * Only the owner can change visibility (plus the moderation path that lets a
   * Webot admin or the org's admin pull one back to 'private').
   */
  visibility?: SignatureTemplateVisibility;
  /**
   * Display position of this template inside its marketplace section (the
   * Webot section for system templates, the org section otherwise). Lower
   * comes first; templates without the field sort after all ordered ones
   * (oldest-first). Written in bulk by the marketplace reorder endpoint
   * (drag & drop) — Webot admins for the Webot section, org admins for
   * their org's section.
   */
  marketplaceSortOrder?: number;
  /**
   * Internal name for the template — what the realtor sees in their template
   * list and document grid. NOT shown to signers (their messages and signing
   * page use `customerFacingName` when set, falling back to `name`).
   */
  name: string;
  /**
   * Customer-facing label for the template. Substituted into the SMS / WA
   * message the signer receives ("X sent you the {customerFacingName} for
   * signature") and the completion confirmation. Lets the realtor name a
   * template internally as "Buyer Brokerage v3 (final)" while the signer
   * sees "Buyer Brokerage Agreement". Optional — falls back to `name` when
   * absent.
   */
  customerFacingName?: string;
  description?: string;
  /**
   * Optional short pastel badge rendered immediately before the template name
   * across the dashboard (marketplace, lists, pickers). Used to mark templates
   * — e.g. Webot's "easy" vs "robust" system contracts — now that names no
   * longer carry emojis. See {@link SignatureNameTag}.
   */
  nameTag?: SignatureNameTag;

  /** Editable HTML body of the template. Absent until the user has saved any content. */
  content?: SignatureTemplateContent;
  fields: SignatureTemplateField[];
  /**
   * Absolutely-positioned listings tables placed on the document body. Each
   * block expands at send time to fit however many listings the realtor
   * picked. Multiple blocks per template are allowed — useful when one
   * template needs separate "current listings" / "comparable listings"
   * sections, for example.
   */
  listingsBlocks: ListingsBlock[];
  /**
   * Singleton send-time "additional terms" region — see
   * {@link AdditionalTermsBlock}. `null` (vs undefined) is the explicit
   * "removed" state: template updates go through Firestore `update()` with
   * ignoreUndefinedProperties, where undefined leaves the stored value
   * untouched.
   */
  additionalTermsBlock?: AdditionalTermsBlock | null;
  signerRoles: SignatureSignerRole[];
  /** See {@link SignatureDocumentKinds}. Absent: a contract. Set by a script, never by the editor. */
  documentKind?: SignatureDocumentKind;

  // Document numbering (prefix + counter) lives entirely on the per-user
  // counter doc — see {@link SignatureTemplateUserCounter}. The template
  // intentionally carries NO numbering fields so favorited / org / system
  // templates can't share counter or prefix state across users.

  archivedAt?: Date;
};

/**
 * Per-user, per-template document numbering for a signature template:
 *   - `nextDocumentNumber`: the next number the allocator will hand out.
 *   - `numberPrefix`: optional prefix this user wants prepended (e.g. "INV-").
 *
 * Both fields are per-user so that favoriting / sharing a template never
 * leaks numbering state across users. Stored at
 * `signature-template-user-counters/{userId}_{templateId}`.
 */
export type SignatureTemplateUserCounter = Partial<BaseDocument> & {
  userId: string;
  templateId: string;
  nextDocumentNumber: number;
  /** Optional prefix prepended to formatted document numbers (e.g. "INV-"). Per-user. */
  numberPrefix?: string;
};

/** Sentinel used as ownerUserId for built-in Webot default templates. */
export const SIGNATURE_SYSTEM_TEMPLATE_OWNER = 'webot:system';

/**
 * A reusable, named group of templates that a realtor sends together. Sending
 * a bundle creates one {@link SignatureDocument} per included template, all
 * tagged with `bundleId = bundle.id` and a shared signer set. Field
 * deduplication during send-prep is handled in the dashboard — fields with
 * the same `(name, signerRoleId)` across templates are collected once and
 * projected back per template at send time.
 *
 * Visibility mirrors {@link SignatureTemplate} (private/organization/public).
 * Visibility on the bundle does NOT cascade access to the included templates;
 * the send path re-checks per-template view rights and rejects the send if
 * the caller can't view every template in `templateIds`.
 */
export type SignatureTemplateBundle = Partial<BaseDocument> & {
  ownerUserId: string;
  /** Owning org (mirrors SignatureTemplate.organizationId). Optional for legacy/system. */
  organizationId?: string;
  /** Internal name shown in the bundles list. */
  name: string;
  /** Customer-facing label substituted into the bundle SMS/WA. Falls back to `name`. */
  customerFacingName?: string;
  description?: string;
  /**
   * Ordered list of template ids contained in this bundle. The order drives
   * `bundleSendOrder` on each created SignatureDocument and the order the
   * signer sees pending docs on the sign page. Must contain >= 1 entry.
   */
  templateIds: string[];
  /**
   * Visibility for the bundle itself. Does NOT cascade to bundled templates — see type docs.
   * 'public' is Webot admins only — see {@link canSetSignatureVisibility}.
   */
  visibility?: SignatureTemplateVisibility;
  archivedAt?: Date;
};

export const SignatureSignerStatuses = ['pending', 'sent', 'viewed', 'signed', 'declined'] as const;
export type SignatureSignerStatus = typeof SignatureSignerStatuses[number];

/**
 * Which engine serves a document's signing ceremony. `'native'` is the only
 * engine; the field is set at send time. Documents that predate the native
 * engine have it absent (or a legacy value) — the sign page treats anything
 * that isn't `'native'` as a legacy document that can no longer be signed.
 */
export const SignatureSigningEngines = ['native'] as const;
export type SignatureSigningEngine = typeof SignatureSigningEngines[number];

/**
 * Append-only per-signer audit event. The native signing path records one of
 * these at every meaningful step (view, otp, consent, sign, decline). Together
 * with `otpVerifiedAt`, the consent stamp, and the PDF hashes, this is the
 * evidentiary trail that lets a realtor demonstrate WHO signed, WHEN, and from
 * WHERE — an SES-tier audit trail backing our native audit-log certificate.
 *
 * `ip` / `userAgent` are server-recorded (never trusted from the client body).
 * `sessionId` / `timezone` are client-supplied context (best-effort).
 */
export const SignatureAuditEventTypes = [
  'view',
  'otp_sent',
  'otp_verified',
  'otp_skipped_in_person',
  'consent',
  'sign',
  'sign_addendum',
  'sign_questionnaire',
  'decline'
] as const;
export type SignatureAuditEventType = typeof SignatureAuditEventTypes[number];

export type SignatureAuditEvent = {
  type: SignatureAuditEventType;
  at: Date; // server clock at ingest
  ip?: string; // server-recorded
  userAgent?: string; // server-recorded
  sessionId?: string; // client-supplied per-signing-session id
  timezone?: string; // client-supplied IANA tz (e.g. "Asia/Jerusalem")
};

/**
 * One structured value a SIGNER entered during the native signing ceremony,
 * distinct from `SignatureDocumentSigner.fieldValues` (which holds the values
 * the realtor PREFILLED before sending). Signature / initials / image values
 * hold a GCS path to the stamped PNG, not the raw base64 — the bytes live in
 * storage, the document stays lean.
 */
export type SignatureSignerFieldValue = {
  type: SignatureFieldType;
  /** text/date/number/percentage/phone/email/checkbox value. */
  value?: string;
  /** GCS storage path of the stamped PNG for signature/initials/image fields. */
  imageStoragePath?: string;
  enteredAt?: Date;
};

/**
 * Version marker for the electronic-signature consent disclosure the signer
 * accepts before signing. Bump this whenever the disclosure wording changes so
 * each signed document records exactly which disclosure the signer agreed to.
 */
export const CURRENT_SIGNATURE_CONSENT_DISCLOSURE_VERSION = '2026-07-01';

export const SignatureWhatsappDeliveryStatuses = ['pending', 'sent', 'failed'] as const;
export type SignatureWhatsappDeliveryStatus = typeof SignatureWhatsappDeliveryStatuses[number];

export type SignatureWhatsappDelivery = {
  status: SignatureWhatsappDeliveryStatus;
  attemptCount: number;
  lastAttemptAt?: Date;
  messageId?: string;
  error?: string; // human-readable, surfaced to the realtor in UI
};

export type SignatureDocumentSigner = {
  id: string; // uuid, generated by backend
  roleId: string;
  contactId?: string;
  name: string;
  phone: string; // international format, e.g. +972545551234
  email?: string;
  // Values the sender prefilled before sending (keyed by field.name).
  // Echoed to the signer as read-only defaults on the signing page.
  fieldValues: Record<string, string>;

  // The signing slug + its public URL, minted per signer at send time. Used to
  // open the public signing page and to resolve the signer from a slug.
  signing?: {
    slug: string; // used to open the public signing page
    publicSigningUrl: string; // "https://app.webot.co.il/sign/{slug}"
  };

  status: SignatureSignerStatus;
  sentAt?: Date;
  viewedAt?: Date;
  signedAt?: Date;
  declinedAt?: Date;

  whatsappDelivery: SignatureWhatsappDelivery;

  // Phone-OTP verification gate. We require a signer to verify possession of
  // `phone` (via a 6-digit SMS code) before our UI mounts the native signing
  // ceremony.
  // The fact + timestamp + IP are recorded here so the realtor can later
  // demonstrate "this contract was signed by someone who controlled the
  // phone number on this date" for audit/legal purposes.
  otpVerifiedAt?: Date;
  otpVerifiedFromIp?: string;
  /** Owner-authorized in-person signing; never phone-possession proof. */
  inPersonOtpSkip?: {
    at: Date;
    authorizedByUserId: string;
    ip?: string;
  };

  // ===== Native signing engine fields (may be absent on legacy documents) =====
  /** Stable per-signer signature id, printed on the signature stamp + audit certificate. Minted at send (native) or first sign. */
  signatureUuid?: string;
  /** Append-only audit trail for this signer (view / otp / consent / sign / decline). */
  auditEvents?: SignatureAuditEvent[];
  /** Server-recorded electronic-signature consent (when the signer accepted the disclosure). */
  consentAcceptedAt?: Date;
  /** Disclosure version in force when the signer consented. */
  consentDisclosureVersion?: string;
  /** Server-recorded IP at the moment consent was accepted. */
  consentIp?: string;
  /** Structured values the SIGNER entered during the ceremony (keyed by field.name). Distinct from prefilled `fieldValues`. */
  signerFieldValues?: Record<string, SignatureSignerFieldValue>;

  // ===== Addresses addendum (hidden-addresses documents only) =====
  /**
   * When the document carries an {@link SignatureDocument.addressesAddendum},
   * the main-document signature is a two-phase ceremony: the signer's main
   * values are persisted and `mainSignedAt` is stamped, but `status` stays
   * short of 'signed' until the addendum is also signed. Absent on documents
   * without an addendum (their main submit signs in one phase).
   */
  mainSignedAt?: Date;
  /** When the signer signed the addresses addendum (flips status → 'signed', unless a seller questionnaire is still pending). */
  addendumSignedAt?: Date;
  /** Values the signer entered on the addendum (keyed by addendum field.name). */
  addendumFieldValues?: Record<string, SignatureSignerFieldValue>;

  // ===== Seller questionnaire (documents with `sellerQuestionnaire` only) =====
  /**
   * When the signer completed + signed the seller questionnaire (the last
   * post-signing task — flips status → 'signed'). Like `mainSignedAt`, the
   * main-document signature is persisted first; the ceremony only completes
   * once the questionnaire is filled and signed.
   */
  questionnaireSignedAt?: Date;
  /** The structured answers the signer submitted on the questionnaire form. */
  questionnaireAnswers?: SellerQuestionnaireAnswers;
  /** GCS storage path of the questionnaire signature PNG. */
  questionnaireSignatureStoragePath?: string;
};

export const SignatureDocumentStatuses = [
  'draft',
  'sent',
  'partial',
  'completed',
  'declined',
  'expired'
] as const;
export type SignatureDocumentStatus = typeof SignatureDocumentStatuses[number];

export type SignatureDocumentSignedFile = {
  storagePath: string;
  url: string;
  /**
   * URL of the audit certificate PDF we compose alongside the signed PDF.
   * (Legacy documents may instead carry an expiring external audit-log URL
   * from before the native migration — see the backfill in the migration plan.)
   */
  auditLogUrl?: string;
  /** SHA-256 (hex) of the final composed signed PDF — tamper-evidence for native docs. */
  sha256?: string;
};

/**
 * The "addresses addendum" — a second, generic document shown to the signer
 * AFTER they sign a hidden-addresses main document. It reveals the full
 * listing addresses and requires its own signature. Rendered + rasterized by
 * the dashboard at send time (same pipeline as the main document) and
 * archived here; the composer stamps + appends its pages into the final
 * signed PDF.
 */
export type SignatureAddressesAddendum = {
  /** Self-contained HTML snapshot (rendered by the signing canvas after the main signature). */
  html: string;
  /** Public URL of the archived addendum PDF (source the composer stamps). */
  pdfUrl: string;
  /** SHA-256 (hex) of the archived addendum PDF — recorded on the audit certificate. */
  pdfSha256: string;
  totalPages: number;
  /** Signature-field geometry extracted from the same DOM that produced the PDF. */
  fields: REST_schema.SignatureSendField[];
};

export type SignatureDocument = Partial<BaseDocument> & {
  ownerUserId: string;
  templateId: string;
  templateNameSnapshot: string; // frozen at send time
  // Frozen at create time alongside `templateNameSnapshot`. The webhook
  // handler uses this to title the completion SMS to the signer without
  // re-fetching the (possibly-mutated) template. Optional for back-compat
  // with documents created before the field existed.
  templateCustomerFacingNameSnapshot?: string;

  documentNumber: number;
  formattedNumber: string; // e.g. "RA-42"

  contactIds: string[]; // the signer contacts (usually 1)
  listingIds: string[]; // 0 or more ProcessedListingData IDs

  signers: SignatureDocumentSigner[];

  /**
   * Which engine serves this document's signing ceremony. Absent = legacy
   * signing ceremony. Set to 'native' by the send path; anything else (or
   * absent) marks a legacy document. Used by the public signing page to branch.
   */
  signingEngine?: SignatureSigningEngine;

  /**
   * Snapshot of the rendered HTML the signers received at send time. The
   * signing canvas renders this, and it lets the realtor re-open the exact
   * document that was sent (useful before signedDocument is populated).
   */
  sentHtml?: string;

  /**
   * Public URL of the archived sent PDF (frontend-rendered). The signing canvas
   * renders it and the composer reloads it to stamp the signer's values.
   */
  sentPdfUrl?: string;

  /**
   * Field geometry captured at send time (normalized 0–1 areas per page). The
   * signing canvas overlays inputs from this, and the PDF composer stamps
   * values into the archived sent PDF using it. Required for a document to be
   * signable. Absent on legacy documents.
   */
  sentFields?: REST_schema.SignatureSendField[];
  /** Page count of the sent PDF — drives the native canvas paging. */
  sentPdfTotalPages?: number;
  /** SHA-256 (hex) of the sent (source) PDF — recorded on the audit certificate. */
  sentPdfSha256?: string;

  /**
   * True when this document was sent with hidden (redacted) listing
   * addresses. Implies `addressesAddendum` is present — the signing ceremony
   * reveals the full addresses only after the main signature.
   */
  hideAddresses?: boolean;
  /** The addresses-disclosure addendum for hidden-addresses sends. */
  addressesAddendum?: SignatureAddressesAddendum;

  /**
   * Whether signers must pass phone-OTP (SMS code) verification before
   * viewing/signing. ABSENT = true (default-on). `false` only when the sender
   * explicitly disabled OTP in the send form for this document — the signer
   * then goes straight from the link to the document, and the audit
   * certificate states that possession proof was waived by the sender.
   */
  otpRequired?: boolean;

  /**
   * Present when this document carries the statutory SELLER QUESTIONNAIRE
   * (שאלון מוכר) as a post-signing task. Holds the send-time prefill snapshot
   * (property + realtor details); the signer's answers land on each signer's
   * `questionnaireAnswers`. The signing ceremony only completes once every
   * signer has also filled + signed the questionnaire.
   */
  sellerQuestionnaire?: SignatureSellerQuestionnaire;
  /**
   * The completed seller questionnaire composed as its OWN standalone PDF
   * (the statutory form is a distinct legal document — it is NOT appended to
   * the signed contract). Uploaded at completion; its SHA-256 is recorded on
   * the audit certificate. Downloadable by the realtor (detail panel) and the
   * signer (completed-document viewer).
   */
  sellerQuestionnaireFile?: { storagePath: string; url: string; sha256: string };
  /**
   * Copied from the template at send. 'ownerQuestionnaire': the questionnaire IS this document — no
   * main step; `signedDocument` is the filled questionnaire's PDF (the same object as
   * `sellerQuestionnaireFile`) and `templateCustomerFacingNameSnapshot` its name by property
   * ({@link questionnaireDocumentTitle}). Absent: a contract.
   */
  documentKind?: SignatureDocumentKind;

  signedDocument?: SignatureDocumentSignedFile;

  status: SignatureDocumentStatus;
  sentAt?: Date;
  completedAt?: Date;
  archivedAt?: Date;
  notes?: string;

  /**
   * When the realtor last edited-and-resent this document AFTER the original
   * send (allowed only while no signer has signed). The sent artifacts
   * (`sentHtml`/`sentPdfUrl`/`sentFields`/hashes) reflect the LATEST edit;
   * signing slugs are preserved so previously-delivered links stay valid.
   */
  editedAt?: Date;
  /** Number of post-send edits performed on this document (absent = 0). */
  editCount?: number;
  /**
   * Snapshot of the send-form inputs that are baked into the rendered HTML
   * and not otherwise recoverable from the document (additional terms,
   * per-source field overrides, per-listing overrides). Persisted at send
   * time so the edit-after-send flow can reopen the form losslessly.
   * Absent on documents sent before this field existed.
   */
  sendFormSnapshot?: SignatureSendFormSnapshot;

  /**
   * When this document was sent as part of a {@link SignatureTemplateBundle},
   * the bundle's id. Set on every doc created by the bundle-send endpoint
   * (`POST /api/v1/signatures/bundles/send`). Unset for single-template sends.
   */
  bundleId?: string;
  /**
   * Index of this document within the bundle's `templateIds` order (0-based).
   * Drives the order pending docs are shown on the sign page inbox.
   */
  bundleSendOrder?: number;
  /**
   * Snapshot of the bundle's `name` at send time. Frozen so the sign-page
   * UI never has to refetch the (possibly-mutated/archived) bundle to render
   * the "Part of: {bundle}" chip on the inbox.
   */
  bundleNameSnapshot?: string;
};

/**
 * Send-form inputs that only exist baked into the rendered HTML — captured at
 * send time so edit-after-send can prefill the form without data loss. Stored
 * verbatim (server never validates/interprets it); shapes mirror the send
 * form's local state in the dashboard's NewSignatureForm.
 */
export type SignatureSendFormSnapshot = {
  /** The send-time "additional terms" section (title + rich-text HTML). */
  additionalTerms?: { title: string; html: string };
  /** Per-prefill-source value overrides (keyed by `SignaturePrefillSource`). */
  fieldOverrides?: Record<string, string>;
  /**
   * Send-time prefills of SIGNER-fillable fields, keyed by the field's tag key
   * (`linkGroupId || name`). `allowEdit: true` → the value rode the signer's
   * `fieldValues` and seeded the ceremony input (signer may change it);
   * `allowEdit: false` → the field was demoted to prefilled for this send
   * (value baked into the HTML, no field tag emitted, so the lock is
   * server-enforced by construction). Captured so edit-after-send restores
   * both the value and the checkbox.
   */
  signerPrefills?: Record<string, { value: string; allowEdit: boolean }>;
  /** Per-listing overrides keyed by listing id (address/price/rooms/sqm as typed). */
  listingOverrides?: Record<
    string,
    Partial<Record<'address' | 'redactedAddress' | 'price' | 'rooms' | 'squareMeter', string>>
  >;
};

/**
 * One row in `signature-slug-index` — doc id === the signing slug. Lets the
 * public signing page + OTP service resolve a slug to its (document, signer)
 * in O(1) instead of scanning every signature document. Written per signer at
 * send time; lazily backfilled when a scan-fallback resolves a missing slug.
 */
export type SignatureSlugIndex = Partial<BaseDocument> & {
  slug: string; // === doc id
  documentId: string;
  signerId: string;
  ownerUserId: string;
};

// One row per signer (id = signerId). Re-issuing an OTP overwrites the prior
// row by design — only the latest active code is valid. Hash is HMAC-SHA256
// over the plaintext code with a server-side pepper (SIGNATURE_OTP_PEPPER).
export type SignatureOtpRecord = Partial<BaseDocument> & {
  signerId: string;
  documentId: string;
  slug: string;
  codeHash: string;
  expiresAt: Date;
  attempts: number; // count of verify attempts on the current code
  resendCount: number; // total SMS sends in current window
  lastSentAt: Date;
  windowStartedAt: Date; // start of the current resend-quota window
  lastIp?: string;
  createdAt: Date;
  updatedAt: Date;
};

// -------------------------------------------------------------------------------------------------
// The client registration form (`app.webot.co.il/r/<alias>/join`, the client portal): what a buyer or
// renter answers. A send WRITES the realtor's contact (a new card at חדש, or the existing one
// overwritten, every change documented on it) and is logged in `client-intake-leads`, server-only
// (docs/rules/client-portal.md in the server repo). The option ids are the wire; these labels are
// the CRM's wording (the note on the card). The portal words its own questions.
// -------------------------------------------------------------------------------------------------
export const CLIENT_INTAKE_LEADS_COLLECTION_NAME = 'client-intake-leads';
export const CLIENT_INTAKE_FORM_VERSION = 1;

export const CLIENT_INTAKE_TRACKS = ['buy', 'rent'] as const;
export type ClientIntakeTrack = typeof CLIENT_INTAKE_TRACKS[number];
export const CLIENT_INTAKE_PURPOSES = ['first', 'upgrade', 'investment'] as const;
export type ClientIntakePurpose = typeof CLIENT_INTAKE_PURPOSES[number];
export const CLIENT_INTAKE_FINANCING = ['approved', 'inProgress', 'noMortgage', 'notChecked'] as const;
export type ClientIntakeFinancing = typeof CLIENT_INTAKE_FINANCING[number];
export const CLIENT_INTAKE_EQUITY = ['under500k', 'from500kTo1m', 'from1mTo2m', 'over2m', 'preferNotToSay'] as const;
export type ClientIntakeEquity = typeof CLIENT_INTAKE_EQUITY[number];
export const CLIENT_INTAKE_TIMINGS = ['asap', 'within3Months', 'within6Months', 'yearPlus', 'flexible'] as const;
export type ClientIntakeTiming = typeof CLIENT_INTAKE_TIMINGS[number];
export const CLIENT_INTAKE_LEASES = ['year', 'twoYearsPlus', 'short'] as const;
export type ClientIntakeLease = typeof CLIENT_INTAKE_LEASES[number];
export const CLIENT_INTAKE_FURNISHING = ['furnished', 'partly', 'unfurnished', 'any'] as const;
export type ClientIntakeFurnishing = typeof CLIENT_INTAKE_FURNISHING[number];
export const CLIENT_INTAKE_PETS = ['none', 'dog', 'cat', 'other'] as const;
export type ClientIntakePet = typeof CLIENT_INTAKE_PETS[number];
export const CLIENT_INTAKE_CHANNELS = ['whatsapp', 'call'] as const;
export type ClientIntakeChannel = typeof CLIENT_INTAKE_CHANNELS[number];
export const CLIENT_INTAKE_BEST_TIMES = ['morning', 'noon', 'evening'] as const;
export type ClientIntakeBestTime = typeof CLIENT_INTAKE_BEST_TIMES[number];

export const CLIENT_INTAKE_PROPERTY_TYPE_KEYS = ['apartment', 'gardenApartment', 'penthouse', 'duplex', 'privateHouse', 'semiDetached', 'studio', 'lot'] as const;
export type ClientIntakePropertyType = typeof CLIENT_INTAKE_PROPERTY_TYPE_KEYS[number];
/** Each form choice as the CRM's `interestedInPropertyTypes` value, byte for byte. */
export const CLIENT_INTAKE_PROPERTY_TYPES: Record<ClientIntakePropertyType, PropertyType> = {
  apartment: 'דירה',
  gardenApartment: 'דירת גן',
  penthouse: 'גג/ פנטהאוז',
  duplex: 'דופלקס',
  privateHouse: "בית פרטי/ קוטג'",
  semiDetached: 'דו משפחתי',
  studio: 'סטודיו/ לופט',
  lot: 'מגרשים',
};

export const CLIENT_INTAKE_MUST_HAVES = [
  'protectedSpace',
  'anyShelter',
  'parking',
  'elevator',
  'terrace',
  'storage',
  'garden',
  'airConditioning',
  'accessible',
  'renovated',
  'housingUnit',
  'notGroundFloor',
  'notTopFloor',
] as const;
export type ClientIntakeMustHave = typeof CLIENT_INTAKE_MUST_HAVES[number];
/** What each track asks about. */
export const CLIENT_INTAKE_MUST_HAVES_BY_TRACK: Record<ClientIntakeTrack, readonly ClientIntakeMustHave[]> = {
  buy: ['protectedSpace', 'anyShelter', 'parking', 'elevator', 'terrace', 'storage', 'garden', 'accessible', 'renovated', 'housingUnit', 'notGroundFloor', 'notTopFloor'],
  rent: ['protectedSpace', 'anyShelter', 'parking', 'elevator', 'terrace', 'airConditioning', 'storage', 'renovated', 'accessible', 'notGroundFloor', 'notTopFloor'],
};

/** The CRM's wording of every answer: the note written with a lead, and the leads inbox. */
export const CLIENT_INTAKE_LABELS = {
  track: { buy: 'קנייה', rent: 'שכירות' },
  purpose: { first: 'דירה ראשונה', upgrade: 'משפרי דיור', investment: 'השקעה' },
  financing: { approved: 'יש אישור עקרוני', inProgress: 'אישור עקרוני בתהליך', noMortgage: 'בלי משכנתא', notChecked: 'עוד לא בדקו משכנתא' },
  equity: { under500k: 'עד 500 אלף', from500kTo1m: '500 אלף עד מיליון', from1mTo2m: 'מיליון עד 2 מיליון', over2m: 'מעל 2 מיליון', preferNotToSay: 'העדיפו לא לציין' },
  timing: { asap: 'כמה שיותר מהר', within3Months: 'תוך 3 חודשים', within6Months: 'תוך חצי שנה', yearPlus: 'שנה ומעלה', flexible: 'גמישים' },
  lease: { year: 'שנה', twoYearsPlus: 'שנתיים ומעלה', short: 'תקופה קצרה' },
  furnishing: { furnished: 'מרוהטת', partly: 'מרוהטת חלקית', unfurnished: 'לא מרוהטת', any: 'לא משנה' },
  pets: { none: 'בלי חיות מחמד', dog: 'כלב', cat: 'חתול', other: 'חיית מחמד אחרת' },
  channel: { whatsapp: 'וואטסאפ', call: 'שיחת טלפון' },
  bestTime: { morning: 'בוקר', noon: 'צהריים', evening: 'ערב' },
  entry: { immediate: 'מיידית', flexible: 'גמישה' },
  mustHave: {
    protectedSpace: 'ממ"ד',
    anyShelter: 'מרחב מוגן כלשהו',
    parking: 'חניה',
    elevator: 'מעלית',
    terrace: 'מרפסת',
    storage: 'מחסן',
    garden: 'גינה',
    airConditioning: 'מיזוג',
    accessible: 'נגישות',
    renovated: 'משופצת',
    housingUnit: 'יחידת דיור',
    notGroundFloor: 'לא קומת קרקע',
    notTopFloor: 'לא קומה אחרונה',
  },
} as const;

/**
 * What a send became: `added` (a new contact), `updated` (an existing card), `new` (the contact write
 * failed; `client_intake_contact{status=error}`). `handled` and `dismissed` are the leads inbox's
 * (2026-10-16..19, retired when the form began writing contacts): old documents may still hold them.
 */
export const CLIENT_INTAKE_LEAD_STATUSES = ['new', 'added', 'updated', 'handled', 'dismissed'] as const;
export type ClientIntakeLeadStatus = typeof CLIENT_INTAKE_LEAD_STATUSES[number];

/**
 * The log of the form's sends in `client-intake-leads`, one document per (realtor, phone), server-only:
 * the last send whole, how many there were, and what the last one became.
 */
export type ClientIntakeLead = Partial<BaseDocument> & {
  realtorId: string;
  alias: string;
  formVersion: number;
  status: ClientIntakeLeadStatus;
  track: ClientIntakeTrack;
  answers: REST_schema.ClientIntakeAnswers;
  contact: {
    name: string;
    phoneInternational: string;
    email?: string;
    channel?: ClientIntakeChannel;
    bestTimes?: ClientIntakeBestTime[];
  };
  /** The contact fields the send wrote, already through the CRM's own validation. */
  proposedFields: Record<string, unknown>;
  /** Every answer the CRM has no field for, as one Hebrew note (dated, under the card's "על הלקוח"). */
  note: string;
  /** The places, by name. */
  areaNames: string[];
  flags: { alsoSelling?: boolean; preApproved?: boolean };
  suggestedListingIds: string[];
  submissions: number;
  lastSubmittedAt: Date;
  /** The last send's key: a retried send finds it and changes nothing. */
  lastIdempotencyKey?: string;
  resolvedAt?: Date;
  resolvedBy?: string;
  contactId?: string;
  notifiedAt?: Date;
};

export namespace REST_schema {
  // Reusable mini user type for user references across the API
  export type MiniUser = {
    userName: string;
    userId: string;
    userMail: string;
    userAvatar: string;
  };

  // --- WhatsApp inbox (per-number) ----------------------------------------------------------

  /**
   * An APPROVED template on the number's WABA, lean enough for the closed-window picker.
   * `paramNames` present ⇒ NAMED format; otherwise the template takes `paramCount` positional
   * values. `bodyText` keeps the raw `{{…}}` placeholders so the UI can preview.
   */
  export type WhatsappNumberTemplateView = {
    name: string;
    language: string;
    category: string;
    bodyText: string;
    headerText?: string;
    footerText?: string;
    paramCount: number;
    paramNames?: string[];
  };
  export type WhatsappNumberTemplatesResponse = { templates: WhatsappNumberTemplateView[] };

  export type WhatsappSendTemplateFromInboxRequest = {
    templateName: string;
    language: string;
    /** Positional array or named map — must match the template's parameter format. */
    params?: string[] | Record<string, string>;
  };

  // POST /api/v1/whatsapp/assistant/:numberKey/test — dry-run one assistant turn.
  // Runs the real prompt builder and the real model, and writes/sends NOTHING, so the owner can
  // iterate on their persona without spending WhatsApp messages on themselves.
  export type WhatsappAssistantTestRequest = {
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    /** An unsaved draft to try instead of the stored prompt. */
    systemPromptOverride?: string;
  };
  export type WhatsappAssistantTestResponse = {
    /** The reply exactly as WhatsApp would render it, control tags already stripped. */
    reply: string;
    /** The control the assistant emitted, when it ended the conversation. */
    control?: { status: 'completed' | 'stopped'; reason?: string };
  };

  // POST /api/v1/whatsapp/assistant/:numberKey — update the number's assistant config.
  // WHITELIST: every field a member may change is named here.
  export type WhatsappAssistantUpdateRequest = {
    enabled?: boolean;
    systemPrompt?: string;
    maxTurnsPerConversation?: number;
    autoStart?: WhatsappAssistantAutoStart;
    openerTemplateName?: string;
    openerTemplateLanguage?: string;
    // The whole ladder, replacing the stored one. Every step's template must be APPROVED on this
    // number, with no parameters, and a media header must have its media.
    followUps?: WhatsappAssistantFollowUps;
  };
  export type WhatsappAssistantUpdateResponse = {
    config: WhatsappAssistantConfig;
  };

  // --- Sharing a number with the office ------------------------------------------------------
  // `whatsapp-number-access.memberMails` is the ACL for everything about a number. These two
  // routes are the dashboard's only way to change it; the rules are in
  // `whatsappNumberSharingRules.ts` (who may: the owner, or an admin of the owner's org, who
  // is already a member; who may be added: that org's roster; what a save means: the desired
  // set among the candidates, preserving members the dialog cannot see).

  /** Someone the number is shared with, as the dialog lists them. */
  export type WhatsappNumberSharingMember = {
    userId: string;
    displayName: string | null;
    avatarUrl: string | null;
    isOwner: boolean;
  };

  /** Someone the caller MAY share the number with — a colleague from the owner's organization. */
  export type WhatsappNumberSharingCandidate = {
    userId: string;
    displayName: string | null;
    avatarUrl: string | null;
    /** An admin of (one of) the organizations this candidate came from. */
    isAdmin: boolean;
    /** Already in `memberMails`. */
    isMember: boolean;
    /**
     * Whether the colleague's account holds the `officialWhatsapp` feature. Sharing with an
     * account that does not is allowed and harmless, but they will not see the WhatsApp pages
     * until support enables it — the dialog says so rather than letting the share look done.
     */
    hasOfficialWhatsapp: boolean;
    orgIds: string[];
  };

  // GET /api/v1/whatsapp/numbers/:numberKey/sharing — members only (404 otherwise, like every
  // number-scoped route). `role` is null for a member who may look but not change the list;
  // `candidates` is empty for them.
  export type WhatsappNumberSharingView = {
    numberKey: string;
    ownerMail: string | null;
    role: 'owner' | 'org_admin' | null;
    members: WhatsappNumberSharingMember[];
    candidates: WhatsappNumberSharingCandidate[];
    /** The organizations whose roster `candidates` was drawn from. */
    orgs: { id: string; name: string | null }[];
  };

  // POST /api/v1/whatsapp/numbers/:numberKey/sharing — set the membership among the candidates.
  // `memberMails` is the DESIRED list of candidates (the owner may be included or not — they
  // are always a member). A listed email that is not a candidate is a 400 for the whole request.
  export type WhatsappNumberSharingUpdateRequest = {
    memberMails: string[];
  };
  export type WhatsappNumberSharingUpdateResponse = WhatsappNumberSharingView & {
    added: string[];
    removed: string[];
  };

  // --- WhatsApp templates management --------------------------------------------------------

  /**
   * `POST /api/v1/whatsapp/status/check` — ask Meta directly what is blocking this connection.
   *
   * No request body: the connection is resolved from the verified Firebase token, never from the
   * request, so one customer can never probe another's.
   */
  export type WhatsappStatusCheckResponse = {
    status: WhatsappConnectionStatus;
    issues: WhatsappConnectionIssue[];
    /** ISO string over the wire. */
    checkedAt: string;
    /**
     * Meta refused at least one read (a Tech Provider cannot see every field on a customer's
     * WABA), so absence of an issue is not proof of its absence. The UI says so rather than
     * showing an unqualified all-clear.
     */
    partial: boolean;
  };

  export type WhatsappCreateTemplateRequest = {
    hebrewTitle: string;
    /** Author on this number's WABA (requires membership in its access doc). Absent = own connection. */
    numberKey?: string;
    requestedCategory: 'MARKETING' | 'UTILITY';
    headerFormat?: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO';
    headerText?: string;
    /** Both come from ONE `POST /templates/media` response — never assembled by the client. */
    headerMediaHandle?: string;
    headerMediaUrl?: string;
    headerMediaMimeType?: string;
    /** See WhatsappTemplate.headerMediaSource. Meaningful only with an IMAGE header. */
    headerMediaSource?: 'listing';
    bodyText: string;
    footerText?: string;
    buttons?: WhatsappTemplateButton[];
    params: WhatsappTemplateParam[];
    /**
     * OPT-IN (2026-09-14): true adds the "הסרה" quick reply to a MARKETING template. The builder
     * asks and defaults it OFF since 2026-10-27 (Haim: added only when the realtor asks for it).
     * An omitted flag means NO button.
     */
    includeOptOutButton?: boolean;
    /** false ⇒ persist a DRAFT doc, no Meta call. Defaults true. */
    submitImmediately?: boolean;
  };

  export type WhatsappTemplateValidationIssue = {
    severity: 'block' | 'warn';
    code: string;
    field: 'header' | 'body' | 'footer' | 'buttons' | 'params' | 'category';
    hebrewMessage: string;
    autoFixAvailable: boolean;
  };

  export type WhatsappValidateTemplateResponse = {
    issues: WhatsappTemplateValidationIssue[];
    canSubmit: boolean;
    predictedCategory: WhatsappTemplateCategory;
    /** Code-point lengths against Hebrew-safe budgets, NOT String.length. */
    lengths: { header: number; body: number; footer: number; bodyAtMaxParams: number };
  };

  /** The wire projection of WhatsappTemplate: `id` + ISO-string dates. */
  export type WhatsappTemplateView = Omit<
    WhatsappTemplate,
    | 'editTimestamps'
    | 'submittedAt'
    | 'approvedAt'
    | 'lastSyncedAt'
    | 'pausedUntil'
    | 'disabledAt'
    | keyof BaseDocument
  > & {
    id: string;
    submittedAt?: string;
    approvedAt?: string;
    lastSyncedAt?: string;
    pausedUntil?: string;
    disabledAt?: string;
  };

  /**
   * `POST /templates/:id/sources` — where each param's value COMES FROM, and whether the header
   * image is the picked listing's. Webot-side metadata only: Meta never sees bindings, so this
   * never resubmits the template or resets its approval. Labels and example values are content
   * and stay with the edit route.
   */
  export type WhatsappTemplateSourcesRequest = {
    params: { paramName: string; binding: WhatsappParamBinding; fallbackValue?: string }[];
    /** 'listing' sets it (an IMAGE header only, refused otherwise); null clears it; absent leaves it. */
    headerMediaSource?: 'listing' | null;
  };

  export type WhatsappEditTemplateRequest = {
    hebrewTitle?: string;
    headerFormat?: 'NONE' | 'TEXT' | 'IMAGE';
    headerText?: string;
    /** Omitted ⇒ the template keeps the media it has. */
    headerMediaHandle?: string;
    headerMediaUrl?: string;
    bodyText?: string;
    footerText?: string;
    buttons?: WhatsappTemplateButton[];
    params?: WhatsappTemplateParam[];
    /** true ADDS the opt-out if absent; omitting it keeps the template's existing buttons. */
    includeOptOutButton?: boolean;
    /** Graph permits a category change only while REJECTED / PAUSED. */
    category?: 'MARKETING' | 'UTILITY';
  };

  /**
   * One upload, two artefacts. `handle` is Meta's approval SAMPLE (spent in the create call and
   * never sendable); `url` is a public, content-addressed copy of the same bytes, sent as the
   * header's media link on every message. They are not interchangeable, which is why both ride
   * back together and the client passes both straight into the create request.
   */
  export type WhatsappTemplateMediaResponse = {
    handle: string;
    url: string;
    mimeType: string;
    headerFormat: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  };

  export type WhatsappStarterPackResponse = {
    created: string[];
    skipped: string[];
    failed: { name: string; hebrewMessage: string }[];
  };

  export type WhatsappListTemplatesResponse = {
    templates: WhatsappTemplateView[];
    /**
     * `sync=1` was asked for and the Meta reconcile threw. The stored list is still returned (a
     * token blip must not blank the page), so the caller has to read THIS to know whether the
     * refresh actually happened — absent means it did, or none was requested.
     */
    syncFailed?: boolean;
  };

  // --- WhatsApp groups ----------------------------------------------------------------------

  export type WhatsappGroupMemberInput = {
    phone: string; // any parseable form; normalised to E.164 server-side
    firstName?: string;
    lastName?: string;
    displayName?: string;
    /** CRM contact link — carried when the audience is built from a contact selection. */
    contactId?: string;
    ownerUserId?: string;
    customFields?: Record<string, string>;
  };

  export type WhatsappCreateGroupRequest = {
    name: string;
    members: WhatsappGroupMemberInput[];
    /** Absent = personal. 'org' shares the audience with the whole agency (requires membership). */
    scope?: 'personal' | 'org';
    orgId?: string;
    source?: 'manual' | 'file_import' | 'whatsapp_import' | 'crm_selection' | 'crm_filter';
    description?: string;
  };
  export type WhatsappCreateGroupResponse = {
    groupId: string;
    memberCount: number;
    /** Rows that could not be normalised to a mobile E.164 — returned, not written. */
    invalidRows: { phone: string; reason: string }[];
  };
  export type WhatsappGroupView = {
    id: string;
    name: string;
    memberCount: number;
    createdTime?: string;
    /** 'personal' when absent. */
    scope?: 'personal' | 'org';
    orgId?: string;
    /** The creator — lets the UI say whose audience an org-shared one is. */
    createdBy?: string;
    source?: 'manual' | 'file_import' | 'whatsapp_import' | 'crm_selection' | 'crm_filter';
    description?: string;
  };

  /**
   * Create an audience from a CRM selection — `mode:'ids'` (ticked rows) or `mode:'filter'`
   * (server-resolved match set, `expectedCount` verified). `view`/`orgId` are the CRM SCOPE
   * fields; the audience's own sharing rides `audienceScope`/`audienceOrgId`.
   */
  export type CreateAudienceFromSelectionRequest = {
    name: string;
    description?: string;
    selection: unknown; // ContactSelection — typed server-side
    view?: 'mine' | 'org';
    orgId?: string;
    audienceScope?: 'personal' | 'org';
    audienceOrgId?: string;
  };
  export type CreateAudienceFromSelectionResponse = WhatsappCreateGroupResponse;
  export type WhatsappListGroupsResponse = { groups: WhatsappGroupView[] };
  export type WhatsappGroupMembersQueryRequest = { cursor?: string; limit?: number };
  export type WhatsappGroupMembersQueryResponse = {
    members: (WhatsappGroupMember & { id: string })[];
    nextCursor?: string;
  };

  // --- Blacklist (blocked numbers) ----------------------------------------------------------

  export type WhatsappBlacklistRow = {
    phone: string; // E.164
    displayName?: string; // joined from the contact when one exists
    /** Hebrew label of how the number got here. */
    sourceHebrew: string;
    reason?: WhatsappSuppressionReason;
    /** The free-text note typed when the number was blocked (reasonDetail on the doc). */
    note?: string;
    addedAt?: string;
    /** 'personal' when absent. Org rows block the number for the whole agency. */
    scope?: 'personal' | 'org';
    orgId?: string;
    /** False for Meta-owned entries (user_preferences) — only Meta's own resume lifts those. */
    removable: boolean;
  };
  export type WhatsappBlacklistQueryRequest = { cursor?: string; limit?: number };
  export type WhatsappBlacklistQueryResponse = { rows: WhatsappBlacklistRow[]; nextCursor?: string };
  export type WhatsappBlacklistAddRequest = { phone: string; note?: string; scope?: 'personal' | 'org'; orgId?: string };
  export type WhatsappBlacklistRemoveRequest = { phone: string; confirm: true; scope?: 'personal' | 'org'; orgId?: string };

  /**
   * Bulk pre-send check for EVERY WhatsApp-Web (extension) send loop. The caller sends the whole
   * recipient list; the response names only the numbers that must NOT be messaged.
   *
   * FAIL-CLOSED CONTRACT: a client that cannot reach this endpoint (network error, non-2xx, parse
   * failure) MUST treat the whole batch as blocked and abort the send — never "assume clear".
   */
  export type WhatsappBlacklistCheckRequest = { phones: string[] }; // ≤ 5000 per call
  export type WhatsappBlacklistCheckBlockedRow = {
    phone: string; // normalized E.164
    reason: WhatsappSuppressionReason | 'banned' | 'contact_opt_out' | 'invalid_phone';
    scope: 'personal' | 'org' | 'system';
    hebrew: string;
  };
  export type WhatsappBlacklistCheckResponse = { blocked: WhatsappBlacklistCheckBlockedRow[] };

  // --- WhatsApp campaigns -------------------------------------------------------------------

  export type WhatsappCreateCampaignRequest = {
    /** THIS campaign's header image/video, overriding the template's default. https only. */
    headerMediaUrl?: string;
    name: string;
    templateDocId: string;
    /** Send FROM this number (requires membership in its access doc). Absent = own connection. */
    numberKey?: string;
    contactIds?: string[]; // contact phone numbers (E.164)
    groupIds?: string[]; // expanded into recipients at create; members are NOT contacts
    /**
     * Recipients straight from a CRM SELECTION (the contacts page's bulk bar): the ticked rows, or
     * `mode:'filter'` for "all N matching". Resolved SERVER-SIDE by the same resolver as
     * `/audiences/from-selection` (the read door's scope, `expectedCount` verified) and added to the
     * campaign directly — no audience is written. `view`/`orgId` are the CRM scope fields, exactly as
     * every bulk endpoint takes them.
     */
    crmSelection?: { selection: unknown; view?: 'mine' | 'org'; orgId?: string };
    staticParamValues?: Record<string, string>;
    perRecipientParams?: Record<string, Record<string, string>>;
    /** Defaults for params whose per-recipient value resolves empty. See WhatsappCampaign. */
    paramFallbacks?: Record<string, string>;
    listingId?: string;
    scheduledFor?: string; // ISO; future ⇒ campaign starts 'scheduled'
    sendWindow?: { startHour: number; endHour: number };
    /**
     * Requested inter-message gap, seconds. NARROW-ONLY: the server clamps to its own floor
     * (a request can slow a campaign, never speed it) and refuses an inverted range
     * (CAMPAIGN_INVALID_PACING). Max honoured gap: 15 minutes.
     */
    pacing?: { gapSecondsMin?: number; gapSecondsMax?: number };
    /** Minutes; default and floor WHATSAPP_MIN_RECENCY_MINUTES (10) — "skip nobody" but a message minutes old. */
    skipIfMessagedWithinMinutes?: number;
    /** The EXACT Hebrew wording rendered to the realtor. Stored verbatim as evidence. */
    consentAttestationText: string;
    consentAttestationAccepted: true; // literal — cannot be omitted
    /**
     * Run the start pre-flight inside create: future scheduledFor lands 'scheduled', otherwise
     * 'running' with the inline first send. On refusal the campaign is still created as a draft
     * and the response's `activation` says why.
     */
    activate?: boolean;
  };

  export type WhatsappCreateCampaignResponse = {
    campaignId: string;
    totals: WhatsappCampaign['totals'];
    skippedAtCreate: { phone: string; reason: string }[];
    /**
     * Per-binding fill rates over the WHOLE recipient set, so the wizard can say "יתמלא אצל 4,812
     * מתוך 5,000" before the realtor ever opens the preview (which samples 500). Absent when the
     * template uses no auto-filled parameters.
     */
    bindingCoverage?: WhatsappParamCoverage[];
    /** The status after `activate` (or 'draft' when not requested / activation failed). */
    status?: WhatsappCampaignStatus;
    /** Present only when `activate` was requested and the start phase refused. */
    activation?: { ok: boolean; errorCode?: string; error?: string };
  };

  /**
   * Sharing ONE listing with selected contacts over official WhatsApp
   * (`POST /whatsapp/listing-share/status` and `/send`). The listing is added to each contact's
   * minisite first; the message carries that contact's own link to the listing inside it.
   */
  export type WhatsappListingShareVariant = "image" | "text";

  export type WhatsappListingShareStatusRequest = {
    listingId: string;
    /** Send FROM this number (membership required). Absent = the caller's own connection. */
    numberKey?: string;
    /** Resolve the REAL link this contact will receive, for the preview (E.164). */
    previewContactId?: string;
  };

  export type WhatsappListingShareStatusResponse = {
    numberKey: string;
    /** `image` when the listing has a photo Meta can fetch, else `text`. */
    variant: WhatsappListingShareVariant;
    templateDocId: string;
    templateStatus: WhatsappTemplateStatus | "MISSING";
    /** True only when the template is APPROVED on Meta right now. */
    sendable: boolean;
    /** The body with the link placeholder, for the confirm dialog's preview. */
    previewBody: string;
    previewFooter?: string;
    headerImageUrl?: string;
    /**
     * The exact link `previewContactId` will receive (`…/myListings/{minisiteId}/{listingId}`).
     * Absent when that contact has no minisite yet — one is created at send.
     */
    previewLink?: string;
  };

  export type WhatsappListingShareSendRequest = {
    listingId: string;
    numberKey?: string;
    /** E.164 phones — each one's minisite gets the listing. */
    contactIds: string[];
    /** As on a campaign; default and floor WHATSAPP_MIN_RECENCY_MINUTES ("skip nobody"). */
    skipIfMessagedWithinMinutes?: number;
    consentAttestationText: string;
    consentAttestationAccepted: true;
  };

  export type WhatsappListingShareSendResponse = {
    campaignId: string;
    variant: WhatsappListingShareVariant;
    totals: WhatsappCampaign["totals"];
    skippedAtCreate: { phone: string; reason: string }[];
    status?: WhatsappCampaignStatus;
    activation?: { ok: boolean; errorCode?: string; error?: string };
    /** Contacts whose minisite could not be updated — not messaged, so no dead link goes out. */
    minisiteFailures: { phone: string; reason: string }[];
  };

  export type WhatsappCampaignSummary = {
    id: string;
    name: string;
    status: WhatsappCampaignStatus;
    templateName: string;
    totals: WhatsappCampaign['totals'];
    createdTime?: string;
    startedAt?: string;
    finishedAt?: string;
    scheduledFor?: string;
    pausedReason?: string;
    /** The creator (an email) and the create-time snapshot of their name and face. */
    userId: string;
    createdByName?: string | null;
    createdByAvatarUrl?: string | null;
    /** The sending number, when the campaign was created with one. */
    numberKey?: string;
    responseStats?: WhatsappCampaign['responseStats'];
  };

  export type WhatsappQueryCampaignsRequest = {
    status?: WhatsappCampaignStatus;
    limit?: number;
    cursor?: string;
    /** Default 'campaign' — one_off scheduled sends stay off the campaigns page unless asked for. */
    kind?: 'campaign' | 'one_off' | 'all';
    /**
     * Every campaign sent FROM this number, whoever created it — the caller must be a member of
     * its access doc. Absent = the caller's own campaigns (the pre-sharing behaviour). Campaigns
     * created before numbers were stamped carry no `numberKey` and only ever list under their owner.
     */
    numberKey?: string;
  };
  export type WhatsappQueryCampaignsResponse = {
    campaigns: WhatsappCampaignSummary[];
    nextCursor?: string;
  };

  // --- Scheduled single sends (one_off campaigns) --------------------------------------------

  export type WhatsappCreateScheduledSendRequest = {
    templateDocId: string;
    /** Send FROM this number (membership-gated). Absent = own connection. */
    numberKey?: string;
    /** 1..10 recipients (E.164 or any parseable form). */
    contactIds: string[];
    staticParamValues?: Record<string, string>;
    perRecipientParams?: Record<string, Record<string, string>>;
    /** ISO. Absent/past = send now (subject to the 09:00–20:00 window — a held send waits). */
    scheduledFor?: string;
    /** The listing the template's `listing.*` params were filled from (the values ride as static ones). */
    listingId?: string;
    /** THIS send's header image/video (https), e.g. the picked listing's photo. Absent = the template's own. */
    headerMediaUrl?: string;
  };
  export type WhatsappCreateScheduledSendResponse = {
    campaignId: string;
    status: WhatsappCampaignStatus;
    scheduledFor?: string;
    /** Present when the start phase refused (created as draft). */
    activation?: { ok: boolean; errorCode?: string; error?: string };
  };

  export type WhatsappScheduledSendView = {
    id: string;
    status: WhatsappCampaignStatus;
    templateName: string;
    contactIds: string[];
    scheduledFor?: string;
    createdTime?: string;
    totals: WhatsappCampaign['totals'];
  };
  /** `contactId` narrows to one contact's sends (the panel's pending badge); absent = all. */
  export type WhatsappScheduledSendsQueryRequest = { contactId?: string; includeDone?: boolean };
  export type WhatsappScheduledSendsQueryResponse = { sends: WhatsappScheduledSendView[] };

  export type WhatsappRescheduleSendRequest = { scheduledFor: string };

  // --- Per-template analytics ----------------------------------------------------------------

  export type WhatsappTemplateStatsRequest = {
    numberKey?: string; // absent = own connection's number
    templateName: string;
    language: string;
    /** 1..90; default 30. */
    sinceDays?: number;
  };
  export type WhatsappTemplateStatsResponse = {
    sent: number;
    delivered: number; // delivered or read
    read: number;
    failed: number;
    bySource: { campaign: number; inbox: number; api: number; automation: number };
    sinceDays: number;
  };

  export type WhatsappCampaignPreviewRow = {
    contactId: string;
    displayName?: string;
    renderedText: string;
    willSend: boolean;
    skipReason?: WhatsappSkipReason;
    hebrewSkipReason?: string;
    missingParams: string[];
  };

  export type WhatsappCampaignPreviewResponse = {
    /** One row per queued recipient — the whole list, no cap. */
    rows: WhatsappCampaignPreviewRow[];
    /**
     * The three totals always add up: `totalWillSend + totalSkipped === totalSelected`, with
     * `skippedBreakdown` summing to `totalSkipped`.
     */
    totalSelected: number;
    totalWillSend: number;
    totalSkipped: number;
    /** `not_queued_at_create`: picked, but dropped at create for a value the message needs — such a recipient has no row. */
    skippedBreakdown: { reason: WhatsappSkipReason | 'not_queued_at_create'; count: number; hebrew: string }[];
    /** e.g. "כ-3 שעות, בין 09:00 ל-20:00" — from the gap, the hourly cap and the daily budget of the connection's LIVE tier. */
    estimatedDurationHebrew: string;
    warnings: string[]; // Hebrew
  };

  // --- The recipients list: delivery + response, one row per person --------------------------

  /**
   * `all` is everyone; `responded` / `awaiting` / `replied` are response states; `opted_out` is
   * everyone who asked to be removed after the campaign reached them; the rest are delivery
   * states. The chips' counts come off the live campaign doc (`totals` + `responseStats`).
   */
  export const whatsappCampaignPeopleFilters = [
    'all',
    'responded',
    'awaiting',
    'replied',
    'opted_out',
    'queued',
    'sent',
    'delivered',
    'read',
    'failed',
    'skipped',
  ] as const;
  export type WhatsappCampaignPeopleFilter = typeof whatsappCampaignPeopleFilters[number];

  /**
   * `activity` (default): the responders first — awaiting before replied, newest reply first —
   * then everyone else in phone order; `phone`: phone order for all.
   */
  export type WhatsappCampaignPeopleSort = 'activity' | 'phone';

  // POST /whatsapp/campaigns/:id/recipients/query
  export type WhatsappCampaignRecipientsQueryRequest = {
    filter?: WhatsappCampaignPeopleFilter;
    sort?: WhatsappCampaignPeopleSort;
    /**
     * A phone (any form — its digits match as a prefix of the E.164) or a name prefix. With a
     * search the answer is ONE unpaged page of up to 50 matches; `sort` and `cursor` are ignored.
     */
    search?: string;
    cursor?: string;
    limit?: number;
    /** Older callers: the same as `filter` set to that delivery state. */
    state?: WhatsappRecipientState;
  };

  export type WhatsappCampaignRecipientView = Omit<
    WhatsappCampaignRecipient,
    keyof BaseDocument | 'queuedAt' | 'claimedAt' | 'sentAt' | 'deliveredAt' | 'readAt' | 'failedAt' | 'repliedAt' | 'response' | 'optedOutAt'
  > & {
    id: string;
    queuedAt?: string;
    sentAt?: string;
    deliveredAt?: string;
    readAt?: string;
    failedAt?: string;
    /** When this person asked to be removed after the campaign reached them. */
    optedOutAt?: string;
    response?: {
      status: 'awaiting' | 'replied';
      conversationId: string;
      /** At most CAMPAIGN_RESPONSE_MAX_MESSAGES, oldest first; `messageCount` says how many there really were. */
      messages: { wamid: string; type: string; text?: string; caption?: string; at: string }[];
      messageCount: number;
      firstAt: string;
      lastAt: string;
      /** Our answer, with the dashboard user's name resolved when there is one. */
      reply?: { type: string; text?: string; at: string; source: WhatsappCampaignResponseReplySource; userId?: string; byName?: string | null };
      /** The thread's 24h customer-service window — a free-form reply is possible until it. */
      windowExpiresAt?: string;
      windowOpen: boolean;
    };
  };

  export type WhatsappCampaignRecipientsQueryResponse = {
    /**
     * With `sort: 'activity'` and no cursor: the responders matching the filter come first and
     * COMPLETE (newest reply first, up to 500), followed by the first page of the rest; later
     * pages hold the rest only. With `sort: 'phone'`: one phone-ordered sequence.
     */
    recipients: WhatsappCampaignRecipientView[];
    nextCursor?: string;
    /** True when the campaign holds more responders than one query returns. */
    truncated?: boolean;
  };

  /**
   * POST /whatsapp/campaigns/:id/param-fallbacks — replace the campaign's default values for
   * params that resolve empty per recipient. Refused once the campaign reached a terminal state.
   */
  export type WhatsappCampaignParamFallbacksRequest = { paramFallbacks: Record<string, string> };

  export type WhatsappRetryFailedRequest = { contactIds?: string[] };
  export type WhatsappRetryFailedResponse = {
    requeued: number;
    refused: {
      contactId: string;
      reason: 'permanent_error' | 'attempts_exhausted' | 'not_failed';
    }[];
  };

  /**
   * "שליחה בכל זאת" on one skipped person (`POST /campaigns/:id/recipients/:waId/send-anyway`).
   * `sent`: it went out now; `queued`: it waits for the campaign's send window or for the campaign
   * to be resumed; `skipped`: the gate refused again (an opt-out since, a message minutes ago);
   * `failed`: Meta refused it. `message` is the Hebrew sentence for the realtor.
   */
  export type WhatsappSendAnywayResponse = {
    outcome: 'sent' | 'queued' | 'skipped' | 'failed';
    recipient: WhatsappCampaignRecipientView;
    message: string;
  };

  export type WhatsappCampaignActionResponse = {
    status: WhatsappCampaignStatus;
    totals: WhatsappCampaign['totals'];
  };

  // --- A reply from the campaign page -------------------------------------------------------

  // POST /whatsapp/campaigns/:id/responses/:waId/reply — a free-form reply to one responder,
  // inside the thread's 24h window (WINDOW_CLOSED otherwise). Rides the inbox's own send, marks
  // the response answered with the reply stored on it, and clears the thread's unread count.
  export type WhatsappCampaignReplyRequest = { text: string };
  export type WhatsappCampaignReplyResponse = { recipient: WhatsappCampaignRecipientView; wamid: string };

  // --- Pacing, after create --------------------------------------------------------------------

  // POST /whatsapp/campaigns/:id/pacing — change how fast a campaign sends, before it starts or
  // while it runs (the runner reads `pacing` on every slice). NARROW-ONLY like create: a gap can
  // slow the campaign, never speed it past the engine floor (CAMPAIGN_INVALID_PACING); the window
  // is clamped to 09:00–20:00. Refused on a finished campaign (CAMPAIGN_NOT_EDITABLE).
  export type WhatsappCampaignPacingRequest = {
    gapSecondsMin?: number;
    gapSecondsMax?: number;
    sendWindow?: { startHour: number; endHour: number };
  };
  export type WhatsappCampaignPacingResponse = {
    pacing: WhatsappCampaign['pacing'];
    /** For what is still queued, at the new pace. */
    estimatedDurationHebrew: string;
  };

  // --- Sent-messages log ---------------------------------------------------------------------

  /**
   * POST /whatsapp/messages/log — a paged, filterable log of every OUTBOUND message on one
   * number, across the campaign / inbox / api rails (all recorded in `whatsapp-messages`; the
   * legacy automation rail sends from Webot SYSTEM numbers and is not part of a number's log).
   *
   * `phone` switches the query to the conversation index and filters the rest in memory, so a
   * phone search works with any other filter combination. `source`/`status` are filtered in
   * memory within each fetched page — a page may return fewer than `limit` rows while `nextCursor`
   * is still set; keep paging until it is absent.
   */
  export type WhatsappSentLogRequest = {
    numberKey: string;
    /** Any phone shape — normalized to digits and matched against the conversation. */
    phone?: string;
    templateName?: string;
    source?: "campaign" | "inbox" | "api";
    status?: WhatsappMessageStatus;
    /**
     * Template sends only — drops free-form chat replies and coexistence phone echoes. The
     * UI's default: the log's job is "which template reached whom", not the chat history.
     */
    onlyTemplates?: boolean;
    /** 1..90; default 30. */
    sinceDays?: number;
    /** ISO timestamp cursor from the previous page's `nextCursor`. */
    beforeTimestamp?: string;
    /** 1..100; default 50. */
    limit?: number;
  };
  export type WhatsappSentLogRow = {
    id: string;
    waId: string;
    conversationId: string;
    type: string;
    text?: string;
    templateName?: string;
    templateLanguage?: string;
    source?: "campaign" | "inbox" | "api" | "automation";
    status: WhatsappMessageStatus;
    campaignId?: string;
    errorMessage?: string;
    timestamp: string; // ISO
  };
  export type WhatsappSentLogResponse = {
    rows: WhatsappSentLogRow[];
    /** Absent when the log is exhausted. Pass back as `beforeTimestamp`. */
    nextCursor?: string;
  };

  export type PublicRealtorInfoRequest = {
    realtorMail: string;
  };

  export type PublicRealtorInfoResponse = {
    ownerFullName: string | undefined;
    companyName: string | undefined;
    photoURL: string | undefined;
    companyLogoUrl: string | undefined;
    madlanTopAgentValue: 'top_10' | 'top_10_gold' | null | undefined;
    madlanAgentId: string | undefined;
    madlanAgentLink: string | undefined;
    ownerPhoneNumber: string | undefined;
    ownerMetaPixelId: string | undefined;
    minisiteThemeColor: string | undefined;
    minisiteTextColor: string | undefined;
    /** @deprecated Kept for old deployed frontends. New clients use minisiteTagline + minisiteAboutText (already resolved against this legacy field). */
    minisiteCustomShortBio: string | undefined;
    // Resolved server-side: falls back to a short legacy minisiteCustomShortBio when the user never set a tagline.
    minisiteTagline: string | undefined;
    // Resolved server-side: falls back to a long legacy minisiteCustomShortBio. Markdown supported.
    minisiteAboutText: string | undefined;
    minisiteInstagramUrl: string | undefined;
    minisiteFacebookUrl: string | undefined;
    minisiteAddress: string | undefined;
    minisiteHeroPattern: MinisiteHeroPattern | undefined;
    minisiteHeroImageUrl: string | undefined;
    /**
     * Whether this realtor's minisites render the watermark-free versions of listing photos, from
     * `User.watermarkTermsAcceptedAt` — the ONE gate (2026-09-30). The public minisite already
     * blocks on this response, so the decision arrives in the same payload as everything else it
     * needs, which is what lets the image swap and the hidden-photo list be resolved from a single
     * snapshot rather than two.
     */
    watermarkTermsAccepted: boolean;
    /**
     * @deprecated No longer written — permanently `undefined`. Kept as a key for ONE release so an
     * older cached SPA bundle, which reads it to decide the same thing, keeps rendering rather than
     * flipping old minisites back to watermarked photos. Remove in the release after.
     */
    minisiteShowWatermarkFreeImages: boolean | undefined;
  };

  // --- Google Calendar (mounted at /api/v1/google-calendar) ---------------

  /** POST /connect — body. `returnPath` is where the popup/redirect lands afterwards. */
  export type GoogleCalendarConnectRequest = {
    returnPath?: string;
  };

  export type GoogleCalendarConnectResponse = {
    authUrl: string;
  };

  /** GET /status, POST /disconnect, POST /sync-now, POST /settings all return this. */
  export type GoogleCalendarStatusResponse = {
    connected: boolean;
    status: GoogleCalendarConnectionStatus;
    googleEmail?: string;
    calendarId?: string;
    calendarSummary?: string;
    timeZone?: string;
    direction?: GoogleCalendarSyncDirection;
    syncTasks?: boolean;
    autoDetectAttendees?: boolean;
    connectedAt?: string;
    lastSyncedAt?: string;
    lastError?: string;
    /** Only present right after a manual sync. */
    lastSyncResult?: GoogleCalendarSyncResult;
  };

  export type GoogleCalendarSyncResult = {
    pulledFromGoogle: number;
    pushedToGoogle: number;
    deletedLocally: number;
    deletedInGoogle: number;
    notificationsSent: number;
    errors: number;
  };

  export type GoogleCalendarListEntry = {
    id: string;
    summary: string;
    primary: boolean;
    timeZone?: string;
    backgroundColor?: string;
    accessRole?: string;
  };

  export type GoogleCalendarListResponse = {
    calendars: GoogleCalendarListEntry[];
  };

  /** POST /settings — every field optional; only the provided ones change. */
  export type GoogleCalendarUpdateSettingsRequest = {
    calendarId?: string;
    direction?: GoogleCalendarSyncDirection;
    syncTasks?: boolean;
    autoDetectAttendees?: boolean;
  };

  export type orgAdminStatsRequestSchema = {
    dateFrom: string;
    organizationId?: string;
  };

  export type _orgAdminStatsResponse_callsDataSchema = {
    phoneNumber: string;
    sumAnswered: number;
    sumNotAnswered: number;
    totalCalls: number;
    newLeads: number;
  };

  export type _orgAdminStatsResponse_userDataSchema = {
    userMail: string;
    userFullName: string;
    userPhotoURL: string;
    callsData: _orgAdminStatsResponse_callsDataSchema[];
    virtualNumbersCount: number;
    sumAnswered: number;
    sumNotAnswered: number;
    totalCalls: number;
    newLeadsFromVirtualNumbers: number;
    totalContactsCount: number;
    newLeadsFromWhatsappBots: number;
    calendarMeetingsCount: number;
    bulkMessagesSentCount: number;
  };

  export type orgAdminStatsResponseSchema = {
    userData: (_orgAdminStatsResponse_userDataSchema | null)[];
    teamMembersCount: number;
    totalWhatsappBotsCount: number;
    totalVirtualNumbersCount: number;
    totalNewLeadsFromVirtualNumbers: number;
    totalNewLeadsFromWhatsappBots: number;
    totalBulkMessagesSentCount: number;
    totalCalendarMeetingsCount: number;
    totalNotAnsweredCalls: number;
    totalCalls: number;
  };

  export type MarkNoSpamContactsResponseSchema = {
    allContactsCount: number;
    markedSpamContacts: string[];
    skippedContactsCount: number;
    markedSpamContactsCount: number;
    unmarkedSpamContactsCount: number;
  };

  export namespace Calls {
    /**
     * GET /api/v1/calls/recording/download — query params identifying the call.
     * Sent with an Authorization (Firebase ID token) header. The response is the
     * audio file itself (Content-Disposition: attachment), not JSON.
     */
    export type DownloadRecordingRequest = {
      virtualNumber: string;
      callId: string;
    };
  }

  export namespace VirtualNumbers {
    /**
     * The customer-facing view of a virtual number's settings
     * (`payCall-settings/<number>`). Deliberately a subset of `PayCallSettings`:
     * internal fields (`notesAboutCustomer`, `ownerMails`) never leave the server,
     * and provisioning fields (`number`, `provider`, `hasAiEnabled`) are read-only.
     */
    export type VirtualNumberSettings = {
      /** Local virtual number — the Firestore doc id. Read-only. */
      number: string;
      /** Free-text label naming the lead source this number is used for. */
      tag?: string;
      /** Name the no-answer WhatsApp bot introduces the realtor with. */
      customerIntroName?: string;
      /** Short/first name used in no-answer bot copy. */
      customerUserName?: string;
      /** Agency name shown to callers. */
      customerOrganizationName?: string;
      /** Call-to-action line of the no-answer WhatsApp message. */
      noPhoneReplyCallToAction?: string;
      /** Local number the no-answer bot tells the caller to WhatsApp instead. */
      noResponseBotWhatsappForwardingNumber?: string;
      /** Read-only — AI call transcription is provisioned by Webot, not self-serve. */
      hasAiEnabled: boolean;
      /** Read-only — telephony provider backing this number. */
      provider?: VirtualNumberProvider;
      /** Mail of the number's owner (first entry of `ownerMails`). */
      ownerMail?: string;
      /** Display name of the owner, resolved from their user doc. */
      ownerFullName?: string;
      /** True when the number belongs to the requesting user (vs. a teammate's). */
      isOwn: boolean;
      /** False when the settings doc doesn't exist yet (nothing to edit until it does). */
      exists: boolean;
    };

    /** GET /api/v1/virtual-numbers */
    export type ListVirtualNumbersResponse = {
      virtualNumbers: VirtualNumberSettings[];
    };

    /**
     * PATCH /api/v1/virtual-numbers/:number — every field optional; only the keys
     * present in the body are written. `null`/`''` clears a field.
     */
    export type UpdateVirtualNumberRequest = {
      tag?: string;
      customerIntroName?: string;
      customerUserName?: string;
      customerOrganizationName?: string;
      noPhoneReplyCallToAction?: string;
      noResponseBotWhatsappForwardingNumber?: string;
    };

    export type UpdateVirtualNumberResponse = {
      virtualNumber: VirtualNumberSettings;
    };
  }

  export namespace Webhooks {
    export type updateContactSchema = Partial<RealEstateContact> & {
      phoneNumberInternational: string;
      email?: string;
      fullName?: string;

      // customData is for webhooks from gohighlevel
      customData?: Partial<RealEstateContact> & {
        phoneNumberInternational: string;
        email?: string;
        fullName?: string;
      };
    };

    export const webhookTypes = [
      'paycall-merkazyia-webhooks',
      'update-contact',
      'on-new-webot-lead',
      'on-new-wooki-lead',
      'publish-webot-lead-to-kala-crm',
      'new-sale-event-from-cardcom'
    ] as const;
    export type WebhookType = typeof webhookTypes[number];
  }

  export type RealtorsWithContactsForListing = {
    realtorName?: string;
    realtorMail: string;
    realtorPhoneNumberInternational?: string;
    realtorAvatar?: string;
    /**
     * How much of THIS colleague's contacts the caller may see, per the agency's CRM
     * permissions on the org they share (`crmPermissions[email] ?? DEFAULTS[role]`):
     * `full` — the caller may browse the agency book (`browse`), so each contact carries its
     * id, phone and name exactly as the /crm agency view shows it; `redacted` — identity is
     * withheld (no id, no name). Absent on responses from before this field existed: redacted.
     */
    contactAccess?: "full" | "redacted";
    /** The shared org that grants `contactAccess` — pass it as `orgId` on agency-scoped writes. */
    sharedOrgId?: string;
    partialContactDetails: RealEstateContactWithConfidence[];
  };

  export type MatchConfidence = {
    confidencePercent: number; // 0-100
    /**
     * A WIDE match: something the customer explicitly asked for is contradicted by the listing
     * (`matchReasons` carries the `mismatch`). Returned so the realtor can opt into seeing it,
     * never shown by default, and never counted in the new-listing alert or the `listing-matches`
     * cache. Absent/false = a normal match.
     */
    isLooseMatch?: boolean;
    // Optional only because a card can be built from a cache doc that predates tiers
    // (or, for reasons, from any cache doc). The live matcher always sets both.
    matchTier?: ListingMatchTier;
    matchReasons?: ListingMatchReason[];
  };
  /**
   * WHOSE BOOK a matched contact is in (plan D4) — the door's stamp on every candidate: the owner's
   * email for a personal book, `org:<orgId>` for a pooled office's one record, which the dashboard
   * then opens, edits and records sends against as the office's. `holderIds` is a pooled record's
   * list (lowercased emails): absent on a personal row and on a redacted one. Both absent on a
   * payload from an older server, or a snapshot stored before 2026-10-22: read the book as `realtorId`.
   */
  export type MatchedContactBookStamp = { bookKey?: string; holderIds?: readonly string[] };
  export type RealEstateContactWithConfidence = Partial<RealEstateContact> &
    MatchConfidence &
    MatchedContactBookStamp & { realtorId: string };

  export type RelevantContactsForListingResponse = {
    fromMyContacts: RealEstateContactWithConfidence[];
    fromOrgContacts: RealtorsWithContactsForListing[];
    /**
     * Whether this caller may see COLLEAGUES' matched buyers at all, from the agency CRM
     * permissions alone — independent of whether this listing happens to have any
     * (`listings/listingMatchOrgAccess.decideOrgMatchesAccess`).
     *
     * `hidden` means there is no office tab for this person: no agency, no colleague in it, or
     * every shared agency set their `pool` to `none`. An empty `fromOrgContacts` cannot say
     * that on its own, and the dashboard used to guess from "is in an agency" — which offered a
     * revoked member a "של המשרד (0)" tab whose empty state claimed the office HAD been searched
     * for them. Absent on responses from before this field existed.
     */
    orgAccess?: "full" | "redacted" | "hidden";
  };

  /**
   * `POST /getBuyersSuggestionsForListingAndRealtor/:listingId/last` — the LAST COMPUTED result for
   * (caller, listing), read from the server-only `listing-match-results` snapshot, so a reopened
   * listing shows its reasons and office rows at once while the live compute runs.
   * `snapshot: null` when there is none, it predates the current match rules, or the listing's
   * matching fields changed since. Colleague access is re-applied from CURRENT permissions on every
   * read. Own rows are a PROJECTION of the contact — never feed them to a bulk send; wait for the
   * live response.
   */
  export type LastRelevantContactsForListingResponse = {
    snapshot: RelevantContactsForListingResponse | null;
    /** ISO time the snapshot was computed. */
    computedAt?: string;
    /** The snapshot hit the size cap and holds only the first rows of a very large result. */
    truncated?: boolean;
  };

  // Homely import response schema
  export type HomelyImportResponse = {
    contacts: RealEstateContact[];
    message: string;
    success: boolean;
    unvalidatedLocations?: string[]; // Array of locations that could not be validated
    validatedLocations?: ProcessedListingAddress[]; // Array of locations that were successfully validated
    warnings?: string[]; // Array of warnings about the import process
  };

  // Contact search request schema
  export type ContactSearchRequestSchema = {
    searchTerm: string;
  };

  /**
   * Search result type for aggregated contact data
   */
  export interface ContactSearchResult {
    phoneNumberInternational: string;
    displayName: string;
    hitsAcrossUsersInOrgs: number;
    contactObjects: (GenericContact & { user: REST_schema.MiniUser })[];
  }

  // Contact search response schema
  export type ContactSearchResponseSchema = {
    results: Array<ContactSearchResult>;
    totalResults: number;
    searchTerm: string;
  };

  // Contact details data sources
  export const contactDetailsDataSources = [
    'contacts',
    'calls',
    'sentMessages',
    'calendarEvents',
    'favoriteListings'
  ] as const;
  export type ContactDetailsDataSource = typeof contactDetailsDataSources[number];

  // Contact details request schema
  export type ContactDetailsRequestSchema = {
    phoneNumberInternational: string;
    dataSources?: ContactDetailsDataSource[];
  };

  // Timeline action types
  //
  // `assignment` is a מתווך מטפל designation change, read from `crm_contact_audit`
  // (`action = 'assign'`) — the first thing in the product to write one of that ledger's
  // declared actions. Unlike the client-synthesised `contact-created` row, it IS an audit
  // record; see `docs/rules/crm-contacts-postgres.md`.
  export type ContactTimelineActionType =
    | 'message-sent'
    | 'call'
    | 'meeting'
    | 'task'
    | 'favorite-listings-minisite-creation'
    | 'note'
    | 'signature'
    | 'assignment';

  // Contact timeline action
  export type ContactTimelineAction = {
    actionType: ContactTimelineActionType;
    actionDescription: string;
    actionTime?: Date;
    actionUser: MiniUser;
  };

  // Contact object across users
  export type ContactObjectAcrossUsers = {
    user: MiniUser;
    contact: RealEstateContact;
    callsCount: number;
    messagesSentCount: number;
    meetingsCount: number;
    tasksCount: number;
    lastMeetingTime: Date | null;
  };

  // Contact details response schema
  export type ContactDetailsResponseSchema = Array<{
    phoneNumberInternational: string;
    displayName: string;
    timeline: ContactTimelineAction[];
    contactObjectsAcrossUsers: ContactObjectAcrossUsers[];
  }>;

  // --- Contact phone change (re-key) --------------------------------------------------------
  // POST /api/v1/contacts/change-phone — OWN BOOK ONLY in v1 (no ownerUserId).
  //
  // A contact's Firestore doc id IS its international phone, so changing the phone is a
  // RE-KEY: copy the document to the new id, port every satellite that references the old
  // id, and hard-delete the old document LAST (so a mid-way crash is re-runnable, never
  // lossy). Refused with 409 when ANY document — live OR soft-deleted — already sits at
  // the target phone; merging two histories is a separate future feature.

  export type ChangeContactPhoneRequest = {
    /** The contact's CURRENT doc id — its international (E.164) phone number. */
    contactId: string;
    /** The new phone, in any parseable format; normalized server-side to international. */
    newPhone: string;
  };

  /** How many documents each satellite port rewrote / re-keyed / deleted. */
  export type ChangeContactPhonePortedSummary = {
    contactDocuments: number;
    calendarEvents: number;
    signatureDocuments: number;
    userNotes: number;
    sharedContacts: number;
    favoriteListings: number;
    listingPageViews: number;
    callSummaries: number;
    facebookLeads: number;
    /**
     * NOT ported — always 0. The `sentMessages` onWrite CF re-derives `contactId` from
     * `sentTo` on the port write itself (a guaranteed self-revert), so the step was dropped.
     * The dashboard's messages tab matches by `sentTo` variants and is unaffected.
     */
    sentMessages: number;
    whatsappSuppressions: number;
    /** `listing-matches` cache docs referencing the old id are DELETED (rebuildable cache). */
    listingMatchesDeleted: number;
  };

  export type ChangeContactPhoneResponse = {
    oldContactId: string;
    newContactId: string;
    portedSummary: ChangeContactPhonePortedSummary;
    mirrorSynced: boolean;
  };

  // --- CRM row enrichments: matches counts + seller listing links ---------------------------
  // Both POST endpoints follow the /contacts/holders contract: seeded ONLY by rows the
  // caller was already served (ids in, enrichment out — never a filter, which would be an
  // inference oracle over the agency book), scope resolved server-side from
  // `dashboard-orgs`, and refusal DEGRADES to a benign `enabled: false` answer for exactly
  // the scope errors holders catches — never a 403 the client would report on every page.

  // --- מתווך מטפל: the handling realtor an agency designated for a person -------------------
  //
  // POST /api/v1/contacts/assignments. Same ids-in / enrichment-out contract as the three
  // above, with ONE deliberate difference that is the whole reason it is a separate endpoint:
  //
  // ⚠️ IT IS AUTHORIZED ON ORG MEMBERSHIP ALONE — NOT on `browse`, NOT on `pool`.
  //
  // `/contacts/holders` resolves scope through `resolveCrmAccess({view:'org'})`, which
  // refuses a member without `browse` — and `browse` is FALSE in `DEFAULT_PERMISSIONS.member`.
  // Folding the designation into that response would show it to admins and to nobody else,
  // i.e. to everyone except the realtors it exists to stop colliding. The two are different
  // facts: "three colleagues also hold this person" is the agency's BOOK, which `browse`
  // governs; "the agency designated Dana to handle this person" is a coordination decision an
  // admin published TO the agency, and it names a colleague whose name and photo
  // `GET /api/v1/crm/org/directory` already serves to every member with no permission field
  // of any kind. Do not "unify" this into holders.
  //
  // A designation GRANTS NOTHING — it never widens `visibleOwnerIds`, and the assignee's
  // access comes from owning a copy. See M008 in `server/db/crmMigrations.ts`.

  export type CrmContactAssignmentsRequest = {
    /** Contact ids (E.164 phones) from a page already served to this caller. Capped at 200. */
    contactIds: string[];
    /** Narrows a multi-agency caller to one org. Omitted = every agency the caller is in. */
    orgId?: string;
  };

  /** One agency's designation for one person. */
  export type CrmContactAssignment = {
    orgId: string;
    orgName: string | null;
    /** Lowercased email. The client resolves the name and face from the org directory. */
    assigneeUserId: string;
    assignedAt: string;
    /**
     * Who made the designation. ADMINS ONLY — who handles the lead is published to the whole
     * agency on purpose; who made the management decision is management information.
     */
    assignedBy?: string;
    /**
     * The assignee is no longer in `users ∪ admins` of that org.
     *
     * DERIVED per request from the roster this endpoint already read — never stored, and
     * never repaired by a job: a cron seeding anything from `dashboard-orgs` is exactly what
     * `docs/rules/crm-contacts-postgres.md` forbids. A stale row grants nothing, so it is
     * inert; the UI renders it as "מטפל קודם" and an admin re-designates.
     */
    stale: boolean;
  };

  export type CrmContactAssignmentsResponse = {
    /** False = the caller is in no agency (or asked about nothing). Render nothing, not an error. */
    enabled: boolean;
    /**
     * contactId → the designations that apply to it, one per agency the caller belongs to.
     *
     * An ARRAY because a caller can be in several agencies and the same person can be
     * designated differently in each; each entry names its own `orgId`. A contact with no
     * designation is ABSENT from the map — and the client must keep "absent" (nobody is
     * designated) distinct from "the request has not answered", or a pending lookup renders
     * as an empty seat that is actually taken.
     */
    assignments: Record<string, CrmContactAssignment[]>;
  };

  /**
   * POST /api/v1/crm/org/contacts/assign — set or clear the designation.
   *
   * On the ORG router, not `/contacts`: the gate is "are you an admin of the org you named"
   * (plus the outgoing handler, who may hand off), which is not a contacts read scope. It is
   * the one sanctioned path that CREATES a contact document under another user's email, and
   * it earns that by verifying the target is a current member and using `.create()`.
   */
  export type CrmAssignContactRequest = {
    orgId?: string;
    contactId: string;
    /** `null` REMOVES the designation — the row is deleted, never nulled. */
    assigneeUserId: string | null;
    /** Which holder's record to duplicate, when the assignee holds none. */
    copyFromOwnerUserId?: string | null;
  };

  /** What actually happened to the assignee's book. */
  export type CrmAssignContactCopyStatus =
    /** No assignee (a clear), so there was nothing to copy. */
    | 'not_needed'
    /** The assignee already had a live copy — including the case where a concurrent create won. */
    | 'already_held'
    /** The assignee has no copy and the caller named no source. The designation still landed. */
    | 'no_source_chosen'
    /** A copy was created from `fromOwnerUserId`. */
    | 'create'
    /**
     * A POOLED agency (plan D4): there is no copy to make — the office holds one record per
     * person — so the assignee was put on its holder list ("my contacts") instead, in the same
     * transaction as the designation. `already_held` when they were on it already.
     */
    | 'held';

  export type CrmAssignContactResponse = {
    orgId: string;
    contactId: string;
    assigneeUserId: string | null;
    previousAssigneeUserId: string | null;
    copy: { status: CrmAssignContactCopyStatus; fromOwnerUserId?: string };
    /** The new copy reached the Postgres mirror inline; false = the drain will catch it. */
    mirrorSynced: boolean;
  };

  /**
   * POST /api/v1/crm/org/contacts/assign-bulk — the same designation, over a selection.
   *
   * ONE assignee for MANY contacts; every other rule is the single endpoint's, applied per
   * contact by the same core (authority, current membership, `.create()` copy). The batch
   * never fails as a whole once the org resolved: each contact reports its own outcome, so
   * one soft-deleted copy in the assignee's book cannot block the other 199. Re-sending the
   * identical request is the retry — the landed ones report `noop`, the failed ones try again.
   *
   * Capped at 200 (`MAX_BULK_ASSIGN_TARGETS`) and requires an ENUMERATED list, never a
   * filter: every contact may name its own copy source, which a filter cannot express.
   */
  export type CrmAssignContactsBulkRequest = {
    orgId?: string;
    /** `null` REMOVES the designation from every listed contact. */
    assigneeUserId: string | null;
    /** Deduplicated by `contactId` server-side (first occurrence wins). */
    contacts: {
      contactId: string;
      /** Which holder's record to duplicate for THIS contact, when the assignee holds none. */
      copyFromOwnerUserId?: string | null;
    }[];
    /**
     * A MOVE rather than a copy: once the assignee holds the contact, PURGE the record it was
     * copied from. ORG-ADMIN ONLY — the server refuses it (403) for a caller who is not in the
     * org's `admins[]`; the hand-off authority a current handler has does not extend to it.
     *
     * Refused (400) unless `assigneeUserId` is set AND EVERY contact names a
     * `copyFromOwnerUserId`: "delete without copying" is just a delete wearing a different
     * button, and the request shape makes that unsayable rather than merely discouraged. A
     * source that IS the assignee is kept (it is their only copy), and a source whose copy
     * did not land is kept — the purge only ever follows a copy that exists.
     */
    deleteSourceAfterCopy?: boolean;
  };

  /**
   * `assigned` / `unassigned` — the row changed. `noop` — the requested state already held
   * (a retry, or a person already designated to this realtor). `failed` — this contact's
   * own refusal or error, with `error` carrying the sentence the single endpoint would have
   * returned as its 4xx message.
   */
  export type CrmAssignContactsBulkItemStatus = 'assigned' | 'unassigned' | 'noop' | 'failed';

  /**
   * What happened to the SOURCE record when `deleteSourceAfterCopy` was set.
   *
   * `deleted` — purged (archived first, then removed, exactly as `/contacts/delete` does).
   * `kept` — deliberately not deleted: the source is the assignee's own record, or there was
   *          no document at that address to delete.
   * `delete_failed` — the copy landed but the purge did not; `error` says why. The contact now
   *          exists in BOTH books, which re-sending the same request converges.
   */
  export type CrmAssignContactsBulkSourceStatus = 'deleted' | 'kept' | 'delete_failed';

  export type CrmAssignContactsBulkResult = {
    contactId: string;
    status: CrmAssignContactsBulkItemStatus;
    /** Absent on `failed`. */
    copy?: CrmAssignContactCopyStatus;
    /** Present only when the request set `deleteSourceAfterCopy` and this item did not fail. */
    sourceRecord?: CrmAssignContactsBulkSourceStatus;
    error?: string;
  };

  export type CrmAssignContactsBulkResponse = {
    orgId: string;
    assigneeUserId: string | null;
    /** In REQUEST order, one entry per deduplicated contact. */
    results: CrmAssignContactsBulkResult[];
    /**
     * `copied` counts the `create` copies among the successes; `assigned` + `unassigned` +
     * `noop` + `failed` partition `results`. `sourceDeleted` / `sourceDeleteFailed` count the
     * `sourceRecord` outcomes and are 0 unless `deleteSourceAfterCopy` was set.
     */
    summary: { assigned: number; unassigned: number; noop: number; copied: number; failed: number; sourceDeleted: number; sourceDeleteFailed: number };
  };

  // ---------------------------------------------------------------- Office tasks
  //
  // A task one office member puts in a colleague's calendar. It is written into the ASSIGNEE's
  // own calendar and is theirs from then on; the assigner keeps a handle on it through
  // `CalendarEvent.assignedByUserId` and these routes. See docs/rules/calendar-task-assignment.md.

  /** The task form's fields — nothing server-owned. Dates travel as ISO strings. */
  export type CalendarTaskFields = {
    title: string;
    description?: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    status?: CalendarEventStatus;
    attendees?: CalendarEventAttendee[];
    listingIds?: string[];
    listingsData?: ProcessedListingData[];
    textColor?: string;
    backgroundColor?: string;
  };

  /** POST /api/v1/calendar-tasks/assign */
  export type CalendarTaskAssignRequest = {
    /** Whose calendar the task goes into: the caller, or someone who shares an office with them. */
    assigneeUserId: string;
    task: CalendarTaskFields;
    /**
     * Hand an EXISTING task over instead of creating one: the copy is written to the assignee and
     * this one deleted, in one transaction. The caller must own it, or be the colleague who
     * assigned it.
     */
    source?: { ownerUserId: string; taskId: string };
  };

  export type CalendarTaskAssignResponse = {
    task: CalendarEvent;
    /**
     * The WhatsApp to the assignee. `skipped`: none is due (their own task, an inactive account,
     * the template not approved yet) — never an error. `pending`: still being sent when we answered.
     */
    notification: 'sent' | 'skipped' | 'pending';
  };

  /** POST /api/v1/calendar-tasks/update — the colleague who assigned a task edits it. A field present is a field set. */
  export type CalendarTaskUpdateRequest = {
    ownerUserId: string;
    taskId: string;
    task: Partial<CalendarTaskFields>;
  };

  export type CalendarTaskUpdateResponse = { task: CalendarEvent };

  /** POST /api/v1/calendar-tasks/cancel — the colleague who assigned a task removes it. */
  export type CalendarTaskCancelRequest = { ownerUserId: string; taskId: string };

  /** POST /api/v1/contacts/matches-counts — each served row's minisite matches count. */
  export type CrmMatchesCountsRequest = {
    view?: 'mine' | 'org';
    orgId?: string;
    /**
     * The page's rows. `ownerUserId` may be omitted on the "mine" tab (defaults to the
     * caller); a pair naming an owner outside the caller's resolved scope is silently
     * dropped — it simply gets no count. Capped server-side at 200 (one page).
     */
    contacts: { ownerUserId?: string; contactId: string }[];
  };

  export type CrmMatchesCountsResponse = {
    /** False = no answer (scope refused, or nothing asked). Render nothing, not an error. */
    enabled: boolean;
    /**
     * KEY = `JSON.stringify([ownerUserId, contactId])` — compute it exactly that way on
     * the client. The owner is part of the key because in the org view the same person is
     * held by several agents, and a bare contactId would render one agent's count on a
     * colleague's row. JSON quoting (rather than a `__`/`|` join) is what makes the key
     * collision-proof for ANY owner/contact content. Every in-scope requested pair is
     * present — 0 means the contact has no minisite matches, absent means out of scope.
     */
    counts: Record<string, number>;
  };

  /** POST /api/v1/contacts/listing-links — lean summaries of the rows' linked seller listings. */
  export type CrmListingLinksRequest = {
    view?: 'mine' | 'org';
    orgId?: string;
    /**
     * The served rows' `sellingListingIds` / `rentingOutListingIds`, per row. Listing ids
     * are deduplicated across the page and capped server-side at 500.
     */
    links: { contactId: string; listingIds: string[] }[];
  };

  /** What the seller row's linked-listing card renders. Display-only, deliberately lean. */
  export type CrmListingLinkSummary = {
    id: string;
    /** Composed "street number, city" from the parts that exist; '' when none do. */
    address: string;
    price: number | null;
    /** `real-estate-listings` status (onSale/sold/…); null for a market (mirror-only) listing. */
    status: string | null;
    /** Exclusivity END date as epoch ms; null = none recorded (always null for mirror docs). */
    exclusivity: number | null;
    coverPhotoUrl: string | null;
    listedFor: 'sale' | 'rent' | null;
  };

  export type CrmListingLinksResponse = {
    /** False = no answer (scope refused, or nothing asked). Render nothing, not an error. */
    enabled: boolean;
    /** Keyed by listing id. An id absent here appears in `missingIds` instead. */
    listings: Record<string, CrmListingLinkSummary>;
    /**
     * Ids that resolved to nothing the caller may see: deleted listings, junk ids, and
     * listings owned entirely outside the caller's scope. The client renders its empty
     * linked-listing state for these rather than a broken card.
     */
    missingIds: string[];
  };

  // --- POST /api/v1/contacts/office-minisites — what each colleague sent this customer ------
  //
  // Every realtor keeps their OWN minisite for a customer (`favorite-listings-of-contact`, one
  // per (realtor, phone)); an office asked to see the others' — which listings each agent sent
  // and which the customer opened — so that one agent does not send what another already did.
  // Keyed by the PHONE, never by a book, so it answers the same in an office that keeps personal
  // books and in a pooled one (docs/crm/office-pool-design.md §7). Read only.

  export type CrmOfficeMinisitesRequest = {
    /** The customer's phone — the contact id (E.164); older pages filed under a local spelling are found too. */
    contactId: string;
  };

  /** One listing on a colleague's minisite, and what the customer did with it there. */
  export type CrmOfficeMinisiteListing = {
    listingId: string;
    /** Times the customer opened this listing on that page (`listing-page-views.listingDetailViewCounts`). */
    viewCount: number;
    /** When the customer last opened it, epoch ms; null when never, or not recorded. */
    lastViewedAt: number | null;
    /** The customer marked it ♥ / "לא רלוונטי" on the page. */
    liked: boolean;
    disliked: boolean;
    /** The realtor marked it visited with the customer. */
    visited: boolean;
  };

  /**
   * One colleague's minisite for this customer — the page the share flow sends (`pickMinisite`).
   * Never the realtor's private per-listing notes (`additionalDetails`): they are not read at all.
   *
   * `access` says how much the caller is shown: `full` — an admin of the office, or a member allowed
   * to look through the office's customers — gets the page; everybody else in the office gets
   * `count`: that the page exists and how many listings are on it, and nothing more (`minisiteId`
   * null, `listings` empty, no activity, no dates).
   */
  export type CrmOfficeMinisite = {
    /** The colleague's e-mail (lowercased) — the join key with the org directory. */
    realtorId: string;
    /** `User.ownerFullName`; null when unresolved (the caller owns the fallback). */
    realtorName: string | null;
    realtorAvatarUrl: string | null;
    access: 'full' | 'count';
    /** Listings on the page — at both levels. */
    listingCount: number;
    /** The page's id: the public link is `/myListings/{minisiteId}`. Null at `count`. */
    minisiteId: string | null;
    /** Newest-added first. Empty at `count`. */
    listings: CrmOfficeMinisiteListing[];
    /** Opens of the whole page by the customer. */
    totalPageViews: number;
    lastPageViewAt: number | null;
    createdAt: number | null;
    updatedAt: number | null;
  };

  export type CrmOfficeMinisitesResponse = {
    /** False = the caller is in no office: render nothing, not an error. */
    enabled: boolean;
    /** Colleagues' minisites — the ones the caller may open first, most recently updated first. Never the caller's own. */
    minisites: CrmOfficeMinisite[];
  };

  // --- Contact document reads: POST /api/v1/contacts/get and /get-many -------------------
  //
  // The first two verbs of the ONE DOOR for reading contacts (`contactsRead.service.ts`;
  // docs/crm/contacts-postgres-authority-plan.md, D0 and D2). A row from `/contacts/list` is
  // a projection; a card, a panel and an edit form need the DOCUMENT, and they get it here
  // together with the mirror's `version` — the number the contact's signal document carries.

  /**
   * One contact. Either `contactId` (the exact document id a card already holds) or
   * `candidates` (every spelling a phone could have been filed under, from
   * `getPhoneCandidates`, in order of preference — at most 8; the first that exists wins);
   * both at once is refused. `ownerUserId` defaults to the caller and is intersected with
   * the resolved scope, never trusted; `view` / `orgId` resolve the scope exactly as
   * `/contacts/list` does.
   */
  export type CrmContactGetRequest = {
    contactId?: string;
    candidates?: string[];
    ownerUserId?: string;
    view?: 'mine' | 'org';
    orgId?: string;
  };

  export type CrmContactGetResponse = {
    /** False is an ANSWER (200, `doc: null`): a phone in nobody's book is a stranger's call. */
    found: boolean;
    ownerUserId: string;
    /**
     * The BOOK the document came from — `owner_user_id` on the mirror: the owner's email for
     * a personal book, `org:<orgId>` for an agency's pooled book (plan D4). Equal to
     * `ownerUserId` today. Stamped on everything the door returns, so no caller derives
     * "mine" from a path or an owner again.
     */
    bookKey: string;
    /** The members holding this person in a pooled book; `[]` for a personal book (always, today). */
    holderIds: string[];
    /** The document id that was found — one of the candidates — or null. */
    contactId: string | null;
    doc: RealEstateContact | null;
    /**
     * `crm_contact.version` for this contact; 0 while the mirror has no row (or no column)
     * for it. Compare against the signal document's `v`: a signal above this number means
     * the document on screen is stale and should be fetched again.
     */
    version: number;
    /**
     * True when the scope blurs the free text (`seeNotes` false; the names too when
     * `seeName` is). Without `seeNotes` the document is PROJECTED onto the list
     * projection's fields (an allowlist — the national ID, the email, the address, the
     * extra phones and the bulk-message text are not served to a basic-pool colleague)
     * and the four blurred fields carry the list's placeholder. A fact about the SCOPE,
     * not the document: true even when the document had nothing to hide. A colleague's
     * document at `pool: 'none'` is refused (403), never redacted.
     */
    redacted: boolean;
  };

  /**
   * Several contacts of ONE book by id — the feed's catch-up and a bulk-selected panel. At
   * most 5,000 ids (the presence cap); above it the request is REFUSED, never trimmed,
   * because a caller reads a missing id as "not in the book". `view: 'org'` (P2) is
   * accepted under the EXPORT endpoint's gates — the agency's `export` permission and its
   * `maxExportRows` against the id count — since 5,000 full documents of a colleague's book
   * is exactly what an export is; `ownerUserId` picks the book inside the scope and the
   * documents are redacted per scope like `/contacts/get`.
   */
  export type CrmContactGetManyRequest = {
    contactIds: string[];
    ownerUserId?: string;
    view?: 'mine' | 'org';
    orgId?: string;
  };

  export type CrmContactGetManyResponse = {
    contacts: Array<{ contactId: string; bookKey: string; holderIds: string[]; doc: RealEstateContact; version: number }>;
    /**
     * Asked about and not found — an answer, not an error. An entry that cannot be a
     * document id (not a string, empty, or not a valid path segment) is DROPPED before the
     * read, as the lookup drops it, and appears in neither list.
     */
    missing: string[];
  };

  /**
   * GET /api/v1/contacts/book — which book the signed-in user's contacts live in (plan D4).
   *
   * `bookKey` is the mirror's `owner_user_id` for the caller's own contacts: their email for a
   * personal book, `org:<orgId>` for a pooled agency's. `mode` is the agency's `crmBookMode`
   * (`'personal'` for a solo realtor or a personal-book agency). Answered from the server's
   * in-process registry — zero I/O — so a client may ask on every load. A pooled mode requires
   * the dashboard's `CONTACT_DOCUMENT_SOURCE = 'server'`: a pooled record has no Firestore
   * document for the `'firestore'` listener to find.
   *
   * WHO IS POOLED: a member on the office's roster whose personal book the office has MERGED
   * (the flip, or the member sync for somebody who joined later). Somebody on the roster who is
   * not merged yet keeps `bookKey` = their own email and `mode: 'personal'` — their contacts are
   * where they always were, and the office tab shows them the office's book. The one mixed
   * answer: while the office's switch RUNS (`migrating` / `reversing`) every roster member is
   * told that mode with the office's `orgId`, merged or not, because every one of them has
   * their writes refused — so `bookKey` may still be the caller's own email beside a busy mode.
   * Key readers by `bookKey`, and withhold writes by `mode`; never infer one from the other.
   */
  export type CrmContactBookResponse = {
    bookKey: string;
    mode: ContactBookMode;
    orgId: string | null;
  };

  /**
   * POST /api/v1/contacts/holders/add · /remove — "my contacts" inside a POOLED agency.
   *
   * `userId` defaults to the caller (put ME on / take ME off this person's list); naming
   * somebody else needs the caller to be an ADMIN of the agency, and the target to be a
   * current member. `orgId` may name the caller's pooled agency and nothing else — a member
   * is in exactly one. Refused for a caller whose book is personal. Removing the last holder
   * leaves an unheld office record; it deletes nothing.
   */
  export type CrmContactHolderChangeRequest = {
    contactId: string;
    userId?: string;
    orgId?: string;
  };

  export type CrmContactHolderAddResponse = {
    orgId: string;
    contactId: string;
    /** Lowercased, as every other member id on the wire. */
    userId: string;
    /** False when the member was already on the list — the request is idempotent. */
    added: boolean;
    /** Every holder after the change. */
    holderIds: string[];
  };

  export type CrmContactHolderRemoveResponse = {
    orgId: string;
    contactId: string;
    userId: string;
    /** False when the member was not on the list. */
    removed: boolean;
    holderIds: string[];
  };

  // ===================== THE POOLED-BOOK SWITCH (`/api/v1/crm/org/pool*`) =====================
  //
  // An agency admin's switch into ONE record per person for the whole office (plan D4 of
  // `docs/crm/contacts-postgres-authority-plan.md`): the state, a preview with its confirm
  // token, the merge (resumable, polled through the progress call) and the reversal. Admin-only
  // on the roster of `dashboard-orgs`, and only for an agency in the server's
  // `POOL_ELIGIBLE_ORG_IDS` allowlist. A pooled mode REQUIRES the dashboard's
  // `CONTACT_DOCUMENT_SOURCE = 'server'`: a pooled record has no Firestore document for the
  // `'firestore'` listener to find, and the server cannot check which build a member runs.

  /** Why the switch is refused right now — a stable code, plain Hebrew for the screen, and who is in the way. */
  export type CrmOrgPoolRefusalCode =
    | 'not_eligible'
    | 'pooling_disabled'
    | 'already_pooled'
    | 'switch_running'
    | 'run_state_mismatch'
    | 'no_members'
    | 'member_in_other_org'
    | 'member_book_too_large'
    | 'mirror_behind'
    | 'not_pooled'
    | 'holders_not_cut_over';
  export type CrmOrgPoolRefusal = { code: CrmOrgPoolRefusalCode; message: string; offenders?: string[] };

  /** The merge run's state row (`crm_contact_merge_run`): `migrating` / `reversing` while a switch runs and refuses every writer of the book. */
  export type CrmOrgPoolRunState = 'preview' | 'migrating' | 'done' | 'reversing' | 'reversed' | 'failed';
  export type CrmOrgPoolRun = {
    state: CrmOrgPoolRunState;
    startedAt: string;
    finishedAt: string | null;
    /** The last person merged (or taken apart) — the resume point of a chunked run; opaque to the client. */
    cursor: string | null;
    /** The preview's numbers the run was confirmed on. */
    copies: number | null;
    people: number | null;
    conflicts: number | null;
    error: string | null;
  };

  /** The overlap the preview measured over the members' live copies (`probe-org-contact-overlap.ts`'s aggregate). */
  export type CrmOrgPoolCounts = {
    membersWithContacts: number;
    copies: number;
    people: number;
    dupPeople: number;
    dup2: number;
    dup3: number;
    dup4plus: number;
    statusConflicts: number;
    budgetConflicts: number;
    nameConflicts: number;
    multiNotes: number;
    /** Soft-deleted copies across the member books — merged with the rest, counted apart. */
    tombstones: number;
  };

  /** How far a switch is: the office's records that exist against the member copies still in personal books. */
  export type CrmOrgPoolProgress = {
    state: CrmOrgPoolRunState | null;
    officeRecords: number;
    memberCopies: number;
    cursor: string | null;
    error: string | null;
  };

  /** GET /api/v1/crm/org/pool?orgId= */
  export type CrmOrgPoolStateResponse = {
    orgId: string;
    orgName: string;
    mode: ContactBookMode;
    /** In the server's allowlist — the switch is offered at all. */
    eligible: boolean;
    /**
     * A TRIAL office (`POOL_TRIAL_ORG_IDS` on the server): every member is a test login of the team.
     * The card then offers the merge and the member sync from a browser that reads contact documents
     * from the server (`?contactDocs=server`) even while the build's own mode is not `'server'`.
     * Absent from a server older than 2026-10-14 — read it as false.
     */
    trial?: boolean;
    /** Everything that would stop a preview today; empty means the preview may run. */
    refusals: CrmOrgPoolRefusal[];
    members: string[];
    /**
     * Roster members with contacts the office has NOT merged: somebody added to the office after
     * the flip (or left out of a sync by a refusal that stands for them), who keeps a working
     * personal book and sees the office's through the office tab — and, rarely, a merged member
     * whose personal book holds rows again (taken off the roster and put back before a sync
     * ran). The member sync (`POST /crm/org/pool/members/sync`) merges both. Empty for a
     * personal office.
     */
    unmergedMembers: string[];
    /**
     * People who are no longer on the roster and are still on the list of some of the office's
     * records, with how many. Their names are never shown as holders anywhere; the member sync
     * takes them off the lists and detaches their book from the office. Empty for a personal
     * office.
     */
    formerHolders: CrmOrgPoolFormerHolder[];
    run: CrmOrgPoolRun | null;
    progress: CrmOrgPoolProgress;
  };

  export type CrmOrgPoolFormerHolder = { userId: string; records: number };

  export type CrmOrgPoolSyncMembersRequest = { orgId?: string };

  /** A roster member the sync left out, with the reason that stands for THEM (the flip's refusal codes, per person). */
  export type CrmOrgPoolMemberSkip = { userId: string; code: CrmOrgPoolRefusalCode; message: string };

  /**
   * POST /api/v1/crm/org/pool/members/sync — brings a POOLED office's book in line with its
   * roster: every member the office has not merged yet has their personal book folded INTO the
   * office's (a person the office already holds keeps the office's record as the base; a new
   * person becomes a new record; the member goes on the list of each), and everybody who left
   * the roster is taken off every list. Admin-only, like the rest of the switch.
   *
   * While it merges, the office is busy exactly as during the flip: every write is refused with
   * the retry message for a minute or two, so this is an off-hours button. With nobody to merge
   * it only releases the former members' lists and the office is never marked busy. A call that
   * drops mid-way leaves the run at its cursor; calling again (this route or migrate) resumes.
   */
  export type CrmOrgPoolSyncMembersResponse = {
    orgId: string;
    state: 'done';
    /** Members whose personal book this call merged into the office's. */
    joined: string[];
    /** Members it left out, and why. They stay in `unmergedMembers`. */
    skipped: CrmOrgPoolMemberSkip[];
    /** Records the office did not hold before. */
    created: number;
    /** Records the office already held that a member's copy was folded into. */
    merged: number;
    /** Personal copies archived and removed. */
    copies: number;
    conflicts: number;
    chunks: number;
    /** Former members taken off the office's lists, with how many records each was on. */
    releasedHolders: CrmOrgPoolFormerHolder[];
    durationMs: number;
    /** True when this call picked up a run an earlier call left at its cursor. */
    resumed: boolean;
  };

  export type CrmOrgPoolPreviewRequest = { orgId?: string };

  export type CrmOrgPoolMergeConflictSample = { contactId: string; field: string; values: ContactMergeConflictValue[] };

  export type CrmOrgPoolPlan = {
    /** One transaction below 150,000 copies; chunks of about `chunkCopies` copies above it, each its own transaction. */
    mode: 'single_transaction' | 'chunked';
    chunks: number;
    chunkCopies: number;
  };

  /** POST /api/v1/crm/org/pool/preview — READ-ONLY: it measures and answers, and writes nothing, the run row included. */
  export type CrmOrgPoolPreviewResponse = {
    orgId: string;
    orgName: string;
    mode: ContactBookMode;
    members: string[];
    counts: CrmOrgPoolCounts;
    /** Rows per member book, tombstones apart. */
    memberBooks: { userId: string; live: number; deleted: number }[];
    plan: CrmOrgPoolPlan;
    sampleConflicts: CrmOrgPoolMergeConflictSample[];
    refusals: CrmOrgPoolRefusal[];
    /**
     * `sha256` of the preview's numbers, the office, its roster and the Israel calendar day;
     * `null` when something refuses. Hand it back to migrate: a book that changed since the
     * preview, or a preview from yesterday, no longer matches.
     */
    confirmToken: string | null;
    validForDay: string;
  };

  export type CrmOrgPoolMigrateRequest = { orgId?: string; confirmToken?: string };

  /** POST /api/v1/crm/org/pool/migrate — runs the merge inside the request; a dropped request resumes from the cursor on the next call. */
  export type CrmOrgPoolMigrateResponse = {
    orgId: string;
    state: 'done';
    people: number;
    copies: number;
    conflicts: number;
    chunks: number;
    durationMs: number;
    /** True when this call picked up a run an earlier call left at its cursor. */
    resumed: boolean;
  };

  /** GET /api/v1/crm/org/pool/progress?orgId= */
  export type CrmOrgPoolProgressResponse = CrmOrgPoolProgress & { orgId: string; mode: ContactBookMode };

  export type CrmOrgPoolReverseRequest = { orgId?: string };

  /**
   * POST /api/v1/crm/org/pool/reverse — the office split AS IT STANDS (since 2026-10-22): every
   * live record copied to each member who has it in "my customers", every change made in the
   * shared book with it; a record on nobody's list to the admin who ran the reversal; a record
   * deleted for the office stays deleted. Until then the reversal restored the members' copies
   * from the day of the merge.
   */
  export type CrmOrgPoolReverseResponse = {
    orgId: string;
    state: 'reversed';
    /** Office records taken apart. */
    records: number;
    /** Personal copies written: one per record per member who held it (or, for a record nobody held, the admin). */
    copies: number;
    /** Records on nobody's list that went to the admin who ran the reversal. */
    toAdmin: number;
    /** Records deleted for the whole office: they stay deleted (the `unmerge` archive keeps them). */
    deleted: number;
    /**
     * Live records no copy of which landed, counted so that none is lost silently: on nobody's
     * list with no admin of the office to take them (a reversal run by the script), or every copy
     * refused (a book that is not Postgres's, a row already there). They stay in the `unmerge`
     * archive (`crm_contact_audit`). 0 on an ordinary reversal.
     */
    orphaned: number;
    chunks: number;
    durationMs: number;
  };

  /**
   * POST /api/v1/contacts/tags — the caller's tag vocabulary: every distinct tag on the live
   * contacts of their OWN book, with how many carry it, most used first and then alphabetical.
   * The tag picker's list. It replaces the `contact-tags/{email}` document a Cloud Function
   * keeps on every contact write (an aggregate that only ever grows and carries no counts);
   * that function retires at P5. Scoped exactly like `/contacts/summary`: own book only, a
   * request naming another `view` is refused. `limit` is clamped to [1, 500], never refused;
   * absent means the cap.
   */
  export type CrmContactTagsRequest = {
    limit?: number;
    view?: 'mine';
  };

  export type CrmContactTagCount = { tag: string; count: number };

  export type CrmContactTagsResponse = {
    tags: CrmContactTagCount[];
  };

  // --- Listing org activity (org-mates' notes / meetings / tasks on a listing) -------------
  // POST /api/v1/listing-org-activity/query
  // For each requested listing id: which OTHER members of the caller's organization(s)
  // have activity on it — org-shared notes, meetings and tasks — plus a short
  // newest-first timeline. The caller's own activity is deliberately excluded: the
  // indicator answers "has anyone ELSE in my agency touched this listing?".
  // Dates cross the wire as ISO strings.
  export type ListingOrgActivityRequest = {
    listingIds: string[]; // deduped + capped server-side
  };

  export type ListingOrgActivityItemType = 'note' | 'meeting' | 'task';

  export type ListingOrgActivityItem = {
    actionType: ListingOrgActivityItemType;
    actionDescription: string;
    actionTime: string; // ISO string
    actionUser: MiniUser;
  };

  export type ListingOrgActivity = {
    /** Distinct org-mates with activity on the listing, most recent first. */
    realtors: MiniUser[];
    /** Newest-first, capped per listing. */
    items: ListingOrgActivityItem[];
    /** Total items before the per-listing cap. */
    totalCount: number;
  };

  export type ListingOrgActivityResponse = {
    /** Listings with no org activity are simply absent from the map. */
    activityByListingId: { [listingId: string]: ListingOrgActivity };
  };

  // --- Listing claim / marketplace-source links ---------------------------------------------
  // /api/v1/listings/*
  //
  // A realtor working a property out of לוח נכסים takes her OWN copy of the market
  // ad (`real-estate-listings`), corrects whatever the ad got wrong, and records the
  // seller. The copy keeps a link back to every marketplace ad for the same property,
  // and the board then shows HER copy in place of the original.
  //
  // Why a copy rather than an override on the shared market document: the market doc
  // is one row read by every customer and rewritten hourly by the scraper, so a
  // per-realtor correction there is either untrustworthy (any customer can poison
  // everyone's matching) or unstable (the next scrape wins).

  /** The seller as the realtor confirmed it — NOT as it was scraped. See ListingContact. */
  export type ListingSellerInput = {
    firstName?: string;
    lastName?: string;
    /**
     * Free text. The client should pass an international (E.164) number when it has
     * one, but a realtor mid-call may type anything; the server stores what it gets.
     *
     * ⚠ Most scraped seller numbers are Yad2 VIRTUAL numbers (073/055 prefixes).
     * The client is responsible for surfacing that and letting her fix it before
     * this ever reaches the server — a mask saved here becomes a contact document
     * id that can never be reached.
     */
    phone?: string;
    /** Set when the phone already resolved to a contact in the caller's book. */
    contactId?: string;
  };

  // POST /api/v1/listings/claim
  export type ClaimListingRequest = {
    /** `yad2-listings-latest` document id — the market ad being claimed. */
    sourceListingId: string;
    /** Optional: stored as the copy's single `listingContacts` entry. */
    seller?: ListingSellerInput;
  };

  export type ClaimListingResponse = {
    /** The `real-estate-listings` id of the caller's copy. */
    listingId: string;
    /**
     * True when the caller had already claimed this ad and the existing copy was
     * returned untouched. The claim is idempotent per (caller, sourceListingId) so a
     * double-tap or a retried request can never mint a second copy.
     */
    alreadyClaimed: boolean;
    /** Filled when the neighborhood was inferred from the street — report it so she can correct it. */
    inferredNeighborhood?: string;
    /** Set instead when the street spans several neighborhoods and none dominates: ASK, never guess. */
    neighborhoodOptions?: string[];
  };

  // POST /api/v1/listings/link-source · POST /api/v1/listings/unlink-source
  export type LinkListingSourceRequest = {
    /** `real-estate-listings` id owned by the caller. */
    listingId: string;
    /** `yad2-listings-latest` id of the marketplace ad to attach/detach. */
    sourceListingId: string;
  };

  export type LinkListingSourceResponse = {
    listingId: string;
    /** The full set after the write, so the client never has to guess the merge result. */
    sourceListingIds: string[];
  };

  // POST /api/v1/listings/link-contact
  export type LinkListingContactRequest = {
    listingId: string;
    /** Contact document id (international phone). Pass null to unlink and keep the typed snapshot. */
    contactId: string | null;
    /** Which `listingContacts` entry to stamp; defaults to 0. */
    contactIndex?: number;
  };

  // POST /api/v1/listings/source-candidates
  //
  // "Which published marketplace ads are this same property?" — the search behind the
  // listing form's source picker. It RANKS; it never links anything itself.
  //
  // ## Filters decide the SET; sameness decides the ORDER
  //
  // The two axes are orthogonal on purpose, and neither may do the other's job:
  //
  //  - A CONTRADICTION IS NOT A FILTER. A candidate that disagrees about rooms, street or
  //    size still comes back — scored 0, carrying `contradictions` — because the realtor
  //    is usually looking at that very ad in another tab, and a row that silently
  //    disappeared is indistinguishable from a broken search. The client hides those rows
  //    behind a toggle and prints the reason on each one, which turns a silent deletion
  //    into an informed override.
  //  - THE SEED IS DELIBERATELY THIN: transaction type, city and a wide price window, and
  //    nothing else. Street, rooms and size are the SAMENESS signals and are never seeded
  //    as filters — narrowing on an unevenly-extracted field excludes exactly the partial
  //    ad that pairwise matching exists to fold in. That is the production failure that
  //    killed the composite-key design; see listingSameness.ts.
  //
  // Every seeded value comes back in `appliedFilters` + `seededFilterKeys`, so the UI can
  // render it as a VISIBLE, individually clearable chip rather than an invisible default.

  /**
   * The filter half of a candidate search — a subset of `ListingsSearchRequest`, handed to
   * the same query builder and therefore covered by the same per-read audit trail.
   *
   * `listingOrigins` is deliberately ABSENT, and pinning it server-side to the real
   * marketplaces is not merely a default: `resolveClaimedSourceIds` injects the caller's
   * claimed-ad exclusion list whenever `webot` is among the requested origins, so admitting
   * `webot` here would make the very ads this search exists to find silently vanish from
   * it. Do not add the key, and do not expose an origin control in the filter UI.
   *
   * Merge semantics: a key PRESENT here wins over the seeded value, and an explicit `null`
   * CLEARS a seeded key (JSON cannot otherwise distinguish "unset" from "I cleared it").
   * The client holds the whole filter set — it is what the chips render — so in practice it
   * sends all of it back on every request.
   */
  export type ListingSourceCandidateFilters = {
    listingListedFor?: ListingListedFor | null;
    searchTerm?: string | null;
    locations?: ListingsSearchLocation[] | null;
    minRooms?: number | null;
    maxRooms?: number | null;
    minPrice?: number | null;
    maxPrice?: number | null;
    minFloor?: number | null;
    maxFloor?: number | null;
    minParkingSpots?: number | null;
    maxParkingSpots?: number | null;
    /** Built area (שטח בנוי) in m², inclusive — the same bounds as `ListingsSearchRequest`. */
    minSquareMeter?: number | null;
    maxSquareMeter?: number | null;
    propertyTypes?: string[] | null;
    adTypes?: string[] | null;
    agencyNames?: string[] | null;
    sellerNames?: string[] | null;
    /** Keys validated server-side against the same amenity whitelist the listings search uses. */
    booleanFilters?: Record<string, boolean> | null;
    isAssetExclusive?: boolean | null;
    onlyPriceChanged?: boolean | null;
    hidePotentiallyRemovedListings?: boolean | null;
    publishedFromMs?: number | null;
    publishedToMs?: number | null;
  };

  /**
   * `sameness` (the default) orders the page by `scoreListingSameness` and ignores
   * `direction`. Any other field is passed to the listings query as a global sort, and the
   * sameness score is STILL reported on every row — picking price or date keeps the badge
   * and obeys her.
   *
   * Only `updatedTime` (both directions), `price` (both) and `listingPublishedTime` desc
   * ride a matching index; the others sort correctly but scan.
   */
  export type ListingSourceCandidateSortField = 'sameness' | ListingsSearchSortField;

  export type ListingSourceCandidateSort = {
    field: ListingSourceCandidateSortField;
    direction?: 'asc' | 'desc';
  };

  export type ListingSourceCandidatesRequest = {
    /** Rank against a saved listing. Mutually exclusive with `draft`. */
    listingId?: string;
    /** Rank against unsaved form state, so the picker works before the listing exists. */
    draft?: {
      city?: string;
      street?: string;
      streetNumber?: number | null;
      rooms?: number | null;
      sizeOfPropertyM2?: number | null;
      price?: number | null;
      transactionType?: TransactionType | null;
    };
    /** Overrides merged over the seeded filters — see `ListingSourceCandidateFilters`. */
    filters?: ListingSourceCandidateFilters;
    sort?: ListingSourceCandidateSort;
    /** Page size, which is also the sameness ranking window. Default 60, capped at 300. */
    limit?: number;
    /** 0-based. Capped server-side. */
    page?: number;
  };

  /** Which of the caller's OWN listings already holds this marketplace ad. */
  export type ListingSourceCandidateLink = {
    /** `real-estate-listings` id. */
    listingId: string;
    /** True when that listing is the one the picker is currently open on. */
    isThisListing: boolean;
    /** The holding listing's status — a `sold`/`removed` copy still holds the link. */
    status: string | null;
  };

  export type ListingSourceCandidate = {
    /** `yad2-listings-latest` id. */
    id: string;
    origin: ListingOrigin;
    /** "street number, city" from the parts that exist — nothing invented. */
    address: string;
    /**
     * The parts behind `address`, kept separately so a row can lay them out and so a
     * contradiction can name the value it disagrees with instead of just the field.
     */
    street: string | null;
    houseNumber: number | null;
    city: string | null;
    neighborhood: string | null;
    propertyType: string | null;
    floor: number | null;
    price: number | null;
    roomsCount: number | null;
    squareMeter: number | null;
    coverImageUrl: string | null;
    originalListingUrl: string | null;
    sellerName: string | null;
    agencyName: string | null;
    /** Epoch ms the scrape last re-saw this ad — the staleness signal the row renders. */
    updatedTimeMs: number | null;
    /** Epoch ms it was published (falls back to when we first stored it). */
    publishedTimeMs: number | null;
    /** 0-100. Ordering and a badge — never a threshold the server acts on by itself. */
    score: number;
    /**
     * The fields that positively agreed, so a row can say WHY it is suggested. A picker
     * that cannot explain itself gets clicked wrongly.
     */
    matchedOn: string[];
    /**
     * The fields both ads state and DISAGREE on. Non-empty ⇒ `score` is 0. Rendered as the
     * reason a row is filed under "contradicts this listing" rather than being dropped.
     */
    contradictions: string[];
    /** True when the pair would also pass the strict `isSameListing` gate. */
    confident: boolean;
    /** Null when nothing of the caller's holds it. See `ListingSourceCandidateLink`. */
    linkedTo: ListingSourceCandidateLink | null;
    /** `linkedTo !== null`, kept for readability at call sites. */
    alreadyLinked: boolean;
  };

  /** The target's comparable core — what the sameness rank is measured against. */
  export type ListingSourceCandidateSeed = {
    city: string | null;
    street: string | null;
    streetNumber: number | null;
    rooms: number | null;
    sizeOfPropertyM2: number | null;
    price: number | null;
    transactionType: TransactionType | null;
  };

  export type ListingSourceCandidatesResponse = {
    candidates: ListingSourceCandidate[];
    /** Ads matching `appliedFilters`. Exact below the search's own cap. */
    totalHits: number;
    /** True when the real total is at or above that cap — render as "N+". */
    totalHitsCapped: boolean;
    page: number;
    /**
     * Rows per page, and ALSO the window the sameness rank orders: the score sorts the
     * PAGE, it does not reorder the corpus. Ranking globally would mean scoring every
     * match before returning any row, and the page a realtor actually reads is the
     * freshest one. Reach further with the filters — a street, a room count, a word from
     * the ad — rather than with more pages.
     */
    pageSize: number;
    hasMore: boolean;
    /** The seed merged with the caller's overrides — exactly what was applied. */
    appliedFilters: ListingSourceCandidateFilters;
    /** Keys of `appliedFilters` that came from the seed, so a chip can say so honestly. */
    seededFilterKeys: string[];
    seed: ListingSourceCandidateSeed;
    /**
     * No location and no free text ⇒ nothing was searched at all. Deliberately distinct
     * from "no results": an unnarrowed scan would rank arbitrary rows from across the
     * country, and rendering that as a zero-result search is a lie the realtor cannot act
     * on. The client asks for a city or an area instead.
     */
    needsNarrowing: boolean;
    /** How the free-text term was applied: all words / any word. Absent when no term. */
    searchTermMode?: 'all' | 'any' | 'none';
  };

  // Madlan location resolution types
  export const MadlanLocationResolutionTypes = [
    'ACCURATE',
    'STREET_ONLY',
    'NEIGHBORHOOD_ONLY'
  ] as const;
  export type MadlanLocationResolutionType = typeof MadlanLocationResolutionTypes[number];

  // Madlan API schemas
  // POST /api/v1/madlan/publish-listing
  export type MadlanPublishListingRequest = {
    processedListingId: string;
    confirmedAddress?: string; // The validated address from Madlan autocomplete
    locationResolution?: MadlanLocationResolutionType; // Location resolution type for Madlan
  };

  // Madlan agent bulletins status from API response
  export type MadlanAgentBulletinsStatus = {
    publicRentUsedQuota: number;
    publicSaleUsedQuota: number;
    publicSaleAndRentTotalQuota: number;
    promotedBulletinsUsedQuota: number;
    promotedBulletinsTotalQuota: number;
    publicBulletinsUsedQuota: number;
    publicBulletinsTotalQuota: number;
  };

  // Current bulletin status from API response
  export type MadlanCurrentBulletinStatus = {
    bulletinId: string;
    isPublic: boolean;
    isPromoted: boolean;
    exclusive: boolean;
  };

  // POST /api/v1/madlan/publish-listing
  //
  // `success: true` with a `warning` is a real outcome, not a contradiction: Madlan
  // accepts a bulletin over quota and returns it FROZEN (isPublic false) with an
  // explanatory <warning> and no <bulletinId>. The same field carries "published, but
  // the photos did not upload". Render it - a warning dropped here is exactly how a
  // realtor ends up with an imageless listing and no idea why.
  export type MadlanPublishListingResponse = {
    success: boolean;
    bulletinId?: string;
    errorMessage?: string;
    warning?: string;
    agentBulletinsStatus?: MadlanAgentBulletinsStatus;
    currentBulletinStatus?: MadlanCurrentBulletinStatus;
  };

  // Madlan autocomplete completion types
  export const MadlanCompletionTypes = ['address', 'street'] as const;
  export type MadlanCompletionType = typeof MadlanCompletionTypes[number];

  // POST /api/v1/madlan/autocomplete
  export type MadlanAutocompleteRequest = {
    address: string;
    completionTypes?: MadlanCompletionType[];
  };

  export type MadlanAutocompleteAddress = {
    name: string;
  };

  // POST /api/v1/madlan/autocomplete
  export type MadlanAutocompleteResponse = {
    success: boolean;
    results: MadlanAutocompleteAddress[];
    address: string;
    completionTypes: MadlanCompletionType[];
    errorMessage?: string;
    // true when Madlan's search did not answer at all (blocked / down), as opposed to answering
    // with no match. The publish must not depend on it: Madlan's publish validates the address.
    searchUnavailable?: boolean;
  };

  // Madlan edit listing schemas
  // POST /api/v1/madlan/edit-published-listing
  export type MadlanEditListingRequest = {
    processedListingId: string;
  };

  // POST /api/v1/madlan/edit-published-listing
  // `skipped` means the bulletin and its photos already match what Madlan holds, so
  // nothing was sent. The caller is a Firestore trigger that fires on EVERY listing
  // write, so this is the common case, not an error.
  export type MadlanEditListingResponse = {
    success: boolean;
    bulletinId?: string;
    errorMessage?: string;
    warning?: string;
    skipped?: boolean;
    agentBulletinsStatus?: MadlanAgentBulletinsStatus;
    currentBulletinStatus?: MadlanCurrentBulletinStatus;
  };

  // GET /api/v1/getUpdateContactWebhookForCustomer
  export type GetUpdateContactWebhookForCustomerRequest = {
    // No parameters needed - customerMail comes from authenticated user
  };

  // GET /api/v1/getUpdateContactWebhookForCustomer
  export type GetUpdateContactWebhookForCustomerResponse = {
    /** The account's address with its secret key (`/webhooks/update-contact/whk_…`). */
    webhookUrl: string;
    customerMail: string;
    /** The legacy `userHash` address: still honoured for this account, and when it was last used. */
    legacy?: { active: boolean; lastCallAt?: string };
  };

  // POST /api/v1/update-contact-webhook/rotate
  export type RotateUpdateContactWebhookKeyResponse = {
    webhookUrl: string;
  };

  // POST /api/v1/generateCreative
  export type GenerateCreativeRequest = {
    prompt: string;
    imageUrls: string[];
    originalCreativeId?: string; // for regeneration requests - when regenerating
    listingIds?: string[]; // listing IDs this creative was generated for (for tab filtering)
    quality?: MakeoverQuality; // output resolution (default DEFAULT_CREATIVE_QUALITY = '1K'); price scales with it
  };
  export type GenerateCreativeResponse = {
    success: boolean;
    generatedCreativeId?: string;
    generatedImageUrls?: string[];
    errorMessage?: string;
    // Machine-readable failure signal. 'insufficient_coins' lets the UI prompt the
    // user to buy coins instead of showing a generic error. Returned with HTTP 200
    // + success:false (the frontend client throws on non-2xx, which would erase these fields).
    errorCode?: 'insufficient_coins' | 'internal_error';
    coinsRequired?: number; // present when errorCode === 'insufficient_coins'
    coinsAvailable?: number; // present when errorCode === 'insufficient_coins'
  };

  // ===================== ADMIN (shared) schemas =====================

  // GET /api/v1/admin/users/search?search= — centralized lean admin user search
  // (autocomplete by email prefix). Reusable across admin surfaces, not coins-specific.
  export type AdminUserSearchResult = {
    id: string; // email
    ownerFullName?: string;
    companyName?: string;
    organizationIds?: string[];
    advancedFeatures?: AdvancedFeature[];
    ownerPhoneNumber?: string;
    posthogTrackingEnabled?: boolean;
  };
  export type AdminUserSearchResponse = { users: AdminUserSearchResult[] };

  // GET /api/v1/admin/orgs/search?search= — centralized lean admin org search (by name).
  export type AdminOrgSearchResult = { id: string; name: string; usersCount: number };
  export type AdminOrgSearchResponse = { orgs: AdminOrgSearchResult[] };

  // ===================== ADMIN user management (admin users screen) =====================

  /**
   * Full user profile as served to the admin users screen. The whole user doc
   * minus stored credentials (`apiToken` grants MCP access as the user,
   * `madlanAgentPassword` is a stored password) — the screen has no use for
   * either, so they never leave the server.
   */
  export type AdminUserProfile = Omit<User, 'apiToken' | 'madlanAgentPassword'> & { id: string };

  // GET /api/v1/admin/users/:userId/profile
  export type AdminUserProfileResponse = { user: AdminUserProfile };

  // POST /api/v1/admin/users/update — only whitelisted fields are accepted;
  // anything else is rejected server-side.
  export type AdminUpdateUserRequest = {
    userId: string; // email
    posthogTrackingEnabled?: boolean;
    advancedFeatures?: AdvancedFeature[];
    /**
     * Whitelisted profile fields (ADMIN_EDITABLE_USER_*_FIELDS). The admin
     * screen's save-on-blur sends one field at a time.
     */
    fields?: AdminEditableUserFields;
  };
  export type AdminUpdateUserResponse = { user: AdminUserProfile };

  /**
   * Usage stats computed by domainLogic.calculateUserStats over the last 60
   * days. Date fields arrive as ISO strings over the wire.
   */
  export type AdminUserStats = {
    userMail: string;
    sinceDate: Date | string;
    usageScore: number; // 0..1 weighted usage score
    createdTime: Date | string;
    contactsCount: number;
    sentMessagesCount: number;
    customerMinisitesMadeByUserCount: number;
    calendarMeetingsMadeByUserCount: number;
    virtualNumbersCount: number;
    newLeadsFromVirtualNumbers: number;
    hasIntegratedVirtualNumber: boolean;
    isUsingContactsFeatureWell: boolean;
    isUsingSentMessagesFeatureWell: boolean;
    isUsingMinisiteFeatureWell: boolean;
    isUsingCalendarFeatureWell: boolean;
    isUsingLeadsFromVirtualNumbersFeatureWell: boolean;
  };

  // GET /api/v1/admin/users/:userId/stats
  export type AdminUserStatsResponse = { stats: AdminUserStats };

  // ===================== DB EXPLORER (owner-only Postgres console) =====================
  // /api/v1/admin/db/* — a browser DBA console over the Cloud SQL databases
  // (`listings` + `crm`, both on webot-bot:europe-west1:listings-search).
  // Access is NOT the admin list: both repos gate on DB_EXPLORER_ALLOWED_EMAILS
  // (the server 403s everyone else, the dashboard hides the page).

  export type DbExplorerDbKey = 'listings' | 'crm';

  // GET /api/v1/admin/db/info — instance identity + deep links into the GCP console.
  export type DbExplorerDatabaseInfo = {
    key: DbExplorerDbKey;
    configured: boolean; // password present in this environment
    database: string;
    user?: string;
    version?: string; // server version() when reachable
    sizePretty?: string; // pg_database_size(current_database()), human form
    error?: string; // set when configured but unreachable
  };
  export type DbExplorerInfoResponse = {
    project: string;
    region: string;
    instance: string;
    instanceConnectionName: string;
    consoleUrl: string; // Cloud SQL instance overview page in the GCP console
    studioUrl: string; // Cloud SQL Studio (Google's query UI) for the instance
    databases: DbExplorerDatabaseInfo[];
  };

  // GET /api/v1/admin/db/:db/tables — everything in the public schema, with size + row estimates.
  export type DbExplorerTableInfo = {
    name: string;
    kind: 'table' | 'partitioned table' | 'view' | 'materialized view' | 'foreign table';
    estimatedRows: number | null; // pg_class.reltuples; null until the first ANALYZE
    totalBytes: number;
    tableBytes: number;
    indexBytes: number;
    prettyTotalSize: string;
  };
  export type DbExplorerTablesResponse = { db: DbExplorerDbKey; tables: DbExplorerTableInfo[] };

  // GET /api/v1/admin/db/:db/tables/:table/schema
  export type DbExplorerColumnInfo = {
    name: string;
    position: number;
    dataType: string; // information_schema data_type (e.g. 'jsonb', 'ARRAY')
    udtName: string; // concrete type (e.g. '_text' for text[])
    nullable: boolean;
    default: string | null;
    isGenerated: boolean; // GENERATED ALWAYS AS ... STORED — never writable
    generationExpression: string | null;
    isIdentity: boolean;
    isPrimaryKey: boolean;
  };
  export type DbExplorerIndexInfo = { name: string; definition: string };
  export type DbExplorerTableSchemaResponse = {
    db: DbExplorerDbKey;
    table: string;
    columns: DbExplorerColumnInfo[];
    primaryKey: string[]; // empty = no PK; structured row edit/delete are disabled then
    indexes: DbExplorerIndexInfo[];
    estimatedRows: number | null;
    prettyTotalSize: string;
  };

  // POST /api/v1/admin/db/:db/tables/:table/rows — one filtered/sorted page of rows.
  export type DbExplorerFilterOp =
    | 'eq'
    | 'neq'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'like' // raw LIKE, caller supplies the wildcards
    | 'ilike' // raw ILIKE
    | 'contains' // convenience: ILIKE %value%
    | 'in' // value = comma/newline-separated list
    | 'isnull'
    | 'notnull';
  export type DbExplorerFilter = { column: string; op: DbExplorerFilterOp; value?: string };
  export type DbExplorerRow = Record<string, unknown>;
  export type DbExplorerBrowseRequest = {
    filters?: DbExplorerFilter[];
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
    limit?: number; // default 50, max 500
    offset?: number;
  };
  export type DbExplorerBrowseResponse = {
    rows: DbExplorerRow[];
    columns: string[]; // display order (schema order)
    primaryKey: string[];
    totalCount: number; // exact up to the cap
    totalCountCapped: boolean; // true → render as "totalCount+"
    durationMs: number;
  };

  // POST /api/v1/admin/db/:db/tables/:table/rows/insert
  export type DbExplorerInsertRequest = { values: DbExplorerRow };
  // POST /api/v1/admin/db/:db/tables/:table/rows/update — keyed strictly on the full PK.
  export type DbExplorerUpdateRequest = { primaryKey: DbExplorerRow; changes: DbExplorerRow };
  // POST /api/v1/admin/db/:db/tables/:table/rows/delete
  export type DbExplorerDeleteRequest = { primaryKey: DbExplorerRow };
  export type DbExplorerMutationResponse = { rowCount: number; row?: DbExplorerRow | null };

  // POST /api/v1/admin/db/:db/query — the SQL console. readOnly (default true)
  // runs the session under default_transaction_read_only so any write fails
  // loudly; a single SELECT statement is capped via a LIMIT wrapper, and every
  // result is additionally sliced to maxRows before it goes over the wire.
  export type DbExplorerSqlRequest = { sql: string; readOnly?: boolean; maxRows?: number };
  export type DbExplorerSqlStatementResult = {
    command: string; // SELECT / UPDATE / EXPLAIN / ...
    rowCount: number | null;
    fields: { name: string; dataType: string }[];
    rows: DbExplorerRow[];
    truncated: boolean; // rows were cut at maxRows
  };
  export type DbExplorerSqlResponse = {
    results: DbExplorerSqlStatementResult[];
    durationMs: number;
    readOnly: boolean;
  };

  // Error body for any db-explorer 4xx — pg diagnostics surfaced to the UI.
  export type DbExplorerErrorResponse = {
    error: string;
    code?: string; // pg SQLSTATE (e.g. 42601 syntax error)
    detail?: string;
    hint?: string;
    position?: string; // 1-based char offset into the submitted SQL
  };

  // ===================== SALES MAP (מפת משרדים) =====================
  // /api/v1/admin/sales-map/* — every agency + realtor in the market with their Webot status, and live GoHighLevel.
  // Access is SALES_MAP_ALLOWED_EMAILS on every route (server-enforced); the dashboard hides the page for anyone else.
  // The snapshot is built offline (adHocOperations/realtor-market-report) and stored gzipped in the crm Postgres
  // database, which no browser can reach — never in Firestore (webot admins can read any Firestore doc) or a bucket.

  export type SalesMapWebotStatus = 'active' | 'churned' | 'never';
  export type SalesMapPhoneKind = 'real' | 'listingReal' | 'yad2Virtual' | 'madlanListing';
  export type SalesMapPhone = {
    p: string; // local format, 05XXXXXXXX
    src: 'webot' | 'yad2' | 'madlan';
    kind: SalesMapPhoneKind; // yad2Virtual / madlanListing work only while that listing is live — show `seen`
    seen?: number; // epoch ms the listing carrying this number was last seen
    who?: string; // office phones: whose number it is
    owner?: { name: string; st: SalesMapWebotStatus }; // the number belongs to a DIFFERENT Webot user
  };
  // AI match of a GoHighLevel lead onto this realtor/office, baked into the snapshot at build time
  export type SalesMapLeadMatch = {
    c: string; // GHL contact id
    conf: 'high' | 'medium' | 'low';
    name?: string; // the lead's name in GHL
    added?: string; // YYYY-MM-DD the lead entered GHL
    stage?: string; // 'pipeline › stage (status)' at extract time
    rep?: string; // assigned GHL user name at extract time
    why?: string; // the AI's one-line reason
  };
  export type SalesMapWebotInfo = {
    st: SalesMapWebotStatus;
    email: string;
    name?: string;
    company?: string;
    role?: string;
    signup?: number | null;
    match?: 'high' | 'medium'; // confidence of the Webot user → market realtor link
    via?: string; // Hebrew: how the link was made
    otherEmails?: string[];
    unverified?: boolean; // access without a Cardcom-verified payment
    accessAfterChurn?: boolean;
    promoted?: boolean;
    exMember?: boolean;
    paidShared?: boolean; // the office pays for this member
    officeAccount?: { name: string; last?: number };
    phoneOwner?: { name: string; st: SalesMapWebotStatus };
    firstPay?: number;
    lastPay?: number;
    lastAmt?: number;
    paid?: number; // total ₪
    failing?: boolean;
    cs?: string; // customer-success status key
    csNote?: string;
    adminNote?: string;
  };
  export type SalesMapRealtor = {
    id: string;
    ag: string | null; // agency id
    name: string;
    aka?: string[];
    phones: SalesMapPhone[];
    cities?: string[];
    listings: number;
    src?: { yad2: number; madlan: number };
    lastSeen?: number;
    url?: string; // a recent listing
    w: SalesMapWebotInfo | null;
    rentOnly?: boolean;
    nameless?: boolean;
    madlanMerge?: string;
    webotOnly?: boolean; // Webot user with no live listing
    gl?: SalesMapLeadMatch[];
  };
  export type SalesMapAgency = {
    id: string;
    name: string;
    aka?: string[];
    net?: string; // network id (SalesMapNetwork.id)
    q?: string; // branch qualifier inside the network
    cities?: string[];
    src: { yad2: number; madlan: number };
    listings: number;
    lastSeen?: number;
    realtors: number;
    ws: { active: number; churned: number; never: number };
    phones: SalesMapPhone[];
    accounts?: number; // Cardcom accounts paying from this office
    flags?: string[]; // Hebrew badges (developer, commercial, named after the realtor…)
    kind?: 'webot'; // exists only in Webot (no live listings)
    gl?: SalesMapLeadMatch[]; // leads matched to the office but to no specific realtor
  };
  export type SalesMapNetwork = { id: string; name: string; branches: number };
  export type SalesMapSnapshot = {
    meta: {
      generatedAt: number; // data extract time
      builtAt: number;
      windowDays: number;
      withMadlanCsv: boolean;
      ghlExtractedAt?: number;
      // every GoHighLevel lead, and how many the AI matched (high/medium) to a realtor row / only to an office
      ghlLeads?: { total: number; matchedToRealtors: number; matchedToOffices: number };
      kpis: {
        agencies: number;
        agenciesWebotOnly: number;
        networkBranches: number;
        realtors: number;
        webotOnly: number;
        noOffice: number;
        active: number;
        churned: number;
        never: number;
        none: number;
        accounts: number;
      };
      definitions: [string, string][]; // [Hebrew term, Hebrew explanation]
    };
    networks: SalesMapNetwork[];
    cities: string[];
    agencies: SalesMapAgency[];
    realtors: SalesMapRealtor[];
  };
  // GET /snapshot → the SalesMapSnapshot JSON (served gzip-encoded); header X-Sales-Map-Snapshot-Id.
  // 404 { code: 'NO_SNAPSHOT' } until one is published.

  export type SalesMapSubjectKind = 'realtor' | 'office';
  export type SalesMapSubject = { kind: SalesMapSubjectKind; key: string; name?: string };

  // GET /ghl/context — reference data for the live panel (cached server-side for a few minutes)
  export type SalesMapGhlUser = { id: string; name: string };
  export type SalesMapGhlPipeline = { id: string; name: string; stages: { id: string; name: string }[] };
  export type SalesMapGhlContextResponse = {
    actor: { email: string; ghlUserId?: string; ghlUserName?: string }; // writes need ghlUserId (matched by email)
    users: SalesMapGhlUser[];
    pipelines: SalesMapGhlPipeline[];
    tags: string[]; // every tag defined in the GHL location
    unavailable: ('conversations' | 'customFieldNames')[]; // API-key scopes we lack; the UI says so
  };

  export type SalesMapGhlOpportunity = {
    id: string;
    name?: string;
    pipelineId: string;
    pipelineName?: string;
    stageId?: string;
    stageName?: string;
    status: string; // open | won | lost | abandoned
    monetaryValue?: number;
    assignedTo?: string;
    assignedToName?: string;
    createdAt?: string;
    updatedAt?: string;
    lastStageChangeAt?: string;
  };
  export type SalesMapGhlContactSummary = {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    companyName?: string;
    city?: string;
    tags: string[];
    source?: string;
    dateAdded?: string;
    assignedTo?: string;
    assignedToName?: string;
    opportunities: SalesMapGhlOpportunity[];
    matchedBy: ('phone' | 'email' | 'aiMatch' | 'link')[];
    url: string; // opens the contact in the GHL app
  };
  export type SalesMapGhlNote = { id: string; body: string; dateAdded?: string; userId?: string; userName?: string };
  export type SalesMapGhlTask = { id: string; title: string; body?: string; dueDate?: string; completed: boolean; assignedTo?: string; assignedToName?: string };
  export type SalesMapGhlAppointment = { id: string; title?: string; startTime?: string; endTime?: string; status?: string; assignedUserId?: string; assignedUserName?: string };
  export type SalesMapGhlContactDetail = SalesMapGhlContactSummary & {
    additionalPhones: string[];
    additionalEmails: string[];
    website?: string;
    dnd?: boolean;
    customFields: { id: string; value: string }[]; // names need a scope we lack (see context.unavailable)
    notes: SalesMapGhlNote[]; // newest first
    tasks: SalesMapGhlTask[];
    appointments: SalesMapGhlAppointment[];
  };

  // POST /ghl/lookup — every GHL contact for one realtor/office: by phone, email, the snapshot's AI matches, saved links
  export type SalesMapGhlLookupRequest = { subject: SalesMapSubject; phones: string[]; emails: string[]; contactIds: string[] };
  export type SalesMapGhlLookupResponse = { contacts: SalesMapGhlContactSummary[] };

  // GET /ghl/contacts/:contactId
  export type SalesMapGhlContactResponse = { contact: SalesMapGhlContactDetail };

  // POST /ghl/contacts/:contactId/notes — posted as the caller's own GHL user
  export type SalesMapGhlAddNoteRequest = { body: string };
  export type SalesMapGhlAddNoteResponse = { note: SalesMapGhlNote };

  // POST /ghl/contacts — create the GHL contact for a realtor/office that has none (optionally with the first note).
  // 409 { code: 'DUPLICATE', contact } when GHL already has a contact with that phone/email: link it explicitly instead.
  export type SalesMapGhlCreateContactRequest = {
    subject: SalesMapSubject;
    firstName: string;
    lastName?: string;
    phone?: string;
    email?: string;
    companyName?: string;
    city?: string;
    tags?: string[];
    note?: string;
  };
  // noteError: the contact was created and linked, but the first note failed — show it and keep the draft
  export type SalesMapGhlCreateContactResponse = { contact: SalesMapGhlContactSummary; note?: SalesMapGhlNote; noteError?: string };

  // POST /ghl/links · POST /ghl/links/delete — remember (or forget) that a GHL contact is this realtor/office
  export type SalesMapGhlLinkRequest = { subject: SalesMapSubject; contactId: string };
  export type SalesMapGhlLink = { kind: SalesMapSubjectKind; key: string; contactId: string; linkedBy: string; linkedAt: string };
  // GET /ghl/links — every saved link, so the map can badge realtors that are in GHL
  export type SalesMapGhlLinksResponse = { links: SalesMapGhlLink[] };

  // POST /ghl/contacts/:contactId/stage — move the opportunity (creates one in that pipeline when the contact has none)
  export type SalesMapGhlSetStageRequest = {
    pipelineId: string;
    stageId: string;
    opportunityId?: string;
    status?: 'open' | 'won' | 'lost' | 'abandoned';
  };
  export type SalesMapGhlSetStageResponse = { opportunity: SalesMapGhlOpportunity };

  // POST /ghl/contacts/:contactId/assign — userId null unassigns; also reassigns the opportunity when given
  export type SalesMapGhlAssignRequest = { userId: string | null; opportunityId?: string };
  export type SalesMapGhlAssignResponse = { ok: true };

  // POST /ghl/contacts/:contactId/tags
  export type SalesMapGhlTagsRequest = { add?: string[]; remove?: string[] };
  export type SalesMapGhlTagsResponse = { tags: string[] };

  // Error body for any sales-map 4xx/5xx
  export type SalesMapErrorResponse = {
    error: string;
    code?: 'FORBIDDEN' | 'NO_SNAPSHOT' | 'DUPLICATE' | 'NO_GHL_USER' | 'NOT_FOUND' | 'INVALID' | 'GHL_ERROR' | 'RATE_LIMITED' | 'NOT_READY';
    contact?: SalesMapGhlContactSummary; // with DUPLICATE
  };

  // ===================== SALES TRAINING schemas =====================
  // /api/v1/admin/sales-training — SALES_TRAINING_ALLOWED_EMAILS only, on every route (server-enforced); the dashboard
  // hides the page for anyone else. The portal is one self-contained HTML page plus its call-audio files, published
  // from adHocOperations/sales-calls-review/publish-training.ts into the crm database.

  // GET /manifest — what the current release contains
  export type SalesTrainingManifest = {
    releaseId: string;
    builtAt: string; // ISO
    page: { bytes: number; sha256: string };
    files: { path: string; bytes: number; sha256: string }[]; // audio, e.g. "audio/c-<alias>.mp3" (never a real call id)
  };
  // GET /page — the portal HTML (text/html, gzip-encoded on the wire)
  // GET /audio/:name — one audio file of the release (audio/mpeg)

  export type SalesTrainingErrorResponse = {
    error: string;
    code?: 'FORBIDDEN' | 'NOT_PUBLISHED' | 'NOT_FOUND';
  };

  // ===================== SALES TRAINING V2 schemas =====================
  // /api/v1/admin/sales-training-v2 — the simple onboarding portal for sales reps. Readable by the owners
  // (SALES_TRAINING_OWNER_EMAILS) and by the emails they add from the page (crm table sales_training_viewer); the
  // reader list itself is the owners' only. The page is one HTML fragment the dashboard shows in a sandboxed frame.

  // GET /me — any signed-in user, about themselves only (the sidebar asks it): may I open the portal, am I an owner?
  export type SalesTrainingV2Access = { canView: boolean; owner: boolean };
  // GET /page — the portal HTML (text/html, gzip-encoded on the wire)

  // GET /viewers, POST /viewers { email }, POST /viewers/remove { email } — owners only; each answers the whole list
  export type SalesTrainingV2Viewer = { email: string; addedBy: string; addedAt: string }; // addedAt: ISO
  export type SalesTrainingV2ViewersResponse = { owners: string[]; viewers: SalesTrainingV2Viewer[] };
  export type SalesTrainingV2ViewerRequest = { email: string };

  export type SalesTrainingV2ErrorResponse = {
    error: string;
    // INVALID: not an email · OWNER: an owner is always on the list · LIMIT: the list is full · NOT_READY: the table
    // has not been created yet (the next CRM migration run creates it)
    code?: 'FORBIDDEN' | 'INVALID' | 'OWNER' | 'LIMIT' | 'NOT_READY';
  };

  // ===================== SALES RECRUITING schemas =====================
  // /api/v1/admin/sales-recruiting — SALES_RECRUITING_ADMIN_EMAILS only, on every route (server-enforced); the
  // dashboard hides the page for anyone else. Wire shapes: every date is an ISO string.

  /** How far the applicant got on their own. Written by the system only; it moves forward and never back. */
  export type SalesRecruitingFunnelStatus = 'new' | 'intro_sent' | 'not_relevant' | 'replied' | 'link_sent' | 'opened' | 'started' | 'completed';
  /** The reviewers' verdict. A person's decision: it may move in any direction, and absent means "not decided". */
  export type SalesRecruitingReviewStatus = 'to_call' | 'no_answer' | 'talked' | 'interview' | 'hired' | 'rejected' | 'withdrew';

  /** One question of the screening form with the applicant's answer in words. */
  export type SalesRecruitingAnswerLine = {
    questionId: string;
    shortTitle: string;
    question: string;
    kind: 'single' | 'multi' | 'text';
    answer: string; // empty when unanswered
    points?: number; // scored closed questions only
    maxPoints?: number;
    reviewHint?: string; // open questions: what a good answer looks like
  };

  export type SalesRecruitingNote = { id: string; text: string; by: string; at: string };

  export type SalesRecruitingApplicant = {
    id: string;
    name: string;
    phone: string; // E.164
    phoneDisplay: string; // as a person writes it
    conversationId: string; // `<numberKey>__<waId>`: their thread on the recruiting number, for the inbox drawer
    status: SalesRecruitingFunnelStatus;
    source: 'meta_lead' | 'direct';
    isTest: boolean; // one of ours simulating a lead, or a bare-form entry: may be deleted
    formLink: string;
    formVersion: number;
    createdAt?: string;
    introSentAt?: string;
    introState?: 'sending' | 'failed'; // `sending` that stays = cut off: nobody knows whether it arrived
    introError?: string;
    repliedAt?: string;
    notRelevantAt?: string;
    linkSentAt?: string;
    linkState?: 'sending' | 'failed';
    linkError?: string;
    openedAt?: string;
    openCount?: number;
    startedAt?: string;
    lastActivityAt?: string;
    stepsCompleted: string[]; // the screens they finished, by title
    stepsTotal: number;
    completedAt?: string;
    durationSec?: number;
    score?: { points: number; max: number; flags: string[] };
    answers: SalesRecruitingAnswerLine[];
    lead?: { platform?: string; adName?: string; createdTime?: string; email?: string; answers: { question: string; answer: string }[] };
    review?: { status: SalesRecruitingReviewStatus; by: string; at: string };
    notes: SalesRecruitingNote[]; // oldest first
    archivedAt?: string;
  };

  /** The hourly intake: new leads from the sheet get their document and Ariella's intro. */
  export type SalesRecruitingAutomation = {
    enabled: boolean; // the hourly run's switch
    configured: boolean; // whether a leads sheet is set at all (its id never leaves the server)
    hours: { first: number; last: number }; // Israel time, sending hours
    lastRun?: {
      at: string;
      trigger: 'cron' | 'manual';
      ok: boolean;
      skipped?: string;
      sheetRows?: number;
      created?: number;
      sent?: number;
      heldBack?: number;
      failed?: number;
      cutOff?: number;
      error?: string;
      by?: string;
    };
  };

  // GET /applicants
  export type SalesRecruitingListResponse = { applicants: SalesRecruitingApplicant[]; automation: SalesRecruitingAutomation };
  // POST /applicants/:id/review
  export type SalesRecruitingSetReviewRequest = { status: SalesRecruitingReviewStatus | null };
  // POST /applicants/:id/notes
  export type SalesRecruitingAddNoteRequest = { text: string };
  // POST /applicants/:id/notes/:noteId/delete · /archive · /unarchive · /send-intro · /send-link · /give-back-intro
  // Every write answers the applicant as it now stands. An action also says what became of it: `outcome` is a code the
  // page puts into words (sent, held_back, blocked, failed, cut_off, not_new, link_sent, nothing_to_send, link_failed,
  // link_unknown, given_back) and `detail` the reason behind it, when there is one.
  // There is no delete: an applicant's document is what tells the hourly intake "this lead is known". They are archived.
  export type SalesRecruitingApplicantResponse = { applicant: SalesRecruitingApplicant; outcome?: string; detail?: string };
  // POST /automation
  export type SalesRecruitingSetAutomationRequest = { enabled: boolean };
  // POST /automation · POST /intake/run
  export type SalesRecruitingAutomationResponse = { automation: SalesRecruitingAutomation };

  export type SalesRecruitingErrorResponse = {
    error: string;
    code?: 'FORBIDDEN' | 'NOT_FOUND' | 'INVALID' | 'NOT_ALLOWED';
  };

  // ===================== COINS schemas =====================

  // GET /api/v1/coins/balance — returns CoinBalanceBreakdown for the authenticated user.

  // GET /api/v1/coins/admin/users/:userId — full coin detail for one user.
  export type AdminUserCoinsResponse = {
    userId: string;
    balance: CoinBalanceBreakdown;
    packs: CoinsPack[];
    operations: CoinsOperation[];
  };

  // GET /api/v1/coins/admin/orgs/:organizationId — org-level coin detail.
  export type AdminOrgCoinsResponse = {
    organizationId: string;
    name?: string;
    totalCoins: number;
    packs: CoinsPack[];
    operations: CoinsOperation[];
  };

  // POST /api/v1/coins/admin/grant — admin grants a coin pack.
  export type AdminGrantCoinsRequest = {
    userId?: string; // required for user-scope; omitted for org-scope (pack is keyed to organizationId)
    scope: 'user' | 'org';
    organizationId?: string; // required for org-scope
    coins: number;
    source: 'oneTimePurchase' | 'gift';
    paidPriceCents?: number;
    expirationDateISO?: string; // omit for a permanent pack
    details?: string;
  };

  // POST /api/v1/coins/admin/adjust — admin corrects or revokes an existing pack.
  export type AdminAdjustCoinsRequest = {
    packId: string;
    newCurrentCoins?: number; // set absolute remaining
    revoke?: boolean;
    details?: string;
  };
  export type AdminCoinsMutationResponse = { success: boolean; balance?: CoinBalanceBreakdown };

  // POST /api/v1/generateMakeovers
  // Generate faithful AI "makeovers" of up to 5 of a listing's real photos.
  export type GenerateMakeoverItem = {
    sourceImageUrl: string; // MUST be one of the listing's own image URLs
    sceneType?: MakeoverSceneType; // default 'auto'
    intensity?: MakeoverIntensity; // how much to change (default 'restyle')
    stylePrompt?: string; // optional style directive (preset label or free text); never a layout directive
    additionalNotes?: string; // optional free-text: specific instructions on what to change in this photo
    engine?: MakeoverEngine; // model engine / performance tier (default DEFAULT_MAKEOVER_ENGINE = 'standard')
    quality?: MakeoverQuality; // output resolution 1K/2K (default DEFAULT_MAKEOVER_QUALITY = '1K')
  };
  export type GenerateMakeoversRequest = {
    listingId: string;
    items: GenerateMakeoverItem[]; // 1..5
  };
  export type GeneratedMakeoverResult = {
    id: string; // GeneratedUserCreative doc id
    sourceImageUrl: string;
    makeoverImageUrl?: string; // present once status === 'completed'
    status: 'starting' | 'in-progress' | 'completed' | 'failed';
    sceneType?: MakeoverSceneType;
    engine?: MakeoverEngine; // engine used for this image
    quality?: MakeoverQuality; // resolution used for this image
    coinsCost?: number; // coins this image cost (engine × quality)
    errorMessage?: string;
  };
  export type GenerateMakeoversResponse = {
    success: boolean;
    batchId?: string;
    makeovers?: GeneratedMakeoverResult[];
    errorMessage?: string;
    // Coins billing: a makeover costs CREATIVE_EDIT_COSTS.makeover coins each. When the
    // user can't afford the whole batch we reject it up front (no images generated).
    errorCode?: 'insufficient_coins' | 'internal_error';
    coinsRequired?: number;
    coinsAvailable?: number;
  };

  // POST /api/v1/setMakeoverShowcase
  // Choose which of a listing's makeovers appear in the minisite "AI generated" section.
  export type MakeoverShowcaseImageInput = {
    makeoverId: string;
    makeoverImageUrl: string;
    sourceImageUrl: string;
    sceneType?: MakeoverSceneType;
  };
  export type SetMakeoverShowcaseRequest = {
    listingId: string;
    enabled?: boolean; // toggle the showcase on/off (defaults to true on first write)
    shareWithOrg?: boolean; // org admins only: true sets organizationId on the doc, false clears it
    images?: MakeoverShowcaseImageInput[]; // full replacement set; omit to only toggle `enabled`/`shareWithOrg`
  };
  export type SetMakeoverShowcaseResponse = {
    success: boolean;
    errorMessage?: string;
  };

  // POST /api/v1/deleteMakeover — marks the makeover record 'deleted' and removes it from the user's showcase.
  export type DeleteMakeoverRequest = {
    makeoverId: string;
  };
  export type DeleteMakeoverResponse = {
    success: boolean;
    errorMessage?: string;
  };

  // POST /api/v1/removeListingWatermarks — AI-clean watermarks / realtor branding off listing photos.
  // Results are saved on the LISTING document (yad2-listings-latest → watermarkFreeImages) as a
  // shared cache: already-cleaned images are returned as no-ops (alreadyExisted: true), never
  // regenerated. Every request also arrayUnions the caller into watermarkRemovalRequestedBy —
  // the per-user display gate — so an all-no-op run still activates clean display for the caller.
  export type RemoveListingWatermarksRequest = {
    listingId: string;
    imageUrls?: string[]; // subset of the listing's imageUrls to clean; omit to clean ALL of them
    // Retry: regenerate the listed images even when a clean version already exists,
    // REPLACING the stored version. Only honored when imageUrls is explicitly given.
    forceRegenerate?: boolean;
  };
  export type RemovedListingWatermarkResult = {
    sourceImageUrl: string;
    cleanImageUrl?: string; // present when status === 'completed'
    status: 'completed' | 'failed';
    alreadyExisted?: boolean; // true when the clean version already existed (nothing was generated)
    errorMessage?: string;
  };
  export type RemoveListingWatermarksResponse = {
    success: boolean; // true when every requested image ended up with a clean version
    results?: RemovedListingWatermarkResult[];
    errorMessage?: string;
  };

  // POST /api/v1/public/listing-makeovers
  // Public resolver used by minisites: returns the showcased makeovers per listing,
  // applying user-scope-over-org-scope precedence. No auth (mirrors /public/realtor-info).
  export type PublicListingMakeoversRequest = {
    realtorMail: string;
    listingIds: string[];
  };
  export type PublicListingMakeoverImage = {
    url: string; // the AI "after" image
    sourceImageUrl: string; // the original "before" image
    sceneType?: MakeoverSceneType;
  };
  export type PublicListingMakeoversResponse = {
    makeoversByListingId: { [listingId: string]: PublicListingMakeoverImage[] };
  };

  // --- Listing presentation: "edit once, every minisite" (see ListingPresentation) ---------
  // POST /api/v1/listing-presentations/save
  export type SaveListingPresentationRequest = {
    listingId: string;
    /** `null` or `''` clears it. `undefined` leaves it unchanged. */
    descriptionOverride?: string | null;
    picturesVisibilityMode?: ListingPicturesVisibilityMode;
    /** ORIGINAL image urls. Kept even when the mode is not `'some'`. */
    hiddenPictures?: string[];
  };
  export type SaveListingPresentationResponse = {
    success: boolean;
    /** How many of the realtor's minisites the save was applied to. Drives "הוחל על N מיניסייטים". */
    appliedToMinisites: number;
    errorMessage?: string;
  };

  // GET /api/v1/listing-presentations/:listingId
  export type GetListingPresentationResponse = {
    descriptionOverride?: string;
    picturesVisibilityMode: ListingPicturesVisibilityMode;
    hiddenPictures: string[];
    /** Minisites of this realtor that currently contain the listing — "מופיע אצל N לקוחות". */
    minisiteCount: number;
    /**
     * Minisites carrying a one-off exception, which this edit will NOT change. Surfaced in the
     * dialog: without it the escape hatch is an invisible trap — the realtor edits globally, three
     * customers do not update, and nothing on screen explains why.
     */
    customPresentationCount: number;
    /**
     * True when there is no global record yet and these values were seeded from the realtor's
     * existing per-contact edits. The first save promotes exactly what they can see on screen.
     */
    seededFromLegacy: boolean;
  };
  // --- MCP Server schemas ---

  // GET /api/v1/mcp/connection-info
  export type McpConnectionInfoResponse = {
    mcpEndpoint: string;
    /** The per-user MCP token (`wmcp_…`). Never the extension's `apiToken`. */
    mcpToken: string;
    instructions: string;
  };

  // POST /api/v1/mcp/regenerate-token
  export type McpRegenerateTokenResponse = {
    mcpToken: string;
    mcpEndpoint: string;
  };

  // --- Chat (in-app /dashboard/ai) schemas ---
  // All chat endpoints are gated by `validateFirebaseToken + addUserToRequest`
  // and scoped to `req.user.id`. Path params identify chats the caller owns;
  // a chat belonging to a different user returns 404 (not 403) on purpose so
  // we don't leak chat-id existence across users.

  // POST /api/v1/chat (Server-Sent Events stream — not JSON)
  /** Body of the streaming chat request. */
  export type ChatStreamRequest = {
    /**
     * Conversation history. The LAST entry MUST be the new user turn — that's
     * what the assistant is being asked to respond to. Earlier entries are
     * prior context, forwarded to the model verbatim.
     */
    messages: ChatMessage[];
    /**
     * Continue an existing chat. Omit on the first turn — the server mints a
     * new chat id and emits it back via the named `chat` SSE event.
     */
    chatId?: string;
  };
  // Response: text/event-stream. Each chunk is `data: <json>\n\n` (or
  // `event: <name>\ndata: <json>\n\n` for `chat`/`done`/`error`). Per-chunk
  // payloads follow Anthropic's Messages stream events plus three custom
  // synthetic events: `chat_meta` (chatId minted/resolved), `chat_title`
  // (Haiku-generated title pushed live), and `done`/`error` (terminal).

  // POST /api/v1/chat/transcribe
  /** Body of the voice-dictation transcription request. */
  export type TranscribeVoiceRequest = {
    /** Base64-encoded audio bytes. */
    audio: string;
    /** MIME type — must start with `audio/` (e.g. `audio/webm`, `audio/mp4`). */
    mimeType: string;
    /**
     * Optional terminology hint. The current input text the user has typed
     * is passed here so the STT model can bias toward repeated names/jargon.
     */
    contextHint?: string;
  };
  export type TranscribeVoiceResponse = {
    /** Transcribed text. Empty string when STT returned no recognised content. */
    text: string;
  };

  // GET /api/v1/chat/preferences
  // Inputs: none.
  export type GetChatPreferencesResponse = {
    /** Server fills in defaults for any unset fields, so every key is present. */
    preferences: Required<ChatPreferences>;
  };

  // PUT /api/v1/chat/preferences
  /** Patch — only the fields present in the body are updated. Unknown fields
   *  or wrong-typed values are rejected with HTTP 400 (NOT silently dropped). */
  export type UpdateChatPreferencesRequest = Partial<ChatPreferences>;
  export type UpdateChatPreferencesResponse = {
    preferences: Required<ChatPreferences>;
  };

  // GET /api/v1/contacts/smart-defaults
  // Inputs: none. Returns the realtor's smart defaults for the new/edit-contact
  // modal — served from the Firestore cache, recomputed synchronously (and
  // re-cached) when the cache is missing or older than the freshness TTL. The
  // compute is cheap (~50 doc reads) and runs at most once per TTL per realtor;
  // the dashboard prefetches on page load, so the occasional slower call is
  // off the modal-open critical path.
  export type GetContactSmartDefaultsResponse = {
    defaults: ContactSmartDefaults;
  };

  // POST /api/v1/chat/actions
  /** Body of the inline-action dispatcher. Confirmed `<webot-action>` button
   *  clicks land here; the server runs the matching handler under the
   *  authenticated user (no ambient AI authority). */
  export type ExecuteChatActionRequest = {
    /**
     * Action discriminator (e.g. `create_minisite`, `set_task_status`).
     * Dispatched server-side to the matching handler in `chatActions.service`.
     */
    type: string;
    /** Action-specific parameters; shape depends on `type`. */
    params?: Record<string, unknown>;
  };
  export type ExecuteChatActionResponse = {
    ok: boolean;
    type: string;
    /** Handler-specific success payload. Shape depends on `type`. */
    result?: unknown;
    /**
     * Set when `ok` is false. Action-level failures return HTTP 400 with this
     * body (NOT thrown) so the UI can render an inline error pill instead of
     * bubbling up.
     */
    error?: string;
  };

  // GET /api/v1/chat?limit=50
  /** Query string for the user's chat list. */
  export type ListChatsQuery = {
    /** Page size cap. Server clamps to [1, 100]; defaults to 50. */
    limit?: number;
  };
  export type ListChatsResponse = {
    /** Caller's own chats, newest first (by `lastMessageAt`). */
    chats: AiChat[];
  };

  // GET /api/v1/chat/:chatId
  /** Path params for fetching a single chat + its messages. */
  export type GetChatParams = {
    /** Chat doc id. Must exist AND belong to the caller, otherwise 404. */
    chatId: string;
  };
  export type GetChatResponse = {
    chat: AiChat;
    /** Messages ordered by `sequence` ascending (oldest first). */
    messages: AiChatMessage[];
  };

  // DELETE /api/v1/chat/:chatId
  /** Path params for chat deletion. */
  export type DeleteChatParams = {
    /** Chat doc id. Must belong to the caller, otherwise 404. Deletes both
     *  the chat doc and every message belonging to it (single Firestore batch). */
    chatId: string;
  };
  export type DeleteChatResponse = {
    ok: true;
  };

  // --- Admin AI-review schemas ---
  // All three endpoints below are gated by `validateWebotAdmin` middleware
  // (returns 403 for non-admin emails). The authenticated request user is
  // ALWAYS the admin viewing — never the user being inspected. The user
  // being inspected is identified by URL path params.

  /**
   * Aggregated activity row for one user with at least one AI chat. Returned
   * by `GET /api/v1/chat/admin/users`, sorted by `lastMessageAt` descending.
   */
  export type AdminAiUserActivity = {
    /** dashboard-users-v2 doc id (the user's email). */
    userId: string;
    /** Display name from the user doc, if present. Falls back to `userId`. */
    ownerFullName?: string;
    /** User's profile photo URL (`photoURL` or `userImageUrl` on the user doc). */
    photoURL?: string;
    /** ISO8601 timestamp of the user's most recent assistant turn across all their chats. */
    lastMessageAt: string;
    /** Total number of AI chats this user has. */
    chatCount: number;
    /** Sum of `messageCount` across all of the user's chats. */
    totalMessages: number;
  };

  // GET /api/v1/chat/admin/users
  // Inputs: none (no path params, no query, no body).
  // Returned list is server-sorted by `lastMessageAt` desc and capped at 200 users.
  export type ListAdminAiUsersResponse = {
    users: AdminAiUserActivity[];
  };

  // GET /api/v1/chat/admin/users/:userId/chats?limit=50
  /** Path params for the per-user chat list. */
  export type ListAdminChatsForUserParams = {
    /** dashboard-users-v2 doc id (email) of the user being inspected. URL-encoded in the path segment. */
    userId: string;
  };
  /** Query string for the per-user chat list. */
  export type ListAdminChatsForUserQuery = {
    /** Page size cap. Server clamps to [1, 100]; defaults to 50. */
    limit?: number;
  };
  export type ListAdminChatsForUserResponse = {
    /** The user's chats, newest first (by `lastMessageAt`). */
    chats: AiChat[];
  };

  // GET /api/v1/chat/admin/chats/:chatId
  /** Path params for fetching a single chat + its messages. */
  export type GetAdminChatParams = {
    /** Chat doc id. Must exist in the `ai-chats` collection; admins can load chats belonging to any user. URL-encoded path segment. */
    chatId: string;
  };
  export type GetAdminChatResponse = {
    /** Chat doc as stored. */
    chat: AiChat;
    /** Messages ordered by `sequence` ascending — same shape the user-facing endpoint returns for the chat owner. */
    messages: AiChatMessage[];
  };

  // --- Per-user signature template counter ---
  // The "next document number" for a signature template is stored per-user,
  // not on the template doc, so favorited / shared / system templates can't
  // leak counter state across users. These endpoints are the ONLY way to
  // read or write it — the SignatureTemplate type intentionally has no
  // counter field.

  /** Single per-user counter slice — both numbering fields ship together so
   *  the UI and renderer never have to fetch them separately. */
  export type SignatureTemplateCounterDto = {
    /** Next number the allocator will hand out for the caller. Defaults to 1 when no counter doc exists yet. */
    nextDocumentNumber: number;
    /** Optional per-user prefix prepended to formatted numbers (e.g. "INV-"). Omitted when unset. */
    numberPrefix?: string;
  };

  // GET /api/v1/signatures/templates/user-counters
  // Inputs: none.
  // Returns every counter slice the caller has, keyed by templateId.
  // Templates without a counter doc are omitted; the caller defaults missing
  // entries to `{ nextDocumentNumber: 1 }`. Used by the templates-list page
  // to avoid an N+1 of per-template counter fetches.
  export type ListSignatureTemplateCountersResponse = {
    counters: Record<string, SignatureTemplateCounterDto>;
  };

  // GET /api/v1/signatures/templates/:templateId/user-counter
  /** Path params for the single-template counter read. */
  export type GetSignatureTemplateCounterParams = {
    /** Template doc id. Must be viewable by the caller (own / system / public / org-visible), otherwise 404. */
    templateId: string;
  };
  export type GetSignatureTemplateCounterResponse = SignatureTemplateCounterDto;

  // PUT /api/v1/signatures/templates/:templateId/user-counter
  /** Path params for the single-template counter write. */
  export type SetSignatureTemplateCounterParams = {
    /** Template doc id. Must be viewable by the caller, otherwise 404. Editing the counter does NOT require ownership — favoriters maintain their own sequence. */
    templateId: string;
  };
  export type SetSignatureTemplateCounterRequest = {
    /** Positive integer (>=1). The server rejects non-integers and values < 1 with HTTP 400. */
    nextDocumentNumber: number;
    /** Optional per-user prefix. Pass an empty string to clear; omit to leave the persisted prefix unchanged. */
    numberPrefix?: string;
  };
  export type SetSignatureTemplateCounterResponse = SignatureTemplateCounterDto;

  // --- Default templates per signing scenario ---
  // GET /api/v1/signatures/settings/default-templates
  // Inputs: none. Returns all three layers so the settings UI can show which
  // value is a personal override vs the Webot-wide default:
  //   - `global`: the Webot-admin-managed defaults every user inherits.
  //   - `user`: the caller's personal overrides (only keys they overrode).
  //   - `effective`: per-key user-over-global merge — what the send flow uses.
  export type GetSignatureDefaultTemplatesResponse = {
    global: SignatureDefaultTemplates;
    user: SignatureDefaultTemplates;
    effective: SignatureDefaultTemplates;
  };

  // PUT /api/v1/signatures/settings/default-templates        (personal overrides)
  // PUT /api/v1/signatures/settings/default-templates/global (Webot admin only)
  // Partial update: only the keys present in `defaultTemplates` are touched.
  // A string value sets that scenario's template id (must be viewable by the
  // caller for the personal endpoint; must exist for the global endpoint);
  // `null` clears the entry (for personal overrides that means "fall back to
  // the global default"). Unknown keys are rejected with HTTP 400.
  export type SetSignatureDefaultTemplatesRequest = {
    defaultTemplates: Partial<Record<SignatureDefaultTemplateKey, string | null>>;
  };
  // Both PUTs return the same three-layer shape as the GET, post-update.
  export type SetSignatureDefaultTemplatesResponse = GetSignatureDefaultTemplatesResponse;

  // --- Send a signature document ---
  // POST /api/v1/signatures/documents/:documentId/send
  //
  // The dashboard renders the template HTML, rasterizes it into a PDF in the
  // browser (see `signaturesHtmlToPdf.ts` in the dashboard repo), extracts
  // each field tag's bounding box during the same render, and POSTs the bundle
  // here. The server parses the field tags into `sentFields`, stores the sent
  // PDF, mints signing slugs, and serves the document through the native
  // signing ceremony. The HTML is persisted on the document for debugging only.
  //
  // The "render the PDF on the server" path (Playwright/Chromium) was
  // removed — the browser already had the same DOM rendered for preview,
  // and shipping a headless Chrome to Cloud Run cost more than it saved.

  /** Field area on the rendered PDF in normalized (fraction-of-page) format. */
  export type SignatureSendFieldArea = {
    /** Left edge, fraction of page width (0-1). */
    x: number;
    /** Top edge, fraction of page height (0-1). Origin is top-left. */
    y: number;
    /** Width, fraction of page width (0-1). */
    w: number;
    /** Height, fraction of page height (0-1). */
    h: number;
    /** 1-based page index. */
    page: number;
  };

  /** One signer field extracted from the rendered DOM. The shape matches the
   * dashboard's `signaturesHtmlToPdf.ts` `ExtractedField` — keep them in sync. */
  export type SignatureSendField = {
    /** Field name (used by the signer-side UI as the input identifier). */
    name: string;
    /** Field type — `text`, `signature`, `image`, etc. */
    type: string;
    /** Signer role name. Must match a role used in the document's signers[]. */
    role: string;
    /**
     * The template's flag, frozen at send. What the SIGNER must fill is
     * `isSignatureFieldRequiredForSigner` (an email field never), which is also what
     * the signing page is served here.
     */
    required: boolean;
    title?: string;
    description?: string;
    /** One area per (field instance, page). A field that spans multiple
     * pages emits multiple entries with the same name + different page. */
    areas: SignatureSendFieldArea[];
    /** HTML5 validation pattern enforced on the signer side (email fields). */
    validationPattern?: string;
    /** Custom message shown when `validationPattern` fails. */
    invalidMessage?: string;
    /** Locks the field so the signer can't edit it. Used by the signing-date field, which our engine stamps at compose time — NEVER mark a realtor-prefilled signer field readonly (the composer would stamp the signing date over it); a locked prefill is baked into the HTML and simply not emitted as a field tag. */
    readonly?: boolean;
    /** Pre-filled value (fallback for signing-date). NOTE: editable send-time prefills of signer fields do NOT ride this — they ride `signer.fieldValues`, echoed to the ceremony as `prefilledValues`, which seeds the inputs. */
    defaultValue?: string;
    /** Value font size in px (matches the HTML renderer's label/prefilled font). Used by the native composer to size stamped signer text; defaults to 14. */
    fontSize?: number;
    /** Horizontal alignment of the stamped value ('left' | 'center' | 'right'); defaults to 'right'. */
    align?: string;
    /** Date display format for date-typed fields (e.g. 'DD/MM/YYYY'); drives the native date picker's stored/stamped string. */
    dateFormat?: string;
  };

  /** Authenticated canonical renderer: generated HTML is compiled in pinned Chromium. */
  export type RenderSignatureDocumentRequest = { html: string };
  export type RenderSignatureDocumentResponse = {
    pdfBase64: string;
    fields: SignatureSendField[];
    totalPages: number;
    renderVersion: string;
    htmlSha256: string;
    pdfSha256: string;
    pageDimensions: Array<{ width: number; height: number }>;
  };

  // Aliases kept for call sites that use these names.
  export type SignatureFieldArea = SignatureSendFieldArea;
  export type SendSignatureFieldContract = SignatureSendField;

  // --- Native public signing ceremony (no auth; slug + OTP gated) ---

  /** Response of POST /public/documents/:slug/view — everything the native signing canvas needs. */
  export type PublicSigningContentResponse = {
    /** Exact PDF + geometry + identity revision; echo on submit. Missing only on old servers. */
    artifactRevision?: string;
    documentId: string;
    slug: string;
    status: SignatureDocumentStatus;
    /** Role name this signer fills (fields are pre-filtered to it server-side). */
    role: string;
    totalPages: number;
    /** Public URL of the sent (source) PDF (fallback / download). */
    sentPdfUrl: string;
    /** The exact self-contained HTML the signer's document was rendered from — the canvas displays this in an iframe. */
    sentHtml: string;
    /** Signer-fillable field geometry for this signer's role. */
    fields: SendSignatureFieldContract[];
    /** Values the realtor prefilled (already baked into the PDF; echoed for display). */
    prefilledValues: Record<string, string>;
    /** Disclosure version the signer must consent to. */
    consentDisclosureVersion: string;
    /**
     * True when this is a hidden-addresses document with an addresses
     * addendum the signer must ALSO sign after the main document. The
     * addendum's content is intentionally NOT included here — it reveals the
     * full addresses, so it's only returned by the main submit response.
     */
    hasAddendum?: boolean;
    /** True when the document carries a seller questionnaire the signer must fill AFTER the main signature. */
    hasQuestionnaire?: boolean;
    /**
     * Which ceremony phase this payload's html/fields belong to. 'addendum'
     * when the signer already signed the main document and only the
     * addresses addendum is left (resume-after-refresh) — the canvas then
     * submits to /submit-addendum. 'questionnaire' when only the seller
     * questionnaire is left — the canvas renders the coded form from
     * `questionnaire` (html/fields are empty) and submits to
     * /submit-questionnaire. Absent/'main' otherwise.
     */
    phase?: 'main' | 'addendum' | 'questionnaire';
    /** The questionnaire payload — present only when `phase` is 'questionnaire' (resume-after-refresh). */
    questionnaire?: PublicSellerQuestionnairePayload;
    /**
     * 'ownerQuestionnaire': the questionnaire is the whole document — `phase` is 'questionnaire' from
     * the first view, and its submit carries the consent and `artifactRevision`.
     */
    documentKind?: SignatureDocumentKind;
  };

  /**
   * Everything the coded seller-questionnaire form needs: the send-time
   * prefill snapshot (property + realtor) plus the per-signer owner prefill
   * (name/phone/email from the signer record; ת"ז + address prefer what the
   * signer just typed into the MAIN document's contact-bound fields, falling
   * back to the linked contact card). Returned by the submit that unlocks the
   * questionnaire phase (or by /view on resume) — never before the main
   * signature is on record.
   */
  export type PublicSellerQuestionnairePayload = {
    version: string;
    property: SellerQuestionnairePropertyDetails;
    realtor: SellerQuestionnaireRealtorDetails;
    /** Per-signer prefill for the "בעל הזכות בנכס" section. */
    owner: SellerQuestionnaireOwnerDetails;
    documentFormattedNumber: string;
    /** Customer-facing document title, shown above the questionnaire. */
    documentTitle: string;
  };

  /**
   * The addresses addendum payload the signing canvas renders as a SECOND
   * signing step. Returned by the main submit (never by /view) so the full
   * addresses are only revealed after the main document is signed.
   */
  export type PublicSigningAddendumPayload = {
    /** Exact addendum revision; echo on submit. Missing only on old servers. */
    artifactRevision?: string;
    /** Self-contained HTML of the addendum document (iframe-rendered). */
    html: string;
    /** Archived addendum PDF matching these fields. Optional for older API responses. */
    sentPdfUrl?: string;
    totalPages: number;
    /** Signer-fillable field geometry (the addendum signature field). */
    fields: SendSignatureFieldContract[];
  };

  /** Request body of POST /public/documents/:slug/submit. */
  export type PublicSignSubmitRequest = {
    /** Revision returned by /view. The server rejects missing/stale revisions with 409. */
    expectedArtifactRevision?: string;
    /** Keyed by field.name. signature/initials/image → base64 PNG data URLs; others → strings. */
    fieldValues: Record<string, string>;
    consentAccepted: boolean;
    consentDisclosureVersion: string;
    sessionId?: string;
    timezone?: string;
  };

  export type PublicSignSubmitResponse = {
    status: SignatureDocumentStatus;
    /** Origin-relative path to the branded viewer page (`/d/:documentId`) once every signer has signed and the PDF is composed; null otherwise. */
    signedDocumentUrl: string | null;
    /**
     * Present when the document carries an addresses addendum the signer
     * still has to sign: the main values were persisted, but the ceremony
     * isn't complete — the canvas must render this payload as a second
     * signing step and POST /public/documents/:slug/submit-addendum.
     */
    addendum?: PublicSigningAddendumPayload;
    /**
     * Present when the signer still has to fill + sign the seller
     * questionnaire (and no addendum precedes it): the canvas renders the
     * coded questionnaire form and POSTs
     * /public/documents/:slug/submit-questionnaire.
     */
    questionnaire?: PublicSellerQuestionnairePayload;
  };

  /** Request body of POST /public/documents/:slug/submit-addendum. */
  export type PublicSignSubmitAddendumRequest = {
    /** Revision returned with the addendum. The server rejects missing/stale revisions with 409. */
    expectedArtifactRevision?: string;
    /** Keyed by addendum field.name — the addendum signature as a base64 PNG data URL. */
    fieldValues: Record<string, string>;
    sessionId?: string;
    timezone?: string;
  };

  /** Request body of POST /public/documents/:slug/submit-questionnaire. */
  export type PublicSignSubmitQuestionnaireRequest = {
    answers: SellerQuestionnaireAnswers;
    /** The owner's questionnaire signature as a base64 PNG data URL. */
    signatureDataUrl: string;
    /**
     * A questionnaire-only document has no main step, so its electronic-signature consent and the
     * revision /view served ride this submit (both required there). Ignored after a main step, which
     * recorded both.
     */
    consentAccepted?: boolean;
    consentDisclosureVersion?: string;
    expectedArtifactRevision?: string;
    sessionId?: string;
    timezone?: string;
  };

  export type PublicSignDeclineRequest = {
    reason?: string;
    sessionId?: string;
    timezone?: string;
  };

  export type PublicSignDeclineResponse = { status: SignatureDocumentStatus };

  /**
   * Response of POST /public/otp/verify. `signerSession` is this browser's proof of the signer's phone,
   * for the document's owner; the sign page sends it back in the `X-Signer-Session` header on every
   * public signing call until `expiresAt` (server: signatureSignerSession.ts).
   */
  export type PublicSignOtpVerifyResponse = { signerSession: string; expiresAt: string };

  /**
   * Lean, non-sensitive metadata for the public completed-document viewer page
   * (`/d/:documentId`). Powers the branded page linked from completion SMS +
   * the dashboard. Only served for completed documents that have a signed PDF.
   */
  export type PublicDocumentViewMeta = {
    documentId: string;
    /** Customer-facing title (falls back to internal name / formatted number). */
    title: string;
    formattedNumber: string;
    status: SignatureDocumentStatus;
    completedAt?: string;
    /** Whether a standalone seller-questionnaire PDF is available (adds its download). */
    hasQuestionnaire?: boolean;
    /** The questionnaire's property was offered for this ('rent' names it שאלון בעל זכות בנכס). */
    questionnaireListedFor?: ListingListedFor;
    /** 'ownerQuestionnaire': the questionnaire IS the document — one file, no "signed document" tab. */
    documentKind?: SignatureDocumentKind;
  };

  /** Path params for the send endpoint. */
  export type SendSignatureDocumentParams = {
    /** Firestore id of the draft SignatureDocument to send. Must be owned
     * by the caller and in `status: 'draft'`; otherwise 4xx. */
    documentId: string;
  };

  /**
   * The addresses addendum as shipped by the send endpoints for a
   * hidden-addresses send. Mirrors the main document's payload shape (the
   * dashboard renders + rasterizes it through the same pipeline).
   */
  export type SignatureSendAddendum = {
    /** Self-contained rendered HTML of the addendum document. */
    html: string;
    /** Base64-encoded PDF produced by rasterizing `html` in the browser. */
    pdfBase64: string;
    /** Field geometry (the addendum signature field) extracted from the same DOM. */
    fields: SignatureSendField[];
    totalPages: number;
  };

  /** Request body for the send endpoint. All four base fields are required. */
  export type SendSignatureDocumentRequest = {
    /** Final HTML the dashboard rasterized — prefilled values inlined,
     * signer placeholders as `<*-field>` tags. Persisted on the
     * document as `sentHtml` for debugging. Server rejects empty strings
     * with HTTP 400. */
    html: string;
    /** Base64-encoded PDF produced by rasterizing `html` in the browser.
     * Stored as the sent PDF (reloaded at completion to stamp signer values)
     * and archived to Storage (URL is persisted on `sentPdfUrl`). Server
     * rejects empty strings with HTTP 400. */
    pdfBase64: string;
    /** Field positions extracted from the same DOM that produced the PDF.
     * Server validates this is an array but trusts the shape — bad shapes
     * surface later during rendering/validation, not as 400s here. */
    fields: SignatureSendField[];
    /** Number of pages in `pdfBase64`. Must be a positive integer; the
     * server rejects 0/negatives/non-integers with HTTP 400. */
    totalPages: number;
    /**
     * True for a hidden-addresses send. Requires `addendum` — the server
     * rejects `hideAddresses: true` without one (a redacted document whose
     * full addresses are never disclosed would be a broken ceremony).
     */
    hideAddresses?: boolean;
    /** The addresses addendum (required when `hideAddresses` is true). */
    addendum?: SignatureSendAddendum;
    /**
     * Whether signers must pass phone-OTP (SMS code) verification before
     * viewing/signing. Omitted/true = enforce (default). `false` = the sender
     * explicitly disabled OTP for this send.
     */
    otpRequired?: boolean;
    /**
     * Whether to message each signer their signing link on send. Omitted/true =
     * send WhatsApp + SMS (default). `false` = send NOTHING to the signer —
     * the sender delivers the link personally (share-link / per-signer
     * "resend WhatsApp" remain available afterwards). On the edit-resend
     * endpoint this also suppresses the "document was edited" SMS.
     */
    notifySigners?: boolean;
    /**
     * Attach the statutory seller questionnaire as a post-signing task. The
     * dashboard sends the property prefill (address is required — full, never
     * redacted); the server snapshots the realtor details from the sender's
     * profile and persists the combined {@link SignatureSellerQuestionnaire}.
     */
    sellerQuestionnaire?: SignatureSendSellerQuestionnaire;
    /**
     * Send-form inputs that only exist baked into the rendered HTML (additional
     * terms, per-source/per-listing overrides). Stored verbatim on the document
     * so the edit-after-send flow can reopen the form losslessly.
     */
    sendFormSnapshot?: SignatureSendFormSnapshot;
  };

  /** The seller-questionnaire prefill as shipped by the send endpoints. */
  export type SignatureSendSellerQuestionnaire = {
    /** Property prefill — `address` must be non-empty (the server rejects otherwise). */
    property: SellerQuestionnairePropertyDetails;
    /** The picked listing's sale/rent type. Any other value is dropped. */
    listedFor?: ListingListedFor;
  };

  /** Response is the updated SignatureDocument with `status: 'sent'`,
   * per-signer signing slugs populated, and the WhatsApp delivery results
   * recorded on each signer. */
  export type SendSignatureDocumentResponse = SignatureDocument;

  // --- Edit a SENT (but not yet signed) document ---
  // POST /api/v1/signatures/documents/:documentId/edit-resend
  // Allowed only while the document is `status: 'sent'` and NO signer has
  // signed (or declined) any part of it. Replaces the sent artifacts in place
  // (HTML / PDF / field geometry / hashes), PRESERVES each signer's signing
  // slug so previously-delivered links keep working, and notifies every
  // signer by SMS that the document was edited and is ready to sign.

  /** One signer update in an edit-resend. Matched to the existing signer by
   * `id`; provided identity fields replace the stored ones (slug / status
   * bookkeeping is preserved server-side). */
  export type EditSignatureDocumentSignerInput = {
    /** The existing signer's id (uuid). Unknown ids are rejected with 400. */
    id: string;
    name?: string;
    /** International format. Changing it redirects the "edited" SMS (and any
     * future OTP) to the new number. */
    phone?: string;
    email?: string;
    contactId?: string;
    /** Replaces the signer's prefilled field values (full map, not a merge). */
    fieldValues?: Record<string, string>;
  };

  /** Body: same rendered-artifact payload as the send endpoint, plus optional
   * metadata updates that normally ride the draft PATCH. */
  export type EditSignatureDocumentRequest = SendSignatureDocumentRequest & {
    signers?: EditSignatureDocumentSignerInput[];
    notes?: string;
    contactIds?: string[];
    listingIds?: string[];
  };

  export type EditSignatureDocumentParams = {
    /** Firestore id of the SENT SignatureDocument to edit. Must be owned by
     * the caller, `status: 'sent'`, native engine, and have zero signatures. */
    documentId: string;
  };

  /** Response is the updated SignatureDocument (still `status: 'sent'`) with
   * `editedAt`/`editCount` stamped and the refreshed sent artifacts. */
  export type EditSignatureDocumentResponse = SignatureDocument;

  // --- Signature template bundles ---
  // POST/GET/PATCH/DELETE /api/v1/signatures/bundles
  // Reusable named groupings of templates a realtor sends together.

  /** Subset of {@link SignatureTemplateBundle} returned by list/get endpoints. */
  export type SignatureBundleDto = {
    id: string;
    ownerUserId: string;
    organizationId?: string;
    name: string;
    customerFacingName?: string;
    description?: string;
    templateIds: string[];
    visibility?: SignatureTemplateVisibility;
    createdTime?: string;
    updatedTime?: string;
  };

  export type ListSignatureBundlesResponse = { bundles: SignatureBundleDto[] };

  export type GetSignatureBundleResponse = SignatureBundleDto;

  export type CreateSignatureBundleRequest = {
    name: string;
    customerFacingName?: string;
    description?: string;
    templateIds: string[]; // length >= 1, every id must be viewable by the caller
    visibility?: SignatureTemplateVisibility; // 'public' → 403 unless the caller is a Webot admin
  };

  // Owner may patch any field. Non-owners (Webot admins, or admins of the
  // bundle's organization) may send a VISIBILITY-ONLY patch — the marketplace
  // moderation path ("remove from marketplace" = visibility: 'private').
  // A patch that would leave the bundle 'public' is a 403 for a non-admin.
  export type UpdateSignatureBundleRequest = Partial<CreateSignatureBundleRequest>;

  /**
   * PUT /signatures/templates/marketplace-order — persist a drag & drop
   * reorder of one marketplace section. `organizationId` omitted targets the
   * Webot (system) section and requires a Webot admin; set, it targets that
   * organization's section and requires an admin of that org (or a Webot
   * admin). `orderedTemplateIds` is the section's template ids in the desired
   * display order; each id must belong to the targeted section.
   */
  export type ReorderMarketplaceTemplatesRequest = {
    organizationId?: string;
    orderedTemplateIds: string[];
  };

  /**
   * POST /api/v1/signatures/bundles/send — atomically (pre-create + parallel)
   * sends every template in the bundle to the same signer set.
   *
   * The dashboard renders one HTML/PDF per template (same renderer as the
   * single-doc send), deduping shared `(name, signerRoleId)` field values
   * client-side first. The per-document entries below must match the bundle's
   * `templateIds` exactly (same length, same order, same ids) — the server
   * rejects mismatches with HTTP 400.
   *
   * Exactly ONE of `bundleId` / `adHoc` must be set.
   */
  export type SendSignatureBundleRequest = {
    /** Send a persisted bundle. Mutually exclusive with `adHoc`. */
    bundleId?: string;
    /**
     * Send a set of templates together as ONE bundle without a persisted
     * bundle doc. Used by the send form's seller + exclusivity quick-pick,
     * which ships the seller template and the exclusivity template as a pair.
     *
     * The server mints a grouping id and tags every created document with it
     * (`bundleId` + `bundleNameSnapshot`), so the signer gets one message and
     * the sent list groups the docs exactly like a saved bundle — but nothing
     * is written to the bundles collection and the user's bundle list stays
     * clean. Every id must be viewable by the caller (checked per template,
     * same as the persisted path); ids are deduped preserving order.
     */
    adHoc?: {
      /** Templates to send together, in send order. Length >= 1. */
      templateIds: string[];
      /** Display name for the group — becomes `bundleNameSnapshot` on each doc and names the signer's message. */
      name: string;
    };
    contactIds: string[];
    listingIds: string[]; // union across the bundle's templates
    /** Bundle-level OTP policy — applies to EVERY document in the bundle. Omitted/true = enforce (default). */
    otpRequired?: boolean;
    /**
     * Whether to message each signer their signing link on send (WhatsApp +
     * SMS). Omitted/true = send (default). `false` = send NOTHING to the
     * signers — the sender delivers the links personally.
     */
    notifySigners?: boolean;
    signers: Array<{
      roleId: string;
      contactId?: string;
      name: string;
      phone: string;
      email?: string;
      fieldValues?: Record<string, string>;
    }>;
    notes?: string;
    /** One entry per templateId in bundle order. */
    perDocument: Array<{
      templateId: string;
      html: string;
      pdfBase64: string;
      fields: SignatureSendField[];
      totalPages: number;
      /** Hidden-addresses send for THIS template — same contract as the single-doc send. */
      hideAddresses?: boolean;
      addendum?: SignatureSendAddendum;
      /** Seller questionnaire for THIS template — same contract as the single-doc send. */
      sellerQuestionnaire?: SignatureSendSellerQuestionnaire;
      /**
       * Explicit document number for THIS document instead of the caller's
       * next auto number (positive integer). Same semantics as the single-doc
       * override: the per-user counter continues from
       * `max(currentNext, override + 1)`.
       */
      documentNumberOverride?: number;
    }>;
  };

  export type SendSignatureBundleResponse = {
    bundleId: string;
    /** Every document the bundle attempted to send. Failures stay in `status: 'draft'`. */
    documents: SignatureDocument[];
    /** Per-template send outcome — useful for the UI to surface partial failures. */
    perDocumentResults: Array<{
      templateId: string;
      documentId: string;
      status: 'sent' | 'failed';
      error?: string;
    }>;
  };

  /**
   * POST /api/v1/signatures/documents/:documentId/signers/:signerId/send-set: the automatic send of a
   * SET of contracts (one bundle preparation) as ONE message, one WhatsApp + one SMS carrying ONE link,
   * that of the first contract the client still has to sign; after the SMS code the sign page lists
   * every contract still waiting (Trello #231 item 6). A document outside any set is sent alone.
   * Every contract the message covers is stamped with that one delivery.
   */
  export type SendSignatureSetResponse = {
    /** Every contract of the set as stored after the send (one already signed is unchanged). */
    documents: SignatureDocument[];
    /** The contract whose link the message carries. */
    entryDocumentId: string;
    /** The contracts the message covers: those the client still had to sign. */
    coveredDocumentIds: string[];
    /** Per channel. WhatsApp `sent` means Meta accepted it, not that it arrived. */
    whatsapp: 'sent' | 'failed';
    sms: 'sent' | 'failed';
  };

  // --- Public sign page: pending docs slice ---
  // Returned by GET /api/v1/signatures/public/sender-by-slug/:slug whenever
  // the signer this slug points at has already passed OTP (otpVerifiedAt is
  // set on the signer record). The verify path fans the stamp out across
  // every same-phone+same-owner pending signer, so any one OTP unlocks
  // visibility of all sibling docs.
  //
  // Intentionally excludes any bundle metadata: bundles are an internal
  // organisational concept for the realtor. The signer just sees "documents
  // to sign" and their names — they should never see "this is part of
  // bundle X" terminology.
  export type PendingDocSlice = {
    documentId: string;
    /** This signer's slug for THIS document (used to open the signing page). */
    slug: string;
    templateNameSnapshot: string;
    templateCustomerFacingNameSnapshot?: string;
    documentFormattedNumber: string;
    status: SignatureDocumentStatus;
    /** Which engine serves THIS doc — currently always the native engine. */
    signingEngine?: SignatureSigningEngine;
    /**
     * When THIS signer was sent this document — i.e. their "received" time —
     * as an ISO string. Falls back to the document's creation time when the
     * signer has no recorded send time (legacy docs). The sign page renders
     * it in small grey text and orders the multi-doc inbox by it, newest
     * received first.
     */
    receivedAt?: string;
    /**
     * What this signer is actually waiting on for THIS doc:
     *   - 'main' (or absent): the document itself still needs their signature.
     *   - 'addendum': main is signed; the addresses confirmation is pending.
     *   - 'questionnaire': main is signed; the seller questionnaire is pending.
     * Lets the inbox present post-signing tasks distinctly ("fill the seller
     * questionnaire") instead of a generic "sign" row.
     */
    pendingPhase?: 'main' | 'addendum' | 'questionnaire';
    /** The questionnaire's property was offered for this ('rent' names it שאלון בעל זכות בנכס). */
    questionnaireListedFor?: ListingListedFor;
    /** 'ownerQuestionnaire': the questionnaire is the whole document — nothing was signed before it. */
    documentKind?: SignatureDocumentKind;
  };

  /**
   * One signed document for the signer's (phone, ownerUserId) tuple. Returned
   * by `GET /signatures/public/sender-by-slug/:slug` alongside `pendingDocs`
   * and gated on the same `otpVerifiedAt` check — lets the sign page surface
   * "previously signed" docs with a download link without a second round-trip.
   * `signedDocumentUrl` is null until the document is fully completed and its
   * signed PDF composed (in a multi-signer doc a signer can be done while the
   * PDF is only produced once the LAST signer finishes), so a signer's own
   * completed doc may briefly appear here without a downloadable URL.
   */
  export type SignedDocSlice = {
    documentId: string;
    templateNameSnapshot: string;
    templateCustomerFacingNameSnapshot?: string;
    documentFormattedNumber: string;
    signedAt?: string;
    /** Origin-relative path to the branded viewer page (`/d/:documentId`), or null until composed. */
    signedDocumentUrl: string | null;
  };

  // --- Admin: cross-customer signature document audit ---
  // Query params for GET /api/v1/signatures/admin/documents (admin-gated).
  // All optional. `status` accepts a single SignatureDocumentStatus, or the
  // literal 'all' to disable status filtering; when omitted, the server defaults
  // to the "non-draft" set (sent/partial/completed/declined/expired). `cursor`
  // is the ISO `createdTime` of the last row from the previous page.
  export type AdminListSignatureDocumentsRequest = {
    ownerUserId?: string;
    status?: SignatureDocumentStatus | 'all';
    dateFrom?: string; // ISO
    dateTo?: string; // ISO
    search?: string;
    includeArchived?: boolean;
    limit?: number;
    cursor?: string; // ISO createdTime
  };

  export type AdminListSignatureDocumentsResponse = {
    documents: SignatureDocument[];
    nextCursor: string | null;
  };

  // --- Office admin: a read-only view of the agents' signature documents ---
  // GET /api/v1/signatures/org/documents*, for a caller listed in `dashboard-orgs.admins`, over the
  // documents owned by the members of the offices they administer.
  //
  // Both shapes are ALLOWLISTS on purpose: a field added to SignatureDocument later must not reach a
  // colleague by default. Deliberately absent: `signers[].signing` (an OTP-waived signing link is a
  // credential to sign as the customer), the owner's notes, the send-form snapshot, the rendered HTML
  // and every storage URL. What the signer typed and the audit trail are absent from this JSON too, but
  // that is not a secrecy boundary: the admin may download the signed PDF and the audit certificate,
  // which print them. The files are served by the authenticated /org/documents/:documentId/* endpoints.
  export type OrgSignatureDocumentSigner = Pick<
    SignatureDocumentSigner,
    | 'id'
    | 'roleId'
    | 'name'
    | 'phone'
    | 'email'
    | 'status'
    | 'sentAt'
    | 'viewedAt'
    | 'signedAt'
    | 'declinedAt'
    | 'otpVerifiedAt'
    | 'mainSignedAt'
    | 'addendumSignedAt'
    | 'questionnaireSignedAt'
  > & {
    /** Delivery state only. The provider error text and message id stay with the owner. */
    whatsappDelivery: Pick<SignatureWhatsappDelivery, 'status' | 'attemptCount' | 'lastAttemptAt'>;
    /** Set when the owner waived the OTP in person for this signer. The authorizer and the IP are not sent. */
    inPersonOtpSkip?: { at: Date };
  };

  export type OrgSignatureDocument = Pick<
    SignatureDocument,
    | 'id'
    | 'createdTime'
    | 'updatedTime'
    | 'ownerUserId'
    | 'templateId'
    | 'templateNameSnapshot'
    | 'templateCustomerFacingNameSnapshot'
    | 'documentNumber'
    | 'formattedNumber'
    | 'contactIds'
    | 'listingIds'
    | 'signingEngine'
    | 'sentPdfTotalPages'
    | 'hideAddresses'
    | 'otpRequired'
    | 'status'
    | 'sentAt'
    | 'completedAt'
    | 'editedAt'
    | 'editCount'
    | 'bundleId'
    | 'bundleSendOrder'
    | 'bundleNameSnapshot'
  > & {
    signers: OrgSignatureDocumentSigner[];
    /** The document as it was sent, before any signature (GET .../prepared-pdf). */
    hasPreparedPdf: boolean;
    /** The final signed PDF (GET .../signed-pdf). A `partial` document serves a "signed so far" interim instead. */
    hasSignedPdf: boolean;
    /** The audit certificate (GET .../signed-pdf?type=audit). */
    hasAuditCertificate: boolean;
    /** The document carries the seller questionnaire as a post-signing task. */
    hasSellerQuestionnaire: boolean;
    /** The completed seller questionnaire exists as its own PDF (GET .../signed-pdf?type=questionnaire). */
    hasSellerQuestionnaireFile: boolean;
    /** The questionnaire's property was offered for this ('rent' names it שאלון בעל זכות בנכס). */
    sellerQuestionnaireListedFor?: ListingListedFor;
    /** 'ownerQuestionnaire': the questionnaire IS the document (`hasSignedPdf` is that questionnaire). */
    documentKind?: SignatureDocumentKind;
  };

  // Query params for GET /api/v1/signatures/org/documents. Drafts and archived documents are never
  // returned by this surface, whatever the filters.
  export type OrgListSignatureDocumentsRequest = {
    /** One of the offices the caller administers. Omitted = every office they administer. */
    orgId?: string;
    /** Narrow to one agent, who must be a member of an office in scope. */
    ownerUserId?: string;
    status?: Exclude<SignatureDocumentStatus, 'draft'>;
    templateId?: string;
    dateFrom?: string; // ISO, on createdTime
    dateTo?: string; // ISO, on createdTime
    /** Default 50, at most 100. A page may come back shorter than this with a non-null `nextCursor`. */
    limit?: number;
    /** `<ISO createdTime>|<documentId>` of the last row the server EXAMINED for the previous page. */
    cursor?: string;
  };

  export type OrgListSignatureDocumentsResponse = {
    documents: OrgSignatureDocument[];
    nextCursor: string | null;
    /** The agents in scope (the roster of the administered offices), for the agent filter. */
    ownerUserIds: string[];
  };

  export type OrgGetSignatureDocumentResponse = OrgSignatureDocument;

  // --- Listings search (mounted at /api/v1/listings-search) -----------------
  // Postgres-backed replacement for the browser-side Algolia queries. One
  // request replaces the old per-(origin × location) Algolia fan-out; per-user
  // webot ownership/org scoping is enforced server-side (never a client key).

  /** A location filter entry. A canonical id (from the locations autocomplete) beats the raw strings; raw strings are the fallback for manually-typed addresses. */
  export type ListingsSearchLocation = {
    city?: string;
    neighborhood?: string;
    street?: string;
    canonicalLocationId?: string;
    canonicalLevel?: 'city' | 'neighborhood' | 'street';
  };

  export type ListingsSearchSortField =
    | 'updatedTime'
    | 'price'
    | 'roomsCount'
    | 'squareMeter'
    | 'listingPublishedTime'
    | 'createdTime';

  export type ListingsSearchSort = {
    field: ListingsSearchSortField;
    direction: 'asc' | 'desc';
  };

  /** POST /search — body. `listingListedFor: 'sale'` matches docs whose field is missing (parity with the dashboard's historical `NOT rent` semantics). */
  export type ListingsSearchRequest = {
    listingListedFor: ListingListedFor;
    searchTerm?: string;
    /**
     * How `searchTerm` is read. 'anywhere' (the default): every word anywhere in the listing's
     * text, description included, relaxing to any word on zero hits. 'address': the listing
     * PICKER's reading — every word in the street / neighbourhood / city, quote marks ignored,
     * a number is a house number that ranks, no relaxation, best address match first (server
     * `addressSearchTerm.ts`, Trello #254).
     */
    searchTermMatch?: 'anywhere' | 'address';
    locations?: ListingsSearchLocation[];
    listingOrigins?: ListingOrigin[];
    minRooms?: number;
    maxRooms?: number;
    minPrice?: number;
    maxPrice?: number;
    /**
     * Bring listings with no price back INTO an active budget (they are excluded by
     * default, and a "no price" listing is one whose price is 0 — Yad2's מחיר לא צוין —
     * or, in one row, NULL). Ignored when neither bound is set: it modifies the price
     * filter, it is not a filter of its own.
     */
    includeListingsWithoutPrice?: boolean;
    minFloor?: number;
    maxFloor?: number;
    minParkingSpots?: number;
    maxParkingSpots?: number;
    /**
     * Built area (שטח בנוי) in m², inclusive. A listing that states no size (0.2% of the market
     * mirror, 9% of a realtor's own listings) matches neither bound and drops out of a sized search.
     */
    minSquareMeter?: number;
    maxSquareMeter?: number;
    propertyTypes?: string[];
    adTypes?: string[];
    agencyNames?: string[];
    sellerNames?: string[];
    /** Keys validated server-side against the allowed amenity set (hasElevator, hasBalcony, ...). */
    booleanFilters?: Record<string, boolean>;
    isAssetExclusive?: boolean;
    onlyPriceChanged?: boolean;
    /** Hide third-party listings not re-seen by the scrape in the last 3 days. webot (own) listings are exempt. */
    hidePotentiallyRemovedListings?: boolean;
    /** Published-date window, epoch ms: matches `listingPublishedTime OR createdTime` within the range. */
    publishedFromMs?: number;
    publishedToMs?: number;
    /** Server-side global sort. Defaults to updatedTime desc. */
    sort?: ListingsSearchSort;
    /** Page size, capped at 300. Defaults to 100. */
    maxResults?: number;
    page?: number;
  };

  /** A hit is the stored listing doc; Date fields arrive as epoch-ms numbers (same wire shape Algolia hits had). */
  export type ListingsSearchHit = Partial<ProcessedListingData> & { id: string };

  export type ListingsSearchResponse = {
    hits: ListingsSearchHit[];
    /** Exact below the 10,000 cap; equals the cap when `totalHitsCapped` (render as "10,000+"). */
    totalHits: number;
    /** True when the true total is at or above the cap — counting every match cost +666ms on the default feed. */
    totalHitsCapped?: boolean;
    page: number;
    pageSize: number;
    /** How the free-text term was applied: all words / any word (allOptional fallback) / dropped entirely. Absent when no searchTerm. */
    searchTermMode?: 'all' | 'any' | 'none';
  };

  /** GET /facets/property-types */
  export type ListingsFacetValue = {
    value: string;
    count: number;
  };

  export type ListingsPropertyTypesFacetResponse = {
    values: ListingsFacetValue[];
  };

  /** GET /facets/agency-names?query=&origin= and /facets/seller-names?query=&origin= */
  export type ListingsNamesFacetResponse = {
    names: string[];
  };

  // ===================== LOCATIONS (public autocomplete) =====================
  // GET /api/v1/locations/autocomplete — the canonical registry-backed replacement
  // for Algolia location suggestions. Wire-compatible with the picker: a suggestion
  // carries city / neighborhood? / street? (absent fields absent, not empty) plus
  // additive ids the picker ignores.
  export type LocationSuggestionLevel = 'city' | 'neighborhood' | 'street';
  export type LocationSuggestion = {
    city?: string;
    neighborhood?: string;
    street?: string;
    locationId: string;
    level: LocationSuggestionLevel;
    cityCode: number;
    listingCount: number;
    parentId?: string;
    coords?: { lat: number; lon: number };
    /**
     * Set only on street suggestions produced under `expandStreetNeighborhoods`:
     * the canonical id of the `neighborhood` this expanded entry carries. A
     * street suggestion NEVER carries `neighborhood` outside that opt-in — the
     * default wire contract (street = city + street, no neighborhood) is what
     * every search/alert picker and filter builder relies on.
     */
    neighborhoodId?: string;
    /**
     * DISPLAY ONLY. Street suggestions: the neighborhoods this street runs
     * through, best-first, for rendering as secondary text under the street name.
     *
     * Deliberately NOT `neighborhood`: a `neighborhood` on the default wire would
     * narrow every raw-fallback search filter into a street∧neighborhood
     * conjunction and change what alert pickers persist. This field names what the
     * street runs through; it never becomes part of what a pick MEANS, and the
     * picker maps only city/neighborhood/street/coords/ids into a picked location.
     */
    hoodNames?: string[];
    /** DISPLAY ONLY: how many neighborhoods the street runs through in total. */
    hoodCount?: number;
  };
  export type LocationsAutocompleteResponse = { suggestions: LocationSuggestion[] };

  // ===================== CANONICAL LOCATIONS ADMIN =====================
  // Explorer + corrections for the canonical location registry.
  // Endpoints under /api/v1/admin/locations (validateWebotAdmin).

  export type LocationLevel = 'city' | 'neighborhood' | 'street';
  export type LocationOverrideOp =
    | 'add-alias'
    | 'merge'
    | 'create'
    | 'set-parent'
    | 'set-display-name'
    | 'hide';

  // One canonical place, in full detail (aliases + parent + ids + spatial).
  export type AdminLocationNode = {
    id: string;
    level: LocationLevel;
    cityCode: number;
    displayName: string;
    displayCity: string;
    aliases: string[];
    parentId?: string;
    parentName?: string;
    listingCount: number;
    sources?: string[]; // which platforms have listings here: yad2 / madlan / webot / facebook
    semelRechov?: number;
    yad2HoodId?: string;
    yad2StreetId?: string;
    nadlanHoodId?: string;
    registryHoodCode?: number;
    hasSpatialSupport?: boolean;
    hidden?: boolean;
    mergedInto?: string;
    mergedIntoName?: string;
    coords?: { lat: number; lon: number };
  };

  export type AdminLocationOverviewResponse = {
    registryVersion: string;
    builtAt: string;
    counts: { cities: number; neighborhoods: number; streets: number };
    overridesCount: number;
    issuesCount: number;
  };

  export type AdminLocationCity = {
    id: string;
    cityCode: number;
    name: string;
    neighborhoodCount: number;
    streetCount: number;
    listingCount: number;
    hidden: boolean;
  };
  export type AdminLocationCitiesResponse = { cities: AdminLocationCity[]; total: number };

  export type AdminLocationCityDetailResponse = {
    city: AdminLocationNode;
    neighborhoods: AdminLocationNode[];
    streetCount: number;
    streetsSample: AdminLocationNode[];
    spatialCellCount: number;
  };

  export type AdminLocationDetailResponse = {
    location: AdminLocationNode;
    children: AdminLocationNode[];
  };
  export type AdminLocationSearchResponse = { results: AdminLocationNode[] };

  export type AdminLocationResolvePreviewRequest = {
    city?: string;
    neighborhood?: string;
    street?: string;
    coords?: { lat: number; lon: number };
    yad2CityId?: string;
    yad2HoodId?: string;
    yad2StreetId?: string;
  };
  export type AdminLocationResolveBrief = {
    id: string;
    name: string;
    cityCode: number;
    level: LocationLevel;
  } | null;
  export type AdminLocationResolvePreviewResponse = {
    city: AdminLocationResolveBrief;
    neighborhood: AdminLocationResolveBrief;
    street: AdminLocationResolveBrief;
    method: string;
    confidence: number;
    flags: string[];
    spatialAlternative: AdminLocationResolveBrief;
  };

  export type AdminLocationDuplicateIssue = {
    type: 'duplicate-neighborhood';
    cityCode: number;
    cityName: string;
    name: string;
    members: Array<{
      id: string;
      displayName: string;
      listingCount: number;
      parentId?: string;
      hidden?: boolean;
    }>;
    totalListings: number;
  };
  export type AdminLocationIssuesResponse = { issues: AdminLocationDuplicateIssue[] };

  export type AdminLocationSampleListing = {
    id: string;
    address: { city?: string; neighborhood?: string; street?: string };
    price: number | null;
    origin: string | null;
    method: string | null;
    confidence: number | null;
    flags: string[];
  };
  export type AdminLocationListingsResponse = {
    listings: AdminLocationSampleListing[];
    // 'stamp' = matched by canonical id; 'address' = matched by raw text (pre-backfill); 'none' = nothing found.
    via?: 'stamp' | 'address' | 'none';
    sources?: string[]; // distinct origins present in the sample (yad2 / madlan)
    indexed: boolean;
    note?: string;
  };

  // Reverse lookup: "where does this wording map?" — one text, matched across every
  // master location (any level), each with example listings that carry it.
  export type AdminLocationLookupMatch = {
    node: AdminLocationNode;
    examples: AdminLocationSampleListing[];
    via: 'stamp' | 'address' | 'none';
    exampleSources: string[]; // distinct origins among the examples
  };
  export type AdminLocationLookupResponse = { matches: AdminLocationLookupMatch[] };

  // Source spelling → master mapping, scoped to one city (optionally one source).
  export type AdminLocationSourceMappingRow = {
    text: { city?: string; neighborhood?: string; street?: string }; // raw source spelling
    count: number; // listings sharing this exact spelling in the sample
    sources: string[]; // which platforms used this spelling
    master: {
      city: AdminLocationResolveBrief;
      neighborhood: AdminLocationResolveBrief;
      street: AdminLocationResolveBrief;
    };
    fullyMapped: boolean; // every non-empty source field found a master
  };
  export type AdminLocationSourceMappingsResponse = {
    cityCode: number;
    city: string;
    source: string | null;
    scanned: number; // how many listings backed the table (sampled)
    rows: AdminLocationSourceMappingRow[];
  };

  export type LocationOverride = {
    id: string;
    op: LocationOverrideOp;
    locationId: string;
    alias?: string;
    targetId?: string;
    cityCode?: number;
    parentId?: string | null;
    displayName?: string;
    note?: string;
    createdBy: string;
    createdAt: string;
    // Resolved names of the referenced ids (change-log readability). Absent if an id no longer resolves.
    locationName?: string; // the correction's own target (the created master for `create`)
    targetName?: string; // merge destination
    parentName?: string; // set-parent target
    cityName?: string; // create's city
  };
  export type AdminLocationOverridesResponse = { overrides: LocationOverride[] };
  export type CreateLocationOverrideRequest = {
    op: LocationOverrideOp;
    locationId: string;
    alias?: string;
    targetId?: string;
    cityCode?: number;
    parentId?: string | null;
    displayName?: string;
    note?: string;
  };
  export type CreateLocationOverrideResponse = { override: LocationOverride };

  // --- Unresolved queue + resolution provenance ------------------------------
  // Backed by the Postgres listings mirror (the only store holding the canonical
  // stamp next to the raw address text). One row is one distinct SPELLING, not one
  // listing: a single correction closes the whole group.

  export type AdminUnresolvedRow = {
    id: string; // `city|neighborhood|street` — the spelling IS the identity
    city: string | null;
    neighborhood: string | null;
    street: string | null;
    origins: string[];
    listingCount: number;
    sampleListingIds: string[];
    coords?: { lat: number; lon: number };
  };
  export type AdminUnresolvedQueueResponse = {
    rows: AdminUnresolvedRow[];
    totalListings: number;
    scope: { city: string | null; origin: string | null };
  };

  export type AdminUnresolvedCandidate = {
    id: string;
    level: string;
    displayName: string;
    listingCount: number;
    parentId?: string;
    parentName?: string;
    reason: 'shared-token' | 'spatial' | 'parent-of-spatial';
  };
  export type AdminUnresolvedDetailResponse = AdminUnresolvedRow & {
    // What the CURRENT resolver makes of the spelling. `alreadyFixed` means the
    // stored stamp is merely stale — a re-stamp fixes it, no override needed.
    recheck: {
      cityId?: string;
      cityName?: string;
      neighborhoodId?: string;
      neighborhoodName?: string;
      streetId?: string;
      streetName?: string;
      method: string;
      confidence: number;
      flags: string[];
      unresolvedFields: string[];
      alreadyFixed: boolean;
    };
    // Empty is a real signal: no partial match anywhere in the city means the place
    // is genuinely absent from the registry, so the answer is `create`, not a match.
    candidates: AdminUnresolvedCandidate[];
  };

  export type AdminLocationProposalAction = 'add-alias' | 'merge' | 'create' | 'none';
  export type AdminLocationProposal = {
    id: string;
    spelling: { city?: string; neighborhood?: string; street?: string };
    field: 'neighborhood' | 'street';
    action: AdminLocationProposalAction;
    targetId?: string;
    targetName?: string;
    alias?: string;
    displayName?: string;
    parentId?: string;
    parentName?: string;
    confidence: number;
    rationale: string;
    citations?: string[];
    listingCount: number;
    model: string;
    createdAt: string;
    status: 'proposed' | 'applied' | 'dismissed';
    appliedOverrideId?: string;
    reviewedBy?: string;
  };
  export type AdminLocationProposeResponse = {
    proposals: AdminLocationProposal[];
    skipped: string[];
  };
  export type AdminLocationProposalsResponse = { proposals: AdminLocationProposal[] };

  // How every listing's canonical location was reached. `byOverrideAuthor` is what
  // answers "what did the AI decide" — a listing counts there when its match only
  // exists because of an AI-authored correction.
  export type AdminLocationProvenanceResponse = {
    byMethod: Record<string, number>;
    byOverrideAuthor: Record<string, number>;
    byFlag: Record<string, number>;
    unresolved: number;
    total: number;
  };
  export type AdminLocationProvenanceListing = {
    id: string;
    address: { city?: string; neighborhood?: string; street?: string };
    origin: string;
    canonical: CanonicalLocationStampData | null;
  };
  export type AdminLocationProvenanceListingsResponse = {
    listings: AdminLocationProvenanceListing[];
  };

  // GET /api/v1/agency-offer — the caller's live agency-ambassador offer, or null.
  export type AgencyOfferResponse = {
    offer: { campaignId: string; agencyName: string; firstName: string; expiresAt: string; interestedAt: string | null } | null;
  };
  // POST /api/v1/agency-offer/interest — "I'm interested". Idempotent: one Slack lead per customer per campaign.
  export type AgencyOfferInterestRequest = { source: 'dashboard_widget' };
  export type AgencyOfferInterestResponse = { alreadyRegistered: boolean };

  // ---------------------------------------------------------------------------------------------
  // The realtor's public site (`app.webot.co.il/r/<alias>`), served from /api/v1/public/realtor-site
  // (server: services/realtorSite/*; docs/rules/client-portal.md). Read by an anonymous page, so a
  // projection here never carries an email, a seller or owner, a coordinate, a house number, a fee,
  // an internal date or an internal id other than the listing's own.
  // ---------------------------------------------------------------------------------------------

  export type PublicRealtorProfile = {
    alias: string;
    name: string;
    avatarUrl?: string;
    companyName?: string;
    companyLogoUrl?: string;
    /** The realtor's own phone, international format, as their minisites already show it. */
    phone?: string;
    license?: string;
    tagline?: string;
    /** A markdown subset. Render it as nodes, never as HTML. */
    about?: string;
    instagramUrl?: string;
    facebookUrl?: string;
    officeAddress?: string;
    theme: { color?: string; textColor?: string; heroPattern?: MinisiteHeroPattern; heroImageUrl?: string };
    madlanTopAgent?: 'top_10' | 'top_10_gold';
    madlanAgentUrl?: string;
    metaPixelId?: string;
  };

  /** A place the realtor's listings on the site are in: a city, or a city and neighbourhood. */
  export type PublicRealtorArea = { city: string; neighborhood?: string; listings: number };

  /** GET /api/v1/public/realtor-site/:alias */
  export type PublicRealtorSiteResponse = {
    realtor: PublicRealtorProfile;
    counts: { sale: number; rent: number };
    /** Most listings first. */
    areas: PublicRealtorArea[];
    /** Whether `/r/<alias>/join` takes registrations. */
    intakeOpen: boolean;
  };

  export type PublicListingAmenity =
    | 'protectedSpace'
    | 'protectedFloorSpace'
    | 'shelter'
    | 'parking'
    | 'elevator'
    | 'terrace'
    | 'garden'
    | 'storage'
    | 'airConditioning'
    | 'accessible'
    | 'bars'
    | 'housingUnit'
    /** A minisite's market listing that says it is renovated (`isRenovated`); the site tells it by `condition`. */
    | 'renovated';

  /** Where a listing is, as the realtor chose to tell it (`addressDisplayMode`). Never a house number. */
  export type PublicListingPlace = { city: string; neighborhood?: string; street?: string };

  export type PublicListingCard = {
    id: string;
    deal: 'sale' | 'rent';
    status: 'onSale' | 'inNegotiation';
    price: number | null;
    rooms: number | null;
    sizeM2: number | null;
    floor: number | null;
    floorOutOf: number | null;
    propertyType: ListingType | null;
    purpose: PurposeType | null;
    place: PublicListingPlace;
    /** The first few photos, after the realtor's presentation and the watermark-free swap. */
    photos: string[];
    photoCount: number;
    /** On the site for less than two weeks. */
    isNew: boolean;
    amenities: PublicListingAmenity[];
  };

  /** GET /api/v1/public/realtor-site/:alias/listings */
  export type PublicRealtorListingsResponse = { listings: PublicListingCard[] };

  export type PublicListingDetail = PublicListingCard & {
    /** The realtor's presentation text first, else the listing's own. */
    description?: string;
    condition?: ListingState;
    plotM2?: number;
    gardenM2?: number;
    terraceM2?: number;
    parkingSpots?: number;
    /**
     * `month` is `YYYY-MM`, present with `type: 'specificDate'` only. A minisite's market listing also
     * carries the day itself (`day`, `YYYY-MM-DD` on the Israel calendar), as the dashboard's page showed it.
     */
    entrance?: { type: EvictionType; month?: string; day?: string };
    videoUrl?: string;
  };

  /** GET /api/v1/public/realtor-site/:alias/listings/:listingId */
  export type PublicRealtorListingResponse = { listing: PublicListingDetail };

  /**
   * What a site page carries in its own HTML (`<script id="webot-page-data" type="application/json">`),
   * so it shows itself without a request: the API's own answers for this page and alias. Absent parts
   * are fetched as before.
   */
  export type PublicRealtorPageData = {
    alias: string;
    site?: PublicRealtorSiteResponse;
    listings?: PublicRealtorListingsResponse;
    listing?: PublicRealtorListingResponse;
  };

  // ---------------------------------------------------------------------------------------------
  // A buyer's minisite (`app.webot.co.il/myListings/<id>`): the listings a realtor picked for ONE customer,
  // served by the client portal from /api/v1/public/minisite (server: services/minisitePublic/*;
  // docs/rules/client-portal.md, "The minisite"). The id in the address is the page's only credential, so a
  // projection here never carries the realtor's email, the buyer's name or phone, a seller, a coordinate, a
  // house number or the realtor's notes.
  // ---------------------------------------------------------------------------------------------

  /** The realtor who made the page: the site's own projection, without an alias (a minisite is not the site). */
  export type PublicMinisiteRealtor = Omit<PublicRealtorProfile, 'alias'>;

  /** GET /api/v1/public/minisite/:minisiteId */
  export type PublicMinisiteResponse = {
    realtor: PublicMinisiteRealtor;
    /** Newest on the market first, with the realtor's hidden photos gone and the clean copies swapped in. */
    listings: PublicMinisiteListingCard[];
    /** What the buyer marked on the page, by listing id. */
    liked: string[];
    disliked: string[];
    /**
     * Lets the realtor's OWN browser recognise the page (`MINISITE_OWNER_KEY_*`; the dashboard keeps the
     * signed-in account's key): their visits are not counted, and the page offers them its editor. A hash,
     * never the email.
     */
    ownerKey: string;
  };

  /** An AI makeover of one of the listing's photos (`url`), beside the photo it was made from. */
  export type PublicMinisiteMakeover = { url: string; beforeUrl: string };

  /** A minisite's card: the site's card, and the opening of the text the realtor shows this buyer. */
  export type PublicMinisiteListingCard = PublicListingCard & { blurb?: string };

  export type PublicMinisiteListingDetail = PublicListingDetail & {
    /** The ad's own short labels (Yad2's "מרפסת שמש"), as written. */
    tags?: string[];
    /** The ad's own words for its type and condition ("טריפלקס", "חדש (גרו בנכס)"), as the dashboard's page showed them. */
    propertyTypeText?: string;
    conditionText?: string;
    /** The ad's "פרטי מיקום" text. */
    locationDetails?: string;
    makeovers: PublicMinisiteMakeover[];
  };

  /** GET /api/v1/public/minisite/:minisiteId/listings/:listingId */
  export type PublicMinisiteListingResponse = { listing: PublicMinisiteListingDetail };

  /** `none` takes the buyer's mark off (a second tap on the same thumb). */
  export type PublicMinisiteReaction = 'like' | 'dislike' | 'none';

  /** POST /api/v1/public/minisite/:minisiteId/reaction: the buyer's thumb on one of the page's listings. */
  export type PublicMinisiteReactionRequest = {
    listingId: string;
    reaction: PublicMinisiteReaction;
  };
  export type PublicMinisiteReactionResponse = { liked: string[]; disliked: string[] };

  /** POST /api/v1/public/minisite/:minisiteId/view: the page (no `listingId`) or one of its listings was shown. */
  export type PublicMinisiteViewRequest = { listingId?: string };

  /** What a minisite page carries in its own HTML (the same `webot-page-data` block as the realtor's site). */
  export type PublicMinisitePageData = {
    minisiteId: string;
    minisite?: PublicMinisiteResponse;
    listing?: PublicMinisiteListingResponse;
  };

  /**
   * What a signing page carries in its own HTML (`<script id="webot-page-data">` in `/sign/<slug>` and
   * `/d/<documentId>`, served by the server: docs/rules/signatures.md, "The pages come from the server").
   * `sender` is the public sender lookup's answer as a browser WITHOUT a signer session gets it (the sign
   * page's own `PublicSender`); a browser that holds a session asks again with it.
   */
  export type SignaturePageData =
    | { signSlug: string; sender: Record<string, unknown> }
    | { documentId: string; document: PublicDocumentViewMeta };

  // --- Public locations: the registration form's area step, /api/v1/public/locations ---------------

  /** `id` is the registry id a registration sends back (never assemble one from the code). */
  export type PublicCity = { id: string; code: number; name: string };

  export type PublicArea = {
    id: string;
    name: string;
    /** 0-3, bucketed from how many listings the place has had. Never the raw count. */
    popularity: 0 | 1 | 2 | 3;
    /** Has never had a listing. Still a valid wish. */
    empty: boolean;
    /** The wider neighbourhood containing this one. */
    parentId?: string;
    /** Up to five neighbourhoods whose centres are within about 2.5 km, nearest first. */
    near: string[];
  };

  /** GET /api/v1/public/locations/cities?limit= — most listings first. */
  export type PublicCitiesResponse = { cities: PublicCity[] };

  /** GET /api/v1/public/locations/cities/:code/areas — most popular first. */
  export type PublicCityAreasResponse = { city: PublicCity; areas: PublicArea[] };

  export type PublicLocationHit = { level: 'city' | 'neighborhood'; id: string; name: string; city: PublicCity };

  /** GET /api/v1/public/locations/search?q= — cities and neighbourhoods only. */
  export type PublicLocationSearchResponse = { hits: PublicLocationHit[] };

  // --- The registration form: POST /api/v1/public/realtor-site/:alias/join ---------------------------

  export type ClientIntakeAnswers = {
    track: ClientIntakeTrack;
    /** Canonical place ids (cities and neighbourhoods) from /public/locations. */
    areaIds: string[];
    /** Places typed by hand that the search could not find. */
    areaTexts?: string[];
    propertyTypes?: ClientIntakePropertyType[];
    /** 1-6; 6 means "6 and up". */
    roomsMin?: number;
    roomsMax?: number;
    sizeMin?: number;
    /** Shekels: the purchase price, or the monthly rent. */
    budgetMax?: number;
    budgetMin?: number;
    mustHaves?: ClientIntakeMustHave[];
    notes?: string;
    purpose?: ClientIntakePurpose;
    needsToSell?: boolean;
    wantsSellingHelp?: boolean;
    financing?: ClientIntakeFinancing;
    approvedAmount?: number;
    equity?: ClientIntakeEquity;
    timing?: ClientIntakeTiming;
    /** 'immediate', 'flexible' or a month, YYYY-MM. */
    entry?: string;
    lease?: ClientIntakeLease;
    furnishing?: ClientIntakeFurnishing;
    pets?: ClientIntakePet[];
  };

  export type ClientIntakeContact = {
    name: string;
    /** As typed: an Israeli number, local or international. */
    phone: string;
    email?: string;
    channel?: ClientIntakeChannel;
    bestTimes?: ClientIntakeBestTime[];
  };

  export type ClientIntakeJoinRequest = {
    answers: ClientIntakeAnswers;
    contact: ClientIntakeContact;
    /** The client agreed that the realtor may contact them about properties. */
    consent: true;
    /** One per form session: a retried send answers the same and never makes a second lead. */
    idempotencyKey: string;
    /** How long the form was open before the send, in ms (the page's own clock): seconds is not a person. */
    fillMs: number;
    /** A field people never see and leave empty. */
    website?: string;
  };

  /** A listing on the realtor's site that suits the answers. `topMatch`: in the matcher's green band. */
  export type PublicIntakeSuggestion = PublicListingCard & { topMatch: boolean };

  export type ClientIntakeJoinResponse = { ok: true; suggestions: PublicIntakeSuggestion[] };
}

export const TRAINING_DOCS_COLLECTION_NAME = 'training-docs';
export type TrainingDoc = Partial<BaseDocument> & {
  title: string;
  description: string; // markdown format
  videoUrl?: string;
  tags: string[];
  sortOrder: number; // for controlling display order
};

export namespace ExtensionFirebaseSchema {
  export type WhatsappOperation =
    | 'SEND_MESSAGE'
    | 'GET_CONTACTS'
    | 'GET_GROUPS'
    | 'CHECK_STATUS'
    | 'GET_AVATAR'
    | 'GET_MESSAGES';

  /**
   * `cancelled`: its creator stopped waiting before any tab took it (protocol v2: a job is taken with a
   * claim transaction, and a cancelled job can never be taken). `expired`: the same, written by creators
   * before v2.
   */
  export type OperationStatus = 'pending' | 'inProgress' | 'success' | 'failed' | 'expired' | 'cancelled';

  /**
   * Why an operation failed, written by extensions from 1.0.93, for the code that decides what to do next.
   * NOT_READY guarantees nothing was sent (safe to queue again); UNKNOWN_OUTCOME means it may have been.
   */
  export type OperationErrorCode = 'NOT_READY' | 'NUMBER_NOT_ON_WHATSAPP' | 'MEDIA_FETCH_FAILED' | 'SEND_FAILED' | 'UNKNOWN_OUTCOME';

  /** What a WhatsApp tab can do right now, as the extension (1.0.93+) reads it from WhatsApp itself. */
  export type ExtensionTabState = 'loading' | 'ready' | 'logged_out' | 'conflict' | 'offline' | 'asleep' | 'closed';

  export interface SendMessageOperationRequestDetails {
    toPhoneNumber: string;
    message: string;
    mediaLink?: string;
    mediaBase64?: string;
    customFileName?: string;
    /**
     * Set on a RETRY of a message that may already have gone out (Unix seconds of the first attempt). The
     * extension (1.0.93+) looks for a message of ours with the same text in that chat since then, and reports
     * success instead of sending it twice. Older extensions ignore it.
     */
    dedupeSince?: number;
  }

  export interface GetAvatarRequestDetails {
    phone: string;
    imgFull?: boolean;
  }

  export interface GetContactsRequestDetails {
    shouldReturnSlimResponse?: boolean;
    getOnlyContacts?: boolean;
    /**
     * Only the contacts saved in the phone's address book. Absent or false returns every named
     * contact record WhatsApp Web holds, saved or not. An extension older than the build that
     * introduced this (1.0.92) ignores it and returns everything, so the caller still filters
     * on each row's `isMyContact`.
     */
    onlySavedContacts?: boolean;
  }

  export interface GetGroupsRequestDetails {
    // No specific parameters needed for now, but keeping the interface for future extensibility
  }

  export interface GetMessagesRequestDetails {
    /**
     * The chat to extract. Accepts a serialized WhatsApp id ("972501234567@c.us",
     * "120363000000000000@g.us") or a bare phone number ("+972501234567") which the
     * extension normalizes to a private chat id.
     */
    chatId: string;
    /** Max messages to return, newest first. Extension defaults to 200 and hard caps at 5000. */
    limit?: number;
    /** Unix timestamp in SECONDS. The extension stops paging back once it passes this point. */
    sinceTimestamp?: number;
    /**
     * Open the chat in the WhatsApp UI before reading. ON by default - a chat that was never
     * opened this session holds only the single chat-list preview message, so without this
     * there is nothing to page back through. The previously active chat is restored after.
     * Pass false to read strictly what is already in memory.
     */
    openChat?: boolean;
    /**
     * Ask the phone to transfer history for a chat the web client never received
     * (`endOfHistoryTransferType === 0`). Slow and best-effort - the phone pushes messages
     * in asynchronously, so a second call may return more than the first.
     */
    requestHistorySync?: boolean;
  }

  /** Why an extraction returned what it returned - populated on every GET_MESSAGES response. */
  export interface GetMessagesDiagnostics {
    /** Which "load earlier messages" implementation actually worked */
    strategy?: string;
    strategiesTried: string[];
    hasMsgsCollection: boolean;
    endOfHistoryTransferType?: number;
    openedChat: boolean;
    historySyncRequested?: boolean;
    /** Headless (no chat opening) read - which query function was used, and how far it paged */
    headlessStrategy?: string;
    headlessPagingStrategy?: string;
    headlessPagingRounds?: number;
    headlessMessageCount?: number;
    /** The message-related functions this WhatsApp build exposes, for when the above fail */
    availableMessageApis?: string[];
    notes: string[];
  }

  /** One extracted message. Media is described but NOT downloaded (phase 1). */
  export interface ExtractedWhatsappMessage {
    id: string;
    chatId: string;
    /** Unix timestamp in seconds */
    timestamp: number;
    fromMe: boolean;
    /** Serialized id of the sender (group messages) */
    author?: string;
    authorName?: string;
    authorPhoneNumber?: string;
    from?: string;
    to?: string;
    /** WhatsApp message type: chat, image, video, ptt, audio, document, sticker, location, vcard, revoked... */
    type: string;
    subtype?: string;
    body: string;
    caption?: string;
    hasMedia: boolean;
    mediaMimetype?: string;
    mediaFilename?: string;
    mediaSize?: number;
    mediaDurationSeconds?: number;
    isForwarded?: boolean;
    isStarred?: boolean;
    isDeleted?: boolean;
    quotedMessageId?: string;
    quotedBody?: string;
    latitude?: number;
    longitude?: number;
    /** Delivery/read ack as reported by WhatsApp */
    ack?: number;
  }

  /**
   * The GET_MESSAGES payload. Because the content script strips `operationResponseObject`
   * before writing to Firestore, this always arrives via `operationResponseUrl`.
   */
  export interface GetMessagesResponse {
    chatId: string;
    chatName?: string;
    isGroup: boolean;
    messageCount: number;
    /** Unix seconds of the oldest / newest message returned */
    oldestTimestamp?: number;
    newestTimestamp?: number;
    /** True when WhatsApp reported there is nothing older left to load */
    reachedStartOfChat: boolean;
    /** How many "load earlier messages" rounds the extension performed */
    loadRounds: number;
    diagnostics?: GetMessagesDiagnostics;
    messages: ExtractedWhatsappMessage[];
  }

  export type WhatsappExtensionOperation = Partial<BaseDocument> & {
    apiToken: string;
    activeGuid: string;
    operation: WhatsappOperation;
    operationStatus: OperationStatus;
    operationRequestDetails?:
      | SendMessageOperationRequestDetails
      | GetContactsRequestDetails
      | GetGroupsRequestDetails
      | GetMessagesRequestDetails;
    operationResponseText?: string;
    operationResponseUrl?: string;
    operationResponseObject?: any;
    operationError?: string;
    operationFinalUpdateCounter?: number; // Should always be 1
    operationErrorCode?: OperationErrorCode;
    /** Server time the creator queued it (serverTimestamp()). The rules refuse a claim 2 minutes after it. */
    queuedAt?: Date;
    /** The tab (content-script guid) whose claim transaction committed. Only that tab executes the job. */
    claimedBy?: string;
    claimedAt?: Date;
    completedAt?: Date;
    /** Only a tab logged into this WhatsApp number (digits) may take it. */
    targetWaUser?: string;
    /**
     * When the document may be deleted (Firestore TTL on this field). Every reader is done within
     * minutes, and until the extension signs in the collection is readable by anyone, so a kept
     * operation is a kept apiToken. Absent on documents written before 2026-10-16.
     */
    expireAt?: Date;
  };

  // Interface for the WhatsApp Extension Listener document
  export type WhatsappExtensionListener = Partial<BaseDocument> & {
    activeGuid: string;
    apiToken: string;
    /** The lease: the last time this tab said it was there (server time from 1.0.93). */
    forcedUpdatedTime: Date;
    /** From 1.0.93; older extensions write the three fields above, and only while WhatsApp is ready. */
    state?: ExtensionTabState;
    /** WhatsApp's own raw values, for support */
    stateReason?: string;
    stateSince?: Date;
    version?: string;
    /** The WhatsApp number the tab is logged into (digits) */
    waUser?: string | null;
    /** Firestore TTL: a tab that stopped writing is deleted a day after its last lease */
    expireAt?: Date;
  };

  export enum BadgePossibleText {
    LOADING = 'LOADING',
    DISCONNECTED = 'OFF',
    CONNECTED = 'ON',
    KEY = 'KEY'
  }

  // Badge status interface
  export type BadgeStatus = Partial<BaseDocument> & {
    apiToken: string;
    status: BadgePossibleText;
    color: string;
  };

  // Collection path constants
  export const WHATSAPP_OPERATIONS_COLLECTION = 'whatsapp-extension-operations';
  export const WHATSAPP_LISTENERS_COLLECTION = 'whatsapp-extension-listeners';
  export const BADGE_STATUS_COLLECTION = 'whatsapp-extension-badge-status';
}

// ============================================================================
// AI Chat (in-app Claude chat with MCP)
// ============================================================================

export const AI_CHATS_COLLECTION = 'ai-chats';
export const AI_CHAT_MESSAGES_COLLECTION = 'ai-chat-messages';

export type AiChatRole = 'user' | 'assistant';

/**
 * Wire shape for chat messages — used both as the body of the streaming
 * request (`POST /api/v1/chat`) and as the persisted history shape that gets
 * re-sent when continuing a chat. Assistant messages may contain embedded
 * `<artifact>` and `<webot-*>` tags inside `content`; thinking + tool blocks
 * are NOT part of this — they live in `AiChatMessage.trace` separately.
 */
export type ChatMessage = {
  role: AiChatRole;
  content: string;
};

export type AiChat = Partial<BaseDocument> & {
  // userId is the user's email (matches dashboard-users-v2 doc id) so we can
  // query "all chats for this user" without an extra join.
  userId: string;
  title: string;
  // Stays false until the async Haiku title-generation call completes; the UI
  // can show a placeholder ("New chat") and swap once the real title lands.
  titleGenerated: boolean;
  lastMessageAt: Date;
  messageCount: number;
  // Which surface the chat belongs to. Absent (legacy) means 'dashboard'.
  // WhatsApp AI-assistant chats are created with 'whatsapp' so the dashboard
  // list & admin review UI can label them, and so the WhatsApp session logic
  // can tell its own chats apart.
  source?: 'dashboard' | 'whatsapp';
};

export type AiTraceEntry =
  | { kind: 'thinking'; text: string }
  | {
      kind: 'tool_use';
      id?: string;
      name: string;
      serverName?: string;
      input?: unknown;
      // Real MCP execution time (mcp_tool_use_stop → matching mcp_tool_result_start).
      durationMs?: number;
    }
  | { kind: 'tool_result'; content: string; isError?: boolean; truncated?: boolean };

export type AiTraceUsage = {
  // meterVersion 2: everything Anthropic billed for the turn — summed over every request the turn made, each read at
  // its final cumulative message_delta. Absent meterVersion (legacy docs): the three prompt-side fields hold only the
  // FIRST model pass of each request, so every pass a tool call added is missing (~58% of the real bill, 2026-10-05).
  // Never mix the two in an analysis — read through isCumulativeUsage / firstPassOf in claudeChatUsage.ts.
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  meterVersion?: number;
  // Requested id of the last request that started. The price key of a single-model turn.
  model?: string;
  // message_start events seen: 1 on a normal turn, more after a pause_turn continuation or a retry.
  requests?: number;
  // Of those, the requests that never sent a final message_delta. Omitted when 0. Their share of the totals is in
  // `unconfirmed`: not known to have been billed in full, or at all.
  incompleteRequests?: number;
  unconfirmed?: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheCreationTokens: number };
  // A subset of outputTokens (never add it to a cost). Omitted when Anthropic did not report it.
  thinkingTokens?: number;
  // These two always sum to cacheCreationTokens. Both omitted when the split is unknown.
  cacheCreation1hTokens?: number;
  cacheCreation5mTokens?: number;
  // The first model pass of the turn's first request: the prompt-cache hit/miss signal the legacy fields used to be.
  firstPass?: { inputTokens: number; cacheReadTokens: number; cacheCreationTokens: number };
  // Only when more than one model served the turn (capacity fallback). Price each entry at its own model.
  byModel?: Record<
    string,
    {
      inputTokens: number;
      outputTokens: number;
      cacheReadTokens: number;
      cacheCreationTokens: number;
      cacheCreation1hTokens?: number;
      cacheCreation5mTokens?: number;
    }
  >;
  // Meter anomalies, sorted: delta_without_start | non_monotonic | prompt_side_from_start | split_unknown.
  flags?: string[];
};

export type AiChatMessage = Partial<BaseDocument> & {
  chatId: string;
  // Denormalized so message-level queries can scope by user without re-reading the chat doc.
  userId: string;
  role: AiChatRole;
  sequence: number;
  // For user messages: the prompt. For assistant messages: the user-visible text
  // (with embedded <artifact> tags). This is what we re-send to Claude on resume —
  // tool_use/tool_result blocks are intentionally NOT replayed.
  content: string;
  // Reasoning trace — UI-only, never re-sent to Claude. Assistant messages only.
  trace?: AiTraceEntry[];
  usage?: AiTraceUsage;
  stopReason?: string;
  // Wall-clock duration of the whole turn (first event → final_message).
  durationMs?: number;
};

// ============================================================================
// User Feedback
// ============================================================================

export const USER_FEEDBACKS_COLLECTION_NAME = 'user-feedbacks';

export type UserFeedback = Partial<BaseDocument> & {
  // Email of the user who submitted (matches dashboard-users-v2 doc id).
  userId: string;
  // Free-text body of the feedback.
  description: string;
  // Which part of the product the feedback is about (e.g. 'ai-chat'). Free
  // string so we can introduce new tags without a schema change.
  featureTag: string;
  // Free-form bag of context the submitter's screen had at submit time. Kept
  // unstructured on purpose so each featureTag can attach whatever's useful
  // (e.g. ai-chat sends chatId, lastAssistantMessageId, messageCount, locale,
  // chatPreferences) without needing a schema bump per surface.
  metadata?: Record<string, unknown>;
};

// ============================================================================
// Agency ambassador offer (personal upsell campaign, until 2026-12-01)
// ============================================================================

export const AGENCY_AMBASSADOR_OFFERS_COLLECTION_NAME = 'agency-ambassador-offers';
export const AGENCY_AMBASSADOR_CAMPAIGN_ID = 'agency-ambassador-2026-12';

// One doc per eligible customer; doc id = the customer's dashboard-users-v2 doc id (their email).
// Written only by the server's adHocOperations/agency-ambassadors/seed-offers.ts from the list the founder approved.
// The dashboard never reads it directly (no client rule) — it goes through GET /api/v1/agency-offer.
export type AgencyAmbassadorOffer = Partial<BaseDocument> & {
  userId: string;
  campaignId: string;
  // Sales-map office id, and the validated office name shown to this customer (e.g. 'RE/MAX באר שבע').
  agencyId: string;
  agencyName: string;
  // Greeting name; '' when the account name is not a person (then the copy greets without a name).
  firstName: string;
  // Office size when the list was built: distinct agents, and how many of them pay for Webot.
  officeAgents: number;
  officePaying: number;
  // ISO-8601 with offset; the offer is live while now < expiresAt ('2026-12-01T00:00:00+02:00').
  expiresAt: string;
};

// ============================================================================
// Maskyoo SMS (sms.deals gateway)
// ============================================================================

export const MASKYOO_SMS_MESSAGES_COLLECTION = 'maskyoo-sms-messages';

// One doc per outbound SMS. Doc id = sms.deals `Message_id` so the delivery
// callback (which echoes only Message_id) can merge into the same doc.
// Send fields are written when sms.deals accepts the send; delivery fields are
// filled later when the callback hits /api/v1/maskyoo-sms/webhook. Both writes
// use { merge: true } so callbacks and sends can arrive in any order.
export type MaskyooSmsMessage = Partial<BaseDocument> & {
  // sms.deals message id (also used as the Firestore doc id).
  messageId: string;
  // Destination phone in the local Israeli format sms.deals returned.
  destination: string;
  // Sender as configured on the send call (phone, *XXXX speed-dial, or Latin name).
  sender: string;
  // Body sent to the gateway.
  message: string;
  // Public URL we asked sms.deals to call back on.
  callbackUrl: string;
  // Status from the synchronous send response, e.g. "Message in Action".
  sendStatus: string;
  // sms.deals-reported send time (their string, e.g. "2026-05-13 16:25:00").
  sendTime: string;
  // serverTimestamp when our backend persisted the send.
  sentAt?: Date;
  // === Delivery callback fields (filled on /webhook hit) ===
  // Sender echoed back by the callback.
  deliveryFrom?: string;
  // Destination echoed back by the callback.
  deliveryTo?: string;
  // Final delivery status from the callback. Doc-defined values:
  // Success | Recipient "Afllalo" Blocked | Destination MT block | System Error
  // | Missing Message body | Invalid Destination | Expired | blacklist
  deliveryStatus?: string;
  // serverTimestamp when we received the delivery callback.
  deliveryReceivedAt?: Date;
};

// ============================================================================
// Facebook page/group scraping (see docs/facebook-scraping-design.md)
// ============================================================================

export const FACEBOOK_SOURCES_COLLECTION_NAME = 'facebook-sources';
export const FACEBOOK_POSTS_COLLECTION_NAME = 'facebook-posts';

export type FacebookSourceType = 'page' | 'group';

// One doc per monitored Facebook page/group. Sources are shared — multiple
// customers can subscribe to the same group; we scrape once and fan out at
// read time.
export type FacebookSource = Partial<BaseDocument> & {
  // Normalized URL, e.g. https://www.facebook.com/groups/123456789
  url: string;
  sourceType: FacebookSourceType;
  // page_name / group_name — filled after the first successful scrape.
  name?: string;
  // Pause switch — inactive sources are skipped by the cron job.
  active: boolean;
  // Dashboard users (emails) who requested this source.
  subscriberUserIds: string[];
  // Org-level subscriptions (all users of the org see the feed).
  organizationIds?: string[];
  lastScrapedAt?: Date;
  lastScrapeStatus?: 'success' | 'error' | 'empty';
  lastScrapeError?: string;
  // Consecutive runs that returned 0 records — feeds the outage alert.
  consecutiveEmptyRuns?: number;
  // Consecutive runs that errored — feeds the per-source failure alert.
  consecutiveErrorRuns?: number;
  // From the scraper output ("Public group") — private groups are unsupported
  // and must be rejected at onboarding.
  groupCategory?: string;
  memberCount?: number;
  // Operator-configured area context for AI analysis — same shape as
  // ProcessedListingData.address. A group usually implies a city (e.g. the
  // Haifa rentals group → { city: 'חיפה' }); the analysis uses it to resolve
  // ambiguous/partial addresses in posts.
  area?: Partial<ProcessedListingAddress>;
  // Add-time public/private probe result. 'validating' until the first probe
  // or scrape confirms the source; 'invalid' when the URL isn't a reachable
  // PUBLIC group/page (private groups are unsupported).
  validationStatus?: 'validating' | 'valid' | 'invalid';
  validationError?: string;
};

export type FacebookPostAttachment = {
  id?: string;
  // Photo | Video | ... (BrightData's attachment type string)
  type?: string;
  url?: string;
  // Some records carry the media URL here instead of / in addition to `url`.
  attachmentUrl?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
};

// A single scraped comment. Commenter fields are limited to what the
// buyer-lead product needs (display name + public profile URL + text as
// published) — numeric user IDs are deliberately not stored.
export type FacebookScrapedComment = {
  commentId: string;
  // Set by the scraper for correlation; may be stripped before embedding on
  // the post doc where they are redundant.
  postId?: string;
  postUrl?: string;
  commentText?: string;
  commenterName?: string;
  commenterUrl?: string;
  dateCreated?: Date;
  numLikes?: number;
  numReplies?: number;
  isReply?: boolean;
  parentCommentId?: string;
};

// What a Facebook post fundamentally IS:
//   listing_offer  — someone offering a property (for rent / sale / sublet / roommates)
//   listing_wanted — someone LOOKING for a property ("מחפש/ת דירה...")
//   other          — everything else (discussions, services, spam)
export const facebookPostKinds = ['listing_offer', 'listing_wanted', 'other'] as const;
export type FacebookPostKind = typeof facebookPostKinds[number];

// Realtor vs private person — the key distinction on realtor-heavy groups.
// Every analyzed POSTER gets one; commenters get one when inferable.
export const facebookPersonKinds = ['realtor', 'private'] as const;
export type FacebookPersonKind = typeof facebookPersonKinds[number];

// AI profile of a person appearing on Facebook (post author or commenter).
// customerType reuses the platform-wide taxonomy (מוכר/קונה/שוכר/משכיר/מתווך…)
// so Facebook-sourced people align with call-sourced contacts. Queryability
// note: `analysis.poster.*` is dot-notation-queryable in Firestore, but
// `commenterAnalyses` is an ARRAY — commenter-level querying needs derived
// doc-level flags or a separate leads collection (phase-3 decision).
export type FacebookPersonProfile = {
  name?: string;
  customerType?: CustomerType;
  // realtor when the person is (or clearly behaves like) an agent — agency ad
  // style, בלעדיות, office branding — even if not stated outright.
  personKind?: FacebookPersonKind;
  // Phone number found in the text (as written; normalization happens later).
  phone?: string;
  // Free-text Hebrew: what this person wants. For commenters this is INFERRED
  // from the post context too — someone asking details on a sale post of a
  // Kiryat Bialik cottage is a buyer interested in that kind of property.
  lookingFor?: string;
  // For seekers: budget in ILS and desired rooms range, when stated.
  budget?: number;
  roomsMin?: number;
  roomsMax?: number;
};

// AI classification of a post (phase 2). Listing fields are flat/typed so
// they are directly queryable in Firestore. Free-text values are stored as
// written in the post (Hebrew). For listing_wanted posts the listing fields
// describe what is SOUGHT, not an actual property.
export type FacebookPostAnalysis = {
  postKind: FacebookPostKind;
  // 0..1
  confidence: number;
  transactionType?: TransactionType;
  isCommercialProperty?: boolean;
  // Raw address parts as written in the post — downstream exact-location
  // resolution goes through enrichAddresses() (OpenAI + Algolia), which takes
  // exactly {city, street, neighborhood}.
  city?: string;
  neighborhood?: string;
  street?: string;
  houseNumber?: number;
  // Free-text location description when no structured address is given
  // ("5 דקות מהטכניון", "צמוד למסדה").
  locationDescription?: string;
  roomsCount?: number;
  floor?: number;
  squareMeter?: number;
  // ILS. Rent → thousands; sale → hundreds of thousands/millions.
  price?: number;
  // From the platform propertyTypes taxonomy where possible (דירה, בית פרטי/קוטג', פנטהאוז…).
  propertyType?: string;
  // Amenity flags — mirror the call-enrichment schema for cross-source querying.
  hasParking?: boolean;
  hasElevator?: boolean;
  hasTerrace?: boolean;
  hasGarden?: boolean;
  hasStorage?: boolean;
  hasProtectedSpace?: boolean;
  hasAirConditioning?: boolean;
  isRenovated?: boolean;
  includesFurniture?: boolean;
  hasHousingUnit?: boolean;
  hasBars?: boolean;
  // Rental-specific signals common in group posts.
  billsIncluded?: boolean; // "כולל חשבונות"
  noBroker?: boolean; // "ללא תיווך"
  // As written ("מיידי", "1.9"…).
  entryDate?: string;
  isImmediateEntrance?: boolean;
  // The post author's profile (the seller/landlord for offers, the seeker for wanted posts).
  poster: FacebookPersonProfile;
  // One-line Hebrew summary for the feed UI.
  summary?: string;
  // What the photos show / notable condition info (Hebrew).
  imageInsights?: string;
};

// The subset of a post's analysis that identifies WHICH property it offers —
// the input to the prolific-poster realtor rule (facebookPersonKind.service),
// which counts how many different houses one author has offered. Deliberately
// excludes price (it moves between reposts) and propertyType (its synonyms
// split one flat in two).
export type FacebookPropertyFields = Pick<
  FacebookPostAnalysis,
  'city' | 'neighborhood' | 'street' | 'houseNumber' | 'roomsCount' | 'squareMeter'
>;

// Commenter intent relative to the post:
//   interested_in_listing — potential tenant/buyer on an offer post
//   offering_property     — landlord/realtor offering a property (common on seeker posts)
export const facebookCommenterIntents = [
  'interested_in_listing',
  'offering_property',
  'asking_details',
  'price_negotiation',
  'tagged_friend',
  'other'
] as const;
export type FacebookCommenterIntent = typeof facebookCommenterIntents[number];

// AI classification of one commenter (phase 2). The lead direction depends on
// the post kind — see FacebookCommenterIntent.
export type FacebookCommenterAnalysis = FacebookPersonProfile & {
  // Joins back to the raw comment in FacebookPost.comments.
  commentId?: string;
  commenterName: string;
  commenterUrl?: string;
  commentText: string;
  intent: FacebookCommenterIntent;
  // 0..1
  confidence: number;
  // Why the AI classified it this way.
  evidence?: string;
};

// Fields produced by the scraping layer (facebookScraper.service.ts) —
// everything the FacebookPost doc contains before analysis/bookkeeping.
export type FacebookPostScrapedFields = {
  postId: string;
  postUrl: string;
  // Which facebook-sources url produced this post.
  sourceUrl: string;
  sourceType: FacebookSourceType;
  // Post text (Hebrew supported — raw text).
  content?: string;
  datePosted?: Date;
  // user_username_raw — the poster (the seller for listing posts).
  authorName?: string;
  authorUrl?: string;
  numComments?: number;
  numShares?: number;
  numLikes?: number;
  // Post | Reel ...
  postType?: string;
  isSponsored?: boolean;
  attachments: FacebookPostAttachment[];
  pageName?: string;
  groupName?: string;
  groupCategory?: string;
};

// One doc per scraped post. Doc id = `fb-<postId>` → idempotent upserts and
// natural dedupe across runs and across sources (crossposts).
export type FacebookPost = Partial<BaseDocument> &
  FacebookPostScrapedFields & {
    firstSeenAt: Date;
    // Bumped when a later snapshot returns the same post again.
    lastSeenAt: Date;
    // Phase 2 — post analysis.
    analysis?: FacebookPostAnalysis;
    analysisStatus?: 'pending' | 'done' | 'failed' | 'skipped';
    // Phase 2b — comments + commenter classification. Comments are embedded
    // (not a subcollection): read together with the post, bulk-analyzed
    // together, and capped at scrape time, so the 1 MB doc limit is not a
    // concern.
    comments?: FacebookScrapedComment[];
    commentsScrapedAt?: Date;
    commenterAnalyses?: FacebookCommenterAnalysis[];
    commentsAnalyzedAt?: Date;
  };

// A "lead" is one PERSON derived from a post — the post's poster, or one of
// its commenters — surfaced to every subscribing dashboard user.
//
// The lead is stored ONCE, shared, in `facebook-lead-index` (one doc per
// post × person), and each user's mutable state lives in a thin overlay doc in
// `facebook-leads`. `FacebookLead` below is the ASSEMBLED VIEW the API returns
// (index entry + that user's state), not a stored shape.
//
// It used to be fan-out-on-write: one full copy of every lead per subscriber.
// That made subscribing to an existing group show NOTHING (a user's copies only
// came into being during a later scrape, and incremental scrapes skip already
// stored posts), and multiplied storage by subscriber count — ~3.4M docs for
// the same 42k posts at 100 subscribers/group. Reading the shared index instead
// makes subscribing instant and free: the entries already exist, and the query
// simply widens to include the newly subscribed sourceId.
export const FACEBOOK_LEADS_COLLECTION_NAME = 'facebook-leads';

// Shared, subscriber-independent lead content. Doc id = `${postId}__${leadKey}`.
export const FACEBOOK_LEAD_INDEX_COLLECTION_NAME = 'facebook-lead-index';

export const facebookLeadStatuses = ['new', 'hidden', 'converted'] as const;
export type FacebookLeadStatus = typeof facebookLeadStatuses[number];

export const facebookLeadRoles = ['poster', 'commenter'] as const;
export type FacebookLeadRole = typeof facebookLeadRoles[number];

/**
 * Scoping key under which a Facebook lead's notes live in the shared
 * `user-notes` collection (stored in the doc's `contactId` field). Shared by
 * the backend persistence layer and the dashboard notes hook so both agree —
 * DO NOT change the format without migrating existing notes.
 */
export const facebookLeadNoteKey = (lead: {
  postId: string;
  role: FacebookLeadRole;
  commentId?: string;
}): string => {
  const sanitize = (part: string): string => part.replace(/[/.]/g, '_');
  const leadKey = lead.role === 'poster' ? 'poster' : `c-${sanitize(lead.commentId ?? 'unknown')}`;
  return `fb-lead-${sanitize(lead.postId)}-${leadKey}`;
};

/**
 * Facebook notes are scoped to ONE of three things, keyed independently so a
 * note on a person, on their post, and on a specific comment stay separate:
 *   - person  → notes on the lead/person (stable across all their posts)
 *   - post    → notes on a specific post
 *   - comment → notes on a specific comment
 * The resulting key is stored in `UserNotesDocument.facebookNoteScope` (NOT
 * `contactId`, which stays a real international phone number). Returns
 * `undefined` when there's no usable id (e.g. a person with neither URL nor
 * name) so callers can hide the notes control.
 */
export type FacebookNoteScope =
  | { kind: 'person'; personUrl?: string; personName?: string }
  | { kind: 'post'; postId: string }
  | { kind: 'comment'; commentId?: string };

export const facebookNoteScopeKey = (scope: FacebookNoteScope): string | undefined => {
  const sanitize = (part: string): string => part.replace(/[/.]/g, '_');
  switch (scope.kind) {
    case 'person':
      if (scope.personUrl) return `fb-person-url-${sanitize(scope.personUrl)}`;
      if (scope.personName) return `fb-person-name-${sanitize(scope.personName)}`;
      return undefined;
    case 'post':
      return scope.postId ? `fb-post-${sanitize(scope.postId)}` : undefined;
    case 'comment':
      return scope.commentId ? `fb-comment-${sanitize(scope.commentId)}` : undefined;
  }
};

/**
 * The assembled lead the API returns: one shared `FacebookLeadIndexEntry`
 * joined with the requesting user's `FacebookLeadState`. NOT a stored document
 * — nothing writes a doc of this shape. `id` stays
 * `${userId}__${postId}__${leadKey}` so the existing /leads/:id routes and note
 * keys are unchanged.
 */
export type FacebookLead = Partial<BaseDocument> & {
  // Subscriber (feed owner) — the user this assembled view was built for.
  userId: string;
  // Copied from the source — context for org-shared lead notes.
  organizationIds?: string[];
  // Source (facebook-sources) this lead came from.
  sourceId: string;
  sourceUrl: string;
  sourceName?: string;
  // The post the person appeared on.
  postId: string;
  postUrl: string;
  role: FacebookLeadRole;
  // Present for commenter leads — joins to FacebookPost.comments / commenterAnalyses.
  commentId?: string;
  // Facebook profile URL of the person (poster authorUrl or commenter commenterUrl).
  personUrl?: string;
  personName?: string;

  // --- Denormalized queryable fields (from the post analysis / person profile) ---
  customerType?: CustomerType;
  personKind?: FacebookPersonKind;
  // Phone as written in the post/comment text (normalized on convert-to-contact).
  phone?: string;
  postKind: FacebookPostKind;
  transactionType?: TransactionType;
  city?: string;
  price?: number;
  roomsCount?: number;
  propertyType?: string;
  // What the person is looking for (Hebrew) — for commenter leads, inferred from the post.
  lookingFor?: string;
  // One-line Hebrew summary of the post (feed subtitle).
  summary?: string;
  // For commenter leads — the raw comment text (the evidence).
  commentText?: string;
  // First post photo (Facebook CDN URL — ephemeral) + total photo count, so
  // the feed card shows a thumbnail without a per-lead detail fetch.
  coverImageUrl?: string;
  imageCount?: number;
  // Total comments on the underlying post — shown on poster leads in the UI.
  numComments?: number;

  // --- Per-subscriber state (from the user's FacebookLeadState overlay) ---
  status: FacebookLeadStatus;
  // The contact id (international phone) created when this lead was converted.
  convertedContactId?: string;
  // The underlying post's date — displayed, and the feed's secondary sort key.
  leadCreatedAt: Date;
  // When WE first indexed this lead. The feed's PRIMARY sort key — see
  // FacebookLeadIndexEntry.discoveredAt for why it can't be leadCreatedAt.
  discoveredAt: Date;
  /**
   * How many posts this lead represents. 1 for a normal lead; >1 when the same
   * person re-posted the same property and we folded the reposts together.
   * The UI shows it so a collapsed lead never looks like missing data.
   */
  repostCount?: number;
  /** datePosted of the newest post in the group, when repostCount > 1. */
  lastPostedAt?: Date;
};

/**
 * One shared lead: a person on a post, stored once regardless of how many
 * users subscribe to the source. Doc id = `${postId}__${leadKey}` via
 * `facebookLeadIndexDocId`, so re-scraping a post idempotently refreshes it.
 */
export type FacebookLeadIndexEntry = Partial<BaseDocument> & {
  // Source (facebook-sources) this lead came from. The feed scopes on this.
  sourceId: string;
  sourceUrl: string;
  sourceName?: string;
  // Copied from the source — context for org-shared lead notes.
  organizationIds?: string[];
  // The post the person appeared on.
  postId: string;
  postUrl: string;
  role: FacebookLeadRole;
  // Present for commenter leads — joins to FacebookPost.comments / commenterAnalyses.
  commentId?: string;
  // Facebook profile URL of the person (poster authorUrl or commenter commenterUrl).
  personUrl?: string;
  personName?: string;

  // --- Denormalized queryable fields (from the post analysis / person profile) ---
  customerType?: CustomerType;
  personKind?: FacebookPersonKind;
  // Phone as written in the post/comment text (normalized on convert-to-contact).
  phone?: string;
  postKind: FacebookPostKind;
  transactionType?: TransactionType;
  city?: string;
  price?: number;
  roomsCount?: number;
  propertyType?: string;
  // What the person is looking for (Hebrew) — for commenter leads, inferred from the post.
  lookingFor?: string;
  // One-line Hebrew summary of the post (feed subtitle).
  summary?: string;
  // For commenter leads — the raw comment text (the evidence).
  commentText?: string;
  // First post photo (permanent GCS URL) + total photo count, so the feed card
  // shows a thumbnail without a per-lead detail fetch.
  coverImageUrl?: string;
  imageCount?: number;
  // Total comments on the underlying post — shown on poster leads in the UI.
  numComments?: number;

  /**
   * Poster leads only: the normalized display NAME of the person, used only
   * to gather their existing leads when deciding whether a new post is a repost
   * of one of them. NOT a property id — sameness is decided pairwise by
   * `isSameListing`, because the address fields are too unevenly extracted for
   * any single key to work.
   *
   * Realtors repost constantly: on prod this folds ~11.9k of 26.1k listing
   * posts (46%) into existing leads.
   */
  leadPersonKey?: string;
  // Address fields kept on the entry so repost matching needs no post lookup.
  street?: string;
  houseNumber?: number;
  squareMeter?: number;
  /** Every post merged into this lead. Length is the repost count. */
  postIds?: string[];
  /** datePosted of the most recent post in the group — the content shown. */
  lastPostedAt?: Date;
  // The underlying post's date. Displayed, and the secondary sort key.
  leadCreatedAt: Date;
  /**
   * When we first wrote this entry — the feed's PRIMARY sort key, write-once.
   *
   * Sorting the feed on `leadCreatedAt` (the POST's date) buries exactly the
   * leads realtors care about most. A commenter lead is only discovered when
   * the comment pass runs — a median 44h after the post — yet it inherits the
   * post's timestamp, so it is born ~2 days stale and never reaches the top of
   * a feed carrying ~1,400 posts/day. Buyers in a for-sale group come almost
   * entirely from comments, which is why a realtor with 128 buyer leads could
   * see 4. `discoveredAt` puts a freshly-found commenter where it belongs.
   */
  discoveredAt: Date;
};

/**
 * One user's mutable state for one shared lead. Written ONLY when the user acts
 * (hide / convert) — an untouched lead has no doc at all, which is what keeps
 * subscribing free. Doc id = `${userId}__${postId}__${leadKey}`, unchanged from
 * when this collection held full per-user lead copies, so existing hidden and
 * converted leads keep working with no data migration.
 */
export type FacebookLeadState = Partial<BaseDocument> & {
  userId: string;
  postId: string;
  // `poster` or `c-<commentId>` — see `facebookLeadKey`.
  leadKey: string;
  // Denormalized so admin/debug tooling can count acted-on leads per source.
  sourceId?: string;
  status: FacebookLeadStatus;
  // The contact id (international phone) created when this lead was converted.
  convertedContactId?: string;
  statusUpdatedAt?: Date;
};

/** `poster` or `c-<commentId>` — the per-post half of every lead id/key. */
export const facebookLeadKey = (lead: { role: FacebookLeadRole; commentId?: string }): string =>
  lead.role === 'poster' ? 'poster' : `c-${(lead.commentId ?? 'unknown').replace(/[/.]/g, '_')}`;

/** Doc id in `facebook-lead-index` — shared, so deliberately NOT user-scoped. */
export const facebookLeadIndexDocId = (postId: string, leadKey: string): string =>
  `${postId.replace(/[/.]/g, '_')}__${leadKey}`;

/**
 * Doc id in `facebook-leads` (the per-user state overlay) AND the lead's API id.
 * Deliberately `<user>__<indexDocId>`, which is byte-identical to the ids the
 * old per-user fan-out wrote — so every already-hidden and already-converted
 * lead keeps working with no data migration. It also makes the id parseable
 * without splitting on `__` (a comment id may contain one): strip the user
 * prefix and what remains IS the shared index doc id. See `facebookLeadIndexIdFromLeadId`.
 */
export const facebookLeadStateDocId = (userId: string, postId: string, leadKey: string): string =>
  `${userId.replace(/[/.]/g, '_')}__${facebookLeadIndexDocId(postId, leadKey)}`;

/**
 * The shared index doc id embedded in a lead API id, or `undefined` when the id
 * does not belong to `userId`. This IS the ownership check — a lead id that
 * doesn't carry the caller's prefix can never resolve.
 */
export const facebookLeadIndexIdFromLeadId = (
  userId: string,
  leadId: string
): string | undefined => {
  const prefix = `${userId.replace(/[/.]/g, '_')}__`;
  return leadId.startsWith(prefix) && leadId.length > prefix.length
    ? leadId.slice(prefix.length)
    : undefined;
};

// ---- Facebook leads REST request/response shapes (/api/v1/facebook) ----
// Declared here (not in REST_schema) because they reference the Facebook
// domain types above; the REST_schema namespace sits earlier in the file.

// A source as the dashboard sees it (the subscriber's view of a shared
// facebook-sources doc). Omits internal fields like subscriberUserIds.
export type FacebookSourceView = {
  id: string;
  url: string;
  sourceType: FacebookSourceType;
  name?: string;
  active: boolean;
  area?: Partial<ProcessedListingAddress>;
  lastScrapedAt?: Date;
  lastScrapeStatus?: 'success' | 'error' | 'empty';
  groupCategory?: string;
  memberCount?: number;
  validationStatus?: 'validating' | 'valid' | 'invalid';
  validationError?: string;
};

export type GetFacebookSourcesResponse = {
  sources: FacebookSourceView[];
};

export type AddFacebookSourceRequest = {
  url: string;
  area?: Partial<ProcessedListingAddress>;
};

export type AddFacebookSourceResponse = {
  source: FacebookSourceView;
};

// Add-flow step 1 — is this group already monitored by someone?
export type CheckFacebookSourceRequest = {
  url: string;
};

// `exists: true` → the caller was subscribed to the existing shared source
// (returned as `source`); `exists: false` → new group, prompt for an area and
// POST /sources to create it.
export type CheckFacebookSourceResponse = {
  exists: boolean;
  source?: FacebookSourceView;
};

// POST /facebook/sources/import — "ייבוא מהקבוצות שלי": groups picked from the list of groups the realtor joined on
// Facebook, read by the Webot extension in their own browser (only the picked ones are sent). Up to 50 a call; `area`
// is the shared analysis context of the NEW groups (an existing group keeps its own). A new group is probed, never
// scraped at import: its first scan is the realtor's next on-demand refresh.
export type ImportFacebookSourcesRequest = {
  groups: Array<{ fbGroupId: string; slug?: string; name?: string }>;
  area: Partial<ProcessedListingAddress> & { city: string };
};

export type ImportFacebookSourcesResponse = {
  // Groups Webot already monitored: subscribed now, their leads are in the feed.
  subscribed: number;
  // New groups: being checked, scanned on the next refresh.
  created: number;
  // Groups the caller already followed.
  already: number;
  sources: FacebookSourceView[];
};

export type UpdateFacebookSourceRequest = {
  // `active` (pause) is intentionally not settable per-user — the source is
  // shared; pausing would stop the cron for every subscriber.
  area?: Partial<ProcessedListingAddress>;
};

/**
 * Every filter here is applied SERVER-side. It used to be that the dashboard
 * fetched the 200 newest leads across all groups and filtered them in the
 * browser, which silently returned nothing whenever the match lay outside that
 * window — for a user with 24k leads across 57 groups the window spanned 77
 * minutes, so filtering by any one group showed 0 results while the group held
 * hundreds of leads. Do not move filtering back to the client.
 */
export type GetFacebookLeadsRequest = {
  customerType?: CustomerType;
  // Defaults to hiding `hidden` + `converted`; pass explicitly to override.
  status?: FacebookLeadStatus;
  // Must be one of the caller's subscribed sources; ignored otherwise.
  sourceId?: string;
  personKind?: FacebookPersonKind;
  role?: FacebookLeadRole;
  // Include leads the user hid. `converted` leads are always excluded unless
  // `status: 'converted'` is requested explicitly.
  includeHidden?: boolean;
  minRooms?: number;
  minPrice?: number;
  maxPrice?: number;
  // Free-text match over person name / summary / lookingFor / city / comment.
  search?: string;
  // ISO date — only leads discovered since then.
  since?: string;
  // Opaque cursor (`<discoveredAt ISO>|<indexDocId>`) for pagination.
  cursor?: string;
  limit?: number;
};

export type GetFacebookLeadsResponse = {
  leads: FacebookLead[];
  nextCursor?: string;
};

// Full evidence panel for one lead: the lead + its underlying post.
export type GetFacebookLeadDetailResponse = {
  lead: FacebookLead;
  post?: FacebookPost;
};

export type SetFacebookLeadStatusRequest = {
  status: FacebookLeadStatus;
};

export type ConvertFacebookLeadRequest = {
  // The contact the frontend created/updated (from the prefilled dialog).
  // The backend records the link on the lead and marks it converted.
  contactId: string;
};

export type GetFacebookLeadNotesResponse = {
  notes: UserNote[];
};

// A person's full history: every lead (poster posts + commenter comments)
// derived from the same Facebook person, for the person side panel.
export type GetFacebookPersonHistoryRequest = {
  personUrl?: string;
  personName?: string;
};

export type GetFacebookPersonHistoryResponse = {
  leads: FacebookLead[];
};

export type AddFacebookLeadNoteRequest = {
  note: string;
  // Present + non-empty → share the note with these orgs; absent → personal.
  organizationIds?: string[];
};

// ---- Facebook leads: on-demand refresh (POST /facebook/refresh, GET /facebook/refresh/status) ----
// The scheduled scans were retired on 2026-10-06. A subscriber now refreshes their own groups from
// the leads page, once per Asia/Jerusalem day. The request and the allowance live in their own
// collection (`facebook-refresh-requests`), never on the shared lead index.
export const facebookRefreshStates = ['idle', 'queued', 'running', 'done', 'failed'] as const;
export type FacebookRefreshState = typeof facebookRefreshStates[number];

export const facebookRefreshBlockedReasons = [
  'in_progress',
  'quota_used',
  'up_to_date',
  'no_sources',
  'daily_budget_exhausted',
  'not_enabled'
] as const;
export type FacebookRefreshBlockedReason = typeof facebookRefreshBlockedReasons[number];

export const facebookRefreshOutcomes = [
  'started',
  'already_running',
  'quota_used',
  'up_to_date',
  'no_sources',
  'budget_exhausted',
  'not_enabled'
] as const;
export type FacebookRefreshOutcome = typeof facebookRefreshOutcomes[number];

export type FacebookRefreshErrorCode = 'provider_unavailable' | 'worker_stalled' | 'internal';

/** One request's progress. Every instant is an ISO string: the dashboard's request helper does not revive dates. */
export type FacebookRefreshProgress = {
  requestedAt: string;
  startedAt?: string;
  finishedAt?: string;
  phase?: 'posts' | 'comments';
  sourcesPlanned: number;
  sourcesDone: number;
  sourcesFailed: number;
  /** Refreshed moments ago by someone else's request, so not scanned again. */
  sourcesSkippedFresh: number;
  /** Never started: the fleet's daily budget or the run's time budget was reached. */
  sourcesDeferred: number;
  postsScraped: number;
  newLeads: number;
  errorCode?: FacebookRefreshErrorCode;
  /** Failed before any group was scanned, so today's refresh is still available. */
  allowanceReturned?: boolean;
  /**
   * Queued or running: whole seconds since the request was made, on the SERVER's clock. The
   * waiting-time fields below are durations for the same reason: a browser clock that is a
   * minute off must not be able to move an estimate that is itself only minutes long.
   */
  elapsedSeconds?: number;
  /**
   * Queued only: how many requests the worker serves before this one, the one it is running
   * now included. 0 = this request is next. Absent when the server could not tell.
   */
  queueAhead?: number;
  /** Queued only: the server's ESTIMATE of the seconds until a worker starts this request. */
  estimatedStartInSeconds?: number;
  /**
   * Queued or running: the server's ESTIMATE of the seconds until this request finishes,
   * never below 30 while it is going. Built from typical scan times, so it is worded as
   * approximate ("about 6 minutes") and never as a promise.
   */
  estimatedRemainingSeconds?: number;
  /** Running only: the run has outlasted its own estimate by a wide margin, and its worker is alive. */
  runningLong?: boolean;
};

export type FacebookRefreshStatus = {
  /** The caller's latest request: today's, or yesterday's if it is still going. 'idle' = neither. */
  state: FacebookRefreshState;
  /** The ONLY thing the button's enabled state reads. */
  canTrigger: boolean;
  /** Present if and only if `canTrigger` is false. */
  blockedReason?: FacebookRefreshBlockedReason;
  /**
   * `quota_used` / `daily_budget_exhausted`: the next Asia/Jerusalem midnight.
   * `up_to_date`: when the first of the caller's groups is due again — or a recheck a few
   * minutes ahead while one of them is being scanned right now (its first scan, or someone
   * else's refresh).
   */
  nextAvailableAt?: string;
  /** The caller's scannable groups (active, not marked invalid). */
  sourcesTotal: number;
  /** The newest `lastScrapedAt` across the caller's groups, whoever triggered it. */
  lastRefreshedAt?: string;
  request?: FacebookRefreshProgress;
};

export type StartFacebookRefreshResponse = {
  outcome: FacebookRefreshOutcome;
  status: FacebookRefreshStatus;
};

// ---- Facebook sources ADMIN stats (/api/v1/facebook/admin/*) ----
// Webot-admin-only, system-wide view: every source + its scrape health (the
// FULL source doc, unlike the trimmed FacebookSourceView) plus post/lead output
// counts aggregated from facebook-posts / facebook-leads.

export type FacebookSourceAdminStats = {
  // Identity + health (from the source doc).
  id: string;
  url: string;
  sourceType: FacebookSourceType;
  name?: string;
  active: boolean;
  validationStatus?: 'validating' | 'valid' | 'invalid';
  validationError?: string;
  lastScrapedAt?: Date;
  lastScrapeStatus?: 'success' | 'error' | 'empty';
  lastScrapeError?: string;
  consecutiveEmptyRuns?: number;
  consecutiveErrorRuns?: number;
  groupCategory?: string;
  memberCount?: number;
  area?: Partial<ProcessedListingAddress>;
  createdTime?: Date;
  subscriberCount: number;
  // Computed post stats.
  postsTotal: number;
  postsListingOffer: number;
  postsListingWanted: number;
  postsOther: number;
  postsAnalysisFailed: number;
  postsLast24h: number;
  postsLast7d: number;
  lastPostAt?: Date;
  // Computed lead stats.
  leadsTotal: number;
  leadsNew: number;
  leadsHidden: number;
  leadsConverted: number;
  leadsPoster: number;
  leadsCommenter: number;
  leadsLast7d: number;
};

export type GetFacebookAdminSourcesStatsResponse = {
  sources: FacebookSourceAdminStats[];
};

// Drill-down: one source's stats row + its most recent posts/leads. `recentPosts`
// are trimmed server-side (heavy fields like attachments/comments stripped).
export type FacebookAdminSourceDetailResponse = {
  source: FacebookSourceAdminStats;
  recentPosts: FacebookPost[];
  // Shared index rows — the admin view shows what the source PRODUCED, so
  // these carry no per-subscriber status.
  recentLeads: FacebookLeadIndexEntry[];
};

export type SetFacebookSourceActiveRequest = {
  active: boolean;
};
